import { getToken } from '../../services/tokenStore';
import React, { useState, useEffect, useImperativeHandle, forwardRef, useRef, useCallback, useMemo } from 'react';
import { startSpan, endSpan } from '../../telemetry/telemetry';
import {
  Box,
  CircularProgress,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  Divider,
  AppBar,
  Toolbar,
  Grid,
  Menu,
  MenuItem,
  Tooltip,
  ThemeProvider,
  createTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  Select,
  OutlinedInput,
  InputLabel,
  Tab,
  Tabs,
  Alert,
  Backdrop,
  Snackbar,
  Chip,
  List,
  ListItem,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Table as MuiTable,
  TableBody as MuiTableBody,
  TableCell as MuiTableCell,
  TableContainer as MuiTableContainer,
  TableHead as MuiTableHead,
  TableRow as MuiTableRow,
} from '@mui/material';
import {
  RestartAlt as ResetIcon,
  Save as SaveIcon,
  Add as AddIcon,
  FileDownload as ExportIcon,
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  MoreVert as MoreIcon,
  History as HistoryIcon,
  Preview as PreviewIcon,
  Edit as EditIcon,
  Close as CloseIcon,
  Timeline as WorkflowIcon,
  CloudUpload as UploadIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Crop as CropIcon,
  Image as ImageIcon,
  CheckCircle as CheckCircleIcon,
  ErrorOutline as ErrorOutlineIcon,
  ArrowRight as ArrowRightIcon,
  Description as DescriptionIcon,
  PictureAsPdf as PictureAsPdfIcon,
  AutoFixHigh as AutoFixHighIcon,
  AutoAwesome as AutoAwesomeIcon,
  Send as SendIcon,
  ExpandMore as ExpandMoreIcon,
  Spellcheck as SpellcheckIcon,
  ChevronRight as ChevronRightIcon,
  Hub as KnowledgeGraphIcon,
  SmartToy as SmartToyIcon,
} from '@mui/icons-material';

import { Document, Packer, Paragraph, HeadingLevel, Media, Table, TableRow, TableCell, TextRun, SymbolRun, ExternalHyperlink, InternalHyperlink, Bookmark, PageReference, AlignmentType, WidthType, BorderStyle, ImageRun, TableOfContents, Header, Footer, PageNumber, PageOrientation, SectionType, TableLayoutType, Tab as DocxTab, VerticalAlignTable } from 'docx';
import jsPDF from 'jspdf';
import mermaid from 'mermaid';
import { sanitizeMermaidSvg } from '../../utils/sanitizeMermaidSvg';
import { getTrialSchemaImageRejectionReason } from '../../utils/trialSchemaImageQualityCheck';
import { sanitizeRichHtml } from '../../utils/sanitizeRichHtml';
import { getPredefinedSections, filterClinicalIntakeForSection, findPredefinedSection, mergeAppendicesIntoSection12Output } from '../constants/sectionData';
import { generateContent } from '../../services/AIService';
import { validateAndRegenerateSection } from '../../services/universalSectionValidation.service';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import documentEditorService from '../../services/documentEditor.service';
import clinicalIntakeService from '../../services/clinicalIntake.service';
import protocolUsdmCatalogueService from '../../services/protocolUsdmCatalogue.service';
import {
  normalizeUsdmCatalogue,
  buildUsdmCatalogueGlobalContext,
  fetchTemplateLanguageForUsdm,
  mergeTemplateLanguageBlock,
  buildScheduleOfActivitiesHtml,
  scheduleOfActivitiesDataFromFacts,
  buildUsdmSectionStudyData,
  buildUsdmValidationContext,
  extractTitlePageFactsFromUsdm,
} from '../utils/usdmCatalogueContext';
import { resolveUsdmSectionForField } from '../utils/usdmTemplateFieldGeneration';
import { getUsdmProtocolSections } from '../constants/usdmProtocolSectionData';
import { useProject } from '../../context/ProjectContext'; // Keep useProject
import { pdf } from '@react-pdf/renderer';
import ProtocolPdfDocument, { buildProtocolPdfTocEntries } from '../../components/documentEditor/ProtocolPdfDocument';
import { registerPdfFonts } from '../../utils/pdfFontSetup';
import { toSentenceCaseTable } from '../../utils/tableTextFormat';
import { resolveProtocolHeaderFooterInfo, pickHeaderProtocolNumber } from '../../utils/protocolExportHeaderFooter';
import {
  TITLE_PAGE_TABLE_FIELD_KEYS,
  resolveTrialAcronymForTitlePage,
  shouldIncludeTitlePageTableRow,
} from '../../utils/protocolTitlePageFields';
import DocumentCanvas from '../../components/documentEditor/DocumentCanvas';
import PublishedSoaView from '../../components/documentEditor/PublishedSoaView';
import EditSoaDialog from '../../components/documentEditor/EditSoaDialog';
import s9AssessmentScheduleService from '../../services/s9AssessmentSchedule.service';
import SectionValidationIndicator from '../../components/documentEditor/SectionValidationIndicator';
import SectionAssistantButton from '../../components/documentEditor/SectionAssistantButton';
import ClinicalIntakeChangeIndicator from '../../components/ClinicalIntakeChangeIndicator';
import ScrollToTop from '../../components/common/ScrollToTop';
import { API_ENDPOINTS } from '../../config/api';
import axios from '../../config/axios';
import * as styleComplianceService from '../../services/styleCompliance.service';
import {
  soaDataToTableRows,
  stripFootnoteNumberForSoaExport,
  listSoaLegacyPayloadsForExport,
  normalizeScheduleOfActivitiesHeading,
  buildSoaHeaderGroups,
  isSoaSectionBannerRow,
} from '../../utils/soaTableExportModel';
import {
  isLikelyProtocolHeading,
  parseProtocolHeadingLine,
} from '../../utils/protocolHeadingLineUtils';
import administrativeInfoService from '../../services/administrativeInfo.service';
import {
  formatNameAddressPairsForDisplay,
  resolveAdminSponsorFields,
} from '../../utils/administrativeSponsorFields';
import {
  projectListPrimaryLabel,
  resolveProjectSponsorName,
  resolveProjectSponsorProtocolId,
  resolveProjectTechnicalStudyKey,
  resolveProjectTrialAcronym,
  resolveSponsorNameForDisplay,
  resolveSponsorProtocolIdForDisplay,
  resolveTrialAcronymForDisplay,
} from '../../utils/projectIdentityFields';
import { formatRegulatoryIdentifiersForDisplay } from '../../utils/administrativeRegulatoryIdentifiers';
import templateLanguageService from '../../services/templateLanguage.service';
import { saveTrialSchemaDiagram, getTrialSchemaDiagram, saveProtocolXml } from '../../services/project.service';
import protocolRegistrationService from '../../services/protocolRegistration.service';
import { projectService } from '../../services/projectService';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { marked } from 'marked';
import { documentEditorTheme } from '../theme';
import EditableSectionContent from './EditableSectionContent';
import {
  processContentForDisplay,
  convertMarkdownToHTML,
  convertStrayMarkdownHeadingsToHtml,
  repairOrphanedOpeningTags,
  getTableFirstDisplayContent,
  normalizeCollapsedMarkdownTablePipes,
  markdownPlainTextToHtml,
  prepareSectionContentForExport,
  finalizeGeneratedProtocolOutput,
  computeMediaNumberOffsets,
  addFormattingInstructions,
} from '../../utils/formattingUtils';
import {
  isDuplicateSectionHeadingText,
  mergeFragmentedSectionNumbers,
  outlineLevelForProtocolHeadingText,
  outlineLevelFromSectionTitle,
  protocolHeadingStyleForOutlineLevel,
  normalizeProtocolSectionsList,
  sectionNumberPrefixFromTitle,
  canonicalizeM11AppendixSectionTitle,
  getProtocolSectionsForExport,
  isM11AppendixSectionTitle,
  isM11Section1SpecialTitle,
  isMainProtocolSectionTitle,
  isPartialProtocolHeadingLine,
  stripLeadingDuplicateHeadingBlocks,
} from '../../utils/protocolExportContent';
import { richTextToBlocks } from '../../utils/richTextToBlocks';
import { lintProtocolTypographyForSections, mergeTypographyIntoLintResult } from '../../utils/protocolTypographyCompliance';
import { validateExportedDocxBlob } from '../../utils/validateExportedDocxFonts';
import { buildGenerationStylePayload } from '../../utils/generationStyleContext';
import microsoftAuthService from '../../services/microsoftAuth.service';
import microsoftGraphService from '../../services/microsoftGraph.service';
import documentExportService from '../../services/documentExport.service';
import { 
  validateM11SectionsForExport,
  validateTitlePageHeaderConsistency,
  runAllExportValidations,
} from '../../utils/exportValidation';
import { formatValidationErrorMessage } from '../../constants/validationCheckCatalog';
import { runAllAiValidations } from '../../utils/aiValidation';
import {
  validateFontComplianceForExport,
  validateOptionalSectionsForExport,
  validateSubsectionHierarchyForExport,
  validateTableNumberingForExport,
  validateObjectiveHierarchyForExport,
  validateSoALandscapeForExport,
  validateSIUnitsConsistencyForExport,
} from '../../utils/m11Validation';

import WordPreviewDialog from '../../components/forms/WordPreviewDialog';
import MicrosoftWordDialog from '../../components/forms/MicrosoftWordDialog';
import ValidationDialog from '../../components/forms/ValidationDialog';
import ExportValidationDialog from '../../components/forms/ExportValidationDialog';
import validationService from '../../services/validation.service';
import ChangeRequestDialog from '../../components/forms/ChangeRequestDialog';
import ApprovalDialog from '../../components/forms/ApprovalDialog';
import adkWorkflowService from '../../services/adkWorkflow.service';
import style from 'react-syntax-highlighter/dist/esm/styles/hljs/a11y-dark';

const API_BASE_URL = `${(
  process.env.REACT_APP_API_URL ||
  'https://backend-clinical-intake.dizzaroo.com'
).replace(/\/$/, '')}/api`;




// ============================================================================
// DEFAULT FONT CONFIGURATION FOR WORD EXPORT
// ============================================================================
// Aligned to: protocol-style-compliance/Draft 1.0 Style Guide for Regulatory
// Documents_26March 2026.docx.pdf — §4 FORMATTING STANDARDS (C-Title, C-Body,
// Caption, C-Table Text/Header, C-Bullet List, C-Heading 1–5, C-Footnote, §4.6 headers/footers).
// Font: Times New Roman throughout. Sizes in points.
// ============================================================================
const FONT_CONFIG = {
  // C-Title — document title page (§4.1 item 10: 18 pt bold center)
  title: {
    name: 'Times New Roman',
    size: 18,
    bold: true,
    color: '000000',
    lineSpacing: 1.0
  },
  // C-Heading 1 — main section headings (§4.1 item 5: 14 pt bold)
  heading: {
    name: 'Times New Roman',
    size: 14,
    bold: true,
    color: '000000',
    lineSpacing: 1.0
  },
  // C-Heading 2 — subsections (§4.1 item 6: 12 pt bold)
  subheading: {
    name: 'Times New Roman',
    size: 12,
    bold: true,
    color: '000000',
    lineSpacing: 1.0
  },
  // C-Heading 3 (§4.1 item 7: 11 pt bold)
  heading3: {
    name: 'Times New Roman',
    size: 11,
    bold: true,
    color: '000000',
    lineSpacing: 1.0
  },
  // C-Heading 4 (§4.1 item 8: 11 pt bold)
  heading4: {
    name: 'Times New Roman',
    size: 11,
    bold: true,
    color: '000000',
    lineSpacing: 1.0
  },
  // C-Heading 5 (§4.1 item 9: 11 pt, italic yes — size checked in FONT_* lint)
  heading5: {
    name: 'Times New Roman',
    size: 11,
    bold: false,
    italic: true,
    color: '000000',
    lineSpacing: 1.0
  },
  // C-Body Text (§4.1 item 1: 12 pt, line spacing 1.15)
  body: {
    name: 'Times New Roman',
    size: 12,
    bold: false,
    color: '000000',
    lineSpacing: 1.15
  },
  // Caption — table/figure titles (§4.1 item 2 + §4.2: 12 pt TNR; titles bold in Word)
  caption: {
    name: 'Times New Roman',
    size: 12,
    bold: true,
    color: '000000',
    lineSpacing: 1.0
  },
  // C-Table Header (§4.2: 11 pt bold)
  tableHeader: {
    name: 'Times New Roman',
    size: 11,
    bold: true,
    color: '000000',
    lineSpacing: 1.0
  },
  // C-Table Text (§4.1 item 3 + §4.2: 11 pt; min 9 pt if reduced)
  tableCell: {
    name: 'Times New Roman',
    size: 11,
    bold: false,
    color: '000000',
    lineSpacing: 1.0
  },
  // C-Bullet List (§4.1 item 4: 12 pt, line spacing 1.15)
  list: {
    name: 'Times New Roman',
    size: 12,
    bold: false,
    color: '000000',
    lineSpacing: 1.15
  },
  // C-Footnote — table/figure footnotes (§4.2–4.3: 10 pt TNR)
  footnote: {
    name: 'Times New Roman',
    size: 10,
    bold: false,
    color: '000000',
    lineSpacing: 1.0
  },
  // Headers/footers (§4.6: 10 pt TNR)
  headerFooter: {
    name: 'Times New Roman',
    size: 10,
    bold: false,
    color: '000000',
    lineSpacing: 1.0
  }
};

/** Word `w:before` / `w:after` / indents: twentieths of a point (docx-js / OOXML “twips”). */
const DRAFT_PT = (pt) => Math.round(pt * 20);

/** Convert cm to DXA for paragraph indents (1 in = 1440 dxa). */
const DRAFT_CM_TO_DXA = (cm) => Math.round((Number(cm) / 2.54) * 1440);

/** Style guide 4.1: heading space before/after (pt → twips). */
function draftHeadingSpacingTwips(headingLevel) {
  switch (headingLevel) {
    case HeadingLevel.HEADING_1:
      return { before: DRAFT_PT(18), after: DRAFT_PT(6) };
    case HeadingLevel.HEADING_2:
      return { before: DRAFT_PT(12), after: DRAFT_PT(6) };
    case HeadingLevel.HEADING_3:
      return { before: DRAFT_PT(12), after: DRAFT_PT(3) };
    case HeadingLevel.HEADING_4:
      return { before: DRAFT_PT(6), after: DRAFT_PT(3) };
    case HeadingLevel.HEADING_5:
    case HeadingLevel.HEADING_6:
      return { before: DRAFT_PT(6), after: DRAFT_PT(0) };
    default:
      return { before: DRAFT_PT(12), after: DRAFT_PT(6) };
  }
}

/** Map section title outline depth (0 = main) to a Word heading level for spacing. */
function draftHeadingLevelForSectionOutline(outlineLevel) {
  const levels = [
    HeadingLevel.HEADING_1,
    HeadingLevel.HEADING_2,
    HeadingLevel.HEADING_3,
    HeadingLevel.HEADING_4,
  ];
  return levels[Math.max(0, Math.min(levels.length - 1, outlineLevel))];
}

/** Style guide 4.1 items 12–16: TOC 1–5 left indents (cm); TOC6 one extra step. */
const DRAFT_TOC_INDENT_DXA = [
  0,
  0,
  DRAFT_CM_TO_DXA(0.5),
  DRAFT_CM_TO_DXA(1.0),
  DRAFT_CM_TO_DXA(1.5),
  DRAFT_CM_TO_DXA(2.0),
  DRAFT_CM_TO_DXA(2.5),
];

/** Draft 1.0: cell margins 0 pt top/bottom; 0.1 pt left/right (twips: 1 pt = 20 twips). */
const TABLE_CELL_MARGIN_LR_TWIPS = Math.max(1, Math.round(0.1 * 20));
const tableCellMarginsDocx = () => ({
  top: 0,
  bottom: 0,
  left: TABLE_CELL_MARGIN_LR_TWIPS,
  right: TABLE_CELL_MARGIN_LR_TWIPS,
});

/** First numeric group in section title — e.g. "11.2 Title" → "11"; used for Table 11-1 numbering. */
function mainSectionNumberFromTitle(sectionTitle) {
  const m = String(sectionTitle || '').trim().match(/(\d+)/);
  return m ? m[1] : '0';
}

function tableBodyHalfPoints(fontConfig) {
  const raw = Math.round((fontConfig.tableCell?.size ?? 11) * 2);
  return Math.min(22, Math.max(18, raw));
}

function tableHeaderHalfPoints(fontConfig) {
  return Math.round((fontConfig.tableHeader?.size ?? 11) * 2);
}

/** When table body is reduced to 10 pt, footnotes go to 9 pt per style guide. */
function tableFootnoteHalfPoints(fontConfig) {
  // Keep footnotes readable in exported Word docs. We still honor the configured
  // footnote font size (default 10pt) rather than shrinking to 9pt.
  return Math.round((fontConfig.footnote?.size ?? 10) * 2);
}

function toTitleCaseTableCaption(s) {
  const small = new Set(['a', 'an', 'the', 'and', 'or', 'but', 'of', 'in', 'on', 'for', 'to', 'with', 'at', 'by']);
  const words = String(s ?? '').trim().split(/\s+/).filter(Boolean);
  return words
    .map((w, i) => {
      const lower = w.toLowerCase();
      if (i > 0 && small.has(lower)) return lower;
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(' ');
}

function ensureEndsWithPeriod(text) {
  const t = String(text).trim();
  if (!t) return t;
  return /[.!?]$/.test(t) ? t : `${t}.`;
}

// ============================================================================
// DEFAULT TITLE PAGE CONFIGURATION
// ============================================================================
// Edit these values to change the default title page settings for exported documents
// Set enabled: false to disable the title page completely
// Set enabled: true and text: '' to show the field but leave it empty
// Set enabled: true and text: 'Your Text' to show the field with default text
// Available fonts: 'Calibri', 'Arial', 'Times New Roman', 'Georgia', 'Verdana'
// Size is in points (12 = 12pt)
// Color is in hex format without # (e.g., '1976d2' for blue)
// Alignment: 'left', 'center', 'right'
// Line spacing: 1.0 = single, 1.5 = 1.5x, 2.0 = double, 1.15 = 1.15x
// ============================================================================
const DEFAULT_TITLE_CONFIG = {
  enabled: true,
  documentHeader: {
    enabled: true,
    label: 'Header Text:',
    text: '',
    font: 'Times New Roman',
    size: 12,
    bold: true,
    color: '000000',
    alignment: 'center'
  },
  sponsorConfidentialityStatement: {
    enabled: true,
    label: 'Sponsor Confidentiality Statement:',
    text: '',
    font: 'Times New Roman',
    size: 14,
    bold: true,
    color: '000000',
    alignment: 'left',
    lineSpacing: 1.5
  },
  fullTitle: {
    enabled: true,
    label: 'Full Title:',
    text: '',
    font: 'Times New Roman',
    size: 14,
    bold: true,
    color: '000000',
    alignment: 'left',
    lineSpacing: 1.5
  },
  trialAcronym: {
    enabled: true,
    label: 'Trial Acronym:',
    text: '',
    font: 'Times New Roman',
    size: 14,
    bold: true,
    color: '000000',
    alignment: 'left'
  },
  sponsorProtocolIdentifier: {
    enabled: true,
    label: 'Sponsor Protocol Identifier:',
    text: '',
    font: 'Times New Roman',
    size: 14,
    bold: true,
    color: '000000',
    alignment: 'left'
  },
  originalProtocol: {
    enabled: true,
    label: 'Original Protocol:',
    text: '',
    font: 'Times New Roman',
    size: 14,
    bold: true,
    color: '000000',
    alignment: 'left'
  },
  versionNumber: {
    enabled: true,
    label: 'Version Number:',
    text: '',
    font: 'Times New Roman',
    size: 14,
    bold: true,
    color: '000000',
    alignment: 'left'
  },
  versionDate: {
    enabled: true,
    label: 'Version Date:',
    text: new Date().toLocaleDateString(),
    font: 'Times New Roman',
    size: 14,
    bold: true,
    color: '000000',
    alignment: 'left'
  },
  amendmentIdentifier: {
    enabled: true,
    label: 'Amendment Identifier:',
    text: '',
    font: 'Times New Roman',
    size: 14,
    bold: false,
    color: '000000',
    alignment: 'left'
  },
  amendmentScope: {
    enabled: true,
    label: 'Amendment Scope:',
    text: '',
    font: 'Times New Roman',
    size: 14,
    bold: false,
    color: '000000',
    alignment: 'left'
  },
  sponsorProductCodes: {
    enabled: true,
    label: 'Sponsor\'s Investigational Product Code(s):',
    text: '',
    font: 'Times New Roman',
    size: 14,
    bold: false,
    color: '000000',
    alignment: 'left'
  },
  investigationalProductNames: {
    enabled: true,
    label: 'Investigational Product Name(s):',
    text: '',
    font: 'Times New Roman',
    size: 14,
    bold: false,
    color: '000000',
    alignment: 'left'
  },
  trialPhase: {
    enabled: true,
    label: 'Trial Phase:',
    text: '',
    font: 'Times New Roman',
    size: 14,
    bold: true,
    color: '000000',
    alignment: 'left'
  },
  nctNumber: {
    enabled: true,
    label: 'NCT Number:',
    text: '',
    font: 'Times New Roman',
    size: 14,
    bold: false,
    color: '000000',
    alignment: 'left'
  },
  shortTitle: {
    enabled: true,
    label: 'Short Title:',
    text: '',
    font: 'Times New Roman',
    size: 14,
    bold: true,
    color: '000000',
    alignment: 'left'
  },
  sponsorSignatory: {
    enabled: true,
    label: 'Sponsor Signatory:',
    text: '', // Either signature block or approval statement
    font: 'Times New Roman',
    size: 12,
    bold: false,
    color: '000000',
    alignment: 'left'
  },
  medicalExpertContact: {
    enabled: true,
    label: 'Medical Expert Contact:',
    text: '',
    font: 'Times New Roman',
    size: 12,
    bold: false,
    color: '000000',
    alignment: 'left'
  },
  sponsorNameAddress: {
    enabled: true,
    label: 'Sponsor Name and Address:',
    text: '',
    font: 'Times New Roman',
    size: 12,
    bold: false,
    color: '000000',
    alignment: 'left'
  },
  coSponsorNameAddress: {
    enabled: true,
    label: 'Co-Sponsor Name and Address:',
    text: '',
    font: 'Times New Roman',
    size: 12,
    bold: false,
    color: '000000',
    alignment: 'left'
  },
  localSponsorNameAddress: {
    enabled: true,
    label: 'Local Sponsor Name and Address:',
    text: '',
    font: 'Times New Roman',
    size: 12,
    bold: false,
    color: '000000',
    alignment: 'left'
  },
  deviceManufacturerNameAddress: {
    enabled: true,
    label: 'Device Manufacturer Name and Address:',
    text: '',
    font: 'Times New Roman',
    size: 12,
    bold: false,
    color: '000000',
    alignment: 'left'
  },
  regulatoryIdentifiers: {
    enabled: true,
    label: 'Regulatory or Clinical Trial Identifier(s):',
    text: '',
    font: 'Times New Roman',
    size: 12,
    bold: false,
    color: '000000',
    alignment: 'left'
  },
  sponsorApproval: {
    enabled: true,
    label: 'Sponsor Approval:',
    text: '',
    font: 'Times New Roman',
    size: 12,
    bold: false,
    color: '000000',
    alignment: 'left'
  },
  amendmentDetails: {
    enabled: true,
    label: 'Amendment Details:',
    text: '', // One of the statements (Not applicable / First amendment / Amended previously ...)
    font: 'Times New Roman',
    size: 12,
    bold: false,
    color: '000000',
    alignment: 'left'
  }
};

const theme = documentEditorTheme;

// Get all available section titles from predefined sections
const getAvailableSectionTitles = async (projectId) => {
  try {
    const predefinedSections = await getPredefinedSections(projectId);
    if (!Array.isArray(predefinedSections)) {
      console.error('getPredefinedSections did not return an array:', predefinedSections);
      return [];
    }
    return predefinedSections.map(section => section.title).filter(Boolean);
  } catch (error) {
    console.error('Error getting available section titles:', error);
    return []; // Return empty array as fallback
  }
};

// Specialized function to process objectives content and convert to proper markdown tables
function processObjectivesContent(content) {
  
  const lines = content.split('\n');
  const processedLines = [];
  let currentTable = null;
  let inTableSection = false;
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    
    // Skip empty lines
    if (!line) {
      processedLines.push('');
      continue;
    }
    
    // Detect table start - look for "Estimand Characteristic" pattern
    if (line.includes('Estimand Characteristic') && line.includes('Description')) {
      inTableSection = true;
      currentTable = {
        header: ['Estimand Characteristic', 'Description'],
        rows: []
      };
      
      // Add proper markdown table header
      processedLines.push('| Estimand Characteristic | Description |');
      processedLines.push('|--------------------------|-------------|');
      continue;
    }
    
    // If we're in a table section, try to parse table rows
    if (inTableSection) {
      // Check if this line ends the table (next heading or section)
      if (line.match(/^#{1,6}\s/) || line.match(/^\d+\.\d*\s/) || line.match(/^[A-Z][^|]*:$/)) {
        inTableSection = false;
        currentTable = null;
        processedLines.push(line);
        continue;
      }
      
      // Try to parse table row - look for known characteristics
      const tableCharacteristics = [
        'Population', 'Treatment', 'Endpoint', 'Population-level Summary', 
        'Intercurrent Event', 'Strategy', 'Control', 'Primary Endpoint',
        'Secondary Endpoint', 'Safety Endpoint'
      ];
      
      let rowParsed = false;
      
      // Method 1: Already proper markdown table row
      if (line.startsWith('|') && line.endsWith('|')) {
        processedLines.push(line);
        rowParsed = true;
      }
      // Method 2: Tab-separated values
      else if (line.includes('\t')) {
        const parts = line.split('\t');
        if (parts.length >= 2) {
          const characteristic = parts[0].trim();
          const description = parts.slice(1).join(' ').trim();
          processedLines.push(`| ${characteristic} | ${description} |`);
          rowParsed = true;
        }
      }
      // Method 3: Multiple spaces separation
      else if (line.match(/^([^|]+?)\s{2,}(.+)$/)) {
        const match = line.match(/^([^|]+?)\s{2,}(.+)$/);
        if (match) {
          processedLines.push(`| ${match[1].trim()} | ${match[2].trim()} |`);
          rowParsed = true;
        }
      }
      // Method 4: Look for characteristic at start of line
      else {
        for (const char of tableCharacteristics) {
          if (line.startsWith(char)) {
            const description = line.substring(char.length).trim();
            // Remove any leading colons or separators
            const cleanDescription = description.replace(/^[:\s]+/, '');
            if (cleanDescription) {
              processedLines.push(`| ${char} | ${cleanDescription} |`);
              rowParsed = true;
              break;
            }
          }
        }
      }
      
      // Method 5: Try to match pattern like "Population: description" or "Population description"
      if (!rowParsed) {
        for (const char of tableCharacteristics) {
          const regex = new RegExp(`^${char}[:\\s]+(.+)$`, 'i');
          const match = line.match(regex);
          if (match) {
            processedLines.push(`| ${char} | ${match[1].trim()} |`);
            rowParsed = true;
            break;
          }
        }
      }
      
      // If we couldn't parse it as a table row, it might be end of table
      if (!rowParsed) {
        inTableSection = false;
        currentTable = null;
        processedLines.push(line);
      }
    } else {
      // Regular content - keep as is
      processedLines.push(line);
    }
  }
  
  const result = processedLines.join('\n');
  return result;
}
function markdownToDocxElements(markdown, fontConfig = FONT_CONFIG, sectionTitle = '') {
  // Pre-process markdown to convert plain text headings to markdown headings
  let processedMarkdown = markdown || '';
  
  // Special handling for objectives section - detect and convert estimand tables
  // if (sectionTitle.includes('OBJECTIVES') || sectionTitle.includes('ESTIMANDS') || processedMarkdown.includes('Estimand Characteristic')) {
  //   processedMarkdown = processObjectivesContent(processedMarkdown);
  // }
  
  // Convert plain text headings to markdown headings
  // Pattern: lines that start with numbers like "1", "1.1", "1.1.1", etc.
  
  // Convert plain-text lines that look like real protocol headings (not numbered sentences).
  processedMarkdown = processedMarkdown
    .split('\n')
    .map((line) => {
      const parsed = parseProtocolHeadingLine(line);
      if (!parsed) return line;
      if (parsed.kind === 'main') return `# ${parsed.number} ${parsed.title}`;
      const depth = (parsed.number.match(/\./g) || []).length;
      const prefix = '#'.repeat(Math.min(depth + 1, 6));
      return `${prefix} ${parsed.number} ${parsed.title}`;
    })
    .join('\n');
  
  
  // Configure marked with GFM support for proper table parsing
  marked.setOptions({ 
    gfm: true,
    tables: true,
    breaks: false,
    pedantic: false,
    sanitize: false,
    smartLists: true,
    smartypants: false
  });
  
  const tokens = marked.lexer(processedMarkdown);
  const elements = [];
  
  // If no headings were detected, try direct processing
  if (tokens.filter(t => t.type === 'heading').length === 0) {
    const lines = processedMarkdown.split('\n');
    for (const line of lines) {
      const parsed = parseProtocolHeadingLine(line);
      if (parsed) {
        const headingText = `${parsed.number} ${parsed.title}`;
        const depth =
          parsed.kind === 'main' ? 1 : (parsed.number.match(/\./g) || []).length + 1;
        const headingFontConfig =
          depth <= 1 ? fontConfig.heading : fontConfig.subheading;
        const headingLevel =
          depth <= 1 ? HeadingLevel.HEADING_1 :
          depth === 2 ? HeadingLevel.HEADING_2 :
          depth === 3 ? HeadingLevel.HEADING_3 :
          depth === 4 ? HeadingLevel.HEADING_4 :
          depth === 5 ? HeadingLevel.HEADING_5 :
          HeadingLevel.HEADING_6;

        elements.push(new Paragraph({
          children: [
            new TextRun({
              text: headingText,
              font: headingFontConfig.name,
              size: headingFontConfig.size * 2,
              bold: true,
              color: headingFontConfig.color,
            }),
          ],
          heading: headingLevel,
          alignment: AlignmentType.LEFT,
          spacing: {
            line: Math.round(headingFontConfig.lineSpacing * 240),
            after: 240,
            before: depth <= 1 ? 0 : 120,
          },
        }));
        continue;
      }

      if (line.trim()) {
        // Regular paragraph
        elements.push(new Paragraph({
          children: [
            new TextRun({
              text: line,
              font: fontConfig.body.name,
              size: fontConfig.body.size * 2,
              bold: fontConfig.body.bold,
              color: fontConfig.body.color
            })
          ],
          alignment: AlignmentType.JUSTIFIED,
          spacing: {
            line: Math.round(fontConfig.body.lineSpacing * 240), // Convert to twips (240 = 1.0 spacing)
            before: 60 // Reduced from 120 to 60 for better spacing
          }
        }));
      }
    }
    return elements;
  }
  
  for (const token of tokens) {
    if (token.type === 'heading') {
      const parsedHeading = parseProtocolHeadingLine(token.text);
      if (!parsedHeading) {
        elements.push(new Paragraph({
          style: "CBodyText",
          children: textToDocxRuns(token.text, {
            font: fontConfig.body.name,
            size: fontConfig.body.size * 2,
            bold: fontConfig.body.bold,
            color: fontConfig.body.color,
          }),
          alignment: AlignmentType.JUSTIFIED,
          spacing: {
            line: Math.round(fontConfig.body.lineSpacing * 240),
            before: 60,
          },
        }));
        continue;
      }
      const d = Math.max(1, Math.min(6, Number(token.depth) || 1));
      const headingFontConfig =
        d === 1 ? fontConfig.heading :
        d === 2 ? fontConfig.subheading :
        d === 3 ? (fontConfig.heading3 || fontConfig.subheading) :
        d === 4 ? (fontConfig.heading4 || fontConfig.subheading) :
        (fontConfig.heading5 || fontConfig.subheading);
      const headingLevel =
        d === 1 ? HeadingLevel.HEADING_1 :
        d === 2 ? HeadingLevel.HEADING_2 :
        d === 3 ? HeadingLevel.HEADING_3 :
        d === 4 ? HeadingLevel.HEADING_4 :
        d === 5 ? HeadingLevel.HEADING_5 :
        HeadingLevel.HEADING_6;
      elements.push(new Paragraph({
        children: textToDocxRuns(token.text, {
          font: headingFontConfig.name,
          size: headingFontConfig.size * 2, // half-points
          bold: Boolean(headingFontConfig.bold),
          italic: Boolean(headingFontConfig.italic),
          color: headingFontConfig.color,
        }),
        heading: headingLevel,
        alignment: AlignmentType.LEFT,
        spacing: {
          line: Math.round(headingFontConfig.lineSpacing * 240), // Convert to twips (240 = 1.0 spacing)
          after: 240, // Add space after headings (240 twips = 12pt)
          before: 120 // Add space before headings for consistency
        }
      }));
    } else if (token.type === 'paragraph') {
      elements.push(new Paragraph({
         style: "CBodyText",
        children: textToDocxRuns(token.text, {
            font: fontConfig.body.name,
            size: fontConfig.body.size * 2,
            bold: fontConfig.body.bold,
            color: fontConfig.body.color,
        }),
        alignment: AlignmentType.JUSTIFIED,
        spacing: {
          line: Math.round(fontConfig.body.lineSpacing * 240), // Convert to twips (240 = 1.0 spacing)
          before: 60 // Reduced from 120 to 60 for better spacing (60 twips = 3pt)
        }
      }));
    } else if (token.type === 'table') {
      // Convert Markdown table to docx.Table
      const rows = [token.header, ...token.rows];
      const tableRows = rows.map((row, rowIndex) =>
        new TableRow({
          tableHeader: rowIndex === 0,
          children: row.map((cell, cellIndex) => {
            const isColHeader = rowIndex === 0;
            const isRowHeader = rowIndex > 0 && cellIndex === 0;
            const display = isColHeader || isRowHeader ? toSentenceCaseTable(cell) : cell;
            const useHeaderStyle = isColHeader;
            const bold = useHeaderStyle ? fontConfig.tableHeader.bold : false;
            const fontName = useHeaderStyle ? fontConfig.tableHeader.name : fontConfig.tableCell.name;
            const sizeHp = useHeaderStyle ? tableHeaderHalfPoints(fontConfig) : tableBodyHalfPoints(fontConfig);
            const color = useHeaderStyle ? fontConfig.tableHeader.color : fontConfig.tableCell.color;
            return new TableCell({
              children: [
                new Paragraph({
                  style: useHeaderStyle ? 'CTableHeader' : 'CTableText',
                  children: [
                    ...textToDocxRuns(display, {
                      font: fontName,
                      size: sizeHp,
                      bold,
                      color,
                    }),
                  ],
                  alignment: AlignmentType.LEFT,
                  spacing: {
                    line: Math.round(fontConfig.tableCell.lineSpacing * 240),
                    before: 0,
                    after: 0,
                  },
                }),
              ],
              margins: tableCellMarginsDocx(),
              verticalAlign: VerticalAlignTable.CENTER,
              borders: {
                top: { style: BorderStyle.SINGLE, size: 8, color: "000000" },
                bottom: { style: BorderStyle.SINGLE, size: 8, color: "000000" },
                left: { style: BorderStyle.SINGLE, size: 8, color: "000000" },
                right: { style: BorderStyle.SINGLE, size: 8, color: "000000" },
              },
            });
          }),
        }),
      );
      
      const docxTable = new Table({ 
        rows: tableRows,
        width: {
          size: 100,
          type: WidthType.PERCENTAGE,
        },
        borders: {
          top: { style: BorderStyle.SINGLE, size: 8, color: "000000" },
          bottom: { style: BorderStyle.SINGLE, size: 8, color: "000000" },
          left: { style: BorderStyle.SINGLE, size: 8, color: "000000" },
          right: { style: BorderStyle.SINGLE, size: 8, color: "000000" },
          insideHorizontal: { style: BorderStyle.SINGLE, size: 8, color: "000000" },
          insideVertical: { style: BorderStyle.SINGLE, size: 8, color: "000000" },
        },
      });
      
      // Add spacing before and after table
      elements.push(new Paragraph({ text: '' }));
      elements.push(docxTable);
      elements.push(new Paragraph({ text: '' }));
    } else if (token.type === 'list') {
      for (const item of token.items) {
        elements.push(new Paragraph({
          style: "CBulletList",
          children: textToDocxRuns(item.text, {
              font: fontConfig.list.name,
              size: fontConfig.list.size * 2,
              bold: fontConfig.list.bold,
              color: fontConfig.list.color,
          }),
          bullet: { level: 0 },
          alignment: AlignmentType.LEFT,
          spacing: {
            line: Math.round(fontConfig.list.lineSpacing * 240),
            before: 0,
            after: 0,
          }
        }));
      }
    } else if (token.type === 'text') {
      elements.push(new Paragraph({
        children: textToDocxRuns(token.text, {
          font: fontConfig.body.name,
          size: fontConfig.body.size * 2,
          bold: fontConfig.body.bold,
          color: fontConfig.body.color,
        }),
        alignment: AlignmentType.JUSTIFIED,
        spacing: {
          line: Math.round(fontConfig.body.lineSpacing * 240), // Convert to twips (240 = 1.0 spacing)
          before: 60 // Reduced from 120 to 60 for better spacing (60 twips = 3pt)
        }
      }));
    }
  }
  return elements;
}

/** Strip **bold** markers for Word cell text (inline bold can be added later). */
function stripMarkdownBoldMarkers(s) {
  return String(s).replace(/\*\*(.+?)\*\*/g, '$1').replace(/\*(.+?)\*/g, '$1');
}

function parsePipeCellsFromMarkdownLine(line) {
  const t = line.trim();
  if (!t.startsWith('|')) return null;
  const parts = t.split('|');
  if (parts.length < 2) return null;
  return parts.slice(1, -1).map((c) => c.trim());
}

function isMarkdownTableSeparatorRow(cells) {
  if (!cells || cells.length === 0) return false;
  return cells.every((c) => /^:?-+:?$/.test(String(c).replace(/\s/g, '')));
}

/**
 * Reads consecutive GFM pipe-table lines from lines[startIndex].
 * Drops separator rows; returns data rows only (first row is table header).
 */
function parseMarkdownPipeTableBlock(lines, startIndex) {
  const raw = [];
  let j = startIndex;
  while (j < lines.length) {
    const L = lines[j].trim();
    if (!L.startsWith('|')) break;
    raw.push(L);
    j += 1;
  }
  if (raw.length === 0) return { rows: [], nextIndex: startIndex };
  const parsed = [];
  for (const line of raw) {
    const cells = parsePipeCellsFromMarkdownLine(line);
    if (!cells) continue;
    if (isMarkdownTableSeparatorRow(cells)) continue;
    parsed.push(cells);
  }
  return { rows: parsed, nextIndex: j };
}

function buildDocxTableFromMarkdownRows(cellRows, fontConfig) {
  if (!cellRows.length) return null;
  const maxCols = Math.max(...cellRows.map((r) => r.length));
  const normalizedRows = cellRows.map((row) => {
    const r = [...row];
    while (r.length < maxCols) r.push('');
    return r;
  });
  const tableRows = normalizedRows.map((row, rowIndex) => {
    const isHeaderRow = rowIndex === 0;
    return new TableRow({
      tableHeader: isHeaderRow,
      children: row.map((cellText, colIndex) => {
        const textRaw = stripMarkdownBoldMarkers(cellText);
        const isColHeader = isHeaderRow;
        const isRowHeader = !isHeaderRow && colIndex === 0;
        const display =
          isColHeader || isRowHeader ? toSentenceCaseTable(textRaw) : textRaw;
        const useHeaderStyle = isColHeader;
        const bold = useHeaderStyle ? fontConfig.tableHeader.bold : false;
        const fontName = useHeaderStyle ? fontConfig.tableHeader.name : fontConfig.tableCell.name;
        const sizeHp = useHeaderStyle ? tableHeaderHalfPoints(fontConfig) : tableBodyHalfPoints(fontConfig);
        const color = useHeaderStyle ? fontConfig.tableHeader.color : fontConfig.tableCell.color;
        return new TableCell({
          children: [
            new Paragraph({
              style: useHeaderStyle ? 'CTableHeader' : 'CTableText',
              children: [
                ...textToDocxRuns(display, {
                  font: fontName,
                  size: sizeHp,
                  bold,
                  color,
                }),
              ],
              alignment: AlignmentType.LEFT,
              spacing: {
                line: Math.round(fontConfig.tableCell.lineSpacing * 240),
                before: 0,
                after: 0,
              },
            }),
          ],
          margins: tableCellMarginsDocx(),
          verticalAlign: VerticalAlignTable.CENTER,
          borders: {
            top: { style: BorderStyle.SINGLE, size: 8, color: '000000' },
            bottom: { style: BorderStyle.SINGLE, size: 8, color: '000000' },
            left: { style: BorderStyle.SINGLE, size: 8, color: '000000' },
            right: { style: BorderStyle.SINGLE, size: 8, color: '000000' },
          },
        });
      }),
    });
  });
  return new Table({
    rows: tableRows,
    width: { size: 100, type: WidthType.PERCENTAGE },
    borders: {
      top: { style: BorderStyle.SINGLE, size: 8, color: '000000' },
      bottom: { style: BorderStyle.SINGLE, size: 8, color: '000000' },
      left: { style: BorderStyle.SINGLE, size: 8, color: '000000' },
      right: { style: BorderStyle.SINGLE, size: 8, color: '000000' },
      insideHorizontal: { style: BorderStyle.SINGLE, size: 8, color: '000000' },
      insideVertical: { style: BorderStyle.SINGLE, size: 8, color: '000000' },
    },
  });
}
  // Fetch administrative info and template language, then populate title page configuration.
  // `preloadedProject` is optional — this function lives outside the DocumentEditor
  // component (module scope), so it has no access to that component's `currentProject`
  // context value; callers inside the component pass it in directly instead of this
  // function re-fetching a project it may already have on hand.
  const getTitlePageConfigWithData = async (projectId, preloadedProject = null) => {
    try {
      
      // Fetch administrative info (project-scoped)
      const adminResponse = await administrativeInfoService.getAllAdministrativeInfo(projectId);
      
      // Fetch clinical intake data for trial phase and preferred Short Title
      let trialPhase = '';
      let preferredShortTitle = '';
      let shortDescriptionOfTrialPopulation = '';
      try {
        const clinicalIntakeData = await clinicalIntakeService.getLatestClinicalIntake(projectId);
        if (clinicalIntakeData) {
          if (clinicalIntakeData.study_overview) {
            trialPhase = clinicalIntakeData.study_overview.study_phase || '';
          }
          if (clinicalIntakeData.study_identification && clinicalIntakeData.study_identification.preferred_short_title) {
            preferredShortTitle = clinicalIntakeData.study_identification.preferred_short_title;
          }
          // Short title on title page: use "Short description of Trial Population" from clinical intake
          if (clinicalIntakeData.target_population && clinicalIntakeData.target_population.patient_population_description) {
            shortDescriptionOfTrialPopulation = (clinicalIntakeData.target_population.patient_population_description || '').trim();
          }
          if (!shortDescriptionOfTrialPopulation && clinicalIntakeData.study_overview && clinicalIntakeData.study_overview.study_parts) {
            const parts = clinicalIntakeData.study_overview.study_parts;
            const pickGeneralDescription = (cohort) => {
              if (!cohort) return '';
              const general = (cohort.general_description_of_trial_population || '').trim();
              if (general) return general;
              return (cohort.short_description || '').trim();
            };
            for (const partKey of Object.keys(parts)) {
              const part = parts[partKey];
              if (!part) continue;
              const direct = part.direct;
             const fromDirect = pickGeneralDescription(direct);
              if (fromDirect) {
                shortDescriptionOfTrialPopulation = fromDirect;
                break;
              }
              for (const cohortKey of Object.keys(part)) {
                if (cohortKey === 'direct') continue;
                const fromCohort = pickGeneralDescription(part[cohortKey]);
                if (fromCohort) {
                  shortDescriptionOfTrialPopulation = fromCohort;
                  break;
                }
              }
              if (shortDescriptionOfTrialPopulation) break;
            }
          }
        }
      } catch (error) {

      }
      // Fetch confidentiality statement from template language — unlike the
      // fields below, this always comes from the same template/default
      // source regardless of Admin Info vs. USDM; USDM carries no
      // confidentiality-statement equivalent to fall back to.
      const confidentialityStatement = await templateLanguageService.getConfidentialityStatement();

      // Best-effort fallback for projects with no Clinical Intake / Admin
      // Info at all (USDM-catalogue-only protocols): whatever the uploaded
      // USDM file can supply for the title page (title, acronym, protocol
      // id, NCT number, sponsor name, phase). Never overrides real Admin
      // Info data — only fills fields Admin Info left blank. Silently null
      // when there's no catalogue uploaded yet, or the upload doesn't say enough.
      let usdmTitleFallback = null;
      try {
        const latestCatalogueRaw = await protocolUsdmCatalogueService.getLatestCatalogue(projectId);
        usdmTitleFallback = extractTitlePageFactsFromUsdm(latestCatalogueRaw);
      } catch (usdmFallbackErr) {
        usdmTitleFallback = null;
      }
      const usdmField = (key) => usdmTitleFallback?.[key] || '';

      if (adminResponse && adminResponse.data && adminResponse.data.length > 0) {
        const adminInfo = adminResponse.data[0]; // Get the first record
        const adminSponsorResolved = resolveAdminSponsorFields(adminInfo);
        // Prefer the project already loaded in context over a fresh fetch —
        // this project is already open in the editor, so re-fetching it here
        // pays for a second access-control round trip for no benefit, and if
        // that check ever behaves differently from the one that let the user
        // open this project in the first place, it silently blanks every
        // Project-derived title-page field (Sponsor Protocol Identifier,
        // Trial Acronym, Sponsor Name) instead of using data already on hand.
        let projectForIdentity =
          preloadedProject && String(preloadedProject._id) === String(projectId) ? preloadedProject : null;
        if (!projectForIdentity && projectId) {
          try {
            const projectRes = await projectService.getProject(projectId);
            projectForIdentity = projectRes?.data ?? null;
          } catch (projectErr) {
            console.warn('Could not load project for title page identity:', projectErr?.message);
          }
        }
        const canonicalTrialAcronym = resolveTrialAcronymForTitlePage(projectForIdentity, adminInfo);
        const canonicalSponsorName = resolveSponsorNameForDisplay(projectForIdentity, adminInfo);
        const canonicalProtocolId = resolveSponsorProtocolIdForDisplay(projectForIdentity, adminInfo);
        
        // Create a copy of the default config and populate with admin info
        const getFirst = (...keys) => {
          for (const k of keys) {
            if (adminInfo && adminInfo[k]) return adminInfo[k];
          }
          return '';
        };

        const joinIf = (...parts) => parts.filter(Boolean).join('  ');

        const populatedConfig = {
          ...DEFAULT_TITLE_CONFIG,
          documentHeader: {
            ...DEFAULT_TITLE_CONFIG.documentHeader,
            text: adminInfo.document_header || ''
          },
          sponsorConfidentialityStatement: {
            ...DEFAULT_TITLE_CONFIG.sponsorConfidentialityStatement,
             text: confidentialityStatement || (canonicalSponsorName
              ? `This document contains confidential information belonging to ${canonicalSponsorName}`
              : '')
          },
          fullTitle: {
            ...DEFAULT_TITLE_CONFIG.fullTitle,
            // Deliberately NOT canonicalTrialAcronym here — an acronym is not a title, and
            // since it's always available (it comes straight from the Project record),
            // putting it in this chain silently masked usdmField('fullTitle') every time,
            // even when the USDM catalogue actually had a real official title.
            text: preferredShortTitle || usdmField('fullTitle') || ''
          },
          trialAcronym: {
            ...DEFAULT_TITLE_CONFIG.trialAcronym,
            text: canonicalTrialAcronym || usdmField('trialAcronym') || ''
          },
          sponsorProtocolIdentifier: {
            ...DEFAULT_TITLE_CONFIG.sponsorProtocolIdentifier,
            text: canonicalProtocolId || usdmField('sponsorProtocolIdentifier') || ''
          },
          originalProtocol: {
            ...DEFAULT_TITLE_CONFIG.originalProtocol,
            text: adminInfo.original_protocol_indicator === 'C49488' ? 'Yes' : 'No'
          },
          versionNumber: {
            ...DEFAULT_TITLE_CONFIG.versionNumber,
            text: adminInfo.version_number ? adminInfo.version_number.toString() : ''
          },
          versionDate: {
            ...DEFAULT_TITLE_CONFIG.versionDate,
            text: adminInfo.version_date ? new Date(adminInfo.version_date).toLocaleDateString() : new Date().toLocaleDateString()
          },
          amendmentIdentifier: {
            ...DEFAULT_TITLE_CONFIG.amendmentIdentifier,
            text: adminInfo.amendment_identifier || ''
          },
          amendmentScope: {
            ...DEFAULT_TITLE_CONFIG.amendmentScope,
            text: adminInfo.amendment_scope === 'C68846' ? 'Global' : 
                  adminInfo.amendment_scope === 'CNEW' ? 'Not Global' : ''
          },
          sponsorProductCodes: {
            ...DEFAULT_TITLE_CONFIG.sponsorProductCodes,
            text: adminInfo.sponsor_investigational_product_codes || ''
          },
          investigationalProductNames: {
            ...DEFAULT_TITLE_CONFIG.investigationalProductNames,
            text: adminInfo.investigational_product_names || ''
          },
          trialPhase: {
            ...DEFAULT_TITLE_CONFIG.trialPhase,
            text: trialPhase || usdmField('trialPhase') || ''
          },
          nctNumber: {
            ...DEFAULT_TITLE_CONFIG.nctNumber,
            text: adminInfo.nct_number || usdmField('nctNumber') || ''
          },
          shortTitle: {
            ...DEFAULT_TITLE_CONFIG.shortTitle,
            // Same reasoning as fullTitle above — canonicalTrialAcronym removed from this
            // chain so the USDM catalogue's real Brief Study Title gets a chance to be used
            // instead of always being masked by the (always-available) trial acronym.
            text: shortDescriptionOfTrialPopulation || adminInfo.short_title || preferredShortTitle || usdmField('shortTitle') || ''
          },
          sponsorSignatory: {
            ...DEFAULT_TITLE_CONFIG.sponsorSignatory,
            text: getFirst('sponsor_signature_block','sponsor_signatory','protocol_approval_method')
          },
          medicalExpertContact: {
            ...DEFAULT_TITLE_CONFIG.medicalExpertContact,
            text: getFirst('medical_expert_contact','medical_expert','medical_expert_contact_info')
          },
          sponsorNameAddress: {
            ...DEFAULT_TITLE_CONFIG.sponsorNameAddress,
            text: formatNameAddressPairsForDisplay(
              adminInfo.sponsor_entries,
              canonicalSponsorName || getFirst('sponsor_name', 'sponsor') || usdmField('sponsorNameAddress'),
              getFirst('sponsor_legal_address', 'sponsor_address', 'sponsor_location') || usdmField('sponsorAddress')
            ),
          },
          sponsorNameForRunningHeader: {
            text: String(canonicalSponsorName || adminSponsorResolved.sponsorName || '').trim(),
          },
          coSponsorNameAddress: {
            ...DEFAULT_TITLE_CONFIG.coSponsorNameAddress,
            text: formatNameAddressPairsForDisplay(
              adminInfo.co_sponsor_entries,
              getFirst('co_sponsor_name', 'co_sponsor'),
              getFirst('co_sponsor_legal_address', 'co_sponsor_address')
            ),
          },
          localSponsorNameAddress: {
            ...DEFAULT_TITLE_CONFIG.localSponsorNameAddress,
            text: formatNameAddressPairsForDisplay(
              adminInfo.local_sponsor_entries,
              getFirst('local_sponsor_name', 'local_affiliate_name'),
              getFirst('local_sponsor_address', 'local_affiliate_address')
            ),
          },
          deviceManufacturerNameAddress: {
            ...DEFAULT_TITLE_CONFIG.deviceManufacturerNameAddress,
            text: formatNameAddressPairsForDisplay(
              adminInfo.device_manufacturer_entries,
              getFirst('device_manufacturer_name'),
              getFirst('device_manufacturer_address')
            ),
          },
          regulatoryIdentifiers: {
            ...DEFAULT_TITLE_CONFIG.regulatoryIdentifiers,
            text: formatRegulatoryIdentifiersForDisplay(
              adminInfo.regulatory_identifier_entries,
              adminInfo
            ),
          },
          sponsorApproval: {
            ...DEFAULT_TITLE_CONFIG.sponsorApproval,
            text: (() => {
              const mode = adminInfo.sponsor_approval_location;
              if (mode === 'CNEW') {
                return String(adminInfo.sponsor_approval_location_text || '').trim();
              }
              if (adminInfo.sponsor_approval_date) {
                try {
                  const d = new Date(adminInfo.sponsor_approval_date);
                  if (!Number.isNaN(d.getTime())) {
                    return d.toLocaleDateString();
                  }
                } catch {
                  /* use raw string below */
                }
                return String(adminInfo.sponsor_approval_date);
              }
              return getFirst('sponsor_approval_date', 'approval_date', 'date_of_approval');
            })(),
          },
          amendmentDetails: {
            ...DEFAULT_TITLE_CONFIG.amendmentDetails,
            text: (() => {
              const status = getFirst('amendment_status'); // e.g., 'none' | 'first' | 'prior'
              if (status === 'first') return 'This is the first protocol amendment.';
              if (status === 'prior') return 'This protocol has been amended previously. Details of prior amendments are presented in Section 12.3 Prior Protocol Amendment(s).';
              return 'Not applicable. This protocol has not been amended.';
            })()
          }
        };
        
        return populatedConfig;
      } else {
        // No Admin Info record for this project at all. Still resolve
        // Project-derived identity (Sponsor Protocol Identifier, Trial
        // Acronym, Sponsor Name) exactly as the branch above does — Project
        // data doesn't depend on Admin Info existing, so it must not be
        // skipped just because Admin Info is missing. USDM catalogue data is
        // a further fallback on top of that, not a replacement for it.
        const emptyAdminInfo = {};
        const canonicalTrialAcronym = resolveTrialAcronymForTitlePage(preloadedProject, emptyAdminInfo);
        const canonicalSponsorName = resolveSponsorNameForDisplay(preloadedProject, emptyAdminInfo);
        const canonicalProtocolId = resolveSponsorProtocolIdForDisplay(preloadedProject, emptyAdminInfo);
        const sponsorNameForDisplay = canonicalSponsorName || usdmField('sponsorNameAddress');

        return {
          ...DEFAULT_TITLE_CONFIG,
          sponsorConfidentialityStatement: {
            ...DEFAULT_TITLE_CONFIG.sponsorConfidentialityStatement,
            text: confidentialityStatement || (sponsorNameForDisplay
              ? `This document contains confidential information belonging to ${sponsorNameForDisplay}`
              : '')
          },
          fullTitle: { ...DEFAULT_TITLE_CONFIG.fullTitle, text: preferredShortTitle || canonicalTrialAcronym || usdmField('fullTitle') },
          shortTitle: { ...DEFAULT_TITLE_CONFIG.shortTitle, text: shortDescriptionOfTrialPopulation || preferredShortTitle || canonicalTrialAcronym || usdmField('shortTitle') },
          trialAcronym: { ...DEFAULT_TITLE_CONFIG.trialAcronym, text: canonicalTrialAcronym || usdmField('trialAcronym') },
          sponsorProtocolIdentifier: { ...DEFAULT_TITLE_CONFIG.sponsorProtocolIdentifier, text: canonicalProtocolId || usdmField('sponsorProtocolIdentifier') },
          nctNumber: { ...DEFAULT_TITLE_CONFIG.nctNumber, text: usdmField('nctNumber') },
          trialPhase: { ...DEFAULT_TITLE_CONFIG.trialPhase, text: trialPhase || usdmField('trialPhase') },
          sponsorNameAddress: {
            ...DEFAULT_TITLE_CONFIG.sponsorNameAddress,
            text: formatNameAddressPairsForDisplay(null, sponsorNameForDisplay, usdmField('sponsorAddress')),
          },
        };
      }
    } catch (error) {
      console.error('Error fetching administrative info:', error);
      return DEFAULT_TITLE_CONFIG;
    }
  };

  // Generate title page content
  const generateTitlePageContent = (config) => {
    
    if (!config.enabled) {
      return [];
    }
    
  const children = [];
  // Title page requirement: force all text to 14pt regardless of global FONT_CONFIG
  const headingFontName = 'Times New Roman';
  const headingFontSize = 14; // pt
  const bodyFontName = 'Times New Roman';
  const bodyFontSize = 14; // pt
    
    // Add proper spacing at the top
    children.push(new Paragraph({ 
      text: '',
      spacing: {
        after: 360 // Add 18pt space at the top (360 twips = 18pt)
      }
    }));
    
    // Build a borderless key/value table for the title page
    const tableRows = TITLE_PAGE_TABLE_FIELD_KEYS
      .map((fieldKey) => {
      const field = config[fieldKey];
        if (!shouldIncludeTitlePageTableRow(fieldKey, field)) return null;
        const text = (field.text || '').trim();

        const labelRun = new TextRun({
                  text: field.label,
          font: headingFontName,
          size: headingFontSize * 2,
          bold: true,
                  color: field.color || '000000'
        });

        const valueRun = new TextRun({
          text: text,
          font: bodyFontName,
          size: bodyFontSize * 2,
          bold: false,
          color: field.color || '000000'
        });

        const labelCell = new TableCell({
          children: [
            new Paragraph({
              children: [labelRun],
              alignment: AlignmentType.LEFT,
            })
          ],
          margins: { top: 60, bottom: 60, left: 120, right: 120 },
          borders: {
            top: { style: BorderStyle.SINGLE, size: 8, color: '000000' },
            bottom: { style: BorderStyle.SINGLE, size: 8, color: '000000' },
            left: { style: BorderStyle.SINGLE, size: 8, color: '000000' },
            right: { style: BorderStyle.SINGLE, size: 8, color: '000000' },
          }
        });

        const valueCell = new TableCell({
          children: [
            ...(text.includes('\n') && fieldKey === 'regulatoryIdentifiers'
              ? text.split(/\s*\n\s*/).filter(Boolean).map(line => (
                  new Paragraph({
                    children: [new TextRun({
                      text: line,
                      font: bodyFontName,
                      size: bodyFontSize * 2,
                      bold: false,
                      color: field.color || '000000'
                    })],
                    alignment: AlignmentType.LEFT
                  })
                ))
              : [
                  new Paragraph({
                    children: [valueRun],
                    alignment: field.alignment === 'center' ? AlignmentType.CENTER : 
                               field.alignment === 'right' ? AlignmentType.RIGHT : AlignmentType.LEFT,
                  })
                ]
            )
          ],
          margins: { top: 60, bottom: 60, left: 120, right: 120 },
          borders: {
            top: { style: BorderStyle.SINGLE, size: 8, color: '000000' },
            bottom: { style: BorderStyle.SINGLE, size: 8, color: '000000' },
            left: { style: BorderStyle.SINGLE, size: 8, color: '000000' },
            right: { style: BorderStyle.SINGLE, size: 8, color: '000000' },
          }
        });

        return new TableRow({ children: [labelCell, valueCell] });
      })
      .filter(Boolean);

    if (tableRows.length > 0) {
      children.push(
        new Table({
          width: { size: 100, type: WidthType.PERCENTAGE },
          alignment: AlignmentType.CENTER,
          borders: {
            top: { style: BorderStyle.SINGLE, size: 8, color: '000000' },
            bottom: { style: BorderStyle.SINGLE, size: 8, color: '000000' },
            left: { style: BorderStyle.SINGLE, size: 8, color: '000000' },
            right: { style: BorderStyle.SINGLE, size: 8, color: '000000' },
            insideHorizontal: { style: BorderStyle.SINGLE, size: 8, color: '000000' },
            insideVertical: { style: BorderStyle.SINGLE, size: 8, color: '000000' },
          },
          rows: tableRows,
            })
          );
        }
    
    // Sponsor Signatory block (per provided template image)
    const addPara = (runs, opts = {}) => new Paragraph({
      children: runs,
      alignment: opts.align === 'center' ? AlignmentType.CENTER : AlignmentType.LEFT,
      spacing: { after: opts.after ?? 120 },
    });

    // Heading
    children.push(addPara([
      new TextRun({ text: 'Sponsor Signatory', bold: true, size: headingFontSize * 2, font: headingFontName }),
    ], { after: 60 }));

    const sponsorSignatoryText = (config.sponsorSignatory?.text || '').trim();
    const approvalText = (config.sponsorApproval?.text || '').trim();

    if (sponsorSignatoryText) {
      // Show only the signature block selection
      children.push(addPara([
      new TextRun({ text: '[{', font: bodyFontName, size: bodyFontSize * 2 }),
      new TextRun({ text: sponsorSignatoryText, font: bodyFontName, size: bodyFontSize * 2 }),
      new TextRun({ text: '}]', font: bodyFontName, size: bodyFontSize * 2 }),
      ]));
    } else if (approvalText) {
      // Show only the approval statement selection
      children.push(addPara([
        new TextRun({ text: '{This protocol was approved via ', font: bodyFontName, size: bodyFontSize * 2 }),
        new TextRun({ text: approvalText, font: bodyFontName, size: bodyFontSize * 2 }),
        new TextRun({ text: '}.', font: bodyFontName, size: bodyFontSize * 2 }),
      ]));
      } else {
      // No data provided: show instruction with both editable placeholders
      children.push(addPara([
        new TextRun({ text: 'Include either the Sponsor signature or the statement below.', color: 'd32f2f', font: 'Times New Roman' })
      ]));
      children.push(addPara([
        new TextRun({ text: '[{', font: bodyFontName, size: bodyFontSize * 2 }),
        new TextRun({ text: '<Enter sponsor signature block (name and title of sponsor signatory and signature date)>', font: bodyFontName, size: bodyFontSize * 2 }),
        new TextRun({ text: '}]', font: bodyFontName, size: bodyFontSize * 2 }),
      ]));
      children.push(addPara([
        new TextRun({ text: 'or', color: 'd32f2f', font: 'Times New Roman' })
      ]));
      children.push(addPara([
        new TextRun({ text: '{This protocol was approved via ', font: bodyFontName, size: bodyFontSize * 2 }),
        new TextRun({ text: '<describe method>', font: bodyFontName, size: bodyFontSize * 2 }),
        new TextRun({ text: '}.', font: bodyFontName, size: bodyFontSize * 2 }),
      ]));
    }

    // Amendment Details block (render only the applicable statement) when not already in the table
    const amendmentInTable = shouldIncludeTitlePageTableRow('amendmentDetails', config.amendmentDetails);
    if (!amendmentInTable) {
      children.push(addPara([
        new TextRun({ text: 'Amendment Details', bold: true, size: headingFontSize * 2, font: headingFontName })
      ], { after: 60 }));

      const amendmentText = (config.amendmentDetails?.text || '').trim();
      let amendmentFinal = amendmentText;
      if (!amendmentFinal) {
        amendmentFinal = 'Not applicable. This protocol has not been amended.';
      }
      children.push(addPara([
        new TextRun({ text: `{${amendmentFinal}}`, font: bodyFontName, size: bodyFontSize * 2 })
      ]));
    }
    
    // Add page break after title table
    children.push(new Paragraph({ pageBreakBefore: true }));
    
    return children;
  };
// Generate Table of Contents
const generateTableOfContents = (fontConfig) => {
  const tocEntries = [
    { level: 1, number: '1', title: 'PROTOCOL SUMMARY', page: '12' },
    { level: 2, number: '1.1', title: 'Protocol Synopsis', page: '12' },
    { level: 3, number: '1.1.1', title: 'Primary and Secondary Objectives and Estimands', page: '12' },
    { level: 3, number: '1.1.2', title: 'Overall Design', page: '12' },
    { level: 2, number: '1.2', title: 'Trial Schema', page: '15' },
    { level: 2, number: '1.3', title: 'Schedule of Activities', page: '15' },
    { level: 1, number: '2', title: 'INTRODUCTION', page: '16' },
    { level: 2, number: '2.1', title: 'Purpose of Trial', page: '16' },
    { level: 3, number: '2.1.1', title: 'Trial Rationale', page: '16' },
    { level: 3, number: '2.1.2', title: 'Background', page: '16' },
    { level: 2, number: '2.2', title: 'Assessment of Risks and Benefits', page: '16' },
    { level: 3, number: '2.2.1', title: 'Risk Summary and Mitigation Strategy', page: '16' },
    { level: 3, number: '2.2.2', title: 'Benefit Summary', page: '16' },
    { level: 3, number: '2.2.3', title: 'Overall Risk-Benefit Assessment', page: '17' },
    { level: 1, number: '3', title: 'TRIAL OBJECTIVES AND ASSOCIATED ESTIMANDS', page: '18' },
    { level: 2, number: '3.1', title: 'Primary Objective(s) and Associated Estimand(s)', page: '18' },
    { level: 3, number: '3.1.1', title: 'Primary Objective [#]', page: '18' },
    { level: 2, number: '3.2', title: 'Secondary Objective(s) and Associated Estimand(s)', page: '19' },
    { level: 3, number: '3.2.1', title: 'Secondary Objective [#]', page: '19' },
    { level: 2, number: '3.3', title: 'Exploratory Objective(s)', page: '20' },
    { level: 3, number: '3.3.1', title: 'Exploratory Objective [#]', page: '20' },
    { level: 1, number: '4', title: 'TRIAL DESIGN', page: '21' },
    { level: 2, number: '4.1', title: 'Description of Trial Design', page: '21' },
    { level: 3, number: '4.1.1', title: 'Stakeholder Input into Design', page: '22' },
    { level: 2, number: '4.2', title: 'Rationale for Trial Design', page: '22' },
    { level: 3, number: '4.2.1', title: 'Rationale for Estimand(s)', page: '22' },
    { level: 3, number: '4.2.2', title: 'Rationale for Intervention Model', page: '22' },
    { level: 3, number: '4.2.3', title: 'Rationale for Control Type', page: '22' },
    { level: 3, number: '4.2.4', title: 'Rationale for Trial Duration', page: '23' },
    { level: 3, number: '4.2.5', title: 'Rationale for Adaptive or Novel Trial Design', page: '23' },
    { level: 3, number: '4.2.6', title: 'Rationale for Interim Analysis', page: '23' },
    { level: 3, number: '4.2.7', title: 'Rationale for Other Trial Design Aspects', page: '23' },
    { level: 2, number: '4.3', title: 'Trial Stopping Rules', page: '23' },
    { level: 2, number: '4.4', title: 'Start of Trial and End of Trial', page: '23' },
    { level: 2, number: '4.5', title: 'Access to Trial Intervention After End of Trial', page: '24' },
    { level: 1, number: '5', title: 'TRIAL POPULATION', page: '24' },
    { level: 2, number: '5.1', title: 'Description of Trial Population and Rationale', page: '24' },
    { level: 2, number: '5.2', title: 'Inclusion Criteria', page: '25' },
    { level: 2, number: '5.3', title: 'Exclusion Criteria', page: '25' },
    { level: 2, number: '5.4', title: 'Contraception', page: '25' },
    { level: 3, number: '5.4.1', title: 'Definitions Related to Childbearing Potential', page: '25' },
    { level: 3, number: '5.4.2', title: 'Contraception Requirements', page: '25' },
    { level: 2, number: '5.5', title: 'Lifestyle Restrictions', page: '26' },
    { level: 3, number: '5.5.1', title: 'Meals and Dietary Restrictions', page: '26' },
    { level: 3, number: '5.5.2', title: 'Caffeine, Alcohol, Tobacco, and Other Restrictions', page: '26' },
    { level: 3, number: '5.5.3', title: 'Physical Activity Restrictions', page: '26' },
    { level: 3, number: '5.5.4', title: 'Other Activity Restrictions', page: '26' },
    { level: 2, number: '5.6', title: 'Screen Failure and Rescreening', page: '26' },
    { level: 1, number: '6', title: 'TRIAL INTERVENTION AND CONCOMITANT THERAPY', page: '26' },
    { level: 2, number: '6.1', title: 'Description of Investigational Trial Intervention', page: '29' },
    { level: 3, number: '6.1.1', title: 'Medical Devices', page: '29' },
    { level: 2, number: '6.2', title: 'Rationale for Investigational Trial Intervention Dose and Regimen', page: '29' },
    { level: 2, number: '6.3', title: 'Investigational Trial Intervention Administration', page: '29' },
    { level: 3, number: '6.3.1', title: 'Retreatment Criteria', page: '29' },
    { level: 2, number: '6.4', title: 'Investigational Trial Intervention Dose Modification', page: '29' },
    { level: 2, number: '6.5', title: 'Management of Investigational Trial Intervention Overdose', page: '30' },
    { level: 2, number: '6.6', title: 'Preparation, Storage, Handling and Accountability of Investigational Trial Intervention', page: '30' },
    { level: 3, number: '6.6.1', title: 'Preparation of Investigational Trial Intervention', page: '30' },
    { level: 3, number: '6.6.2', title: 'Storage and Handling of Investigational Trial Intervention', page: '30' },
    { level: 3, number: '6.6.3', title: 'Accountability of Investigational Trial Intervention', page: '30' },
    { level: 2, number: '6.7', title: 'Investigational Trial Intervention Assignment, Randomization and Blinding', page: '31' },
    { level: 3, number: '6.7.1', title: 'Participant Assignment to Investigational Trial Intervention', page: '31' },
    { level: 3, number: '6.7.2', title: 'Randomization', page: '31' },
    { level: 3, number: '6.7.3', title: 'Measures to Maintain Blinding', page: '31' },
    { level: 3, number: '6.7.4', title: 'Emergency Unblinding at the Site', page: '32' },
    { level: 2, number: '6.8', title: 'Investigational Trial Intervention Adherence', page: '32' },
    { level: 2, number: '6.9', title: 'Description of Noninvestigational Trial Intervention', page: '32' },
    { level: 3, number: '6.9.1', title: 'Background Trial Intervention', page: '32' },
    { level: 3, number: '6.9.2', title: 'Rescue Therapy', page: '32' },
    { level: 3, number: '6.9.3', title: 'Other Noninvestigational Trial Intervention', page: '33' },
    { level: 2, number: '6.10', title: 'Concomitant Therapy', page: '33' },
    { level: 3, number: '6.10.1', title: 'Prohibited Concomitant Therapy', page: '33' },
    { level: 3, number: '6.10.2', title: 'Permitted Concomitant Therapy', page: '33' },
    { level: 1, number: '7', title: 'PARTICIPANT DISCONTINUATION OF TRIAL INTERVENTION AND DISCONTINUATION OR WITHDRAWAL FROM TRIAL', page: '33' },
    { level: 2, number: '7.1', title: 'Discontinuation of Trial Intervention for Individual Participants', page: '33' },
    { level: 3, number: '7.1.1', title: 'Permanent Discontinuation of Trial Intervention', page: '33' },
    { level: 4, number: '7.1.1.1', title: 'Liver Event Stopping Criteria', page: '33' },
    { level: 4, number: '7.1.1.2', title: 'QTc Stopping Criteria', page: '33' },
    { level: 3, number: '7.1.2', title: 'Temporary Discontinuation of Trial Intervention', page: '34' },
    { level: 3, number: '7.1.3', title: 'Rechallenge', page: '34' },
    { level: 4, number: '7.1.3.1', title: 'Trial Intervention Restart or Rechallenge After Liver Stopping Criteria Are Met', page: '34' },
    { level: 2, number: '7.2', title: 'Participant Discontinuation or Withdrawal from the Trial', page: '34' },
    { level: 2, number: '7.3', title: 'Management of Loss to Follow-Up', page: '34' },
    { level: 1, number: '8', title: 'TRIAL ASSESSMENTS AND PROCEDURES', page: '36' },
    { level: 2, number: '8.1', title: 'Trial Assessments and Procedures Considerations', page: '36' },
    { level: 2, number: '8.2', title: 'Screening/Baseline Assessments and Procedures', page: '36' },
    { level: 2, number: '8.3', title: 'Efficacy Assessments and Procedures', page: '37' },
    { level: 2, number: '8.4', title: 'Safety Assessments and Procedures', page: '37' },
    { level: 3, number: '8.4.1', title: 'Physical Examination', page: '37' },
    { level: 3, number: '8.4.2', title: 'Vital Signs', page: '37' },
    { level: 3, number: '8.4.3', title: 'Electrocardiograms', page: '37' },
    { level: 3, number: '8.4.4', title: 'Clinical Laboratory Assessments', page: '37' },
    { level: 3, number: '8.4.5', title: 'Pregnancy Testing', page: '38' },
    { level: 3, number: '8.4.6', title: 'Suicidal Ideation and Behavior Risk Monitoring', page: '38' },
    { level: 2, number: '8.5', title: 'Pharmacokinetics', page: '38' },
    { level: 2, number: '8.6', title: 'Biomarkers', page: '38' },
    { level: 3, number: '8.6.1', title: 'Genetics, Genomics, Pharmacogenetics and Pharmacogenomics', page: '38' },
    { level: 3, number: '8.6.2', title: 'Pharmacodynamic Biomarkers', page: '39' },
    { level: 3, number: '8.6.3', title: 'Other Biomarkers', page: '39' },
    { level: 2, number: '8.7', title: 'Immunogenicity Assessments', page: '39' },
    { level: 2, number: '8.8', title: 'Medical Resource Utilization and Health Economics', page: '40' },
    { level: 1, number: '9', title: 'ADVERSE EVENTS, SERIOUS ADVERSE EVENTS, PRODUCT COMPLAINTS, PREGNANCY AND POSTPARTUM INFORMATION, AND SPECIAL SAFETY SITUATIONS', page: '41' },
    { level: 2, number: '9.1', title: 'Definitions', page: '41' },
    { level: 3, number: '9.1.1', title: 'Definitions of Adverse Events', page: '41' },
    { level: 3, number: '9.1.2', title: 'Definitions of Serious Adverse Events', page: '41' },
    { level: 3, number: '9.1.3', title: 'Definitions of Product Complaints', page: '41' },
    { level: 4, number: '9.1.3.1', title: 'Definitions of Medical Device Product Complaints', page: '41' },
    { level: 2, number: '9.2', title: 'Timing and Procedures for Collection and Reporting', page: '41' },
    { level: 3, number: '9.2.1', title: 'Timing', page: '42' },
    { level: 3, number: '9.2.2', title: 'Collection Procedures', page: '42' },
    { level: 3, number: '9.2.3', title: 'Reporting', page: '43' },
    { level: 4, number: '9.2.3.1', title: 'Regulatory Reporting Requirements', page: '43' },
    { level: 3, number: '9.2.4', title: 'Adverse Events of Special Interest', page: '43' },
    { level: 3, number: '9.2.5', title: 'Disease-Related Events or Outcomes Not Qualifying as AEs or SAEs', page: '44' },
    { level: 2, number: '9.3', title: 'Pregnancy and Postpartum Information', page: '44' },
    { level: 3, number: '9.3.1', title: 'Participants Who Become Pregnant During the Trial', page: '44' },
    { level: 3, number: '9.3.2', title: 'Participants Whose Partners Become Pregnant During the Trial', page: '44' },
    { level: 2, number: '9.4', title: 'Special Safety Situations', page: '45' },
    { level: 1, number: '10', title: 'STATISTICAL CONSIDERATIONS', page: '45' },
    { level: 2, number: '10.1', title: 'General Considerations', page: '45' },
    { level: 2, number: '10.2', title: 'Analysis Sets', page: '46' },
    { level: 2, number: '10.3', title: 'Analyses of Demographics and Other Baseline Variables', page: '46' },
    { level: 2, number: '10.4', title: 'Analyses Associated with the Primary Objective(s)', page: '46' },
    { level: 3, number: '10.4.1', title: 'Primary Objective [#]', page: '46' },
    { level: 4, number: '10.4.1.1', title: 'Statistical Analysis Method', page: '46' },
    { level: 4, number: '10.4.1.2', title: 'Handling of Data in Relation to Primary Estimand(s)', page: '46' },
    { level: 4, number: '10.4.1.3', title: 'Handling of Missing Data in Relation to Primary Estimand(s)', page: '46' },
    { level: 4, number: '10.4.1.4', title: 'Sensitivity Analysis', page: '46' },
    { level: 4, number: '10.4.1.5', title: 'Supplementary Analysis', page: '46' },
    { level: 2, number: '10.5', title: 'Analysis Associated with the Secondary Objective(s)', page: '47' },
    { level: 3, number: '10.5.1', title: 'Secondary Objective [#]', page: '47' },
    { level: 4, number: '10.5.1.1', title: 'Statistical Analysis Method', page: '47' },
    { level: 4, number: '10.5.1.2', title: 'Handling of Data in Relation to Secondary Estimand(s)', page: '47' },
    { level: 4, number: '10.5.1.3', title: 'Handling of Missing Data in Relation to Secondary Estimand(s)', page: '47' },
    { level: 4, number: '10.5.1.4', title: 'Sensitivity Analysis', page: '47' },
    { level: 4, number: '10.5.1.5', title: 'Supplementary Analysis', page: '47' },
    { level: 2, number: '10.6', title: 'Analyses Associated with the Exploratory Objective(s)', page: '48' },
    { level: 2, number: '10.7', title: 'Safety Analyses', page: '48' },
    { level: 2, number: '10.8', title: 'Other Analyses', page: '48' },
    { level: 2, number: '10.9', title: 'Interim Analyses', page: '48' },
    { level: 2, number: '10.10', title: 'Multiplicity Adjustments', page: '49' },
    { level: 2, number: '10.11', title: 'Sample Size Determination', page: '50' },
    { level: 1, number: '11', title: 'TRIAL OVERSIGHT AND OTHER GENERAL CONSIDERATIONS', page: '50' },
    { level: 2, number: '11.1', title: 'Regulatory and Ethical Considerations', page: '50' },
    { level: 2, number: '11.2', title: 'Trial Oversight', page: '51' },
    { level: 3, number: '11.2.1', title: 'Investigator Responsibilities', page: '51' },
    { level: 3, number: '11.2.2', title: 'Sponsor Responsibilities', page: '51' },
    { level: 2, number: '11.3', title: 'Informed Consent Process', page: '51' },
    { level: 3, number: '11.3.1', title: 'Informed Consent for Rescreening', page: '51' },
    { level: 3, number: '11.3.2', title: 'Informed Consent for Use of Remaining Samples in Exploratory Research', page: '51' },
    { level: 2, number: '11.4', title: 'Committees', page: '52' },
    { level: 2, number: '11.5', title: 'Insurance and Indemnity', page: '52' },
    { level: 2, number: '11.6', title: 'Risk-Based Quality Management', page: '52' },
    { level: 2, number: '11.7', title: 'Data Governance', page: '52' },
    { level: 2, number: '11.8', title: 'Data Protection', page: '52' },
    { level: 2, number: '11.9', title: 'Source Records', page: '52' },
    { level: 3, number: '11.9.1', title: 'Data Quality Assurance', page: '52' },
    { level: 3, number: '11.9.2', title: 'Source Documents', page: '52' },
    { level: 2, number: '11.10', title: 'Protocol Deviations', page: '53' },
    { level: 2, number: '11.11', title: 'Early Site Closure', page: '53' },
    { level: 2, number: '11.12', title: 'Data Dissemination', page: '53' },
    { level: 3, number: '11.12.1', title: 'Publication Policy', page: '53' },
    { level: 1, number: '12', title: 'APPENDIX: SUPPORTING DETAILS', page: '54' },
    { level: 2, number: '12.1', title: 'Clinical Laboratory Tests', page: '54' },
    { level: 2, number: '12.2', title: 'Country/Region-Specific Differences', page: '54' },
    { level: 2, number: '12.3', title: 'Prior Protocol Amendment(s)', page: '54' },
    { level: 2, number: '12.4', title: 'Liver Safety: Suggestions and Guidelines for Liver Events', page: '54' },
    { level: 1, number: '13', title: 'APPENDIX: GLOSSARY OF TERMS AND ABBREVIATIONS', page: '57' },
    { level: 1, number: '14', title: 'APPENDIX: REFERENCES', page: '57' }
  ];

  const tocElements = [];

  // Add TOC title
  tocElements.push(new Paragraph({
    children: [
      new TextRun({
        text: 'Table of Contents',
        font: fontConfig.title.name,
        size: fontConfig.title.size * 2,
        bold: true,
        color: fontConfig.title.color
      })
    ],
    heading: HeadingLevel.HEADING_1,
    alignment: AlignmentType.CENTER,
    spacing: {
      line: Math.round(fontConfig.title.lineSpacing * 240),
      after: 480 // Add space after TOC title
    }
  }));

  // Add TOC entries
  tocEntries.forEach(entry => {
    const indent = (entry.level - 1) * 360; // 360 twips per level (18pt)
    
    tocElements.push(new Paragraph({
      children: [
        new TextRun({
          text: `${entry.number} ${entry.title}`,
          font: fontConfig.body.name,
          size: fontConfig.body.size * 2,
          bold: entry.level === 1,
          color: fontConfig.body.color
        }),
        new TextRun({
          text: ' ',
          font: fontConfig.body.name,
          size: fontConfig.body.size * 2
        }),
        new TextRun({
          text: '.'.repeat(Math.max(1, 80 - entry.title.length - entry.number.length)),
          font: fontConfig.body.name,
          size: fontConfig.body.size * 2,
          color: fontConfig.body.color
        }),
        new TextRun({
          text: ` ${entry.page}`,
          font: fontConfig.body.name,
          size: fontConfig.body.size * 2,
          bold: false,
          color: fontConfig.body.color
        })
      ],
      alignment: AlignmentType.LEFT,
      indent: {
        left: indent
      },
      spacing: {
        line: Math.round(fontConfig.body.lineSpacing * 240),
        after: 60
      }
    }));
  });

  return tocElements;
};

// Create hyperlinks for cross-references within the document
const createDocumentHyperlink = (text, targetSection, fontConfig) => {
  return new Paragraph({
    children: [
      new TextRun({
        text: text,
        font: fontConfig.body.name,
        size: fontConfig.body.size * 2,
        color: "#0000FF", // Blue color for hyperlinks
        underline: {
          type: "single"
        }
      })
    ],
    spacing: {
      line: Math.round(fontConfig.body.lineSpacing * 240),
      before: 60
    }
  });
};

// Process content to detect and create hyperlinks for cross-references
const processHyperlinksInContent = (content) => {
  if (!content) return content;
  
  
  // Common cross-reference patterns in clinical protocols
  const crossReferencePatterns = [
    // Section references like "see Section 1.1", "refer to Section 2.3.1"
    /(see|refer to|see also|as described in|as detailed in)\s+section\s+(\d+(?:\.\d+)*)/gi,
    // Table references like "Table 1", "Table 2.1"
    /(table|figure)\s+(\d+(?:\.\d+)*)/gi,
    // Appendix references like "Appendix A", "Appendix B.1"
    /(appendix)\s+([A-Z](?:\.\d+)*)/gi,
    // Protocol references like "Protocol Section 1.1"
    /(protocol\s+section)\s+(\d+(?:\.\d+)*)/gi
  ];
  
  let processedContent = content;
  
  // Process each pattern
  crossReferencePatterns.forEach(pattern => {
    processedContent = processedContent.replace(pattern, (match, prefix, reference) => {
      // For now, just log the matches - Word 2024 will handle the hyperlinks automatically
      // when it detects proper heading structure
      return match;
    });
  });
  
  return processedContent;
};

// Generate Word 2024 compatible Table of Contents with proper TOC field
const generateWord2024TableOfContents = (fontConfig, sections = []) => {
  
  return [
    new Paragraph({
      children: [
        new TextRun({
          text: "Table of Contents",
          font: fontConfig.heading.name,
          size: 28,
          bold: false,
          color: fontConfig.heading.color,
        }),
      ],
      // Front-matter style (not a TOC entry)
      style: "CFrontMatter",
      alignment: AlignmentType.CENTER,
      spacing: {
        after: 480,
        before: 0,
      },
    }),
    new TableOfContents("", {
      hyperlink: true,
      headingStyleRange: "1-4",
      rightAlignTabPositions: true,
      useOutlineLevels: true,
      // Only paragraphs with explicit outlineLevel (our CHeading content headings).
      // Do NOT use built-in Heading 1–4 styles — that pulls in TOC/LOT/LOF titles and
      // any legacy heading: HeadingLevel.* paragraphs.
      useHeadingStyles: false,
      entriesFromHeadingStyles: false,
      useAppliedParagraphOutlineLevel: true,
      includePageNumbers: true,
      pageNumbersRightAligned: true,
      showPageNumbers: true,
      showLevels: "1-4",
      tabLeader: "dot",
      tabStopPosition: 9350,
    }),
  ];
};
// generateTOCEntries function removed - using only native Word TOC field

// isProperHeading function removed - using only native Word TOC field

// Extract subsections from content - only actual headings, not content
const extractSubsectionsFromContent = (content, sectionRootPrefix = null) => {
  if (!content) return [];
  
  const lines = content.split('\n');
  
  // Merge headings that were broken across lines, e.g.,
  // "4.2.5 Rationale for" on one line and "Trial Duration" on the next
  const mergedLines = [];
  for (let i = 0; i < lines.length; i++) {
    const current = (lines[i] || '').trim();
    if (!current) { mergedLines.push(lines[i]); continue; }
    const next = (lines[i + 1] || '').trim();
    const headingMatch = current.match(/^(\d+(?:\.\d+)+)\s+(.+)$/);
    const nextIsCandidate = next && !/^\d/.test(next) && !/^[-*•]/.test(next);
    if (headingMatch && nextIsCandidate) {
      const title = headingMatch[2];
      const endsWithConnector = /\b(for|of|to|and|or|in|on|with|without)$/i.test(title);
      const nextWordCount = next.split(/\s+/).length;
      if (endsWithConnector || /\bRationale for\s*$/i.test(title) || title.length < 25) {
        mergedLines.push(`${current} ${next}`);
        i++; // skip next line
        continue;
      }
    }
    mergedLines.push(lines[i]);
  }
  
  const effectiveLines = mergedLines;
  const subsections = [];
  
  
  effectiveLines.forEach((line, index) => {
    if (typeof line === 'string' && line.startsWith(REMAINDER_FORCE_PARAGRAPH_PREFIX)) {
      effectiveLines[index] = line.slice(REMAINDER_FORCE_PARAGRAPH_PREFIX.length);
      return;
    }

    const trimmedLine = (effectiveLines[index] || '').trim();
    
    // Look for subsection patterns like "1.1 Title", "1.1.1 Title"
    // FIXED: Only capture the title part, not all content that follows
    const subMatch = trimmedLine.match(/^(\d+(?:\.\d+)+)\s+(.+)$/);
    if (subMatch) {
      const [, number, title] = subMatch;
      const cleanTitle = title.trim();
      if (!isLikelyProtocolHeading(number, cleanTitle, { sectionRootPrefix, kind: 'subsection' })) {
        return;
      }
      if (sectionRootPrefix && !(number === sectionRootPrefix || number.startsWith(`${sectionRootPrefix}.`))) {
        return;
      }
      
      // Basic sanity check to avoid obviously content-like lines
      if (cleanTitle.length <= 120) {
        const { headingText, remainderText, remainderForceParagraph } = splitHeadingAndRemainder(cleanTitle, sectionRootPrefix);
        const finalHeadingText = headingText || cleanTitle;
        subsections.push(`${number} ${finalHeadingText}`);
        if (remainderText) {
          const remainderLine = remainderForceParagraph
            ? `${REMAINDER_FORCE_PARAGRAPH_PREFIX}${remainderText}`
            : remainderText;
          effectiveLines.splice(index + 1, 0, remainderLine);
        }
      } else {
      }
    }
  });
  
  return subsections;
};

// isActualHeading helper removed (no longer needed)

// Helper function to test if a pattern matches in content

const REMAINDER_FORCE_PARAGRAPH_PREFIX = '__FORCE_PARA__';

const splitHeadingAndRemainder = (titleText = '', htmlContextPrefix = null) => {
  if (!titleText) {
    return { headingText: '', remainderText: '', remainderForceParagraph: false };
  }

  const originalText = titleText.trim();
  // Protocol headings (e.g. "7.1 Discontinuation of Trial Intervention") must stay intact.
  // Keyword/boundary splitting below incorrectly cuts at words like "Trial".
  if (parseProtocolHeadingLine(originalText, { sectionRootPrefix: htmlContextPrefix })) {
    return { headingText: originalText, remainderText: '', remainderForceParagraph: false };
  }

  let headingText = originalText;
  let remainderText = '';
  let remainderForceParagraph = false;

  const leadSentenceRegex = /^(.+?[.?!])\s+(.*)$/;
  const firstSentenceMatch = headingText.match(leadSentenceRegex);
  if (firstSentenceMatch && firstSentenceMatch[1] && firstSentenceMatch[1].trim().length > 0) {
    headingText = firstSentenceMatch[1].trim();
    remainderText = (firstSentenceMatch[2] || '').trim();
    remainderForceParagraph = true;
  }

  if (!remainderText) {
    const boundaryRegex = /(?<!\b[A-Z]\.)(?<!\bvs\.)(?<!\bet al\.)(?<!\bInc\.)(?<!\bLtd\.)(?<!\bDr\.)(?<!\bMr\.)(?<!\bMs\.)(?<!\bSr\.)(?<!\bJr\.)(?<!\bSt\.)(?<!\bProf\.)(?<!\bFig\.)(?<!\bRef\.)(?<!\bSec\.)(?<!\bNo\.)(?<!\bJan\.)(?<!\bFeb\.)(?<!\bMar\.)(?<!\bApr\.)(?<!\bJun\.)(?<!\bJul\.)(?<!\bAug\.)(?<!\bSep\.)(?<!\bOct\.)(?<!\bNov\.)(?<!\bDec\.)(?<!\d)\.\s+/;
    const boundaryMatch = headingText.match(boundaryRegex);
    if (boundaryMatch && typeof boundaryMatch.index === 'number') {
      const idx = boundaryMatch.index + boundaryMatch[0].length;
      if (idx > 0 && idx < headingText.length) {
        remainderText = headingText.slice(idx).trim();
        headingText = headingText.slice(0, idx).trim();
        remainderForceParagraph = true;
      }
    }
  }

  if (!remainderText && htmlContextPrefix) {
    const numericRegex = /\s+(\d+(?:\.\d+)*)\b/g;
    const sectionRootParts = htmlContextPrefix.split('.');
    let match;
    while ((match = numericRegex.exec(originalText)) !== null) {
      const candidateNumber = match[1];
      const candidateParts = candidateNumber.split('.');
      const candidatePrefix = candidateParts.slice(0, sectionRootParts.length).join('.');
      const isSamePrefix = candidatePrefix === htmlContextPrefix;
      if (!isSamePrefix) {
        const numberStartOffsetInMatch = match[0].search(/\d/);
        const absoluteIndex = match.index + (numberStartOffsetInMatch >= 0 ? numberStartOffsetInMatch : 0);
        if (absoluteIndex > 0) {
          headingText = originalText.slice(0, absoluteIndex).trim();
          remainderText = originalText.slice(absoluteIndex).trim();
          remainderForceParagraph = true;
          break;
        } else if (absoluteIndex === 0) {
          headingText = '';
          remainderText = originalText.trim();
          remainderForceParagraph = true;
          break;
        }
      }
    }
  }

  if (!remainderText) {
    const keywordRegex = /\s+(?:The|This|These|Participants|Patients|All|In|For|Detailed|Information|Additional|To|An|Each|During|Following|Study|Trial|The primary|The secondary)\b/;
    const keywordMatch = headingText.match(keywordRegex);
    if (keywordMatch) {
      const idx = headingText.indexOf(keywordMatch[0]);
      if (idx > 0) {
        remainderText = headingText.slice(idx).trim();
        headingText = headingText.slice(0, idx).trim();
        remainderForceParagraph = true;
      }
    }
  }

  return { headingText, remainderText, remainderForceParagraph };
};
// Process content to properly separate subsections from content
const processContentForProperSubsections = (content, sectionTitle = 'UNKNOWN', sectionRootPrefix = '') => {
  if (!content) return content;
  
  const derivedPrefixMatch = sectionTitle.match(/^(\d+(?:\.\d+)*)/);
  const effectiveSectionPrefix = sectionRootPrefix || (derivedPrefixMatch ? derivedPrefixMatch[1] : null);
  
  
  // CHECK IF THIS IS A SECTION THAT NEEDS PROCESSING
  const isSection4 = sectionTitle.includes('4. TRIAL DESIGN') || sectionTitle.includes('TRIAL DESIGN') || sectionTitle.startsWith('4.') || content.includes('4.1.1') || content.includes('4.2.4') || content.includes('4.2.5');
  const isSection9 = sectionTitle.includes('9. ADVERSE EVENTS') || sectionTitle.includes('ADVERSE EVENTS') || sectionTitle.includes('SERIOUS ADVERSE EVENTS') || sectionTitle.startsWith('9.') || content.includes('9.1.3') || content.includes('9.2.2');
  
  
  // If this is not Section 4 or Section 9, skip processing
  if (!isSection4 && !isSection9) {
    return content;
  }
  
  
  
  // First, handle the specific pattern from the exported content
  // Pattern: "2.2 Assessment of Risks and Benefits 2.2.1 Risk Summary and Mitigation Strategy content..."
  let processedContent = content;
  
  // EMERGENCY DEBUGGING: Check if this is the right content
  
  // SIMPLE AND DIRECT APPROACH: Handle patterns that might still be concatenated
  // Since HTML is already properly structured, focus on any remaining concatenated patterns
  
  // Handle Section 2 patterns (if any exist)
  processedContent = processedContent.replace(
    /2\.2 Assessment of Risks and Benefits 2\.2\.1 Risk Summary and Mitigation Strategy/g,
    '2.2 Assessment of Risks and Benefits\n\n2.2.1 Risk Summary and Mitigation Strategy'
  );
  
  processedContent = processedContent.replace(
    /2\.2\.2 Benefit Summary/g,
    '\n\n2.2.2 Benefit Summary'
  );
  
  processedContent = processedContent.replace(
    /2\.2\.3 Overall Risk-Benefit Assessment/g,
    '\n\n2.2.3 Overall Risk-Benefit Assessment'
  );
  
  // Handle 4.2.x patterns from the exported content - EXACT patterns from exported content
  const before4_2 = processedContent;
  processedContent = processedContent.replace(
    /4\.2 Rationale for Trial Design 4\.2\.1 Rationale for Estimand\(s\) The primary objectives and endpoints are Overall Survival \(OS\) in the PD-L1 CPS ≥1 and PD-L1 CPS ≥20 populations, and Progression-Free Survival \(PFS\) per RECIST v1\.1 by investigator assessment in the PD-L1 CPS ≥1 population\. OS is a gold standard endpoint in oncology trials, representing the ultimate measure of clinical benefit\. PFS is a critical endpoint to demonstrate anti-tumor activity\. The secondary objectives and endpoints include PFS per iRECIST, ORR, Disease Control Rate \(DCR\), Duration of Response \(DoR\), milestone OS rates, and patient-reported outcomes \(PROs\) related to pain and physical function\. These endpoints are clinically relevant and widely accepted measures of treatment efficacy and patient experience in head and neck squamous cell carcinoma \(HNSCC\)\./g,
    '4.2 Rationale for Trial Design\n\n4.2.1 Rationale for Estimand(s)\n\nThe primary objectives and endpoints are Overall Survival (OS) in the PD-L1 CPS ≥1 and PD-L1 CPS ≥20 populations, and Progression-Free Survival (PFS) per RECIST v1.1 by investigator assessment in the PD-L1 CPS ≥1 population. OS is a gold standard endpoint in oncology trials, representing the ultimate measure of clinical benefit. PFS is a critical endpoint to demonstrate anti-tumor activity. The secondary objectives and endpoints include PFS per iRECIST, ORR, Disease Control Rate (DCR), Duration of Response (DoR), milestone OS rates, and patient-reported outcomes (PROs) related to pain and physical function. These endpoints are clinically relevant and widely accepted measures of treatment efficacy and patient experience in head and neck squamous cell carcinoma (HNSCC).'
  );
  if (before4_2 !== processedContent) {
  } else {
  }
  
  // Handle other 4.2.x patterns - SPECIFIC patterns for remaining concatenated subsections
  processedContent = processedContent.replace(
    /4\.2\.2 Rationale for Intervention Model/g,
    '\n\n4.2.2 Rationale for Intervention Model'
  );
  
  processedContent = processedContent.replace(
    /4\.2\.3 Rationale for Control Type/g,
    '\n\n4.2.3 Rationale for Control Type'
  );
  
  // Handle 4.1.1 - specific pattern from exported content
  const before4_1_1 = processedContent;
  processedContent = processedContent.replace(
    /4\.1\.1 Stakeholder Input into Design Information regarding participant input into the design of this trial was not provided in the structured inputs\./g,
    '4.1.1 Stakeholder Input into Design\n\nInformation regarding participant input into the design of this trial was not provided in the structured inputs.'
  );
  if (before4_1_1 !== processedContent) {
  } else {
  }
  
  // Handle 4.2.4 - specific pattern from exported content
  const before4_2_4 = processedContent;
  processedContent = processedContent.replace(
    /4\.2\.4 Rationale for Trial Duration The trial design includes distinct Screening, Treatment, and Follow-up periods\. The duration of the treatment period will vary based on individual participant response and tolerability, aligning with clinical practice for targeted and immunotherapies\. The Follow-up period is designed to be sufficient to capture long-term survival data, monitor long-term safety, and assess the durability of response, which are essential for evaluating the overall benefit-risk profile of the investigational treatment\./g,
    '4.2.4 Rationale for Trial Duration\n\nThe trial design includes distinct Screening, Treatment, and Follow-up periods. The duration of the treatment period will vary based on individual participant response and tolerability, aligning with clinical practice for targeted and immunotherapies. The Follow-up period is designed to be sufficient to capture long-term survival data, monitor long-term safety, and assess the durability of response, which are essential for evaluating the overall benefit-risk profile of the investigational treatment.'
  );
  if (before4_2_4 !== processedContent) {
  } else {
  }
  
  // Handle 4.2.5 - specific pattern from exported content
  const before4_2_5 = processedContent;
  processedContent = processedContent.replace(
    /4\.2\.5 Rationale for Adaptive or Novel Trial Design The trial incorporates an adaptive design with two potential parts: a Phase II and a Phase III\. An adaptive interim analysis will be performed based on the ORR in the Phase II part\. This adaptive design allows for efficient resource allocation and risk mitigation\. If the investigational combination therapy does not demonstrate a promising signal of anti-tumor activity \(ORR\) in the Phase II part, the trial may not expand to Phase III, thereby conserving resources\. Conversely, if the combination therapy shows a strong signal, it will proceed to a larger Phase III part to confirm the efficacy and safety benefits in a more definitive manner\./g,
    '4.2.5 Rationale for Adaptive or Novel Trial Design\n\nThe trial incorporates an adaptive design with two potential parts: a Phase II and a Phase III. An adaptive interim analysis will be performed based on the ORR in the Phase II part. This adaptive design allows for efficient resource allocation and risk mitigation. If the investigational combination therapy does not demonstrate a promising signal of anti-tumor activity (ORR) in the Phase II part, the trial may not expand to Phase III, thereby conserving resources. Conversely, if the combination therapy shows a strong signal, it will proceed to a larger Phase III part to confirm the efficacy and safety benefits in a more definitive manner.'
  );
  if (before4_2_5 !== processedContent) {
  } else {
  }
  
  processedContent = processedContent.replace(
    /4\.2\.6 Rationale for Interim Analysis/g,
    '\n\n4.2.6 Rationale for Interim Analysis'
  );
  
  processedContent = processedContent.replace(
    /4\.2\.7 Rationale for Other Trial Design Aspects/g,
    '\n\n4.2.7 Rationale for Other Trial Design Aspects'
  );
  
  // Handle 4.4 - specific pattern from exported content
  const before4_4 = processedContent;
  processedContent = processedContent.replace(
    /4\.4 Start of Trial and End of Trial The trial start is defined as the date when the first participant signs informed consent\. The trial end is when the last participant completes their final study visit\. Local definitions may vary and will be documented accordingly\./g,
    '4.4 Start of Trial and End of Trial\n\nThe trial start is defined as the date when the first participant signs informed consent. The trial end is when the last participant completes their final study visit. Local definitions may vary and will be documented accordingly.'
  );
  if (before4_4 !== processedContent) {
  } else {
  }
  
  // Handle 4.5 - specific pattern from exported content
  const before4_5 = processedContent;
  processedContent = processedContent.replace(
    /4\.5 Access to Trial Intervention After End of Trial Information regarding access to the trial intervention after the end of the trial \(e\.g\., through extension trials or post-trial access programs\) was not provided in the structured inputs\./g,
    '4.5 Access to Trial Intervention After End of Trial\n\nInformation regarding access to the trial intervention after the end of the trial (e.g., through extension trials or post-trial access programs) was not provided in the structured inputs.'
  );
  if (before4_5 !== processedContent) {
  } else {
  }
  
  // Handle section 4 patterns - EXACT patterns from exported content (same approach as section 2)
  processedContent = processedContent.replace(
    /4\.2\.7 Rationale for Other Trial Design Aspects The trial design includes stratification factors to ensure balanced groups and to account for known prognostic and predictive factors in HNSCC\. These factors are: i\) PD-L1 CPS status \(CPS ≥20 vs\. 1≤ CPS 4\.3 Trial Stopping Rules/g,
    '4.2.7 Rationale for Other Trial Design Aspects\n\nThe trial design includes stratification factors to ensure balanced groups and to account for known prognostic and predictive factors in HNSCC. These factors are: i) PD-L1 CPS status (CPS ≥20 vs. 1≤ CPS\n\n4.3 Trial Stopping Rules'
  );
  
  // Handle section 9 patterns - EXACT patterns from exported content (same approach as section 2)
  processedContent = processedContent.replace(
    /9\.1\.2 Definitions of Serious Adverse Events A serious adverse event \(SAE\) is an AE that:/g,
    '9.1.2 Definitions of Serious Adverse Events\n\nA serious adverse event (SAE) is an AE that:'
  );
  
  // Handle 9.1.3 - specific pattern from exported content
  const before9_1_3 = processedContent;
  processedContent = processedContent.replace(
    /9\.1\.3 Definitions of Product Complaints A product complaint is any written, electronic, or oral communication that alleges deficiencies related to the identity, quality, durability, reliability, safety, effectiveness, or performance of a marketed product or investigational product \(IP\) after it has been released for distribution\. This includes the IP, placebo, and any associated materials such as labels, leaflets, or packaging\. Product complaints are not considered AEs unless they are associated with an untoward medical occurrence\./g,
    '9.1.3 Definitions of Product Complaints\n\nA product complaint is any written, electronic, or oral communication that alleges deficiencies related to the identity, quality, durability, reliability, safety, effectiveness, or performance of a marketed product or investigational product (IP) after it has been released for distribution. This includes the IP, placebo, and any associated materials such as labels, leaflets, or packaging. Product complaints are not considered AEs unless they are associated with an untoward medical occurrence.'
  );
  if (before9_1_3 !== processedContent) {
  } else {
  }
  
  // Handle the long concatenated line from section 9.2.2
  const before9_2_2 = processedContent;
  processedContent = processedContent.replace(
    /9\.2\.2 Collection Procedures Identification: AEs, SAEs, and other safety events may be identified through clinical observation by the investigator or trial staff, review of laboratory results, physical examinations, vital signs, or through spontaneous reports by the participant\. Severity: AEs will be graded according to the National Cancer Institute Common Terminology Criteria for Adverse Events \(NCI CTCAE\) version 5\.0\. Causality: The investigator will assess the causal relationship between the trial intervention and each AE\/SAE\. Causality will be categorized as related, possibly related, or unrelated\. Recording: All AEs, SAEs, and other safety events will be recorded in the electronic Case Report Form \(eCRF\) within a timeframe consistent with local regulatory requirements and GSK standards \(typically within 7 days of awareness for AEs, and within 24 hours for SAEs\)\. Follow-up: All AEs, SAEs, and other safety events will be followed by the investigator until they resolve, stabilize, or are explained by an alternate etiology\. Follow-up may involve additional clinical evaluations, laboratory tests, or imaging, as deemed necessary by the investigator\. 9\.2\.3 Reporting The primary method for reporting AEs, SAEs, and other safety events is the EDC system\. Backup methods, such as fax or email to the sponsor's designated safety contact, may be used if EDC is unavailable, with subsequent data entry into EDC once available\./g,
    '9.2.2 Collection Procedures\n\nIdentification: AEs, SAEs, and other safety events may be identified through clinical observation by the investigator or trial staff, review of laboratory results, physical examinations, vital signs, or through spontaneous reports by the participant. Severity: AEs will be graded according to the National Cancer Institute Common Terminology Criteria for Adverse Events (NCI CTCAE) version 5.0. Causality: The investigator will assess the causal relationship between the trial intervention and each AE/SAE. Causality will be categorized as related, possibly related, or unrelated. Recording: All AEs, SAEs, and other safety events will be recorded in the electronic Case Report Form (eCRF) within a timeframe consistent with local regulatory requirements and GSK standards (typically within 7 days of awareness for AEs, and within 24 hours for SAEs). Follow-up: All AEs, SAEs, and other safety events will be followed by the investigator until they resolve, stabilize, or are explained by an alternate etiology. Follow-up may involve additional clinical evaluations, laboratory tests, or imaging, as deemed necessary by the investigator.\n\n9.2.3 Reporting\n\nThe primary method for reporting AEs, SAEs, and other safety events is the EDC system. Backup methods, such as fax or email to the sponsor\'s designated safety contact, may be used if EDC is unavailable, with subsequent data entry into EDC once available.'
  );
  if (before9_2_2 !== processedContent) {
  } else {
  }
  
  processedContent = processedContent.replace(
    /9\.2\.3\.1 Regulatory Reporting Requirements Investigator's role: The investigator is responsible for immediately reporting all SAEs and other reportable safety events \(e\.g\., AESIs, pregnancies, product complaints\) to the sponsor within the specified timelines\. The investigator must also report SAEs to their Institutional Review Board \(IRB\) or Ethics Committee \(EC\) as required by local regulations\. Sponsor's role: The sponsor is responsible for submitting SAEs and other reportable safety events to regulatory authorities \(e\.g\., FDA, EMA, local health authorities\) within the timelines prescribed by global and local regulations \(e\.g\., CIOMS, FDA Form 3500A, EudraVigilance\)\. 9\.2\.4 Adverse Events of Special Interest \(AESIs\)/g,
    '9.2.3.1 Regulatory Reporting Requirements\n\nInvestigator\'s role: The investigator is responsible for immediately reporting all SAEs and other reportable safety events (e.g., AESIs, pregnancies, product complaints) to the sponsor within the specified timelines. The investigator must also report SAEs to their Institutional Review Board (IRB) or Ethics Committee (EC) as required by local regulations. Sponsor\'s role: The sponsor is responsible for submitting SAEs and other reportable safety events to regulatory authorities (e.g., FDA, EMA, local health authorities) within the timelines prescribed by global and local regulations (e.g., CIOMS, FDA Form 3500A, EudraVigilance).\n\n9.2.4 Adverse Events of Special Interest (AESIs)'
  );
  
  // More aggressive approach: Handle any remaining concatenated patterns
  // Look for patterns like "4.2.7 Title 4.3 Title" and split them
  const beforeAggressive = processedContent;
  processedContent = processedContent.replace(
    /(\d+\.\d+(?:\.\d+)*)\s+([^0-9]+?)(\s+\d+\.\d+(?:\.\d+)*\s+[^0-9]+?)/g,
    '$1 $2\n\n$3'
  );
  
  // Handle patterns where subsection numbers are concatenated without spaces
  processedContent = processedContent.replace(
    /(\d+\.\d+(?:\.\d+)*)\s+([^0-9]+?)(\d+\.\d+(?:\.\d+)*\s+[^0-9]+?)/g,
    '$1 $2\n\n$3'
  );
  
  // FLEXIBLE PATTERN MATCHING: Apply only when multiple numbered headings appear on the same line.
  const hasInlineMultipleHeadings = /(\d+\.\d+(?:\.\d+)*)[^\n]+(\d+\.\d+(?:\.\d+)*)/.test(processedContent);
  if (hasInlineMultipleHeadings) {
    // Flexible pattern for any remaining concatenated subsections on the SAME line
  const flexiblePatterns = [
      // Insert a break only when a capitalized word immediately follows text from the same numbered block,
      // which indicates two headings concatenated inline.
      { pattern: /(\d+\.\d+(?:\.\d+)*\s+[^0-9\n]+?)(\s+)(\d+\.\d+(?:\.\d+)*\s+[A-Za-z])/g, replacement: '$1\n\n$3' }
    ];
  let flexibleChanges = 0;
  flexiblePatterns.forEach(({ pattern, replacement }, index) => {
    const before = processedContent;
    processedContent = processedContent.replace(pattern, replacement);
    if (before !== processedContent) {
      flexibleChanges++;
    }
  });
  if (flexibleChanges > 0) {
  } else {
    }
  } else {
  }
  
  if (beforeAggressive !== processedContent) {
  } else {
  }
  
  // Clean up any excessive line breaks
  processedContent = processedContent.replace(/\n\n\n+/g, '\n\n');
  
  
  return processedContent;
};
// Helper: table caption — "Table <Section>-<N>:" + tab + title (C-Caption; TNR 12 pt bold; title case).
const createTableCaption = (sectionMainNumber, tableOrdinalInSection, description, fontConfig) => {
  const label = `Table ${sectionMainNumber}-${tableOrdinalInSection}:`;
  const title = toTitleCaseTableCaption(description);
  const cap = fontConfig.caption || fontConfig.body;
  const runs = [
    new TextRun({
      text: label,
      font: cap.name,
      size: cap.size * 2,
      bold: true,
      color: cap.color || '000000',
    }),
    new DocxTab(),
    new TextRun({
      text: title,
      font: cap.name,
      size: cap.size * 2,
      bold: true,
      color: cap.color || '000000',
    }),
  ];
  const anchor = anchorIdForTable(`${sectionMainNumber}-${tableOrdinalInSection}`);
  return new Paragraph({
    style: "CCaption",
    children: anchor ? [new Bookmark({ id: anchor, children: runs })] : runs,
    alignment: AlignmentType.LEFT,
    spacing: {
      after: 120,
      before: 120,
    },
    keepNext: true,
  });
};

// Helper function to create figure caption
const createFigureCaption = (figureNumber, description, fontConfig) => {
  const runs = [
    new TextRun({
      text: `Figure ${figureNumber}. `,
      bold: true,
      color: fontConfig.body.color
    }),
    new TextRun({
      text: description,
      bold: false,
      color: fontConfig.body.color
    })
  ];
  const anchor = anchorIdForFigure(figureNumber);
  return new Paragraph({
    style: "CCaption",
    children: anchor ? [new Bookmark({ id: anchor, children: runs })] : runs,
    alignment: AlignmentType.LEFT,
    spacing: {
      after: 120,
      before: 120
    }
  });
};

// Helper to extract figure description from context or attributes
const extractFigureDescription = (attrs = {}, sectionTitle = '', figureIndex = 1) => {
  const alt = (attrs.alt || '').trim();
  const title = (attrs.title || '').trim();
  if (alt) return alt;
  if (title) return title;
  if (/schema|diagram/i.test(sectionTitle)) return `Diagram from ${sectionTitle.replace(/^\d+(?:\.\d+)*\.?\s+/, '')}`;
  return `Figure from ${String(sectionTitle || 'document').replace(/^\d+(?:\.\d+)*\.?\s+/, '')}`;
};

if (typeof window !== 'undefined' && !window.__protocolMermaidInit) {
  mermaid.initialize({
    startOnLoad: false,
    theme: 'default',
    securityLevel: 'strict',
    flowchart: { htmlLabels: false },
  });
  window.__protocolMermaidInit = true;
}

/** Rasterize an SVG string to a PNG ArrayBuffer via an off-screen canvas (required as the docx
 * SVG image fallback for older Word/viewer compatibility). */
const rasterizeSvgToPng = (svgString, width, height) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob((blob) => {
          if (!blob) { reject(new Error('Canvas toBlob returned null')); return; }
          blob.arrayBuffer().then(resolve, reject);
        }, 'image/png');
      } catch (err) {
        reject(err);
      }
    };
    img.onerror = () => reject(new Error('Failed to load rendered Mermaid SVG for rasterization'));
    img.src = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svgString)))}`;
  });

/** Render raw Mermaid source (same engine used for the canvas preview) into SVG + a PNG fallback
 * suitable for docx's ImageRun({ type: 'svg', ..., fallback: {...} }). */
const renderMermaidSourceToImage = async (mermaidSource, renderId) => {
  const { svg: rawSvg } = await mermaid.render(renderId, mermaidSource);
  const svg = sanitizeMermaidSvg(rawSvg);
  const widthMatch = svg.match(/width="([\d.]+)/);
  const heightMatch = svg.match(/height="([\d.]+)/);
  const naturalWidth = widthMatch ? parseFloat(widthMatch[1]) : 600;
  const naturalHeight = heightMatch ? parseFloat(heightMatch[1]) : 400;
  const maxWidthPt = 500; // keep within the printable page width
  const scale = naturalWidth > maxWidthPt ? maxWidthPt / naturalWidth : 1;
  const width = Math.round(naturalWidth * scale);
  const height = Math.round(naturalHeight * scale);

  const svgBuffer = new TextEncoder().encode(svg).buffer;
  const pngBuffer = await rasterizeSvgToPng(svg, width, height);
  return { svgBuffer, pngBuffer, width, height };
};

/**
 * Find every <pre class="mermaid" ...>...</pre> block in section content (already normalized by
 * prepareSectionContentForExport), render each to an image, and swap it for a bare text marker
 * that survives richTextToBlocks' HTML parsing (same technique as the existing __MAINSECTION_
 * markers) so the synchronous paragraph-building pass can later splice in the pre-rendered image.
 * Must run as an async pre-pass — mermaid.render()/canvas rasterization can't happen inline
 * inside the (synchronous) paragraph builder.
 */
let mermaidExportCounter = 0;
const extractAndRenderMermaidBlocksForExport = async (content) => {
  const raw = String(content || '');
  const images = new Map();
  if (!/<pre[^>]*class=["'][^"']*mermaid/i.test(raw)) {
    return { content: raw, images };
  }

  const unescapeHtml = (s) =>
    String(s || '')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&amp;/g, '&');

  const blockRe = /<pre[^>]*class=["'][^"']*mermaid[^"']*["'][^>]*>([\s\S]*?)<\/pre>/gi;
  const matches = [...raw.matchAll(blockRe)];
  let result = raw;
  for (const match of matches) {
    const source = unescapeHtml(match[1]).trim();
    if (!source) continue;
    mermaidExportCounter += 1;
    const marker = `MERMAIDIMAGEPLACEHOLDER${mermaidExportCounter}ENDPLACEHOLDER`;
    try {
      const renderId = `export-mermaid-${Date.now()}-${mermaidExportCounter}`;
      const image = await renderMermaidSourceToImage(source, renderId);
      images.set(marker, image);
      result = result.replace(match[0], marker);
    } catch (err) {
      console.error('Mermaid export render failed:', err);
      // Leave the original block in place rather than silently dropping the content —
      // it'll fall through as plain text instead of a diagram, but nothing is lost.
    }
  }
  return { content: result, images };
};
// Helper function to extract table description from context
const extractTableDescription = (tableContent, sectionTitle, tableIndex, usedTitles) => {
  const stripLeadingNumberFromTitle = (title) => {
    if (!title) return '';
    // Remove patterns like "13.0 ", "13 ", "13.1.2 ", with optional dot and spaces
    let t = String(title).trim().replace(/^\d+(?:\.\d+)*\.?\s+/, '');
    // Also remove any leading punctuation/colon or dash after number if present
    t = t.replace(/^[:\-\.\s]+/, '').trim();
    return t;
  };

  // Prefer using the provided section/subsection title directly — unless it's
  // already been used as another table's caption (several tables can share one
  // un-subdivided heading), in which case reusing it verbatim would make every
  // table's caption identical and non-distinguishing. Fall through to
  // table-specific signals below instead.
  const sectionDerived =
    sectionTitle && String(sectionTitle).trim().length > 0
      ? stripLeadingNumberFromTitle(String(sectionTitle).trim())
      : '';
  if (sectionDerived && !(usedTitles && usedTitles.has(sectionDerived.toLowerCase()))) {
    return sectionDerived;
  }
  // Try to find caption or description before/after table in content
  const captionMatch = tableContent.match(/(?:caption|description|title)[^>]*>([^<]+)/i);
  if (captionMatch && captionMatch[1]) {
    return stripLeadingNumberFromTitle(captionMatch[1].trim());
  }
  
  // Try to extract from first header row
  const headerMatch = tableContent.match(/<th[^>]*>([^<]+)<\/th>/i);
  if (headerMatch && headerMatch[1]) {
    const headerText = headerMatch[1].trim();
    if (headerText.length > 0 && headerText.length < 100) {
      return headerText;
    }
  }
  
  // Generate description based on section and table headers
  const headers = [];
  const headerMatches = tableContent.matchAll(/<th[^>]*>([^<]+)<\/th>/gi);
  for (const match of headerMatches) {
    headers.push(match[1].trim());
  }
  
  if (headers.length > 0) {
    if (headers.length === 2) {
      return `${headers[0]} and ${headers[1]}`;
    } else if (headers.length === 1) {
      return stripLeadingNumberFromTitle(headers[0]);
    } else {
      return stripLeadingNumberFromTitle(headers.slice(0, 2).join(' and '));
    }
  }
  
  // Fallback descriptions based on section
  if (/SCHEDULE OF ACTIVITIES|SOA/i.test(sectionTitle || '')) {
    return 'Schedule of Activities';
  } else if (/ESTIMAND/i.test(sectionTitle || '')) {
    return `Estimand Characteristics${tableIndex > 1 ? ` (${tableIndex})` : ''}`;
  } else if (/OBJECTIVES|ENDPOINTS/i.test(sectionTitle || '')) {
    return 'Objectives and Endpoints';
  } else {
    return `Table from ${stripLeadingNumberFromTitle(sectionTitle || '')}`;
  }
};
/** Unwrap SOA API shapes so `visits` / `sections` / `s9.tables` exist for Word + native PDF. */
const normalizeSoaExportPayload = (raw) => {
  if (!raw || typeof raw !== 'object') return null;
  const ok = (o) =>
    o &&
    typeof o === 'object' &&
    Array.isArray(o.visits) &&
    Array.isArray(o.sections);
  const attachS9 = (base, source) => {
    if (!base) return null;
    const s9 = source?.s9 || base.s9;
    if (s9 && typeof s9 === 'object' && Array.isArray(s9.tables) && s9.tables.length) {
      return { ...base, s9 };
    }
    // Chat workspace shape: { tables, flags } at top level
    if (Array.isArray(source?.tables) && source.tables.length) {
      return {
        ...base,
        s9: { tables: source.tables, flags: Array.isArray(source.flags) ? source.flags : [] },
      };
    }
    return base;
  };

  if (ok(raw)) return attachS9(raw, raw);
  if (ok(raw.data)) return attachS9(raw.data, raw.data?.s9 ? raw.data : { ...raw.data, ...(raw.s9 ? { s9: raw.s9 } : {}) });
  if (ok(raw.soaData)) return attachS9(raw.soaData, raw.soaData);
  if (ok(raw.result)) return attachS9(raw.result, raw.result);
  if (ok(raw.legacyCompatible)) {
    return attachS9(raw.legacyCompatible, {
      ...raw.legacyCompatible,
      s9: raw.legacyCompatible.s9 || (raw.soa?.tables ? { tables: raw.soa.tables, flags: raw.soa.flags || [] } : undefined),
      tables: raw.soa?.tables,
      flags: raw.soa?.flags,
    });
  }

  // S9-only: tables present, no flat visits/sections yet
  const tables =
    (Array.isArray(raw?.s9?.tables) && raw.s9.tables) ||
    (Array.isArray(raw?.soa?.tables) && raw.soa.tables) ||
    (Array.isArray(raw?.tables) && raw.tables) ||
    null;
  if (tables?.length) {
    return {
      visits: Array.isArray(raw.visits) ? raw.visits : [],
      sections: Array.isArray(raw.sections) ? raw.sections : [],
      footnotes: Array.isArray(raw.footnotes) ? raw.footnotes : [],
      s9: { tables, flags: Array.isArray(raw?.s9?.flags) ? raw.s9.flags : Array.isArray(raw?.flags) ? raw.flags : [] },
    };
  }
  return null;
};

const hasUsableSoaExportData = (data) => {
  if (!data || typeof data !== 'object') return false;
  if (Array.isArray(data?.s9?.tables) && data.s9.tables.length > 0) return true;
  return Array.isArray(data.visits) && Array.isArray(data.sections)
    && (data.visits.length > 0 || data.sections.length > 0);
};

/** Prefer per-schedule S9 tables so Word/PDF place Key Notes under each schedule. */
const withS9TablesForExport = (soaData, s9Tables, flags = []) => {
  const tables = Array.isArray(s9Tables) && s9Tables.length ? s9Tables : null;
  if (!tables) return soaData;
  return {
    ...(soaData && typeof soaData === 'object'
      ? soaData
      : { visits: [], sections: [], footnotes: [] }),
    s9: {
      tables,
      flags: Array.isArray(flags) ? flags : soaData?.s9?.flags || [],
    },
  };
};

/**
 * Resolve SOA for Word/PDF: memory → in-memory S9 tables → latest S9 API → legacy SOA.
 * Ensures multi-schedule footnotes export under each schedule (not one merged table).
 */
const resolveSoaDataForExport = async ({
  generatedSoAData = null,
  s9SoaTables = [],
  projectId = null,
} = {}) => {
  let soaDataToExport = normalizeSoaExportPayload(generatedSoAData) ?? generatedSoAData;
  if (Array.isArray(s9SoaTables) && s9SoaTables.length) {
    soaDataToExport = withS9TablesForExport(soaDataToExport, s9SoaTables);
  }

  if (projectId) {
    try {
      const s9Res = await s9AssessmentScheduleService.getLatestS9Soa({
        projectId,
        studyId: '',
      });
      if (s9Res?.success && s9Res.data) {
        const tablesFromApi =
          (Array.isArray(s9Res.data.soa?.tables) && s9Res.data.soa.tables) ||
          (Array.isArray(s9Res.data.legacyCompatible?.s9?.tables) &&
            s9Res.data.legacyCompatible.s9.tables) ||
          [];
        const fromLegacy = normalizeSoaExportPayload(s9Res.data.legacyCompatible);
        const fromSoa = normalizeSoaExportPayload(s9Res.data);
        const fromApi = fromLegacy || fromSoa;
        if (hasUsableSoaExportData(fromApi)) {
          soaDataToExport = fromApi;
        }
        if (tablesFromApi.length) {
          soaDataToExport = withS9TablesForExport(
            soaDataToExport,
            tablesFromApi,
            Array.isArray(s9Res.data.soa?.flags) ? s9Res.data.soa.flags : [],
          );
        }
      }
    } catch (s9Err) {
      if (s9Err?.response?.status !== 404) {
        console.warn('S9 SOA fetch for export failed:', s9Err?.message || s9Err);
      }
    }
  }

  return hasUsableSoaExportData(soaDataToExport) ? soaDataToExport : null;
};

/**
 * Convert known “symbol characters” into Word symbol runs.
 * - Use this only for characters that are commonly pasted as UI glyphs (checkboxes, ticks, crosses).
 * - Everything else stays as Unicode in TextRun (Word-native, no emoji fonts).
 */
const textToDocxRuns = (text, { font, size, bold, color, italic } = {}) => {
  const s = String(text ?? '');
  if (!s) return [new TextRun({ text: '', font, size, bold, color, italic })];

  // Map of unicode char -> SymbolRun settings (font-encoded)
  // NOTE: these are standard Wingdings encodings used by Word for checkbox/tick/cross glyphs.
  const symbolMap = new Map([
    // ballot/checkbox
    ['☐', { char: 'A8', symbolfont: 'Wingdings' }], // empty box
    ['☑', { char: 'FE', symbolfont: 'Wingdings' }], // checked box
    ['☒', { char: 'FD', symbolfont: 'Wingdings' }], // crossed box
    // ticks / crosses (common pasted glyphs)
    ['✓', { char: 'FC', symbolfont: 'Wingdings' }],
    ['✔', { char: 'FC', symbolfont: 'Wingdings' }],
    ['✗', { char: 'FB', symbolfont: 'Wingdings' }],
    ['✘', { char: 'FB', symbolfont: 'Wingdings' }],
  ]);

  const runs = [];
  let buf = '';
  const flush = () => {
    if (!buf) return;
    runs.push(new TextRun({ text: buf, font, size, bold, color, italic }));
    buf = '';
  };

  for (const ch of s) {
    const sym = symbolMap.get(ch);
    if (sym) {
      flush();
      runs.push(new SymbolRun(sym));
    } else {
      buf += ch;
    }
  }
  flush();
  return runs;
};

const isSafeExternalLink = (href) => {
  const h = String(href || '').trim();
  return /^https?:\/\//i.test(h) || /^mailto:/i.test(h);
};

/**
 * Convert a small HTML fragment into Paragraph children (supports <a href="...">text</a>).
 * Falls back to plain text when parsing fails.
 */
const htmlInlineToDocxChildren = (htmlFragment, runStyle) => {
  const raw = String(htmlFragment ?? '');
  if (!raw) return [new TextRun({ text: '', ...(runStyle || {}) })];
  if (!raw.includes('<a') && !raw.includes('</a')) {
    const plain = raw.replace(/<br\s*\/?>/gi, '\n').replace(/<[^>]*>/g, '');
    return linkifyInternalSectionRefs(plain, runStyle);
  }

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div>${raw}</div>`, 'text/html');
    const root = doc.body && doc.body.firstChild ? doc.body.firstChild : null;
    if (!root) {
      return textToDocxRuns(raw.replace(/<[^>]*>/g, ''), runStyle);
    }

    const out = [];
    const walk = (node) => {
      if (!node) return;
      if (node.nodeType === Node.TEXT_NODE) {
        const t = node.textContent || '';
        if (t) out.push(...linkifyInternalSectionRefs(t, runStyle));
        return;
      }
      if (node.nodeType !== Node.ELEMENT_NODE) return;
      const el = node;
      const tag = String(el.tagName || '').toLowerCase();
      if (tag === 'br') {
        out.push(new TextRun({ text: '\n', ...(runStyle || {}) }));
        return;
      }
      if (tag === 'a') {
        const href = el.getAttribute('href') || '';
        const linkText = (el.textContent || '').trim() || href;
        if (isSafeExternalLink(href)) {
          out.push(
            new ExternalHyperlink({
              link: href,
              children: [
                new TextRun({
                  text: linkText,
                  font: runStyle?.font,
                  size: runStyle?.size,
                  bold: runStyle?.bold,
                  italic: runStyle?.italic,
                  color: '0000FF',
                  underline: { type: 'single' },
                }),
              ],
            }),
          );
        } else {
          out.push(...linkifyInternalSectionRefs(linkText, runStyle));
        }
        return;
      }
      // Recurse for other inline nodes (strong/em/etc. already flattened elsewhere, but safe)
      for (const child of Array.from(el.childNodes || [])) walk(child);
    };

    for (const child of Array.from(root.childNodes || [])) walk(child);
    return out.length ? out : textToDocxRuns(raw.replace(/<[^>]*>/g, ''), runStyle);
  } catch (e) {
    return textToDocxRuns(raw.replace(/<[^>]*>/g, ''), runStyle);
  }
};

const anchorIdForSectionNumber = (sectionNumber) => {
  const n = String(sectionNumber || '').trim();
  if (!n) return null;
  // Word bookmark id must start with a letter and cannot contain spaces; keep it stable.
  // Word bookmark names are restrictive; avoid dots and other punctuation.
  return `sec_${n.replace(/[^0-9A-Za-z]/g, '_')}`;
};

// Bookmark ids for table/figure captions, so List of Tables / List of Figures can carry
// real Word PAGEREF fields (auto-updating page numbers) instead of static text — same
// dynamic-field approach as the main Table of Contents.
const anchorIdForTable = (displayKey) => {
  const n = String(displayKey || '').trim();
  return n ? `tbl_${n.replace(/[^0-9A-Za-z]/g, '_')}` : null;
};
const anchorIdForFigure = (figureNumber) => {
  const n = String(figureNumber || '').trim();
  return n ? `fig_${n.replace(/[^0-9A-Za-z]/g, '_')}` : null;
};

/**
 * Turn "Section 3.2" (and common variants) into clickable internal hyperlinks.
 * Requires headings to be wrapped in matching Bookmarks (see `bookmarkHeadingParagraph`).
 */
const linkifyInternalSectionRefs = (text, runStyle) => {
  const s = String(text ?? '');
  if (!s) return [new TextRun({ text: '', ...(runStyle || {}) })];

  // Matches:
  // - "Section 3.2" / "section 3.2.1"
  // - "Sec. 3.2" / "Sec 3.2"
  // - "§3.2" / "§ 3.2"
  const re = /\b(?:section|sec\.?|§)\s*(\d+(?:\.\d+)+)\b/gi;
  const out = [];
  let last = 0;
  let m;
  while ((m = re.exec(s)) !== null) {
    const idx = m.index;
    const full = m[0];
    const num = m[1];
    if (idx > last) out.push(...textToDocxRuns(s.slice(last, idx), runStyle));

    const anchor = anchorIdForSectionNumber(num);
    if (anchor) {
      out.push(
        new InternalHyperlink({
          anchor,
          children: [
            new TextRun({
              text: full,
              font: runStyle?.font,
              size: runStyle?.size,
              bold: runStyle?.bold,
              italic: runStyle?.italic,
              color: '0000FF',
              underline: { type: 'single' },
            }),
          ],
        }),
      );
    } else {
      out.push(...textToDocxRuns(full, runStyle));
    }
    last = idx + full.length;
  }
  if (last < s.length) out.push(...textToDocxRuns(s.slice(last), runStyle));
  return out.length ? out : textToDocxRuns(s, runStyle);
};

const bookmarkRunsForHeadingText = (headingText, runs) => {
  const m = String(headingText || '').match(/\b(\d+(?:\.\d+)*)\b/);
  const num = m ? m[1] : null;
  const anchor = anchorIdForSectionNumber(num);
  if (!anchor) return runs;
  // IMPORTANT: Bookmarks must be inside a Paragraph's children. Putting a Bookmark where a Paragraph/Table is expected corrupts the DOCX.
  return [new Bookmark({ id: anchor, children: runs })];
};

/** Section body text for export — same fallback as JSON/style lint paths. */
const sectionExportRaw = (section) =>
  String(section?.output || section?.content || '').trim();
const flattenSectionsForWordExport = (list) => {
  const out = [];
  const walk = (nodes) => {
    if (!nodes || !nodes.length) return;
    for (const n of nodes) {
      out.push(n);
      if (n.subsections && n.subsections.length) walk(n.subsections);
    }
  };
  walk(list);
  return out;
};

const sectionTitleLooksLikeSoa = (title) => {
  const n = String(title || '')
    .normalize('NFKC')
    .replace(/\s+/g, ' ')
    .trim();
  return /schedule\s+of\s+activities|\bsoa\b|activities\s+schedule|visit\s+schedule|assessment\s+schedule|soa\s+table/i.test(
    n,
  );
};

/** Common protocol numbering when the title text alone does not say "Schedule of Activities". */
const sectionTitleLooksLikeSoaOrStandardNumber = (title) => {
  const t = String(title || '')
    .normalize('NFKC')
    .replace(/\s+/g, ' ')
    .trim();
  return sectionTitleLooksLikeSoa(t) || /^1\.3(\s|\.)/i.test(t);
};

/** HTML SOA table from the editor (structured SOA data may still drive the Word table). */
const tableHtmlLooksLikeScheduleOfActivities = (tableContent) => {
  if (!tableContent || typeof tableContent !== 'string') return false;
  const h = tableContent.replace(/\s+/g, ' ').toLowerCase();
  if (h.includes('study procedures') && h.includes('visits')) return true;
  if (h.includes('procedures and visits')) return true;
  if (h.includes('keynotes') && (h.includes('visit') || h.includes('period') || h.includes('day'))) return true;
  return false;
};

/** Flatten nested subsection nodes so Word export includes every section with output. */
const sectionsListForWordExport = (topLevel) => {
  const roots = topLevel && topLevel.length ? topLevel : [];
  return flattenSectionsForWordExport(roots);
};

/** Root section number from title — e.g. "12. APPENDIX: SUPPORTING DETAILS" → 12 */
const rootMainSectionNumberFromTitle = (title) => {
  const m = String(title || '').trim().match(/^(\d+)/);
  return m ? parseInt(m[1], 10) : null;
};

/**
 * Detect a new top-level section embedded inside another section's content
 * (e.g. "13 Table of Appendices" inside section 12 output). Excludes subsections like 12.3.
 */
const tryParseEmbeddedMainSectionHeading = (line, sectionRootMainNum) => {
  const trimmed = String(line || '').trim();
  if (!trimmed || sectionRootMainNum == null || Number.isNaN(sectionRootMainNum)) return null;
  if (/^\d+\.\d/.test(trimmed)) return null;

  const sectionLabel = trimmed.match(/^Section\s+(\d+)\s*[:.\-–—]?\s*(.+)$/i);
  if (sectionLabel) {
    const mainNum = parseInt(sectionLabel[1], 10);
    if (mainNum > sectionRootMainNum) {
      return { mainNum, headingText: `${mainNum} ${sectionLabel[2].trim()}` };
    }
    return null;
  }

  const dotZero = trimmed.match(/^(\d+)\.0\s+(.+)$/i);
  if (dotZero) {
    const mainNum = parseInt(dotZero[1], 10);
    if (mainNum > sectionRootMainNum) {
      return { mainNum, headingText: `${dotZero[1]}.0 ${dotZero[2].trim()}` };
    }
    return null;
  }

  const plain = trimmed.match(/^(\d+)\s+(.+)$/);
  if (plain) {
    const mainNum = parseInt(plain[1], 10);
    const titlePart = plain[2].trim();
    if (
      mainNum > sectionRootMainNum &&
      titlePart.length >= 3 &&
      isLikelyProtocolHeading(plain[1], titlePart, { kind: 'main' })
    ) {
      return { mainNum, headingText: `${plain[1]} ${titlePart}` };
    }
  }
  return null;
};

const splitContentAtEmbeddedMainSections = (content, sectionRootMainNum) => {
  if (!content || sectionRootMainNum == null || Number.isNaN(sectionRootMainNum)) return content;
  let result = content;
  for (let n = sectionRootMainNum + 1; n <= sectionRootMainNum + 5; n += 1) {
    const nStr = String(n);
    // Use negative lookbehind (?<!\d) so we don't split "3.2.5 Title" at the ".5"
    result = result.replace(
      new RegExp(`(?<!\\d)([.!?])\\s*(${nStr}(?:\\.0)?\\s+[A-Za-z])`, 'g'),
      '$1\n\n$2',
    );
    result = result.replace(
      new RegExp(`([^\\n\\r])(\\s+)(${nStr}(?:\\.0)?\\s+[A-Za-z][^\\n]{2,80})`, 'g'),
      '$1\n\n$3',
    );
    result = result.replace(
      new RegExp(`([^\\n\\r])(Section\\s+${nStr}\\s*[:.\\-–—]\\s*)`, 'gi'),
      '$1\n\n$2',
    );
  }
  return result;
};

/** Avoid shared `page.size` objects across sections if the serializer ever mutates them. */
const cloneSectionPropertiesForDocx = (props) => {
  if (!props || typeof props !== 'object') return {};
  const page = props.page;
  const next = { ...props };
  if (page && typeof page === 'object') {
    next.page = {
      ...page,
      size: page.size && typeof page.size === 'object' ? { ...page.size } : page.size,
      margin:
        page.margin && typeof page.margin === 'object' ? { ...page.margin } : page.margin,
    };
  }
  return next;
};

/** DOCX header row 1 (left): investigational product name(s), else sponsor product code(s). */
const resolveHeaderProductDisplay = (names, codes) => {
  const normalize = (v) => String(v ?? '').trim();
  const isNotApplicable = (v) => {
    const s = normalize(v).toLowerCase();
    return !s || s === 'n/a' || s === 'na' || s === 'not applicable' || s === 'not app' || s === 'none';
  };

  if (!isNotApplicable(names)) return normalize(names);
  if (!isNotApplicable(codes)) return normalize(codes);
  return '';
};

const exportToWord = async (
  sections,
  trialSchemaImageUrl,
  generatedSoAData,
  fontConfig = FONT_CONFIG,
  titlePageConfig = null,
  projectId = null,
  postExportFontUi = null,
) => {
  
  // Header/footer fields: protocol + product (intake / admin), sponsor + version date (administrative info)
  let protocolNumber = '';
  let amendmentVersion = '';
  let versionDate = '';
  let sponsorName = '';
  let investigationalProductNames = resolveHeaderProductDisplay(
    titlePageConfig?.investigationalProductNames?.text,
    titlePageConfig?.sponsorProductCodes?.text,
  );
  let clinicalIntakeDataForHeader = null;
  if (projectId) {
    try {
      try {
        clinicalIntakeDataForHeader = await clinicalIntakeService.getLatestClinicalIntake(projectId);
      } catch (clinicalErr) {
        console.warn('Could not fetch clinical intake for header:', clinicalErr);
      }

      let projectDataForHeader = null;
      let adminInfoForHeader = null;
      try {
        const adminResponse = await administrativeInfoService.getAllAdministrativeInfo(projectId);

        let projectSponsorName = '';
        try {
          const projectRes = await projectService.getProject(projectId);
          projectDataForHeader = projectRes?.data ?? projectRes;
          projectSponsorName = resolveProjectSponsorName(projectDataForHeader);
        } catch (projectErr) {
          console.warn('Could not fetch project for header sponsor name:', projectErr);
        }

        if (adminResponse?.data && adminResponse.data.length > 0) {
          const adminInfo = adminResponse.data[0];
          adminInfoForHeader = adminInfo;
          sponsorName = String(
            projectSponsorName || resolveAdminSponsorFields(adminInfo).sponsorName
          ).trim();
          if (!investigationalProductNames) {
            investigationalProductNames = resolveHeaderProductDisplay(
              adminInfo.investigational_product_names,
              adminInfo.sponsor_investigational_product_codes,
            );
          }

          if (adminInfo.version_number || adminInfo.versionNumber) {
            const versionNum = adminInfo.version_number || adminInfo.versionNumber;
            amendmentVersion = `Version ${versionNum}`;
          }

          if (adminInfo.version_date) {
            const date = new Date(adminInfo.version_date);
            if (!Number.isNaN(date.getTime())) {
              const day = date.getDate().toString().padStart(2, '0');
              const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
              versionDate = `${day} ${monthNames[date.getMonth()]} ${date.getFullYear()}`;
            } else {
              versionDate = String(adminInfo.version_date).trim();
            }
          }
        }
      } catch (adminErr) {
        console.warn('Could not fetch administrative info for header/footer:', adminErr);
      }

      protocolNumber = pickHeaderProtocolNumber({
        clinicalIntake: clinicalIntakeDataForHeader,
        titlePageConfig,
        project: projectDataForHeader,
        adminInfo: adminInfoForHeader,
      });
    } catch (err) {
      console.warn('Could not fetch protocol information for header:', err);
    }
  }
  
  const effectiveSoAData = normalizeSoaExportPayload(generatedSoAData) ?? generatedSoAData;

  const children = [];
  const docSections = [];
  const tablesIndex = [];
  const figuresIndex = [];
  // Tracks captions already assigned via extractTableDescription's section-title
  // branch, so multiple tables sharing one un-subdivided heading (e.g. several
  // estimand tables under the same "Estimand" subsection) don't all get an
  // identical, non-distinguishing caption.
  const usedTableDescriptions = new Set();
  // Set by the per-section loop right before calling processContentForExport, read by
  // pushMermaidImageBlock inside htmlToParagraphs's closure — mirrors how tablesIndex/
  // figuresIndex/figureCounter are already shared across that same boundary.
  let currentMermaidImages = new Map();
   // Draft 1.0 PDF §4.5: A4 margins differ by orientation. Default export targets A4.
  // Twips: 1 in = 1440; A4 = 8.27 x 11.69 in → 11906 x 16838 twips.
  const A4_PORTRAIT = { width: 11906, height: 16838, orientation: PageOrientation.PORTRAIT };
  const A4_PORTRAIT_MARGIN = { top: 1440, right: 1109, bottom: 2434, left: 1440 }; // right 0.77", bottom 1.69"
  // docx swaps w/h in OOXML when orientation is LANDSCAPE; pass portrait A4 numbers (library convention).
  const A4_LONG_TWIPS = 16838;
  const A4_SHORT_TWIPS = 11906;
  const A4_LANDSCAPE_PAGE_FOR_DOCX = {
    width: A4_SHORT_TWIPS,
    height: A4_LONG_TWIPS,
    orientation: PageOrientation.LANDSCAPE,
  };
  const A4_LANDSCAPE_MARGIN = { top: 1440, right: 2434, bottom: 1109, left: 1440 }; // right 1.69", bottom 0.77"

  const getSoaColumnWidths = (columnCount) => {
    // Fixed column widths to avoid Word squeezing columns (which causes letter-by-letter wrapping).
    // Widths are in twips. We size to the printable width of the landscape page.
    // Key Notes render as a full-width row under each activity (matches the PDF/S9-chat
    // layout — see appendLandscapeSoaSection), not a dedicated trailing column, so every
    // non-label column here is a visit column.
    const n = Number(columnCount) || 0;
    if (n < 2) return undefined;
    const usable =
      A4_LONG_TWIPS - (A4_LANDSCAPE_MARGIN.left || 0) - (A4_LANDSCAPE_MARGIN.right || 0);

    // Wider first column for procedure names; remaining visits evenly split.
    const first = Math.round(usable * 0.24);
    const visitCount = n - 1;
    const remaining = Math.max(0, usable - first);
    const each = Math.max(360, Math.floor(remaining / visitCount)); // minimum ~0.25"

    return [first, ...Array.from({ length: visitCount }, () => each)];
  };

  const pushPortraitSection = (portraitChildren) => {
    docSections.push({
      children: [...portraitChildren],
      properties: {
        // Force a real section break so orientation reliably switches back after a landscape section.
        type: docSections.length > 0 ? SectionType.NEXT_PAGE : undefined,
        page: {
          size: { ...A4_PORTRAIT },
          margin: { ...A4_PORTRAIT_MARGIN },
        },
      },
    });
  };
  let soaHandledAsLandscape = false; // Track whether the SOA section was placed in a landscape section
  const tableOrdinalByMainSection = {};
  const nextTableOrdinalInSection = (sectionTitleForNumbering) => {
    const main = mainSectionNumberFromTitle(sectionTitleForNumbering);
    tableOrdinalByMainSection[main] = (tableOrdinalByMainSection[main] || 0) + 1;
    const ordinal = tableOrdinalByMainSection[main];
    return { main, ordinal, displayKey: `${main}-${ordinal}` };
  };
  let figureCounter = 1; // Initialize figure counter
  let lotInsertIndex = null; // where to insert List of Tables (after TOC) in current children buffer
  // Track the docSection index and intra-section index where LOT should be inserted,
  // in case we flush children into docSections before we actually splice the LOT.
  let lotTargetDocSectionIndex = null;
  let lotInsertIndexWithinSection = null;
  
  // Add title page if configured
  if (titlePageConfig && titlePageConfig.enabled) {
    children.push(...generateTitlePageContent(titlePageConfig));
    
    // Add page break after title page
    children.push(new Paragraph({
      children: [],
      pageBreakBefore: true
    }));
    
    // Add Word 2024 compatible Table of Contents
    children.push(...generateWord2024TableOfContents(fontConfig, sections));
    
    // Add page break after TOC
    children.push(new Paragraph({
      children: [],
      pageBreakBefore: true
    }));

    // Mark where to insert List of Tables (immediately after this page break)
    lotInsertIndex = children.length;
    // Record the target doc section index and index within that section, in case we flush later
    lotTargetDocSectionIndex = docSections.length; // current children will later become this docSection
    lotInsertIndexWithinSection = lotInsertIndex;
  } else {
  }
  
  // Add document title (if no title page or as fallback)
  if (!titlePageConfig || !titlePageConfig.enabled) {
    children.push(
      new Paragraph({
        children: [
          new TextRun({
            text: "Clinical Trial Protocol",
            font: fontConfig.title.name,
            size: fontConfig.title.size * 2,
            bold: fontConfig.title.bold,
            color: fontConfig.title.color
          })
        ],
        // Use CTitle style without outlineLevel so this doesn't appear in the TOC
        style: "CTitle",
        alignment: AlignmentType.CENTER,
        spacing: {
          line: Math.round(fontConfig.title.lineSpacing * 240),
          after: 360
        }
      }),
    );
  }
  
  // Convert section content (HTML or plain text) into docx Paragraph elements.
  const processContentForExport = (content, sectionTitleForContext = 'UNKNOWN_SECTION', sectionRootPrefix = null) => {
    if (!content) return [];
    const withLinks = processHyperlinksInContent(mergeFragmentedSectionNumbers(content));

      // Function to convert HTML to Word paragraphs (with section context for captions)
      const htmlToParagraphs = (htmlContent, sectionTitleForContext = '', sectionPrefixOverride = null) => {
      const paragraphs = [];
      let currentContextTitle = sectionTitleForContext;
        const sectionRootMatch = sectionTitleForContext
          ? sectionTitleForContext.match(/^(\d+(?:\.\d+)*)/)
          : null;
        const htmlContextPrefix = sectionPrefixOverride || (sectionRootMatch ? sectionRootMatch[1] : null);

      const pushBodyParagraph = (text, spacing = {}) => {
        const body = String(text || '').trim();
        if (!body) return;
        // Source content sometimes carries its own redundant "Table N-M:"/"Figure N:" caption
        // line(s) alongside the real one createTableCaption/createFigureCaption emits for the
        // detected <table>/image — e.g. an all-caps section-title-derived variant, or a
        // differently-punctuated restatement. A colon immediately after the table/figure number
        // marks a caption/label (never how a flowing reference sentence reads, e.g.
        // "Table 3-2 summarizes..."), so drop it here rather than duplicate the caption.
        if (/^(Table|Figure)\s+\d+(?:\.\d+)*(?:-\d+)?\s*:/i.test(body)) return;
        paragraphs.push(
          new Paragraph({
            style: 'CBodyText',
            children: linkifyInternalSectionRefs(body, {
              font: fontConfig.body.name,
              size: fontConfig.body.size * 2,
              bold: false,
              color: fontConfig.body.color,
            }),
            alignment: AlignmentType.JUSTIFIED,
            spacing: {
              line: Math.round(fontConfig.body.lineSpacing * 240),
              before: spacing.before ?? 0,
              after: spacing.after ?? 80,
            },
          }),
        );
      };

      const pushProtocolHeadingBlock = (rawText) => {
        const text = String(rawText || '').trim();
        if (!text) return { remainderText: '', remainderForceParagraph: false };

        const { headingText, remainderText, remainderForceParagraph } = splitHeadingAndRemainder(
          text,
          htmlContextPrefix,
        );
        const finalHeadingText = headingText || text;

        if (isDuplicateSectionHeadingText(finalHeadingText, sectionTitleForContext)) {
          return { remainderText, remainderForceParagraph };
        }

        const outlineLevel = outlineLevelForProtocolHeadingText(finalHeadingText, htmlContextPrefix);
        if (outlineLevel == null) {
          pushBodyParagraph(finalHeadingText);
          return { remainderText, remainderForceParagraph };
        }

        const parsedHeading = parseProtocolHeadingLine(finalHeadingText, { sectionRootPrefix: htmlContextPrefix });
        const sectionMainNum = htmlContextPrefix
          ? parseInt(String(htmlContextPrefix).split('.')[0], 10)
          : null;
        const isEmbeddedMainSection =
          parsedHeading?.kind === 'main' &&
          sectionMainNum != null &&
          parseInt(parsedHeading.number, 10) !== sectionMainNum;

        currentContextTitle = finalHeadingText;
        const headingFont = outlineLevel <= 1 ? fontConfig.heading : fontConfig.subheading;
        const headingRuns = textToDocxRuns(finalHeadingText, {
          font: headingFont.name,
          size: headingFont.size * 2,
          bold: headingFont.bold,
          color: headingFont.color,
        });
        const sp = draftHeadingSpacingTwips(
          [
            HeadingLevel.HEADING_1,
            HeadingLevel.HEADING_2,
            HeadingLevel.HEADING_3,
            HeadingLevel.HEADING_4,
            HeadingLevel.HEADING_5,
          ][Math.min(outlineLevel, 4)],
        );
        paragraphs.push(
          new Paragraph({
            style: protocolHeadingStyleForOutlineLevel(outlineLevel),
            children: bookmarkRunsForHeadingText(finalHeadingText, headingRuns),
            outlineLevel,
            pageBreakBefore: isEmbeddedMainSection,
            alignment: AlignmentType.LEFT,
            spacing: {
              line: Math.round(headingFont.lineSpacing * 240),
              after: sp.after,
              before: sp.before,
            },
          }),
        );
        return { remainderText, remainderForceParagraph };
      };
      
      const bodyRunStyle = {
        font: fontConfig.body.name,
        size: fontConfig.body.size * 2,
        bold: false,
        italic: false,
        color: fontConfig.body.color,
      };

      const exportRunsToDocxChildren = (runs, baseStyle) =>
        (runs || []).flatMap((run) =>
          textToDocxRuns(run.text, {
            font: baseStyle.font,
            size: baseStyle.size,
            bold: run.bold ? true : baseStyle.bold,
            italic: run.italic ? true : baseStyle.italic,
            color: baseStyle.color,
          }),
        );

      const pushTableFromHtml = (tableHtml) => {
        const tableMatch = String(tableHtml || '').match(/<table[^>]*>([\s\S]*?)<\/table>/i);
        if (!tableMatch) return;

        // Was: only checked flat visits/sections, which are empty for pure S9-chat data
        // (that data lives in effectiveSoAData.s9.tables — see normalizeSoaExportPayload's
        // "S9-only" branch). That meant S9-chat SOA tables never matched here, so they fell
        // through to the raw-HTML-table dump below (no per-activity footnotes) and only
        // reappeared via the post-loop fallback, misplaced at the very end of the document
        // instead of inline at "1.3 Schedule of Activities". hasUsableSoaExportData already
        // checks s9.tables too, matching what listSoaLegacyPayloadsForExport actually reads.
        const soaReadyForLandscape = hasUsableSoaExportData(effectiveSoAData);
        if (soaReadyForLandscape && tableHtmlLooksLikeScheduleOfActivities(tableMatch[0])) {
          if (!soaHandledAsLandscape) {
            let soaHeading = 'Schedule of Activities';
            if (sectionTitleLooksLikeSoaOrStandardNumber(currentContextTitle || '')) {
              soaHeading = String(currentContextTitle || '').trim() || soaHeading;
            } else if (sectionTitleLooksLikeSoaOrStandardNumber(sectionTitleForContext || '')) {
              soaHeading = String(sectionTitleForContext || '').trim() || soaHeading;
            }
            appendLandscapeSoaSection(soaHeading);
          }
          return;
        }

        const tableRows = [];
        const rows = tableMatch[1].match(/<tr[^>]*>([\s\S]*?)<\/tr>/g);

        if (rows && rows.length > 0) {
          rows.forEach((row, rowIndex) => {
            const cells = row.match(/<t[dh][^>]*>([\s\S]*?)<\/t[dh]>/g);
            if (cells && cells.length > 0) {
              const tableRow = new TableRow({
                tableHeader: rowIndex === 0,
                children: cells.map((cell, colIndex) => {
                  const cellContent = cell.replace(/<[^>]*>/g, '').trim();
                  const isColHeader = rowIndex === 0;
                  const isRowHeader = rowIndex > 0 && colIndex === 0;
                  const display =
                    isColHeader || isRowHeader ? toSentenceCaseTable(cellContent) : cellContent;
                  const useHeaderStyle = isColHeader;
                  const bold = useHeaderStyle ? fontConfig.tableHeader.bold : false;
                  const fontName = useHeaderStyle ? fontConfig.tableHeader.name : fontConfig.tableCell.name;
                  const sizeHp = useHeaderStyle
                    ? tableHeaderHalfPoints(fontConfig)
                    : tableBodyHalfPoints(fontConfig);
                  const color = useHeaderStyle ? fontConfig.tableHeader.color : fontConfig.tableCell.color;

                  return new TableCell({
                    children: [
                      new Paragraph({
                        style: useHeaderStyle ? 'CTableHeader' : 'CTableText',
                        children: [
                          ...textToDocxRuns(display || ' ', {
                            font: fontName,
                            size: sizeHp,
                            bold,
                            color,
                          }),
                        ],
                        spacing: {
                          line: Math.round(fontConfig.tableCell.lineSpacing * 240),
                          before: 0,
                          after: 0,
                        },
                        alignment: AlignmentType.LEFT,
                      }),
                    ],
                    margins: tableCellMarginsDocx(),
                    verticalAlign: VerticalAlignTable.CENTER,
                    borders: {
                      top: { style: BorderStyle.SINGLE, size: 8, color: '000000' },
                      bottom: { style: BorderStyle.SINGLE, size: 8, color: '000000' },
                      left: { style: BorderStyle.SINGLE, size: 8, color: '000000' },
                      right: { style: BorderStyle.SINGLE, size: 8, color: '000000' },
                    },
                  });
                }),
              });
              tableRows.push(tableRow);
            }
          });

          if (tableRows.length > 0) {
            const { main, ordinal, displayKey } = nextTableOrdinalInSection(
              currentContextTitle || sectionTitleForContext,
            );
            const tableDescription = extractTableDescription(
              tableMatch[0],
              currentContextTitle,
              ordinal,
              usedTableDescriptions,
            );
            usedTableDescriptions.add(tableDescription.toLowerCase());
            paragraphs.push(createTableCaption(main, ordinal, tableDescription, fontConfig));
            tablesIndex.push({ number: displayKey, title: tableDescription });
            paragraphs.push(
              new Table({
                rows: tableRows,
                width: { size: 100, type: WidthType.PERCENTAGE },
                borders: {
                  top: { style: BorderStyle.SINGLE, size: 8, color: '000000' },
                  bottom: { style: BorderStyle.SINGLE, size: 8, color: '000000' },
                  left: { style: BorderStyle.SINGLE, size: 8, color: '000000' },
                  right: { style: BorderStyle.SINGLE, size: 8, color: '000000' },
                  insideHorizontal: { style: BorderStyle.SINGLE, size: 4, color: '000000' },
                  insideVertical: { style: BorderStyle.SINGLE, size: 4, color: '000000' },
                },
              }),
            );
            paragraphs.push(new Paragraph({ spacing: { after: 120 } }));
          }
        }
      };

      const pushLegacyMarkerBlock = (text) => {
        const block = String(text || '');
        if (block.includes('__SMART_')) {
          const smartMatch = block.match(/__SMART_([A-Z_]+)_(\d+)_([^_]+)__(.+)/);
          if (smartMatch) {
            const [, type, level, number, title] = smartMatch;
            const levelNum = parseInt(level, 10);
            let headingFontConfig;
            let headingLevel;
            if (type === 'MAIN_SECTION') {
              headingFontConfig = fontConfig.heading;
              headingLevel = HeadingLevel.HEADING_2;
            } else if (type === 'SUBSECTION') {
              headingFontConfig = fontConfig.subheading;
              headingLevel = HeadingLevel.HEADING_3;
            } else if (type === 'SUB_SUBSECTION') {
              headingFontConfig = fontConfig.subheading;
              headingLevel = HeadingLevel.HEADING_4;
            }
            if (headingFontConfig && headingLevel) {
              paragraphs.push(
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `${number} ${title}`,
                      font: headingFontConfig.name,
                      size: headingFontConfig.size * 2,
                      bold: true,
                      color: headingFontConfig.color,
                    }),
                  ],
                  heading: headingLevel,
                  alignment: AlignmentType.LEFT,
                  spacing: {
                    line: Math.round(headingFontConfig.lineSpacing * 240),
                    after: levelNum === 1 ? 240 : 180,
                    before: 120,
                  },
                }),
              );
              return true;
            }
          }
        }
        if (block.includes('__SUBSECTION_')) {
          const subsectionMatch = block.match(/__SUBSECTION_(\d+)_(\d+\.\d+(?:\.\d+)?)__(.+)/);
          if (subsectionMatch) {
            const [, , number, title] = subsectionMatch;
            const depth = (number.match(/\./g) || []).length;
            const headingFontConfig = depth === 1 ? fontConfig.heading : fontConfig.subheading;
            paragraphs.push(
              new Paragraph({
                children: [
                  new TextRun({
                    text: `${number} ${title}`,
                    font: headingFontConfig.name,
                    size: headingFontConfig.size * 2,
                    bold: true,
                    color: headingFontConfig.color,
                  }),
                ],
                heading: depth === 1 ? HeadingLevel.HEADING_2 : HeadingLevel.HEADING_3,
                alignment: AlignmentType.LEFT,
                spacing: {
                  line: Math.round(headingFontConfig.lineSpacing * 240),
                  after: 180,
                  before: 120,
                },
              }),
            );
            return true;
          }
        }
        if (block.includes('__MAINSECTION_')) {
          const mainSectionMatch = block.match(/__MAINSECTION_(\d+)_(\d+\.?\s+[A-Z][A-Z\s&]+)__/);
          if (mainSectionMatch) {
            const [, , title] = mainSectionMatch;
            paragraphs.push(
              new Paragraph({
                style: 'CHeading2',
                children: [
                  new TextRun({
                    text: title,
                    font: fontConfig.heading.name,
                    size: fontConfig.heading.size * 2,
                    bold: fontConfig.heading.bold,
                    color: fontConfig.heading.color,
                  }),
                ],
                outlineLevel: 1,
                alignment: AlignmentType.LEFT,
                spacing: {
                  line: Math.round(fontConfig.heading.lineSpacing * 240),
                  after: 240,
                  before: 120,
                },
              }),
            );
            return true;
          }
        }
        return false;
      };

      const pushImageBlock = (blockHtml) => {
        const block = String(blockHtml || '');
        const srcMatch = block.match(/<img[^>]*src=["']([^"']+)["'][^>]*>/i);
        const altMatch = block.match(/<img[^>]*alt=["']([^"']*)["'][^>]*>/i);
        const titleMatch = block.match(/<img[^>]*title=["']([^"']*)["'][^>]*>/i);
        const src = srcMatch ? srcMatch[1] : '';
        const alt = altMatch ? altMatch[1] : '';
        const title = titleMatch ? titleMatch[1] : '';
        const desc = extractFigureDescription({ alt, title }, currentContextTitle, figureCounter);
        paragraphs.push(createFigureCaption(figureCounter, desc, fontConfig));
        figuresIndex.push({ number: figureCounter, title: desc });
        figureCounter += 1;
        if (src) {
          paragraphs.push(
            new Paragraph({
              children: [
                new TextRun({
                  text: '[Image will be embedded during final export]',
                  font: fontConfig.body.name,
                  size: fontConfig.body.size * 2,
                  color: '666666',
                }),
              ],
              alignment: AlignmentType.LEFT,
              spacing: { after: 120 },
            }),
          );
        }
      };

      // Splice in a pre-rendered Mermaid diagram (see extractAndRenderMermaidBlocksForExport —
      // the marker text is all that survives into this synchronous paragraph-building pass;
      // the actual SVG/PNG bytes were already rendered in the async pre-pass and live in
      // currentMermaidImages, keyed by that same marker).
      const pushMermaidImageBlock = (blockText) => {
        const markerMatch = String(blockText || '').match(/MERMAIDIMAGEPLACEHOLDER\d+ENDPLACEHOLDER/);
        const marker = markerMatch ? markerMatch[0] : null;
        const image = marker ? currentMermaidImages.get(marker) : null;
        if (!image) return;

        const desc = extractFigureDescription(
          { alt: 'Liver Event Stopping Flowchart' },
          currentContextTitle,
          figureCounter,
        );
        paragraphs.push(createFigureCaption(figureCounter, desc, fontConfig));
        figuresIndex.push({ number: figureCounter, title: desc });
        figureCounter += 1;
        paragraphs.push(
          new Paragraph({
            children: [
              new ImageRun({
                type: 'svg',
                data: image.svgBuffer,
                transformation: { width: image.width, height: image.height },
                fallback: {
                  type: 'png',
                  data: image.pngBuffer,
                  transformation: { width: image.width, height: image.height },
                },
              }),
            ],
            alignment: AlignmentType.LEFT,
            spacing: { after: 240 },
          }),
        );
      };

      const pushHeadingRemainder = (remainderText, remainderForceParagraph) => {
        const text = String(remainderText || '').trim();
        if (!text) return;
        if (remainderForceParagraph) {
          pushBodyParagraph(text);
          return;
        }
        const parsed = parseProtocolHeadingLine(text, { sectionRootPrefix: htmlContextPrefix });
        if (parsed && !isPartialProtocolHeadingLine(text, htmlContextPrefix)) {
          pushProtocolHeadingBlock(text);
        } else {
          pushBodyParagraph(text);
        }
      };

      const richBlocks = stripLeadingDuplicateHeadingBlocks(
        richTextToBlocks(htmlContent, { sectionRootPrefix: htmlContextPrefix }),
        sectionTitleForContext,
      );

      for (const block of richBlocks) {
        if (block.type === 'heading') {
          const { remainderText, remainderForceParagraph } = pushProtocolHeadingBlock(block.text);
          pushHeadingRemainder(remainderText, remainderForceParagraph);
          continue;
        }

        if (block.type === 'table') {
          pushTableFromHtml(block.outerHtml || '');
          continue;
        }

        if (block.type === 'list') {
          for (const itemRuns of block.items || []) {
            const itemText = itemRuns.map((r) => r.text).join('').trim();
            if (!itemText) continue;
            paragraphs.push(
              new Paragraph({
                style: 'CBulletList',
                children: exportRunsToDocxChildren(itemRuns, {
                  font: fontConfig.list.name,
                  size: fontConfig.list.size * 2,
                  bold: fontConfig.list.bold,
                  color: fontConfig.list.color,
                }),
                bullet: { level: 0 },
                alignment: AlignmentType.LEFT,
                spacing: {
                  line: Math.round(fontConfig.list.lineSpacing * 240),
                  before: 0,
                  after: 0,
                },
              }),
            );
          }
          continue;
        }

        if (block.type === 'paragraph') {
          const plain = (block.runs || []).map((r) => r.text).join('');
          if (plain.includes('MERMAIDIMAGEPLACEHOLDER')) {
            pushMermaidImageBlock(plain);
            continue;
          }
          if (plain.includes('<img')) {
            pushImageBlock(plain);
            continue;
          }
          if (plain.includes('<blockquote')) {
            const quoteText = plain.replace(/<[^>]*>/g, '').trim();
            if (quoteText) {
              paragraphs.push(
                new Paragraph({
                  children: [
                    new TextRun({
                      text: quoteText,
                      font: fontConfig.body.name,
                      size: fontConfig.body.size * 2,
                      bold: false,
                      color: fontConfig.body.color,
                    }),
                  ],
                  alignment: AlignmentType.JUSTIFIED,
                  spacing: { line: Math.round(fontConfig.body.lineSpacing * 240) },
                  indent: { left: 480 },
                }),
              );
            }
            continue;
          }
          if (pushLegacyMarkerBlock(plain)) continue;

          const trimmed = plain.trim();
          if (!trimmed) continue;

          const parsed = parseProtocolHeadingLine(trimmed, { sectionRootPrefix: htmlContextPrefix });
          if (parsed && !isPartialProtocolHeadingLine(trimmed, htmlContextPrefix)) {
            const { remainderText, remainderForceParagraph } = pushProtocolHeadingBlock(trimmed);
            pushHeadingRemainder(remainderText, remainderForceParagraph);
            continue;
          }

          const children = exportRunsToDocxChildren(block.runs, bodyRunStyle);
          if (!children.length) continue;
          paragraphs.push(
            new Paragraph({
              style: 'CBodyText',
              children,
              alignment: AlignmentType.JUSTIFIED,
              spacing: {
                line: Math.round(fontConfig.body.lineSpacing * 240),
              },
            }),
          );
        }
      }
      
      return paragraphs;
    };
    
          return htmlToParagraphs(withLinks, sectionTitleForContext, sectionRootPrefix);
    };

    const appendLandscapeSoaSection = (headingForTitle) => {
    if (soaHandledAsLandscape) return;
    const heading = normalizeScheduleOfActivitiesHeading(headingForTitle || 'Schedule of Activities');
    const soaChildren = [];
    soaChildren.push(
      new Paragraph({
        style: "CHeading2",
        children: [
          new TextRun({
            text: heading,
            font: fontConfig.heading.name,
            size: fontConfig.heading.size * 2,
            bold: fontConfig.heading.bold,
            color: fontConfig.heading.color,
          }),
        ],
        outlineLevel: 1,
        alignment: AlignmentType.LEFT,
        spacing: { line: Math.round(fontConfig.heading.lineSpacing * 240), after: 120 },
      }),
    );
    _encounteredMainSection = true;

    const schedulePayloads = listSoaLegacyPayloadsForExport(effectiveSoAData);
    if (!schedulePayloads.length && effectiveSoAData?.visits && effectiveSoAData?.sections) {
      schedulePayloads.push(effectiveSoAData);
    }

    if (schedulePayloads.length) {
      schedulePayloads.forEach((schedulePayload, scheduleIdx) => {
      // Key Notes render as a full-width row directly under the activity they annotate
      // (matches SoaNoteRow in the PDF export / S9 chat's inline layout), not a narrow
      // trailing column — soaDataToTableRows always attaches the text via `_keyNotesText`
      // regardless of this flag, so the column itself can be omitted here.
      // Notes not linked to any activity still appear once below the schedule.
      const soaTableData = soaDataToTableRows(schedulePayload, { includeKeyNotesColumn: false });
      if (!(soaTableData.rows && soaTableData.rows.length > 0)) return;
        if (scheduleIdx > 0) {
          soaChildren.push(new Paragraph({ spacing: { before: 240, after: 120 } }));
        }
        const tableRows = [];
        const keys = soaTableData.header || [];

        const headerCell = (text, opts = {}) =>
          new TableCell({
            children: [
              new Paragraph({
                style: 'CTableHeader',
                children: [
                  new TextRun({
                    text: toSentenceCaseTable(text || ''),
                    font: fontConfig.tableHeader.name,
                    size: tableHeaderHalfPoints(fontConfig),
                    bold: fontConfig.tableHeader.bold,
                    color: fontConfig.tableHeader.color,
                  }),
                ],
                alignment: opts.alignment || AlignmentType.CENTER,
              }),
            ],
            margins: tableCellMarginsDocx(),
            verticalAlign: VerticalAlignTable.CENTER,
            ...(opts.columnSpan ? { columnSpan: opts.columnSpan } : {}),
            ...(opts.rowSpan ? { rowSpan: opts.rowSpan } : {}),
          });

        // Grouped 2-tier header (e.g. "Treatment Cycles" spanning its Day 1 / Day 15
        // sub-columns as one merged cell) mirrors SoaGroupedHeaderRows in the PDF export —
        // needs one {period, day} entry per visit column; falls back to a flat header row
        // (repeating the full period/day text per column) if that's not available.
        const visitMeta = soaTableData.visitMeta;
        const useGroupedHeader = Array.isArray(visitMeta) && visitMeta.length === keys.length - 1;

        if (useGroupedHeader) {
          const groups = buildSoaHeaderGroups(visitMeta);
          tableRows.push(
            new TableRow({
              tableHeader: true,
              children: [
                headerCell(keys[0], { alignment: AlignmentType.LEFT, rowSpan: 2 }),
                ...groups.map((group) =>
                  headerCell(group.period || group.days[0] || '—', {
                    columnSpan: group.span > 1 ? group.span : undefined,
                  }),
                ),
              ],
            }),
          );
          tableRows.push(
            new TableRow({
              tableHeader: true,
              children: groups.flatMap((group) => group.days.map((day) => headerCell(day))),
            }),
          );
        } else if (keys.length > 0) {
          tableRows.push(
            new TableRow({
              tableHeader: true,
              children: keys.map((headerText, i) =>
                headerCell(headerText, { alignment: i === 0 ? AlignmentType.LEFT : AlignmentType.CENTER }),
              ),
            }),
          );
        }

        soaTableData.rows.forEach((rowData) => {
          const isSectionBannerRow = isSoaSectionBannerRow(rowData, keys);

          const rowCells = keys.map((headerText, colIndex) => {
            const raw = String(rowData[headerText] ?? '');
            let display = raw;
            if (colIndex === 0 || isSectionBannerRow) {
              display = toSentenceCaseTable(raw);
            }

            let paraStyle = 'CTableText';
            let fontName = fontConfig.tableCell.name;
            let sizeHp = tableBodyHalfPoints(fontConfig);
            let bold = false;
            let color = fontConfig.tableCell.color;
            let align = AlignmentType.CENTER;

            if (isSectionBannerRow) {
              if (colIndex === 0) {
                paraStyle = 'CTableHeader';
                fontName = fontConfig.tableHeader.name;
                sizeHp = tableHeaderHalfPoints(fontConfig);
                bold = fontConfig.tableHeader.bold;
                color = fontConfig.tableHeader.color;
                align = AlignmentType.LEFT;
              } else {
                align = AlignmentType.CENTER;
              }
            } else if (colIndex === 0) {
              align = AlignmentType.LEFT;
            }

            return new TableCell({
              children: [
                new Paragraph({
                  style: paraStyle,
                  children: [
                    new TextRun({
                      text: display || ' ',
                      font: fontName,
                      size: sizeHp,
                      bold,
                      color,
                    }),
                  ],
                  alignment: align,
                }),
              ],
              margins: tableCellMarginsDocx(),
              verticalAlign: VerticalAlignTable.CENTER,
            });
          });
          tableRows.push(new TableRow({ children: rowCells }));

          // Activity-linked Key Notes text: its own full-width row directly beneath the
          // activity, italic, spanning every column — matches SoaNoteRow in the PDF export.
          const noteLines = String(rowData._keyNotesText || '')
            .split('\n')
            .map((l) => l.trim())
            .filter(Boolean);
          if (noteLines.length > 0) {
            tableRows.push(
              new TableRow({
                children: [
                  new TableCell({
                    columnSpan: keys.length,
                    children: noteLines.map((line) =>
                      new Paragraph({
                        style: 'CTableText',
                        alignment: AlignmentType.LEFT,
                        children: [
                          new TextRun({
                            text: line,
                            font: fontConfig.tableCell.name,
                            size: tableFootnoteHalfPoints(fontConfig),
                            italic: true,
                            color: fontConfig.tableCell.color,
                          }),
                        ],
                      }),
                    ),
                    margins: tableCellMarginsDocx(),
                    verticalAlign: VerticalAlignTable.CENTER,
                  }),
                ],
              }),
            );
          }
        });

        if (tableRows.length > 0) {
          const { main, ordinal, displayKey } = nextTableOrdinalInSection(heading);
          const tableDescription = 'Schedule of Activities';
          const fnParagraphs = [];
          const footHp = tableFootnoteHalfPoints(fontConfig);
          // Key Notes only under this schedule — no numbers/letters
          if (soaTableData.footnotesBlock && soaTableData.footnotesBlock.length > 0) {
            fnParagraphs.push(
              new Paragraph({
                style: 'CFootnote',
                children: [
                  new TextRun({
                    text: 'Key Notes',
                    font: fontConfig.footnote.name,
                    size: footHp,
                    bold: true,
                    color: fontConfig.footnote.color,
                  }),
                ],
                alignment: AlignmentType.LEFT,
                spacing: { before: 200, after: 120 },
              }),
            );
            soaTableData.footnotesBlock.forEach((line) => {
              const body = ensureEndsWithPeriod(stripFootnoteNumberForSoaExport(line));
              if (!body) return;
              fnParagraphs.push(
                new Paragraph({
                  style: 'CFootnote',
                  alignment: AlignmentType.JUSTIFIED,
                  children: [
                    new TextRun({
                      text: body,
                      font: fontConfig.footnote.name,
                      size: footHp,
                      color: fontConfig.footnote.color,
                    }),
                  ],
                  spacing: { after: 80 },
                }),
              );
            });
          }
          soaChildren.push(
            createTableCaption(main, ordinal, tableDescription, fontConfig),
            new Table({
              rows: tableRows,
              width: { size: 100, type: WidthType.PERCENTAGE },
              layout: TableLayoutType.FIXED,
              columnWidths: getSoaColumnWidths(soaTableData.header?.length || 0),
            }),
            new Paragraph({ spacing: { after: 120 } }),
            ...fnParagraphs,
          );
          tablesIndex.push({ number: displayKey, title: tableDescription });
        }
      });
    } else {
      soaChildren.push(
        new Paragraph({
          children: [
            new TextRun({
              text: '[No Schedule of Activities table available]',
              font: fontConfig.body.name,
              size: fontConfig.body.size * 2,
              italic: true,
              color: '666666',
            }),
          ],
          alignment: AlignmentType.LEFT,
          spacing: { after: 240 },
        }),
      );
    }

    docSections.push({
      properties: {
        type: SectionType.NEXT_PAGE,
        page: {
          size: { ...A4_LANDSCAPE_PAGE_FOR_DOCX },
          margin: { ...A4_LANDSCAPE_MARGIN },
        },
      },
      children: soaChildren,
    });
    soaHandledAsLandscape = true;
  };
  
  let _encounteredMainSection = false;
  const sectionsForWordExport = sectionsListForWordExport(normalizeProtocolSectionsList(sections));
  // Continue Table/Figure numbering across sections sharing a main number (1-1, 1-2, …).
  const wordMediaOffsets = computeMediaNumberOffsets(sectionsForWordExport, { getContent: sectionExportRaw });
  for (const section of sectionsForWordExport) {
    const sectionTitleRaw = String(section?.title || section?.sectionTitle || section?.name || '');
    const sectionTitleNorm = sectionTitleRaw.normalize('NFKC').replace(/\s+/g, ' ').trim();
    const hasOutput = Boolean(sectionExportRaw(section));
    const sectionOutlineLevel = outlineLevelFromSectionTitle(sectionTitleRaw);
    const isMainSection = isMainProtocolSectionTitle(sectionTitleRaw);
    const isAppendixSection = /^1[34]\.\s/i.test(sectionTitleRaw);
    // Use precise regex: trial schema only (not any section containing "schema")
    const isSchemaSection = /trial\s*schema/i.test(sectionTitleNorm);
    const isSoASection = sectionTitleLooksLikeSoaOrStandardNumber(sectionTitleRaw);

    // SOA and Trial Schema can be exported even if the stored section output is empty,
    // because their content may come from generated data (SOA table) / external image (schema).
    // M11 sections 13–14 are also exported with a title stub when not yet generated.
    if (!hasOutput && !isSchemaSection && !isSoASection && !isAppendixSection) {
      continue;
    }

    if (hasOutput) {
    }
      
      // Special handling for Trial Schema and SOA sections - only include image/table
      if (isSchemaSection) {
      
      // Add section title with Word 2024 compatible heading structure
      const sectionMatch = section.title.match(/^(\d+(?:\.\d+)*)\s+(.+)$/);
      let outlineLevel = 0;
      let headingStyle = "CHeading1";
      
      if (sectionMatch) {
        const sectionNumber = sectionMatch[1];
        const dotCount = (sectionNumber.match(/\./g) || []).length;
        
        // Determine heading level based on section number depth for Word 2024 TOC compatibility
        if (dotCount === 0) {
          outlineLevel = 0; // Level 1 in Word UI
          headingStyle = "CHeading1";
        } else if (dotCount === 1) {
          outlineLevel = 1; // Level 2
          headingStyle = "CHeading2";
        } else if (dotCount === 2) {
          outlineLevel = 2; // Level 3
          headingStyle = "CHeading3";
        } else {
          outlineLevel = 3; // Level 4
          headingStyle = "CHeading4";
        }
      }
      
      // Page break before each main section (skip before first main section)
      if (_encounteredMainSection && outlineLevel === 0) {
        children.push(new Paragraph({ children: [], pageBreakBefore: true }));
      }
      
  const normalizedTitleMatch = section.title.match(/^(\d+(?:\.\d+)*)/);
  let normalizedTitle = section.title;
  if (normalizedTitleMatch) {
    const sectionNumber = normalizedTitleMatch[1];
    const rawTitle = section.title.slice(sectionNumber.length).trim();
    let tokens = rawTitle.split(/\s+/);
    if (tokens.length > 0) {
      tokens = tokens.slice(0, 12);
      tokens = tokens.map((token, idx) => idx === 0 ? token : token.toLowerCase());
    }
    normalizedTitle = `${sectionNumber} ${tokens.join(' ')}`.trim();
      }
      
      {
        const headingRuns = textToDocxRuns(normalizedTitle, {
          font: fontConfig.heading.name,
          size: fontConfig.heading.size * 2,
          bold: fontConfig.heading.bold,
          color: fontConfig.heading.color,
        });
        children.push(
          new Paragraph({
            style: headingStyle,
            children: bookmarkRunsForHeadingText(normalizedTitle, headingRuns),
            outlineLevel,
            alignment: AlignmentType.LEFT,
            spacing: {
              line: Math.round(fontConfig.heading.lineSpacing * 240),
              after: 240,
              before: 120,
            },
          }),
        );
        if (outlineLevel === 0) {
          _encounteredMainSection = true;
        }
      }
        
        // Only process image for Trial Schema
        if (trialSchemaImageUrl) {
          // Fetch image data from URL
          try {
            const response = await fetch(trialSchemaImageUrl);
            const imageData = await response.arrayBuffer();
            // Add caption and index as a figure
            const figDescription = extractFigureDescription({ title: 'Trial Schema' }, section.title, figureCounter);
            children.push(createFigureCaption(figureCounter, figDescription, fontConfig));
            figuresIndex.push({ number: figureCounter, title: figDescription });
            figureCounter++;
            children.push(
              new Paragraph({
                children: [
                  new ImageRun({
                    data: imageData,
                  transformation: {
                    width: 400,
                    height: 300,
                  },
                }),
              ],
              alignment: AlignmentType.LEFT,
              spacing: { after: 240 }
            })
          );
          } catch (error) {
            console.error('Error fetching trial schema image:', error);
            children.push(
              new Paragraph({
                children: [new TextRun({
                  text: '[Error loading trial schema image]',
                  font: fontConfig.body.name,
                  size: fontConfig.body.size * 2,
                  italic: true,
                  color: '666666'
                })],
                alignment: AlignmentType.CENTER,
                spacing: { after: 240 }
              })
            );
          }
        } else {
          children.push(
            new Paragraph({
              children: [new TextRun({
                text: '[No trial schema image available]',
                font: fontConfig.body.name,
                size: fontConfig.body.size * 2,
                italic: true,
                color: '666666'
              })],
              alignment: AlignmentType.CENTER,
              spacing: { after: 240 }
            })
          );
        }
        continue; // Skip all other content processing for Trial Schema
      }
      
      if (isSoASection) {
        if (children.length > 0) {
          pushPortraitSection(children);
          children.length = 0;
        }
        appendLandscapeSoaSection(sectionTitleRaw);
        continue;
      }

      const sectionRootMatch = section.title.match(/^(\d+(?:\.\d+)*)/);
      const sectionRootPrefix = sectionRootMatch ? sectionRootMatch[1] : null;
      const sectionHeadingStyle = protocolHeadingStyleForOutlineLevel(sectionOutlineLevel);

      // Main section heading — outline depth follows section number (1 → 0, 1.2 → 1, etc.)
      {
        const headingRuns = textToDocxRuns(section.title, {
          font: fontConfig.heading.name,
          size: fontConfig.heading.size * 2,
          bold: fontConfig.heading.bold,
          color: fontConfig.heading.color,
        });
        children.push(
          new Paragraph({
            style: sectionHeadingStyle,
            children: bookmarkRunsForHeadingText(section.title, headingRuns),
            outlineLevel: sectionOutlineLevel,
            pageBreakBefore: _encounteredMainSection && isMainSection,
            alignment: AlignmentType.LEFT,
            spacing: {
              line: Math.round(fontConfig.heading.lineSpacing * 240),
              after: 120,
            },
          }),
        );
      }
      if (isMainSection) {
        _encounteredMainSection = true;
      }

      if (!hasOutput && isAppendixSection) {
        children.push(
          new Paragraph({
            style: 'CBodyText',
            children: [
              new TextRun({
                text: 'Not applicable.',
                font: fontConfig.body.name,
                size: fontConfig.body.size * 2,
                color: fontConfig.body.color,
              }),
            ],
            alignment: AlignmentType.JUSTIFIED,
            spacing: {
              line: Math.round(fontConfig.body.lineSpacing * 240),
              after: 80,
            },
          }),
        );
        continue;
      }

      // Structured HTML export (same normalization as PDF preview — no strip-all-HTML regex path)
      const preparedContentRaw = prepareSectionContentForExport(
        sectionExportRaw(section),
        section.title,
        wordMediaOffsets[sectionsForWordExport.indexOf(section)],
      );
      // Async pre-pass: render any <pre class="mermaid"> blocks (e.g. the 7.1.1.1 liver-event
      // flowchart) to SVG/PNG now, since mermaid.render()/canvas rasterization can't happen
      // inline inside the synchronous paragraph builder below.
      const { content: preparedContent, images: sectionMermaidImages } =
        await extractAndRenderMermaidBlocksForExport(preparedContentRaw);
      currentMermaidImages = sectionMermaidImages;
      children.push(...processContentForExport(preparedContent, section.title, sectionRootPrefix));

      // Special handling for Trial Schema
      if (section.title === '1.2 TRIAL SCHEMA' && trialSchemaImageUrl) {
        try {
          let imageData = null;
          if (trialSchemaImageUrl.startsWith('data:image')) {
            // Base64 image
            imageData = trialSchemaImageUrl.split(',')[1];
          } else if (trialSchemaImageUrl.startsWith('<svg')) {
            // SVG as string, convert to base64 PNG (not natively supported by docx)
            // For now, skip SVG embedding
            children.push(new Paragraph({ 
              children: [
                new TextRun({
                  text: '[Trial Schema SVG diagram not supported in DOCX export]',
                  font: fontConfig.body.name,
                  size: fontConfig.body.size * 2,
                  color: '666666'
                })
              ]
            }));
          } else {
            // URL, fetch and convert to base64
            const res = await fetch(trialSchemaImageUrl);
            const blob = await res.blob();
            imageData = await blobToBase64(blob);
          }
          if (imageData) {
            const figDescription = extractFigureDescription({ title: 'Trial Schema' }, section.title, figureCounter);
            children.push(createFigureCaption(figureCounter, figDescription, fontConfig));
            figuresIndex.push({ number: figureCounter, title: figDescription });
            figureCounter++;
            children.push(
        new Paragraph({
                children: [
                  new ImageRun({
                    data: Uint8Array.from(atob(imageData), c => c.charCodeAt(0)),
                    transformation: { width: 500, height: 300 },
                  })
                ],
                alignment: AlignmentType.LEFT,
                spacing: { after: 240 }
              })
            );
          } else {
            children.push(new Paragraph({
              children: [
                new TextRun({
                  text: '[No trial schema image available]',
                  font: fontConfig.body.name,
                  size: fontConfig.body.size * 2,
                  color: '666666'
                })
              ]
            }));
          }
        } catch (e) {
          console.error('Error processing trial schema image:', e);
          children.push(new Paragraph({
            children: [
              new TextRun({
                text: '[Error embedding trial schema image]',
                font: fontConfig.body.name,
                size: fontConfig.body.size * 2,
                color: '666666'
              })
            ]
          }));
        }
      }
      // SOA is emitted only in the dedicated landscape section above (no portrait duplicate).

      // Section body rendered via prepareSectionContentForExport + richTextToBlocks
  }
  
  // If we collected tables or figures, insert List of Tables and List of Figures after TOC
  if ((tablesIndex.length > 0 || figuresIndex.length > 0) && (lotInsertIndex !== null || (lotTargetDocSectionIndex !== null && lotInsertIndexWithinSection !== null))) {
    const lotChildren = [];
    // Heading
    lotChildren.push(new Paragraph({
      children: [new TextRun({
        text: 'List of Tables',
        font: fontConfig.heading.name,
        size: fontConfig.heading.size * 2,
        bold: true,
        color: fontConfig.heading.color
      })],
      // Front matter — not a TOC entry
      style: "CFrontMatter",
      spacing: { after: 240 }
    }));
    // Entries as a table (Number | Title | Page — page number is a real PAGEREF field
    // bound to the matching caption's bookmark, so it stays accurate like the main TOC).
    if (tablesIndex.length > 0) {
      const tableRows = tablesIndex.map(entry => {
        const anchor = anchorIdForTable(entry.number);
        return new TableRow({
        children: [
          new TableCell({
            children: [new Paragraph({
              children: [new TextRun({
                text: `Table ${entry.number}.`,
                font: fontConfig.body.name,
                size: fontConfig.body.size * 2,
                bold: true,
                color: fontConfig.body.color
              })],
              alignment: AlignmentType.CENTER
            })],
            margins: { top: 100, bottom: 100, left: 150, right: 150 }
          }),
          new TableCell({
            children: [new Paragraph({
              children: [new TextRun({
                text: entry.title,
                font: fontConfig.body.name,
                size: fontConfig.body.size * 2,
                color: fontConfig.body.color
              })],
              alignment: AlignmentType.CENTER
            })],
            margins: { top: 100, bottom: 100, left: 150, right: 150 }
          }),
          new TableCell({
            children: [new Paragraph({
              children: anchor
                ? [new PageReference(anchor, { hyperlink: true })]
                : [new TextRun({ text: '', font: fontConfig.body.name, size: fontConfig.body.size * 2 })],
              alignment: AlignmentType.CENTER
            })],
            margins: { top: 100, bottom: 100, left: 150, right: 150 }
          })
        ]
        });
      });
      lotChildren.push(new Table({ rows: tableRows, width: { size: 100, type: WidthType.PERCENTAGE } }));
      // Page break after List of Tables
      lotChildren.push(new Paragraph({ children: [], pageBreakBefore: true }));
    }

    // If we have figures, append List of Figures after List of Tables
    if (figuresIndex.length > 0) {
      lotChildren.push(new Paragraph({
        children: [new TextRun({
          text: 'List of Figures',
          font: fontConfig.heading.name,
          size: fontConfig.heading.size * 2,
          bold: true,
          color: fontConfig.heading.color
        })],
        // Front matter — not a TOC entry
        style: "CFrontMatter",
        spacing: { after: 240 }
      }));
      const figureRows = figuresIndex.map(entry => {
        const anchor = anchorIdForFigure(entry.number);
        return new TableRow({
        children: [
          new TableCell({
            children: [new Paragraph({
              children: [new TextRun({
                text: `Figure ${entry.number}.`,
                font: fontConfig.body.name,
                size: fontConfig.body.size * 2,
                bold: true,
                color: fontConfig.body.color
              })],
              alignment: AlignmentType.CENTER
            })],
            margins: { top: 100, bottom: 100, left: 150, right: 150 }
          }),
          new TableCell({
            children: [new Paragraph({
              children: [new TextRun({
                text: entry.title,
                font: fontConfig.body.name,
                size: fontConfig.body.size * 2,
                color: fontConfig.body.color
              })],
              alignment: AlignmentType.CENTER
            })],
            margins: { top: 100, bottom: 100, left: 150, right: 150 }
          }),
          new TableCell({
            children: [new Paragraph({
              children: anchor
                ? [new PageReference(anchor, { hyperlink: true })]
                : [new TextRun({ text: '', font: fontConfig.body.name, size: fontConfig.body.size * 2 })],
              alignment: AlignmentType.CENTER
            })],
            margins: { top: 100, bottom: 100, left: 150, right: 150 }
          })
        ]
        });
      });
      lotChildren.push(new Table({ rows: figureRows, width: { size: 100, type: WidthType.PERCENTAGE } }));
      // Page break after List of Figures
      lotChildren.push(new Paragraph({ children: [], pageBreakBefore: true }));
    }

    // Insert LOT into the correct buffer: either into the original docSection where the TOC lives
    // (if children was flushed), or into the current children buffer if not flushed yet.
    if (lotTargetDocSectionIndex !== null && lotInsertIndexWithinSection !== null && docSections[lotTargetDocSectionIndex]) {
      const targetSection = docSections[lotTargetDocSectionIndex];
      // Ensure children exists on targetSection
      if (Array.isArray(targetSection.children)) {
        targetSection.children.splice(lotInsertIndexWithinSection, 0, ...lotChildren);
      }
    } else if (lotInsertIndex !== null) {
      children.splice(lotInsertIndex, 0, ...lotChildren);
    }
  }
  
  // Post-loop SOA fallback: if SOA data was never placed in a landscape section
  // (e.g. the section title didn't match detection, or the section was absent), add it now.
  if (!soaHandledAsLandscape && effectiveSoAData) {
    if (children.length > 0) {
      pushPortraitSection(children);
      children.length = 0;
    }
    appendLandscapeSoaSection('1.3 Schedule of Activities');
  }

  // Create Word 2024 compatible document with proper properties
  // Push remaining portrait content as final section
  if (children.length > 0) {
    pushPortraitSection(children);
  }

  // Ensure we always have at least one section (docx requires non-empty sections array)
  if (docSections.length === 0) {
    pushPortraitSection([new Paragraph({ text: '' })]);
  }

  // Draft 1.0 PDF §4.6: borderless header/footer tables; layout matches regulatory template:
  // Row1: [Product] | [Sponsor]   Row2: [Protocol number] | [DD Mon YYYY]
  const hfFont = fontConfig.headerFooter?.name || fontConfig.body.name;
  const hfSize = (fontConfig.headerFooter?.size || fontConfig.body.size) * 2;
  const hfRun = (text) =>
    new TextRun({
      text: text ?? '',
      font: hfFont,
      size: hfSize,
      color: '000000',
    });
  const hfPara = (text, alignment) =>
    new Paragraph({
      children: [hfRun(text)],
      alignment,
    });

  const defaultHeaders = {
    default: new Header({
      children: [
        new Table({
          rows: [
            new TableRow({
              children: [
                new TableCell({
                  children: [hfPara(investigationalProductNames, AlignmentType.LEFT)],
                  width: { size: 50, type: WidthType.PERCENTAGE },
                }),
                new TableCell({
                  children: [hfPara(sponsorName, AlignmentType.RIGHT)],
                  width: { size: 50, type: WidthType.PERCENTAGE },
                }),
              ],
            }),
            new TableRow({
              children: [
                new TableCell({
                  children: [hfPara(protocolNumber, AlignmentType.LEFT)],
                  width: { size: 50, type: WidthType.PERCENTAGE },
                }),
                new TableCell({
                  children: [hfPara(versionDate, AlignmentType.RIGHT)],
                  width: { size: 50, type: WidthType.PERCENTAGE },
                }),
              ],
            }),
          ],
          width: { size: 100, type: WidthType.PERCENTAGE },
          borders: {
            top: { style: BorderStyle.NONE, size: 0 },
            bottom: { style: BorderStyle.NONE, size: 0 },
            left: { style: BorderStyle.NONE, size: 0 },
            right: { style: BorderStyle.NONE, size: 0 },
            insideHorizontal: { style: BorderStyle.NONE, size: 0 },
            insideVertical: { style: BorderStyle.NONE, size: 0 },
          },
        }),
      ],
    }),
  };

  const defaultFooters = {
    default: new Footer({
      children: [
        new Table({
          rows: [
            new TableRow({
              children: [
                new TableCell({
                  children: [hfPara('Confidential', AlignmentType.LEFT)],
                  width: { size: 50, type: WidthType.PERCENTAGE },
                }),
                new TableCell({
                  children: [
                    new Paragraph({
                      children: [
                        new TextRun({
                          text: 'Page ',
                          font: hfFont,
                          size: hfSize,
                          color: '000000',
                        }),
                        new TextRun({
                          children: [PageNumber.CURRENT],
                          font: hfFont,
                          size: hfSize,
                          color: '000000',
                        }),
                        new TextRun({
                          text: ' of ',
                          font: hfFont,
                          size: hfSize,
                          color: '000000',
                        }),
                        new TextRun({
                          children: [PageNumber.TOTAL_PAGES],
                          font: hfFont,
                          size: hfSize,
                          color: '000000',
                        }),
                      ],
                      alignment: AlignmentType.RIGHT,
                    }),
                  ],
                  width: { size: 50, type: WidthType.PERCENTAGE },
                }),
              ],
            }),
          ],
          width: { size: 100, type: WidthType.PERCENTAGE },
          borders: {
            top: { style: BorderStyle.NONE, size: 0 },
            bottom: { style: BorderStyle.NONE, size: 0 },
            left: { style: BorderStyle.NONE, size: 0 },
            right: { style: BorderStyle.NONE, size: 0 },
            insideHorizontal: { style: BorderStyle.NONE, size: 0 },
            insideVertical: { style: BorderStyle.NONE, size: 0 },
          },
        }),
      ],
    }),
  };

  const doc = new Document({
    sections: docSections.map((s, idx) => {
      const titlePage = Boolean(titlePageConfig && titlePageConfig.enabled);
      const baseProps = cloneSectionPropertiesForDocx(s.properties);
      const properties =
        idx === 0 && titlePage ? { ...baseProps, titlePage: true } : baseProps;

      const headers = s.headers || defaultHeaders;
      let footers = s.footers || defaultFooters;

      // Draft 1.0 PDF §4.8: if there is a title page, page numbering starts on page 2.
      // Implement by suppressing the footer on the first page only.
      if (idx === 0 && titlePage && !footers.first) {
        footers = { ...footers, first: new Footer({ children: [] }) };
      }

      return { children: s.children, properties, headers, footers };
    }),
    features: { 
      updateFields: true,
      updateFieldsOnOpen: true,
      autoUpdateFields: true,
      updateFieldsOnSave: true,
      updateFieldsOnPrint: true,
      updateFieldsOnClose: true,
      updateFieldsOnCopy: true,
      updateFieldsOnPaste: true,
      // Force immediate field updates
      forceFieldUpdates: true,
      immediateFieldUpdates: true,
      updateAllFieldsOnOpen: true
    },
    // Additional document properties for field updates
    properties: {
      // Force field updates on document events
      updateFieldsOnOpen: true,
      updateFieldsOnSave: true,
      updateFieldsOnPrint: true,
      // Document-level field update settings
      fieldUpdateSettings: {
        updateOnOpen: true,
        updateOnSave: true,
        updateOnPrint: true,
        updateOnClose: true,
        forceUpdate: true,
        immediateUpdate: true
      }
    },
    creator: "Clinical Protocol Management System",
    title: "Clinical Trial Protocol",
    description: "Automatically generated clinical trial protocol with Word 2024 compatibility",
    keywords: ["clinical trial", "protocol", "research", "medical"],
    revision: 1,
    lastModifiedBy: "System",
    styles: {
      paragraphStyles: [
        // Draft 1.0 PDF §4: explicit named styles so Word shows C-* styles (not just Normal/Heading).
        { id: "CTitle", name: "C-Title", basedOn: "Normal", next: "CBodyText", uiPriority: 1, unhideWhenUsed: true, quickFormat: true,
          run: { font: fontConfig.title.name, size: fontConfig.title.size * 2, bold: true, color: "000000" },
          paragraph: { spacing: { line: Math.round((fontConfig.title.lineSpacing || 1.15) * 240), after: DRAFT_PT(18) } } },
        // Front matter (TOC / LOT / LOF titles) — based on Normal, never in TOC.
        { id: "CFrontMatter", name: "C-Front Matter", basedOn: "Normal", next: "CBodyText", uiPriority: 1, unhideWhenUsed: true, quickFormat: true,
          run: { font: fontConfig.heading.name, size: fontConfig.heading.size * 2, bold: true, color: "000000" },
          paragraph: { spacing: { after: DRAFT_PT(12), before: 0 }, keepNext: true } },
        // outlineLevel is set per-paragraph only on real protocol headings (not front matter).
        { id: "CHeading1", name: "C-Heading 1", basedOn: "Normal", next: "CBodyText", uiPriority: 2, unhideWhenUsed: true, quickFormat: true,
          run: { font: fontConfig.heading.name, size: fontConfig.heading.size * 2, bold: true, color: "000000" },
          paragraph: { spacing: { after: DRAFT_PT(6), before: DRAFT_PT(18) }, keepNext: true } },
        { id: "CHeading2", name: "C-Heading 2", basedOn: "Normal", next: "CBodyText", uiPriority: 3, unhideWhenUsed: true, quickFormat: true,
          run: { font: fontConfig.subheading.name, size: fontConfig.subheading.size * 2, bold: true, color: "000000" },
          paragraph: { spacing: { after: DRAFT_PT(6), before: DRAFT_PT(12) }, keepNext: true } },
        { id: "CHeading3", name: "C-Heading 3", basedOn: "Normal", next: "CBodyText", uiPriority: 4, unhideWhenUsed: true, quickFormat: true,
          run: { font: (fontConfig.heading3?.name || fontConfig.body.name), size: (fontConfig.heading3?.size || 11) * 2, bold: true, color: "000000" },
          paragraph: { spacing: { after: DRAFT_PT(3), before: DRAFT_PT(12) }, keepNext: true } },
        { id: "CHeading4", name: "C-Heading 4", basedOn: "Normal", next: "CBodyText", uiPriority: 5, unhideWhenUsed: true, quickFormat: true,
          run: { font: (fontConfig.heading4?.name || fontConfig.body.name), size: (fontConfig.heading4?.size || 11) * 2, bold: true, color: "000000" },
          paragraph: { spacing: { after: DRAFT_PT(3), before: DRAFT_PT(6) }, keepNext: true } },
        { id: "CHeading5", name: "C-Heading 5", basedOn: "Normal", next: "CBodyText", uiPriority: 6, unhideWhenUsed: true, quickFormat: true,
          run: { font: (fontConfig.heading5?.name || fontConfig.body.name), size: (fontConfig.heading5?.size || 11) * 2, bold: false, italic: true, color: "000000" },
          paragraph: { spacing: { after: DRAFT_PT(0), before: DRAFT_PT(6) }, keepNext: false } },
        { id: "CBodyText", name: "C-Body Text", basedOn: "Normal", next: "CBodyText", uiPriority: 20, unhideWhenUsed: true, quickFormat: true,
          run: { font: fontConfig.body.name, size: fontConfig.body.size * 2, color: "000000" },
          paragraph: { spacing: { line: Math.round((fontConfig.body.lineSpacing || 1.15) * 240), before: 0, after: DRAFT_PT(6) }, widowControl: true } },
        { id: "CCaption", name: "C-Caption", basedOn: "Normal", next: "CBodyText", uiPriority: 21, unhideWhenUsed: true, quickFormat: true,
          run: { font: fontConfig.caption.name, size: fontConfig.caption.size * 2, bold: true, color: "000000" },
          paragraph: { spacing: { line: 240, before: DRAFT_PT(6), after: DRAFT_PT(6) }, keepNext: true, widowControl: true } },
        { id: "CTableHeader", name: "C-Table Header", basedOn: "Normal", next: "CTableText", uiPriority: 22, unhideWhenUsed: true, quickFormat: true,
          run: { font: fontConfig.tableHeader.name, size: fontConfig.tableHeader.size * 2, bold: true, color: "000000" },
          paragraph: { spacing: { line: 240, before: 0, after: 0 } } },
        { id: "CTableText", name: "C-Table Text", basedOn: "Normal", next: "CBodyText", uiPriority: 23, unhideWhenUsed: true, quickFormat: true,
          run: { font: fontConfig.tableCell.name, size: fontConfig.tableCell.size * 2, color: "000000" },
          paragraph: { spacing: { line: 240, before: 0, after: 0 } } },
        { id: "CBulletList", name: "C-Bullet List", basedOn: "Normal", next: "CBulletList", uiPriority: 24, unhideWhenUsed: true, quickFormat: true,
          run: { font: fontConfig.list.name, size: fontConfig.list.size * 2, color: "000000" },
          paragraph: {
            spacing: { line: Math.round((fontConfig.list.lineSpacing || 1.15) * 240), before: 0, after: 80 },
            indent: { left: DRAFT_CM_TO_DXA(0.63), hanging: DRAFT_CM_TO_DXA(0.63) },
          } },
        { id: "CFootnote", name: "C-Footnote", basedOn: "Normal", next: "CBodyText", uiPriority: 25, unhideWhenUsed: true, quickFormat: true,
          run: { font: fontConfig.footnote.name, size: fontConfig.footnote.size * 2, color: "000000" },
          paragraph: { spacing: { line: 240, before: 0, after: 0 } } },
        // Base "Normal" style aligned to Draft 1.0 §4.1 body text.
        {
          id: "Normal",
          name: "Normal",
          run: {
            font: fontConfig.body.name,
            size: fontConfig.body.size * 2,
            color: "000000",
          },
          paragraph: {
            spacing: {
              line: Math.round((fontConfig.body.lineSpacing || 1.15) * 240),
              before: 0,
              after: DRAFT_PT(6),
            },
            widowControl: true,
          },
        },
        {
          id: "Heading1",
          name: "Heading 1",
          basedOn: "Normal",
          next: "Normal",
          run: {
            font: fontConfig.heading.name,
            size: fontConfig.heading.size * 2,
            bold: true,
            color: "000000"
          },
          paragraph: {
            spacing: {
              after: DRAFT_PT(6),
              before: DRAFT_PT(18),
            },
            keepNext: true,
          }
        },
        {
          id: "Heading2", 
          name: "Heading 2",
          basedOn: "Normal",
          next: "Normal",
          run: {
            font: fontConfig.subheading.name,
            size: fontConfig.subheading.size * 2,
            bold: true,
            color: "000000"
          },
          paragraph: {
            spacing: {
              after: DRAFT_PT(6),
              before: DRAFT_PT(12),
            },
            keepNext: true,
          }
        },
        {
          id: "Heading3",
          name: "Heading 3", 
          basedOn: "Normal",
          next: "Normal",
          run: {
            font: (fontConfig.heading3?.name || fontConfig.body.name),
            size: (fontConfig.heading3?.size || 11) * 2,
            bold: true,
            color: "000000"
          },
          paragraph: {
            spacing: {
              after: DRAFT_PT(3),
              before: DRAFT_PT(12),
            },
            keepNext: true,
          }
        },
        {
          id: "Heading4",
          name: "Heading 4",
          basedOn: "Normal",
          next: "Normal",
          run: {
            font: (fontConfig.heading4?.name || fontConfig.body.name),
            size: (fontConfig.heading4?.size || 11) * 2,
            bold: true,
            color: "000000"
          },
          paragraph: {
            spacing: {
              after: DRAFT_PT(3),
              before: DRAFT_PT(6),
            },
            keepNext: true,
          }
        },
        {
          id: "Heading5",
          name: "Heading 5",
          basedOn: "Normal",
          next: "Normal",
          run: {
            font: (fontConfig.heading5?.name || fontConfig.body.name),
            size: (fontConfig.heading5?.size || 11) * 2,
            bold: false,
            italic: true,
            color: "000000"
          },
          paragraph: {
            spacing: {
              after: DRAFT_PT(0),
              before: DRAFT_PT(6),
            },
            keepNext: false,
          }
        },
        {
          id: "Heading6",
          name: "Heading 6",
          basedOn: "Normal",
          next: "Normal",
          run: {
            font: (fontConfig.heading5?.name || fontConfig.body.name),
            size: (fontConfig.heading5?.size || 11) * 2,
            bold: false,
            italic: true,
            color: "000000"
          },
          paragraph: {
            spacing: {
              after: DRAFT_PT(0),
              before: DRAFT_PT(6),
            },
            keepNext: false,
          }
        }
        ,
        // Ensure native Word TOC (TOC1–TOC6) renders larger and consistently unbold with hyperlink support
        {
          id: "TOC1",
          name: "TOC 1",
          basedOn: "Normal",
          next: "Normal",
          run: { 
            font: fontConfig.body.name,
            size: (fontConfig.tableCell?.size || fontConfig.body.size) * 2,
            bold: false,
            color: "000000" // Override hyperlink blue color
          },
          paragraph: { 
            spacing: { after: 120 }, 
            indent: { left: 0 } 
          }
        },
        {
          id: "TOC2",
          name: "TOC 2",
          basedOn: "Normal",
          next: "Normal",
          run: { 
            font: fontConfig.body.name,
            size: (fontConfig.tableCell?.size || fontConfig.body.size) * 2,
            bold: false,
            color: "000000" // Override hyperlink blue color
          },
          paragraph: { 
            spacing: { after: 100 }, 
            indent: { left: DRAFT_TOC_INDENT_DXA[2] } 
          }
        },
        {
          id: "TOC3",
          name: "TOC 3",
          basedOn: "Normal",
          next: "Normal",
          run: { 
            font: fontConfig.body.name,
            size: (fontConfig.tableCell?.size || fontConfig.body.size) * 2,
            bold: false,
            color: "000000" // Override hyperlink blue color
          },
          paragraph: { 
            spacing: { after: 80 }, 
            indent: { left: DRAFT_TOC_INDENT_DXA[3] } 
          }
        },
        {
          id: "TOC4",
          name: "TOC 4",
          basedOn: "Normal",
          next: "Normal",
          run: { 
            font: fontConfig.body.name,
            size: (fontConfig.tableCell?.size || fontConfig.body.size) * 2,
            bold: false,
            color: "000000" // Override hyperlink blue color
          },
          paragraph: { 
            spacing: { after: 60 }, 
            indent: { left: DRAFT_TOC_INDENT_DXA[4] } 
          }
        },
        {
          id: "TOC5",
          name: "TOC 5",
          basedOn: "Normal",
          next: "Normal",
          run: { 
            font: fontConfig.body.name,
            size: (fontConfig.tableCell?.size || fontConfig.body.size) * 2,
            bold: false,
            color: "000000" // Override hyperlink blue color
          },
          paragraph: { 
            spacing: { after: 60 }, 
            indent: { left: DRAFT_TOC_INDENT_DXA[5] }  
          }
        },
        {
          id: "TOC6",
          name: "TOC 6",
          basedOn: "Normal",
          next: "Normal",
          run: { 
            font: fontConfig.body.name,
            size: (fontConfig.tableCell?.size || fontConfig.body.size) * 2,
            bold: false,
            color: "000000" // Override hyperlink blue color
          },
          paragraph: { 
            spacing: { after: 60 }, 
            indent: { left: DRAFT_TOC_INDENT_DXA[6] } 
          }
        }
      ]
    }
  });
  
  const blob = await Packer.toBlob(doc);

  let postFont = { ok: true, errors: [], partsScanned: 0 };
  try {
    postFont = await validateExportedDocxBlob(blob, fontConfig);
    if (!postFont.ok && postFont.errors?.length) {
      console.warn('[post-export DOCX font validation]', postFont);
    }
  } catch (e) {
    console.warn('Post-export DOCX font validation failed to run:', e);
    postFont = {
      ok: false,
      errors: [`Validation could not run: ${e?.message || String(e)}`],
      partsScanned: 0,
    };
  }

  if (typeof postExportFontUi?.waitForUserAfterValidation === 'function') {
    const proceed = await postExportFontUi.waitForUserAfterValidation(postFont);
    if (!proceed) {
      return;
    }
  }

  if (postExportFontUi?.returnBlobOnly) {
    return blob;
  }

  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'clinical-trial-protocol.docx';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
  
  
  // Save exported document data to database
  if (projectId) {
    try {
      
      // Convert blob to base64 for storage (optional - can be large)
      const documentBase64 = await blobToBase64(blob);
      const fileSize = blob.size;
      
      // Prepare sections data for storage
      const sectionsData = sections.map(section => ({
        sectionNumber: section.sectionNumber || section.section_number,
        title: section.title,
        output: section.output || section.content,
        content: section.content || section.output,
        subsections: section.subsections || []
      }));
      
      // Prepare metadata
      const exportMetadata = {
        protocolNumber,
        amendmentVersion,
        versionDate,
        sponsorName,
        investigationalProductNames,
        titlePageConfig,
        fontConfig,
        hasSoA: !!(effectiveSoAData && effectiveSoAData.visits && effectiveSoAData.sections),
        soaData: effectiveSoAData || null,
        hasTrialSchema: !!trialSchemaImageUrl,
        trialSchemaImageUrl: trialSchemaImageUrl || null
      };
      
      // Save to database (don't wait for it - fire and forget to not block user)
      documentExportService.saveProtocolExport(
        projectId,
        null, // project ObjectId - can be fetched if needed
        'DOCX',
        'clinical-trial-protocol.docx',
        sectionsData,
        exportMetadata,
        documentBase64, // Store full document as base64
        fileSize
      ).then(result => {
      }).catch(error => {
        console.warn('⚠️ Failed to save exported document data (non-blocking):', error);
        // Don't throw - this is non-critical
      });
      
    } catch (error) {
      console.warn('⚠️ Error preparing export data for database (non-blocking):', error);
      // Don't throw - export was successful, saving to DB is optional
    }
  } else {
  }
};

// Helper to convert Blob to base64
async function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result.split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/** @react-pdf/renderer Image needs a data URL in the browser — remote URLs often fail silently. */
async function resolveImageUrlForNativePdf(imageUrl) {
  if (!imageUrl) return null;
  if (String(imageUrl).startsWith('data:image')) return imageUrl;
  if (String(imageUrl).startsWith('<svg')) return null;
  try {
    const res = await fetch(imageUrl);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const dataUrl = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
    return typeof dataUrl === 'string' ? dataUrl : null;
  } catch (e) {
    console.warn('Could not embed image for native PDF export:', e);
    return null;
  }
}

function countExportableProtocolSections(sectionList) {
  if (!Array.isArray(sectionList)) return 0;
  const normalized = getProtocolSectionsForExport(sectionList);
  return normalized.filter((section) => {
    const hasOutput = section?.output && String(section.output).trim();
    return (
      hasOutput ||
      isM11AppendixSectionTitle(section?.title) ||
      isM11Section1SpecialTitle(section?.title)
    );
  }).length;
}

async function renderNativeProtocolPdfBlob({
  sections: sectionList,
  fontConfig,
  soaData,
  trialSchemaImageUrl,
  headerFooterInfo,
  titlePageConfig,
}) {
  let rawBlob = await pdf(
    <ProtocolPdfDocument
      sections={sectionList}
      fontConfig={fontConfig}
      soaData={soaData}
      trialSchemaImageUrl={trialSchemaImageUrl}
      headerFooterInfo={headerFooterInfo}
      titlePageConfig={titlePageConfig}
    />,
  ).toBlob();

  const skipFirstPage = Boolean(titlePageConfig?.enabled);

  // Insert a real, hyperlinked, page-numbered Table of Contents — same idea as the Word
  // export's native TOC field, but built by locating rendered headings post-layout since
  // @react-pdf/renderer has no equivalent auto-updating TOC field (see nativeProtocolPdfToc.js
  // for why this can't be done with react-pdf's own `bookmark` prop).
  try {
    const tocEntries = buildProtocolPdfTocEntries(sectionList, soaData, trialSchemaImageUrl);
    if (tocEntries.length > 0) {
      const { insertProtocolPdfToc } = await import('../../utils/nativeProtocolPdfToc');
      rawBlob = await insertProtocolPdfToc(rawBlob, tocEntries, { titlePageEnabled: skipFirstPage });
    }
  } catch (tocErr) {
    console.warn('Native PDF TOC insertion failed; exporting without a TOC:', tocErr?.message || tocErr);
  }

  // Stamp page numbers after layout — react-pdf `render` footers miss pages on long docs.
  try {
    const { stampProtocolPdfPageNumbers } = await import('../../utils/stampProtocolPdfPageNumbers');
    return await stampProtocolPdfPageNumbers(rawBlob, {
      skipFirstPage,
      marginRight: 72,
      marginBottom: 28,
      fontSize: 10,
    });
  } catch (stampErr) {
    console.warn('PDF page-number stamp failed; returning unstamped blob:', stampErr?.message || stampErr);
    return rawBlob;
  }
}

/** On layout failure, linear scan finds the first section that triggers the crash. */
async function renderNativeProtocolPdfBlobWithDiagnosis(props) {
  try {
    return await renderNativeProtocolPdfBlob(props);
  } catch (err) {
    const exportable = (props.sections || []).filter((s) => s?.output && s?.title);
    if (exportable.length <= 1) throw err;

    console.warn('Native PDF export failed; scanning sections to locate failing content…');
    for (let n = 1; n <= exportable.length; n += 1) {
      try {
        await renderNativeProtocolPdfBlob({ ...props, sections: exportable.slice(0, n) });
      } catch {
        const failingSection = exportable[n - 1];
        const title = failingSection?.title || `section ${n}`;
        let detail = err?.message || String(err);
        try {
          await renderNativeProtocolPdfBlob({ ...props, sections: [failingSection] });
        } catch (soloErr) {
          detail = soloErr?.message || detail;
        }
        throw new Error(`PDF layout failed near "${title}": ${detail}`);
      }
    }
    throw err;
  }
}

// Helper: Parse content into main content and subsections
function parseSectionContent(content, sectionNumber) {
  if (!content) return { main: '', subsections: [] };
  const lines = content.split(/\r?\n/);
  let mainContent = [];
  let subsections = [];
  let currentSub = null;
  let subPattern = new RegExp(`^${sectionNumber}\\.\\d+\\s+(.+)`); // e.g., 1.1 Title

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const match = line.match(subPattern);
    if (match) {
      // Start new subsection
      if (currentSub) subsections.push(currentSub);
      currentSub = {
        number: `${sectionNumber}.${(match[1] || '').trim()}`,
        title: (match[2] || '').trim(),
        content: ''
      };
    } else if (currentSub) {
      currentSub.content += (currentSub.content ? '\n' : '') + line;
    } else {
      mainContent.push(line);
    }
  }
  if (currentSub) subsections.push(currentSub);
  return {
    main: (mainContent.join('\n') || '').trim(),
    subsections
  };
}

// Helper: Convert a string to camelCase for XML tag names
function toCamelCase(str) {
  return str
    .toLowerCase()
    .replace(/[^a-zA-Z0-9 ]/g, ' ') // Remove special chars, keep spaces
    .replace(/ (.)/g, (m, c) => c.toUpperCase()) // Capitalize first letter after space
    .replace(/\s+/g, ''); // Remove all spaces
}

// Helper: Convert JSON to XML recursively
function jsonToXml(sectionObj, tagName, indent = '') {
  let xml = '';
  const esc = (str) => str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');

  xml += `${indent}<${tagName}`;
  if (sectionObj.number) xml += ` number="${esc(sectionObj.number)}"`;
  if (sectionObj.title) xml += ` title="${esc(sectionObj.title)}"`;
  xml += '>';

  if (sectionObj.main) {
    xml += `\n${indent}  <content>\n${indent}    ${esc(sectionObj.main)}\n${indent}  </content>`;
  }
  if (sectionObj.content && !sectionObj.subsections) {
    xml += `\n${indent}  <content>\n${indent}    ${esc(sectionObj.content)}\n${indent}  </content>`;
  }
  if (sectionObj.subsections && sectionObj.subsections.length > 0) {
    xml += `\n${indent}  <subsections>`;
    for (const sub of sectionObj.subsections) {
      const subTag = sub.title
        ? toCamelCase(sub.title) || 'subsection'
        : 'subsection';
      xml += `\n` + jsonToXml(sub, subTag, indent + '    ');
    }
    xml += `\n${indent}  </subsections>`;
  }
  xml += `\n${indent}</${tagName}>`;
  return xml;
}

// Helper: Strip HTML tags and convert to plain text
const stripHtmlTags = (html) => {
  if (!html) return '';
  const tmp = document.createElement('DIV');
  tmp.innerHTML = html;
  
  // Replace specific HTML elements with meaningful text
  tmp.querySelectorAll('h1, h2, h3, h4, h5, h6').forEach(h => {
    const text = h.textContent.trim();
    h.replaceWith(document.createTextNode(`\n\n${text}\n\n`));
  });
  
  tmp.querySelectorAll('strong, b').forEach(el => {
    const text = el.textContent.trim();
    el.replaceWith(document.createTextNode(text));
  });
  
  tmp.querySelectorAll('ul').forEach(ul => {
    const items = Array.from(ul.querySelectorAll('li')).map(li => `• ${li.textContent.trim()}`).join('\n');
    ul.replaceWith(document.createTextNode(`\n${items}\n`));
  });
  
  tmp.querySelectorAll('ol').forEach(ol => {
    const items = Array.from(ol.querySelectorAll('li')).map((li, idx) => `${idx + 1}. ${li.textContent.trim()}`).join('\n');
    ol.replaceWith(document.createTextNode(`\n${items}\n`));
  });
  
  tmp.querySelectorAll('p').forEach(p => {
    const text = p.textContent.trim();
    if (text) {
      p.replaceWith(document.createTextNode(`\n${text}\n`));
    } else {
      p.remove();
    }
  });
  
  tmp.querySelectorAll('br').forEach(br => {
    br.replaceWith(document.createTextNode('\n'));
  });
  
  tmp.querySelectorAll('table').forEach(table => {
    const rows = Array.from(table.querySelectorAll('tr')).map(tr => {
      const cells = Array.from(tr.querySelectorAll('td, th')).map(cell => cell.textContent.trim()).join(' | ');
      return cells;
    }).join('\n');
    table.replaceWith(document.createTextNode(`\n${rows}\n`));
  });
  
  let text = tmp.textContent || tmp.innerText || '';
  text = text.replace(/\n{3,}/g, '\n\n');
  text = text.replace(/[ \t]+/g, ' ');
  text = text.trim();
  
  return text;
};

// Shared helper function to recursively convert objects/arrays to readable strings
const convertValueToStringRecursive = (val, depth = 0) => {
  // Prevent infinite recursion
  if (depth > 5) {
    return String(val);
  }
  
  if (val === null || val === undefined) {
    return '';
  }
  
  if (typeof val === 'object') {
    if (Array.isArray(val)) {
      // Array: recursively convert each item and join
      return val.map(item => convertValueToStringRecursive(item, depth + 1))
        .filter(item => item && item !== '[object Object]' && item !== '[object Array]')
        .join('; ');
    } else {
      // Object: recursively convert each value and format as key-value pairs
      const entries = Object.entries(val)
        .map(([k, v]) => {
          const convertedV = convertValueToStringRecursive(v, depth + 1);
          if (!convertedV || convertedV === '[object Object]' || convertedV === '[object Array]') {
            return null;
          }
          return `${k}: ${convertedV}`;
        })
        .filter(entry => entry !== null);
      
      return entries.join('; ');
    }
  }
  
  // Primitive value: convert to string
  return String(val);
};

// Helper function to extract key facts from section content using AI
const extractKeyFactsFromSectionWithAI = async (sectionTitle, sectionContent) => {
  if (!sectionContent) return null;
  
  const text = stripHtmlTags(sectionContent);
  
  // Don't process very short sections (likely placeholders)
  if (text.length < 50) return null;
  
  const prompt = `You are a clinical trial documentation expert. Extract key factual information from the following protocol section in a structured, hierarchical manner.

SECTION: ${sectionTitle}

TASK: Extract ONLY the most important factual information following the section's natural hierarchy. Be concise, factual, and avoid repetition.

CRITICAL RULES:
1. Extract facts in a LOGICAL HIERARCHY matching the section structure (main points → subsections → details)
2. Keep each fact SHORT and SIMPLE (max 100 characters per value)
3. NO repetition - if something is mentioned once, extract it once
4. NO illogical numbering (e.g., don't create "endpoint_1", "endpoint_2" unless they are explicitly numbered in the content)
5. Use meaningful, hierarchical keys that reflect the section structure
6. For symbols like < or >, use: "less than", "greater than", "at least", "at most"
7. Extract only UNIQUE facts - avoid duplicating similar information

SECTION-SPECIFIC EXTRACTION GUIDELINES:

Section 1 (Protocol Summary/Trial Design):
- Extract: design_type, number_of_arms, blinding, sample_size, duration (one fact each, no repetition)

Section 2 (Introduction):
- Extract: background_rationale (one concise summary)

Section 3 (Objectives and Endpoints):
- Extract: primary_objective, primary_endpoint, secondary_objectives (as a list if multiple), secondary_endpoints (as a list if multiple)
- Structure by objective type, not arbitrary numbering

Section 4 (Trial Design):
- Extract: intervention_model, allocation_method, randomization_ratio (one fact each)

Section 5 (Trial Population):
- Extract: inclusion_criteria (as a concise list), exclusion_criteria (as a concise list), age_range, population_description
- Group criteria logically, don't number arbitrarily

Section 6 (Interventions):
- Extract: study_drug_name, dosage, route, frequency, administration_method (one fact each)

Section 8 (Assessments):
- Extract: primary_assessments, safety_assessments, visit_schedule (concise summaries)

Section 10 (Statistics):
- Extract: sample_size, statistical_method, alpha_level, power (one fact each)

OUTPUT FORMAT: Return ONLY valid JSON object. No markdown, no explanations. Use double quotes. Keep it simple and structured.

CRITICAL: ALL values MUST be strings (text), NOT objects or arrays. If you need to list multiple items, join them with commas or semicolons in a single string.

Example (concise and logical - ALL VALUES ARE STRINGS):
{
  "primary_objective": "Evaluate efficacy of Drug X vs placebo",
  "primary_endpoint": "Change in symptom score at week 24",
  "secondary_objectives": "Assess safety and quality of life",
  "secondary_endpoints": "AE incidence, QoL questionnaire scores",
  "sample_size": "450 participants",
  "study_design": "Randomized, double-blind, placebo-controlled",
  "inclusion_criteria": "Adults 18-75 years; Confirmed diagnosis; Disease severity score at least 5",
  "exclusion_criteria": "Pregnant women; Severe allergies; Active infections"
}

DO NOT return objects like {"key": "value"} or arrays like ["item1", "item2"]. Always convert them to comma-separated strings.

SECTION CONTENT:
${text.substring(0, 8000)}`;

  try {
    const response = await generateContent(
      prompt,
      '',
      '',
      'fact-extraction',
      `Fact extraction for ${sectionTitle}`
    );
    
    if (!response) return null;
    
    // Try to extract JSON from the response
    let jsonStr = response.trim();
    
    // Remove markdown code blocks if present
    jsonStr = jsonStr.replace(/```json\s*/g, '').replace(/```\s*/g, '');
    
    // Find JSON object
    const jsonMatch = jsonStr.match(/\{[\s\S]*\}/);
    if (!jsonMatch) {
      console.warn(`No JSON found in AI response for ${sectionTitle}`);
      return null;
    }
    
    // Clean up the JSON string - handle encoding issues
    let cleanedJson = jsonMatch[0]
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&amp;/g, '&')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'");
    
    const facts = JSON.parse(cleanedJson);
    
    // Post-process facts to handle encoding in values, convert objects/arrays to strings, and remove duplicates
    const processedFacts = {};
    const seenValues = new Set();
    
    Object.keys(facts).forEach(key => {
      let value = facts[key];
      
      // Convert objects/arrays to strings recursively (using shared helper)
      value = convertValueToStringRecursive(value);
      
      // Convert < and > symbols to text in the values (for display)
      value = value
        .replace(/&lt;/g, 'less than')
        .replace(/&gt;/g, 'greater than')
        .replace(/</g, 'less than')
        .replace(/>/g, 'greater than')
        .replace(/&amp;/g, '&')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/&nbsp;/g, ' ')
        .trim();
      
      // Skip if value is empty or invalid
      if (!value || value === '[object Object]' || value === '[object Array]') {
        return;
      }
      
      // Remove duplicates - if we've seen this exact value before, skip it
      const normalizedValue = value.toLowerCase().replace(/\s+/g, ' ');
      if (!seenValues.has(normalizedValue) && value.length > 0) {
        seenValues.add(normalizedValue);
        processedFacts[key] = value;
      }
    });
    
    // Validate that we got facts
    if (Object.keys(processedFacts).length === 0) {
      return null;
    }
    
    return processedFacts;
  } catch (error) {
    console.error(`Error extracting facts from ${sectionTitle}:`, error);
    return null;
  }
};

// Helper function to fetch and format protocol facts
const getProtocolFacts = async (protocolSections = [], projectId = null) => {
  let adminInfo = null;
  try {
    const adminResponse = await administrativeInfoService.getAllAdministrativeInfo(projectId);
    adminInfo = Array.isArray(adminResponse?.data) ? adminResponse.data[0] : null;
  } catch (error) {
    console.error('Error fetching administrative info:', error);
  }

  // Title fields (official/short title) are entered on the Clinical Intake
  // form (study_identification.official_title / preferred_short_title), not
  // on Administrative Info - that form no longer has title inputs.
  // AdministrativeInfo.official_title/short_title only get populated by a
  // background title-generation sync job, so Clinical Intake is the more
  // reliable source and is checked first.
  let intakeIdentification = null;
  try {
    const intake = await clinicalIntakeService.fetchLatestClinicalIntakeData(projectId);
    intakeIdentification = intake?.study_identification || null;
  } catch (error) {
    console.error('Error fetching clinical intake for title fields:', error);
  }

   const sponsorResolved = resolveAdminSponsorFields(adminInfo);
  const get = (k, d = '') => {
    if (!adminInfo) return d;
    if (k === 'sponsor_name') return sponsorResolved.sponsorName || d;
    if (k === 'sponsor_legal_address' || k === 'sponsor_address') {
      return sponsorResolved.sponsorAddress || d;
    }
    return adminInfo[k] != null && adminInfo[k] !== '' ? adminInfo[k] : d;
  };
  
  // Extract facts from all sections using AI
  const sectionFacts = {};
  
  // Process sections sequentially to avoid overwhelming the AI service
  for (const section of protocolSections) {
    if (section.title && section.output) {
      try {
        const facts = await extractKeyFactsFromSectionWithAI(section.title, section.output);
        if (facts) {
          sectionFacts[section.title] = facts;
        } else {
        }
      } catch (error) {
        console.error(`Error processing ${section.title}:`, error);
      }
    }
  }
  
  
  return {
    protocolId: get('protocol_number',''),
    protocolTitle: intakeIdentification?.official_title || get('official_title', ''),
    shortTitle: intakeIdentification?.preferred_short_title || get('short_title', '') || get('trial_acronym', ''),
    protocolVersion: (get('version_number','') || '').toString(),
    protocolDate: get('version_date','') ? new Date(get('version_date')).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : '',
    sponsorName: get('sponsor_name',''),
    sponsorAddress: get('sponsor_legal_address','') || get('sponsor_address',''),
    contactName: get('sponsor_contact_name',''),
    contactEmail: get('sponsor_contact_email',''),
    contactPhone: get('sponsor_contact_phone',''),
    phase: get('trial_phase','') || get('phase',''),
    therapeuticArea: get('therapeutic_area',''),
    indication: get('indication',''),
    interventionType: get('intervention_type','Drug'),
    studyType: get('study_type','Interventional'),
    trialAcronym: get('trial_acronym',''),
    clinicalTrialIdentifier: get('clinical_trial_identifier',''),
    regulatoryStatus: get('regulatory_status',''),
    investigationalProductCode: get('sponsor_investigational_product_codes',''),
    investigationalProductName: get('investigational_product_names',''),
    eudractNumber: get('eudract_number',''),
    nctNumber: get('nct_number',''),
    irbApprovalDate: get('irb_approval_date','') ? new Date(get('irb_approval_date')).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : '',
    studyStartDate: get('study_start_date','') ? new Date(get('study_start_date')).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : '',
    studyCompletionDate: get('study_completion_date','') ? new Date(get('study_completion_date')).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : '',
    principalInvestigator: get('principal_investigator',''),
    principalInvestigatorAffiliation: get('principal_investigator_affiliation',''),
    sectionFacts: sectionFacts,
    rawAdminInfo: adminInfo
  };
};

// Export protocol facts as text file
const exportProtocolFactsAsText = async (protocolSections = [], projectId = null) => {
  try {
    // Show loading message
    alert('Extracting facts from all protocol sections using AI. This may take 1-2 minutes. Please wait...');
    
    const facts = await getProtocolFacts(protocolSections, projectId);
    
    let text = '═══════════════════════════════════════════════════════\n';
    text += '                 CLINICAL TRIAL PROTOCOL FACTS\n';
    text += '═══════════════════════════════════════════════════════\n\n';
    
    text += '─────────────────────────────────────────────────────\n';
    text += '  PROTOCOL INFORMATION\n';
    text += '─────────────────────────────────────────────────────\n\n';
    
    if (facts.protocolId) text += `Protocol Number:        ${facts.protocolId}\n`;
    if (facts.protocolTitle) text += `Protocol Title:         ${facts.protocolTitle}\n`;
    if (facts.shortTitle) text += `Short Title:            ${facts.shortTitle}\n`;
    if (facts.trialAcronym) text += `Trial Acronym:          ${facts.trialAcronym}\n`;
    if (facts.protocolVersion) text += `Version Number:         ${facts.protocolVersion}\n`;
    if (facts.protocolDate) text += `Version Date:           ${facts.protocolDate}\n`;
    
    text += '\n─────────────────────────────────────────────────────\n';
    text += '  SPONSOR INFORMATION\n';
    text += '─────────────────────────────────────────────────────\n\n';
    
    if (facts.sponsorName) text += `Sponsor Name:           ${facts.sponsorName}\n`;
    if (facts.sponsorAddress) text += `Sponsor Address:        ${facts.sponsorAddress}\n`;
    if (facts.contactName) text += `Contact Name:           ${facts.contactName}\n`;
    if (facts.contactEmail) text += `Contact Email:          ${facts.contactEmail}\n`;
    if (facts.contactPhone) text += `Contact Phone:          ${facts.contactPhone}\n`;
    
    text += '\n─────────────────────────────────────────────────────\n';
    text += '  STUDY CLASSIFICATION\n';
    text += '─────────────────────────────────────────────────────\n\n';
    
    if (facts.phase) text += `Phase:                  ${facts.phase}\n`;
    if (facts.studyType) text += `Study Type:             ${facts.studyType}\n`;
    if (facts.therapeuticArea) text += `Therapeutic Area:       ${facts.therapeuticArea}\n`;
    if (facts.indication) text += `Indication:             ${facts.indication}\n`;
    if (facts.interventionType) text += `Intervention Type:      ${facts.interventionType}\n`;
    if (facts.regulatoryStatus) text += `Regulatory Status:      ${facts.regulatoryStatus}\n`;
    
    text += '\n─────────────────────────────────────────────────────\n';
    text += '  INVESTIGATIONAL PRODUCT\n';
    text += '─────────────────────────────────────────────────────\n\n';
    
    if (facts.investigationalProductCode) text += `Product Code(s):        ${facts.investigationalProductCode}\n`;
    if (facts.investigationalProductName) text += `Product Name(s):        ${facts.investigationalProductName}\n`;
    
    text += '\n─────────────────────────────────────────────────────\n';
    text += '  REGULATORY IDENTIFIERS\n';
    text += '─────────────────────────────────────────────────────\n\n';
    
    if (facts.clinicalTrialIdentifier) text += `Clinical Trial ID:      ${facts.clinicalTrialIdentifier}\n`;
    if (facts.nctNumber) text += `NCT Number:             ${facts.nctNumber}\n`;
    if (facts.eudractNumber) text += `EudraCT Number:         ${facts.eudractNumber}\n`;
    
    text += '\n─────────────────────────────────────────────────────\n';
    text += '  STUDY TIMELINE\n';
    text += '─────────────────────────────────────────────────────\n\n';
    
    if (facts.irbApprovalDate) text += `IRB Approval Date:      ${facts.irbApprovalDate}\n`;
    if (facts.studyStartDate) text += `Study Start Date:       ${facts.studyStartDate}\n`;
    if (facts.studyCompletionDate) text += `Study Completion Date:  ${facts.studyCompletionDate}\n`;
    
    text += '\n─────────────────────────────────────────────────────\n';
    text += '  INVESTIGATORS\n';
    text += '─────────────────────────────────────────────────────\n\n';
    
    if (facts.principalInvestigator) text += `Principal Investigator: ${facts.principalInvestigator}\n`;
    if (facts.principalInvestigatorAffiliation) text += `Affiliation:            ${facts.principalInvestigatorAffiliation}\n`;
    
    // Add section-level facts
    if (facts.sectionFacts && Object.keys(facts.sectionFacts).length > 0) {
      text += '\n\n═══════════════════════════════════════════════════════\n';
      text += '                    PROTOCOL SECTION FACTS\n';
      text += '═══════════════════════════════════════════════════════\n';
      
      Object.entries(facts.sectionFacts).forEach(([sectionTitle, sectionData]) => {
        text += `\n─────────────────────────────────────────────────────\n`;
        text += `  ${sectionTitle.toUpperCase()}\n`;
        text += `─────────────────────────────────────────────────────\n\n`;
        
        // Sort facts logically: primary/important facts first, then secondary
        const sortedFacts = Object.entries(sectionData).sort(([keyA], [keyB]) => {
          const priority = {
            'primary': 1,
            'secondary': 2,
            'study_design': 1,
            'sample_size': 1,
            'number_of_arms': 1,
            'inclusion': 3,
            'exclusion': 4,
            'dosage': 2,
            'route': 2,
            'frequency': 2
          };
          
          const getPriority = (key) => {
            const lowerKey = key.toLowerCase();
            for (const [term, prio] of Object.entries(priority)) {
              if (lowerKey.includes(term)) return prio;
            }
            return 5; // Default priority
          };
          
          return getPriority(keyA) - getPriority(keyB);
        });
        
        sortedFacts.forEach(([key, value]) => {
          if (value) {
            // Format key name (convert snake_case or camelCase to Title Case)
            let formattedKey = key
              .replace(/_/g, ' ')  // Replace underscores with spaces
              .replace(/([A-Z])/g, ' $1')  // Add space before capital letters
              .trim()
              .split(' ')
              .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
              .join(' ');
            
            // Handle different value types: convert objects/arrays to readable strings recursively
            let displayValue = convertValueToStringRecursive(value);
            
            // Handle encoding: convert < and > to text equivalents for better readability
            displayValue = displayValue
              .replace(/&lt;/g, 'less than')
              .replace(/&gt;/g, 'greater than')
              .replace(/</g, 'less than')
              .replace(/>/g, 'greater than')
              .replace(/&amp;/g, '&')
              .replace(/&quot;/g, '"')
              .replace(/&#39;/g, "'")
              .replace(/&nbsp;/g, ' ')
              .trim();
            
            // Skip if value is empty after processing
            if (!displayValue || displayValue === '[object Object]' || displayValue === '[object Array]') {
              return;
            }
            
            // Display full value without truncation
            text += `${formattedKey}:\n  ${displayValue}\n\n`;
          }
        });
      });
    }
    
    text += '\n═══════════════════════════════════════════════════════\n';
    text += `Generated on: ${new Date().toLocaleString('en-US', { 
      year: 'numeric', month: 'long', day: 'numeric', 
      hour: '2-digit', minute: '2-digit', second: '2-digit' 
    })}\n`;
    text += '═══════════════════════════════════════════════════════\n';
    
    // Download as text file
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `protocol-facts-${facts.protocolId || 'unknown'}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    alert('Protocol Facts successfully exported! Check your downloads folder.');
  } catch (error) {
    console.error('Error exporting protocol facts as text:', error);
    alert('Error exporting protocol facts. Please try again.');
  }
};

// Export protocol facts as PDF
const exportProtocolFactsAsPDF = async (protocolSections = [], projectId = null) => {
  try {
    // Show loading message
    alert('Extracting facts from all protocol sections using AI. This may take 1-2 minutes. Please wait...');
    
    const facts = await getProtocolFacts(protocolSections, projectId);
    
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    const contentWidth = pageWidth - 2 * margin;
    let yPos = margin;
    
    // Helper function to add text with automatic page break
    const addText = (text, fontSize = 10, isBold = false, indent = 0) => {
      if (yPos + fontSize / 2 > pageHeight - margin) {
        doc.addPage();
        yPos = margin;
      }
      doc.setFontSize(fontSize);
      doc.setFont('helvetica', isBold ? 'bold' : 'normal');
      const lines = doc.splitTextToSize(text, contentWidth - indent);
      lines.forEach(line => {
        if (yPos + fontSize / 2 > pageHeight - margin) {
          doc.addPage();
          yPos = margin;
        }
        doc.text(line, margin + indent, yPos);
        yPos += fontSize / 2;
      });
    };
    
    // Title
    doc.setFillColor(25, 118, 210);
    doc.rect(0, 0, pageWidth, 40, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('CLINICAL TRIAL PROTOCOL FACTS', pageWidth / 2, 25, { align: 'center' });
    yPos = 50;
    doc.setTextColor(0, 0, 0);
    
    // Protocol Information
    doc.setFillColor(240, 240, 240);
    doc.rect(margin, yPos - 5, contentWidth, 10, 'F');
    addText('PROTOCOL INFORMATION', 14, true);
    yPos += 5;
    
    if (facts.protocolId) addText(`Protocol Number: ${facts.protocolId}`, 10, false, 5);
    if (facts.protocolTitle) addText(`Protocol Title: ${facts.protocolTitle}`, 10, false, 5);
    if (facts.shortTitle) addText(`Short Title: ${facts.shortTitle}`, 10, false, 5);
    if (facts.trialAcronym) addText(`Trial Acronym: ${facts.trialAcronym}`, 10, false, 5);
    if (facts.protocolVersion) addText(`Version Number: ${facts.protocolVersion}`, 10, false, 5);
    if (facts.protocolDate) addText(`Version Date: ${facts.protocolDate}`, 10, false, 5);
    yPos += 10;
    
    // Sponsor Information
    doc.setFillColor(240, 240, 240);
    doc.rect(margin, yPos - 5, contentWidth, 10, 'F');
    addText('SPONSOR INFORMATION', 14, true);
    yPos += 5;
    
    if (facts.sponsorName) addText(`Sponsor Name: ${facts.sponsorName}`, 10, false, 5);
    if (facts.sponsorAddress) addText(`Sponsor Address: ${facts.sponsorAddress}`, 10, false, 5);
    if (facts.contactName) addText(`Contact Name: ${facts.contactName}`, 10, false, 5);
    if (facts.contactEmail) addText(`Contact Email: ${facts.contactEmail}`, 10, false, 5);
    if (facts.contactPhone) addText(`Contact Phone: ${facts.contactPhone}`, 10, false, 5);
    yPos += 10;
    
    // Study Classification
    doc.setFillColor(240, 240, 240);
    doc.rect(margin, yPos - 5, contentWidth, 10, 'F');
    addText('STUDY CLASSIFICATION', 14, true);
    yPos += 5;
    
    if (facts.phase) addText(`Phase: ${facts.phase}`, 10, false, 5);
    if (facts.studyType) addText(`Study Type: ${facts.studyType}`, 10, false, 5);
    if (facts.therapeuticArea) addText(`Therapeutic Area: ${facts.therapeuticArea}`, 10, false, 5);
    if (facts.indication) addText(`Indication: ${facts.indication}`, 10, false, 5);
    if (facts.interventionType) addText(`Intervention Type: ${facts.interventionType}`, 10, false, 5);
    if (facts.regulatoryStatus) addText(`Regulatory Status: ${facts.regulatoryStatus}`, 10, false, 5);
    yPos += 10;
    
    // Investigational Product
    doc.setFillColor(240, 240, 240);
    doc.rect(margin, yPos - 5, contentWidth, 10, 'F');
    addText('INVESTIGATIONAL PRODUCT', 14, true);
    yPos += 5;
    
    if (facts.investigationalProductCode) addText(`Product Code(s): ${facts.investigationalProductCode}`, 10, false, 5);
    if (facts.investigationalProductName) addText(`Product Name(s): ${facts.investigationalProductName}`, 10, false, 5);
    yPos += 10;
    
    // Regulatory Identifiers
    doc.setFillColor(240, 240, 240);
    doc.rect(margin, yPos - 5, contentWidth, 10, 'F');
    addText('REGULATORY IDENTIFIERS', 14, true);
    yPos += 5;
    
    if (facts.clinicalTrialIdentifier) addText(`Clinical Trial ID: ${facts.clinicalTrialIdentifier}`, 10, false, 5);
    if (facts.nctNumber) addText(`NCT Number: ${facts.nctNumber}`, 10, false, 5);
    if (facts.eudractNumber) addText(`EudraCT Number: ${facts.eudractNumber}`, 10, false, 5);
    yPos += 10;
    
    // Study Timeline
    doc.setFillColor(240, 240, 240);
    doc.rect(margin, yPos - 5, contentWidth, 10, 'F');
    addText('STUDY TIMELINE', 14, true);
    yPos += 5;
    
    if (facts.irbApprovalDate) addText(`IRB Approval Date: ${facts.irbApprovalDate}`, 10, false, 5);
    if (facts.studyStartDate) addText(`Study Start Date: ${facts.studyStartDate}`, 10, false, 5);
    if (facts.studyCompletionDate) addText(`Study Completion Date: ${facts.studyCompletionDate}`, 10, false, 5);
    yPos += 10;
    
    // Investigators
    doc.setFillColor(240, 240, 240);
    doc.rect(margin, yPos - 5, contentWidth, 10, 'F');
    addText('INVESTIGATORS', 14, true);
    yPos += 5;
    
    if (facts.principalInvestigator) addText(`Principal Investigator: ${facts.principalInvestigator}`, 10, false, 5);
    if (facts.principalInvestigatorAffiliation) addText(`Affiliation: ${facts.principalInvestigatorAffiliation}`, 10, false, 5);
    yPos += 10;
    
    // Protocol Section Facts
    if (facts.sectionFacts && Object.keys(facts.sectionFacts).length > 0) {
      // Add new page for section facts
      doc.addPage();
      yPos = margin;
      
      // Section title
      doc.setFillColor(25, 118, 210);
      doc.rect(0, 0, pageWidth, 25, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('PROTOCOL SECTION FACTS', pageWidth / 2, 15, { align: 'center' });
      yPos = 35;
      doc.setTextColor(0, 0, 0);
      
      Object.entries(facts.sectionFacts).forEach(([sectionTitle, sectionData]) => {
        // Section header
        doc.setFillColor(200, 200, 200);
        doc.rect(margin, yPos - 5, contentWidth, 10, 'F');
        addText(sectionTitle.toUpperCase(), 12, true);
        yPos += 5;
        
        // Sort facts logically: primary/important facts first, then secondary
        const sortedFacts = Object.entries(sectionData).sort(([keyA], [keyB]) => {
          const priority = {
            'primary': 1,
            'secondary': 2,
            'study_design': 1,
            'sample_size': 1,
            'number_of_arms': 1,
            'inclusion': 3,
            'exclusion': 4,
            'dosage': 2,
            'route': 2,
            'frequency': 2
          };
          
          const getPriority = (key) => {
            const lowerKey = key.toLowerCase();
            for (const [term, prio] of Object.entries(priority)) {
              if (lowerKey.includes(term)) return prio;
            }
            return 5; // Default priority
          };
          
          return getPriority(keyA) - getPriority(keyB);
        });
        
        sortedFacts.forEach(([key, value]) => {
          if (value) {
            // Format key name (convert snake_case or camelCase to Title Case)
            let formattedKey = key
              .replace(/_/g, ' ')  // Replace underscores with spaces
              .replace(/([A-Z])/g, ' $1')  // Add space before capital letters
              .trim()
              .split(' ')
              .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
              .join(' ');
            
            // Handle different value types: convert objects/arrays to readable strings recursively
            let displayValue = convertValueToStringRecursive(value);
            
            // Skip if value is empty or invalid after processing
            if (!displayValue || displayValue === '[object Object]' || displayValue === '[object Array]') {
              return;
            }
            
            // Handle encoding: convert < and > to text equivalents for better readability
            displayValue = displayValue
              .replace(/&lt;/g, 'less than')
              .replace(/&gt;/g, 'greater than')
              .replace(/</g, 'less than')
              .replace(/>/g, 'greater than')
              .replace(/&amp;/g, '&')
              .replace(/&quot;/g, '"')
              .replace(/&#39;/g, "'")
              .replace(/&nbsp;/g, ' ')
              .trim();
            
            addText(`${formattedKey}:`, 10, true, 5);
            
            // Display full value without truncation (PDF will handle wrapping automatically)
            addText(displayValue, 9, false, 10);
            yPos += 3;
          }
        });
        
        yPos += 5;
      });
    }
    
    // Footer
    const timestamp = new Date().toLocaleString('en-US', { 
      year: 'numeric', month: 'long', day: 'numeric', 
      hour: '2-digit', minute: '2-digit', second: '2-digit' 
    });
    doc.setFontSize(8);
    doc.setTextColor(128, 128, 128);
    doc.text(`Generated on: ${timestamp}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
    
    // Save PDF
    doc.save(`protocol-facts-${facts.protocolId || 'unknown'}.pdf`);
    
    alert('Protocol Facts successfully exported as PDF! Check your downloads folder.');
  } catch (error) {
    console.error('Error exporting protocol facts as PDF:', error);
    alert('Error exporting protocol facts. Please try again.');
  }
};

const buildAndSaveProtocolXml = async (sections, generatedSoAData = null, trialSchemaImageUrl = null, projectId = null, titlePageConfig = null) => {
  const normalizedSections = normalizeProtocolSectionsList(sections);
  const esc = (s) => String(s || '')
    .replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;').replace(/'/g,'&apos;');

  // Pull administrative info for header
  let adminInfo = null;
  try {
    const adminResponse = await administrativeInfoService.getAllAdministrativeInfo(projectId);
    adminInfo = Array.isArray(adminResponse?.data) ? adminResponse.data[0] : null;
  } catch (_) {}

  // Title fields (official/short title) are entered on the Clinical Intake
  // form (study_identification.official_title / preferred_short_title), not
  // on Administrative Info - that form no longer has title inputs.
  // AdministrativeInfo.official_title/short_title only get populated by a
  // background title-generation sync job, so Clinical Intake is the more
  // reliable source and is checked first.
  let intakeIdentificationXml = null;
  try {
    const intake = await clinicalIntakeService.fetchLatestClinicalIntakeData(projectId);
    intakeIdentificationXml = intake?.study_identification || null;
  } catch (_) {}

  const sponsorResolvedXml = resolveAdminSponsorFields(adminInfo);
  const get = (k, d = '') => {
    if (!adminInfo) return d;
    if (k === 'sponsor_name') return sponsorResolvedXml.sponsorName || d;
    if (k === 'sponsor_legal_address' || k === 'sponsor_address') {
      return sponsorResolvedXml.sponsorAddress || d;
    }
    return adminInfo[k] != null && adminInfo[k] !== '' ? adminInfo[k] : d;
  };

  // Debug: log all incoming sections
  
  // Sort sections by main number and filter to keep only main sections (not subsections)
  const sorted = [...normalizedSections]
    .filter(s => s?.title)
    // First, deduplicate by _id to remove exact duplicates from database
    .filter((section, index, self) => {
      if (!section._id) return true;
      const isDuplicate = index !== self.findIndex(s => s._id === section._id);
      if (isDuplicate) {
      }
      return !isDuplicate;
    })
    .sort((a,b) => {
      const an = parseInt((a.title.match(/^(\d+)/)||[])[1] || '999', 10);
      const bn = parseInt((b.title.match(/^(\d+)/)||[])[1] || '999', 10);
      return an - bn;
    })
    // Filter out subsections (3.0, 3.1, 3.1.1, etc.) - keep only main sections (3, 4, 5, etc.)
    // Subsections will be extracted from the content of main sections
    .filter((section) => {
      // Match section number at start with optional space/punctuation
      // Pattern: "3", "3.", "3 ", "3.0", "3.1", "3.1.1", etc.
      const titleMatch = section.title.match(/^(\d+)(?:\.(\d+)(?:\.\d+)*)?[\s\.\-]*/);
      if (!titleMatch) {
        return true; // Keep sections without numbers
      }
      const mainNum = titleMatch[1]; // e.g., "3"
      const subNum = titleMatch[2]; // e.g., "0" from "3.0", or undefined
      
      // Only keep sections that don't have a subsection number
      // Keep: "3", "3.", "3 TITLE", "3. TITLE"
      // Filter out: "3.0", "3.1", "3.1.1"
      const isSubsection = subNum !== undefined && subNum !== null;
      return !isSubsection;
    })
    // Deduplicate: keep only the first section for each main identifier
    .filter((section, index, self) => {
      const mainNum = (section.title.match(/^(\d+)/)||[])[1];
      if (!mainNum) return true;
      const firstIndex = self.findIndex(s => {
        const sMainNum = (s.title.match(/^(\d+)/)||[])[1];
        return sMainNum === mainNum;
      });
      const isDuplicate = index !== firstIndex;
      if (isDuplicate) {
      }
      return !isDuplicate;
    });
  

  // Build header
  const protocolId = get('sponsor_protocol_identifier', '');
  // Prefer the Title Page tab's resolved title (which already checks the USDM
  // catalogue ahead of Admin Info) over re-deriving from raw fields here -
  // this XML export used to compute its own title independently and could
  // fall back to trial_acronym, silently exporting an acronym (or a stale
  // placeholder like "Pending") as the protocol title. Never fall back to
  // trial_acronym for a title field - an acronym is not a title.
  const protocolTitle =
    titlePageConfig?.fullTitle?.text || intakeIdentificationXml?.official_title || get('official_title', '');
  const shortTitle =
    titlePageConfig?.shortTitle?.text || intakeIdentificationXml?.preferred_short_title || get('short_title', '');
  const protocolVersion = (get('version_number','') || '').toString();
  const protocolDate = get('version_date','') ? new Date(get('version_date')).toISOString().slice(0,10) : new Date().toISOString().slice(0,10);
  const sponsorName = get('sponsor_name','');
  const sponsorAddr = get('sponsor_legal_address','') || get('sponsor_address','');
  const phase = get('trial_phase','') || get('phase','');
  const therapeuticArea = get('therapeutic_area','');
  const indication = get('indication','');
  const interventionType = get('intervention_type','Drug');
  const studyType = get('study_type','Interventional');

  let xml = '';
  xml += '<?xml version="1.0" encoding="UTF-8"?>\n';
  xml += '<Protocol xmlns="urn:ich:m11:2024" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xsi:schemaLocation="urn:ich:m11:2024 M11_CeSP_v1_0.xsd">\n';
  xml += '  <Header>\n';
  if (protocolId) xml += `    <ProtocolIdentifier>${esc(protocolId)}</ProtocolIdentifier>\n`;
  if (protocolTitle) xml += `    <ProtocolTitle>${esc(protocolTitle)}</ProtocolTitle>\n`;
  if (shortTitle) xml += `    <ShortTitle>${esc(shortTitle)}</ShortTitle>\n`;
  if (protocolVersion) xml += `    <ProtocolVersion>${esc(protocolVersion)}</ProtocolVersion>\n`;
  if (protocolDate) xml += `    <ProtocolDate>${esc(protocolDate)}</ProtocolDate>\n`;
  if (sponsorName || sponsorAddr) {
    xml += '    <Sponsor>\n';
    if (sponsorName) xml += `      <OrganizationName>${esc(sponsorName)}</OrganizationName>\n`;
    if (sponsorAddr) xml += `      <Address>${esc(sponsorAddr)}</Address>\n`;
    xml += '    </Sponsor>\n';
  }
  if (phase) xml += `    <Phase>${esc(phase)}</Phase>\n`;
  if (studyType) xml += `    <StudyType>${esc(studyType)}</StudyType>\n`;
  if (therapeuticArea) xml += `    <TherapeuticArea>${esc(therapeuticArea)}</TherapeuticArea>\n`;
  if (indication) xml += `    <Indication>${esc(indication)}</Indication>\n`;
  if (interventionType) xml += `    <InterventionType>${esc(interventionType)}</InterventionType>\n`;
  xml += '  </Header>\n';

  /** SOA payload for ScheduleTable — same unwrap as DOCX/export uses. */
  const effectiveSoa = normalizeSoaExportPayload(generatedSoAData) ?? generatedSoAData;
  let scheduleTableEmitted = false;

  const footnotePlain = (fn) => {
    if (fn == null) return '';
    if (typeof fn === 'string') return fn;
    if (typeof fn === 'object' && typeof fn.content === 'string') return fn.content;
    return String(fn);
  };

  /** @param {{ nestInSection?: boolean }} [opts] nestInSection: true = inside `<Section>` (default), false = sibling under `<Protocol>` */
  const buildScheduleTableXml = (soa, opts = {}) => {
    const nest = opts.nestInSection !== false;
    const s0 = nest ? '    ' : '  ';
    const s1 = nest ? '      ' : '    ';
    const s2 = nest ? '        ' : '      ';
    if (
      !soa
      || typeof soa !== 'object'
      || !Array.isArray(soa.visits)
      || soa.visits.length === 0
      || !Array.isArray(soa.sections)
      || soa.sections.length === 0
    ) {
      return '';
    }
    const visits = soa.visits;
    const sectionsSoa = soa.sections;
        const footnotes = Array.isArray(soa.footnotes) ? soa.footnotes : [];
    let block = '';
    block += `${s0}<ScheduleTable>\n`;
    block += `${s1}<Visits>\n`;
    visits.forEach((v, vi) => {
      block += `${s2}<Visit index="${vi}" period="${esc(v.period || '')}" day="${esc(v.day || '')}"/>\n`;
    });
    block += `${s1}</Visits>\n`;
    if (footnotes.length > 0) {
      block += `${s1}<Footnotes>\n`;
      footnotes.forEach((fn, fi) => {
        block += `${s2}<Footnote index="${fi + 1}">${esc(footnotePlain(fn))}</Footnote>\n`;
      });
      block += `${s1}</Footnotes>\n`;
    }
    sectionsSoa.forEach((group) => {
      block += `${s1}<SectionGroup title="${esc(group.title || '')}">\n`;
      (group.activities || []).forEach((act) => {
        const vis = Array.isArray(act.visits) ? act.visits.join(',') : '';
        const fn =
          typeof act.footnoteIndex === 'number' && act.footnoteIndex > 0
            ? String(act.footnoteIndex)
            : typeof act.power === 'number' && act.power > 0
              ? String(act.power)
              : '';
        block += `${s2}<Activity name="${esc(act.name || '')}" visits="${esc(vis)}" footnoteIndex="${esc(fn)}"/>\n`;
      });
      block += `${s1}</SectionGroup>\n`;
    });
    block += `${s0}</ScheduleTable>\n`;
    return block;
  };

  /** Main section titles may omit “Schedule of…”; match SOA synonyms on full header line. */
  const sectionLooksLikeScheduleOfActivities = (fullTitle, nameAfterNumber) => {
    const hay = `${fullTitle || ''} ${nameAfterNumber || ''}`.toLowerCase();
    return /schedule\s+of\s+activities|\bsoa\b|draft\s*soa|\bstudy\s+calendar\b|visit\s+schedule/i.test(hay);
  };

  // Helper: Parse structured data from content (DesignType, NumberOfArms, etc.)
  const parseStructuredData = (text) => {
    const data = {};
    
    // Extract all fields
    const fields = [
      { key: 'Intervention', pattern: /Intervention:\s*([^\n]+(?:\n(?!\s*[A-Z][^:]*:)[^\n]+)*)/i },
      { key: 'PopulationType', pattern: /Population Type:\s*([^\n]+(?:\n(?!\s*[A-Z][^:]*:)[^\n]+)*)/i },
      { key: 'PopulationDiagnosis', pattern: /Population Diagnosis or Condition:\s*([^\n]+(?:\n(?!\s*[A-Z][^:]*:)[^\n]+)*)/i },
      { key: 'ControlType', pattern: /Control Type:\s*([^\n]+(?:\n(?!\s*[A-Z][^:]*:)[^\n]+)*)/i },
      { key: 'ControlDescription', pattern: /Control Description:\s*([^\n]+(?:\n(?!\s*[A-Z][^:]*:)[^\n]+)*)/i },
      { key: 'PopulationAgeMinimum', pattern: /Population Age:\s*\n\s*Minimum:\s*([^\n]+)/i },
      { key: 'PopulationAgeMaximum', pattern: /Maximum:\s*([^\n]+)/i },
      { key: 'GeographicScope', pattern: /Site Distribution and Geographic Scope:\s*([^\n]+(?:\n(?!\s*[A-Z][^:]*:)[^\n]+)*)/i },
      { key: 'InterventionModel', pattern: /Intervention Model:\s*([^\n]+(?:\n(?!\s*[A-Z][^:]*:)[^\n]+)*)/i },
      { key: 'InterventionAssignmentMethod', pattern: /Intervention Assignment Method:\s*([^\n]+(?:\n(?!\s*[A-Z][^:]*:)[^\n]+)*)/i },
      { key: 'MasterProtocolIndicator', pattern: /Master Protocol Indicator:\s*([^\n]+)/i },
      { key: 'DrugDeviceCombinationProductIndicator', pattern: /Drug\/Device Combination Product Indicator:\s*([^\n]+)/i },
      { key: 'AdaptiveTrialDesignIndicator', pattern: /Adaptive Trial Design Indicator:\s*([^\n]+)/i },
      { key: 'NumberOfArms', pattern: /Number of Arms:\s*(\d+)/i },
      { key: 'Blinding', pattern: /Trial Blind Schema:\s*([^\n]+)/i },
      { key: 'BlindedRoles', pattern: /Blinded Roles:\s*([^\n]+(?:\n(?!\s*[A-Z][^:]*:)[^\n]+)*)/i },
      { key: 'PlannedSubjects', pattern: /Number of Participants:\s*([^\n]+(?:\n(?!\s*[A-Z][^:]*:)[^\n]+)*)/i },
      { key: 'DurationIntervention', pattern: /Total planned duration of trial intervention for each participant:\s*([^\n]+(?:\n(?!\s*Total planned duration|Committees)[^\n]+)*)/i },
      { key: 'DurationParticipation', pattern: /Total planned duration of trial participation for each participant:\s*([^\n]+(?:\n(?!\s*Committees)[^\n]+)*)/i },
      { key: 'IndependentCommittees', pattern: /Independent Committees:\s*([^\n]+(?:\n(?!\s*[A-Z][^:]*:)[^\n]+)*)/i },
    ];
    
    fields.forEach(field => {
      const match = text.match(field.pattern);
      if (match && match[1]) {
        const value = match[1].trim().replace(/\n{2,}/g, ' ').replace(/\s+/g, ' ').trim();
        if (value && value.length > 0) {
          data[field.key] = value;
        }
      }
    });
    
    return Object.keys(data).length > 0 ? data : null;
  };

  // Helper: Parse duration info
  const parseDuration = (text) => {
    const duration = {};
    const screening = text.match(/(?:Screening|Screening Period):\s*(.+?)(?:\n|$)/i);
    const treatment = text.match(/(?:Treatment|Treatment Period|Cycles):\s*(.+?)(?:\n|$)/i);
    const followUp = text.match(/(?:Follow.?up|Follow-up Period):\s*(.+?)(?:\n|$)/i);
    if (screening && screening[1]) duration.Screening = screening[1].trim();
    if (treatment && treatment[1]) duration.Treatment = treatment[1].trim();
    if (followUp && followUp[1]) duration.FollowUp = followUp[1].trim();
    return Object.keys(duration).length > 0 ? duration : null;
  };

  // Helper: Extract HTML tables to XML <Table><Row><Cell>...</Cell></Row></Table>
  const tablesHtmlToXml = (html) => {
    if (!html || !html.includes('<table')) return '';
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    const tables = tmp.querySelectorAll('table');
    if (!tables || tables.length === 0) return '';
    let out = '';
    tables.forEach((table) => {
      out += '      <Table>\n';
      const rows = table.querySelectorAll('tr');
      rows.forEach((tr) => {
        out += '        <Row>\n';
        const cells = tr.querySelectorAll('th,td');
        cells.forEach((cell) => {
          const cellText = (cell.textContent || '').trim();
          out += `          <Cell>${esc(cellText)}</Cell>\n`;
        });
        out += '        </Row>\n';
      });
      out += '      </Table>\n';
    });
    return out;
  };

  // Helper: Extract Markdown tables (| col |) to XML <Table>
  const tablesMarkdownToXml = (text) => {
    if (!text || !text.includes('|')) return '';
    const lines = text.split(/\r?\n/);
    let out = '';
    let inTable = false;
    let buffer = [];
    const flush = () => {
      if (buffer.length === 0) return;
      out += '      <Table>\n';
      buffer.forEach((line) => {
        const cols = line.split('|').map(c => c.trim()).filter(c => c.length > 0);
        if (cols.length === 0) return;
        out += '        <Row>\n';
        cols.forEach((c) => { out += `          <Cell>${esc(c)}</Cell>\n`; });
        out += '        </Row>\n';
      });
      out += '      </Table>\n';
      buffer = [];
    };
    for (const line of lines) {
      const trimmed = line.trim();
      if (/^\|.*\|$/.test(trimmed)) {
        inTable = true;
        // skip separator rows like |---|
        if (!/^\|\s*:?[- ]+:?\s*(\|\s*:?[- ]+:?\s*)*\|$/.test(trimmed)) {
          buffer.push(trimmed);
        }
      } else {
        if (inTable) { flush(); inTable = false; }
      }
    }
    flush();
    return out;
  };

  // Helper: Parse subsections directly from raw HTML
  const parseSubsectionsFromHtml = (rawHtml, mainSectionNum) => {
    if (!rawHtml) return [];
    
    const subsections = [];
    // Match heading tags that contain subsection numbers (like 3.0, 3.1, 3.1.1, etc.)
    // Pattern: <h1-6>...3.0 TITLE...</h1-6> or <h1-6><strong>3.0 TITLE</strong></h1-6>
    const mainEsc = mainSectionNum.toString().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const headingRegex = new RegExp(
      `<h([1-6])[^>]*>(?:<[^>]+>)*\\s*(${mainEsc}\\.\\d+(?:\\.\\d+(?:\\.\\d+)?)?)\\s+([^<]+?)(?:<[^>]+>)*</h\\1>`,
      'gi'
    );
    
    
    const matches = [];
    let match;
    while ((match = headingRegex.exec(rawHtml)) !== null) {
      matches.push({
        fullMatch: match[0],
        headingLevel: parseInt(match[1]),
        identifier: match[2],
        name: match[3].trim(),
        startIndex: match.index,
        endIndex: match.index + match[0].length
      });
    }
    
    
    // For each match, extract the content until the next heading of same or higher level
    for (let i = 0; i < matches.length; i++) {
      const current = matches[i];
      const next = matches[i + 1];
      
      // Extract content from end of current heading to start of next heading (or end of HTML)
      const contentStart = current.endIndex;
      const contentEnd = next ? next.startIndex : rawHtml.length;
      const rawContent = rawHtml.slice(contentStart, contentEnd);
      
      subsections.push({
        identifier: current.identifier,
        name: current.name,
        rawHtml: rawContent,
        plainText: stripHtmlTags(rawContent).trim()
      });
    }
    
    return subsections;
  };

  // Helper: Parse estimands
  const parseEstimands = (text) => {
    const estimands = [];
    // Look for objective patterns
    const objMatches = text.matchAll(/(?:Primary|Secondary|Exploratory)\s+Objective[^:]*:\s*(.+?)(?=\n\n|\n(?:Primary|Secondary|Exploratory)|$)/gis);
    for (const match of objMatches) {
      const objText = match[1];
      const estimand = {
        Objective: objText.split('\n')[0].trim(),
        Population: objText.match(/(?:Population|Subjects):\s*(.+?)(?:\n|$)/i)?.[1]?.trim() || '',
        Treatment: objText.match(/(?:Treatment|Intervention):\s*(.+?)(?:\n|$)/i)?.[1]?.trim() || '',
        Variable: objText.match(/(?:Endpoint|Variable):\s*(.+?)(?:\n|$)/i)?.[1]?.trim() || '',
      };
      if (estimand.Objective) estimands.push(estimand);
    }
    return estimands.length > 0 ? estimands : null;
  };

  // Sections - process ALL sections, even if pattern doesn't match perfectly
  for (const s of sorted) {
    // More flexible pattern: number at start, then any separator, then title
    const m = s.title.match(/^(\d+(?:\.\d+)*)[\s\.\-]+(.*)$/) || s.title.match(/^(\d+)/);
    if (!m) {
      // If no match, try to extract just the number
      const numMatch = s.title.match(/^(\d+)/);
      if (numMatch) {
        const main = numMatch[1];
        const name = s.title.replace(/^\d+[\s\.\-]+/, '').trim() || s.title;
        const sectionName = esc(name.replace(/\.$/, '').replace(/\s+/g, ' '));
        xml += `  <Section identifier="${esc(main)}" name="${sectionName}">\n`;
        const rawContent = String(s.output || '');
        const cleanContent = stripHtmlTags(rawContent).trim();
        if (cleanContent) {
          xml += `    <Text>\n      ${esc(cleanContent)}\n    </Text>\n`;
        } else {
          xml += `    <Text></Text>\n`;
        }
        xml += '  </Section>\n';
      }
      continue;
    }
    const num = m[1];
    const main = (num.match(/^(\d+)/)||[])[1];
    const name = m[2] ? m[2].trim() : s.title.replace(/^\d+[\s\.\-]+/, '').trim() || s.title;
    const sectionName = esc(name.replace(/\.$/,'').replace(/\s+/g,' '));
    xml += `  <Section identifier="${esc(main)}" name="${sectionName}">\n`;

    // Special case: Schedule of Activities (SOA) — structured table for downstream USDM / M11 parsers
    if (effectiveSoa && sectionLooksLikeScheduleOfActivities(s.title, name)) {
      const scheduleBlock = buildScheduleTableXml(effectiveSoa, { nestInSection: true });
      if (scheduleBlock) {
        xml += scheduleBlock;
        scheduleTableEmitted = true;
      }
      xml += '  </Section>\n';
      continue;
    }
    // Special case: Trial Schema image → Figure/Graphic
    if (/trial\s*schema|schema/i.test(name) && trialSchemaImageUrl) {
      const getMediaType = (url) => {
        try {
          const lower = url.split('?')[0].toLowerCase();
          if (lower.endsWith('.png')) return 'image/png';
          if (lower.endsWith('.jpg') || lower.endsWith('.jpeg')) return 'image/jpeg';
          if (lower.endsWith('.svg')) return 'image/svg+xml';
          if (lower.startsWith('data:image/')) return lower.slice(5, lower.indexOf(';'));
        } catch (_) {}
        return 'image/png';
      };
      const mediaType = getMediaType(trialSchemaImageUrl);
      xml += '    <Figure>\n';
      xml += `      <Title>${esc('Trial Schema')}</Title>\n`;
      // Prefer relative file name if it looks like a local path; otherwise write the URL in href
      const fileName = trialSchemaImageUrl.split('/').pop() || 'trial-schema.png';
      xml += `      <Graphic mediaType="${esc(mediaType)}" fileName="${esc(fileName)}" href="${esc(trialSchemaImageUrl)}"/>\n`;
      xml += '      <Caption>Schematic of study design.</Caption>\n';
      xml += '    </Figure>\n';
      // Do not continue; include figure and still parse the rest as text/subsections
    }

    // Strip HTML tags first (same as DOCX/PDF native export)
    let rawContent = String(s.output || '');
    const content = stripHtmlTags(rawContent).trim();

    // Special case: Schedule of Activities (SOA) - if no SOA data, fall through to generic
    let handled = false;

    // Special case: Trial Objectives and Associated Estimands
    if (/objectives.*estimands|estimands/i.test(name)) {
      const estimands = parseEstimands(content);
      if (estimands && estimands.length > 0) {
        xml += '    <EstimandSet>\n';
        estimands.forEach((est, idx) => {
          xml += `      <Estimand identifier="E${idx + 1}">\n`;
          if (est.Objective) xml += `        <Objective>${esc(est.Objective)}</Objective>\n`;
          if (est.Population) xml += `        <Population>${esc(est.Population)}</Population>\n`;
          if (est.Treatment) xml += `        <Treatment>${esc(est.Treatment)}</Treatment>\n`;
          if (est.Variable) xml += `        <Variable>${esc(est.Variable)}</Variable>\n`;
          xml += `        <IntercurrentEventStrategy type="treatmentPolicy"/>\n`;
          xml += `        <SummaryMeasure>Mean change from baseline with 90% CI</SummaryMeasure>\n`;
          xml += '      </Estimand>\n';
        });
        xml += '    </EstimandSet>\n';
        // Do not mark handled; continue to also include remaining content/subsections
      }
    }

    // Special case: Trial Design
    if (/trial design/i.test(name)) {
      const structData = parseStructuredData(content);
      const duration = parseDuration(content);
      if (structData || duration) {
        xml += '    <DesignOverview>\n';
        if (structData) {
          if (structData.DesignType) xml += `      <DesignType>${esc(structData.DesignType)}</DesignType>\n`;
          if (structData.Randomization) xml += `      <Randomization>${esc(structData.Randomization)}</Randomization>\n`;
          if (structData.ControlType) xml += `      <ControlType>${esc(structData.ControlType)}</ControlType>\n`;
          if (structData.NumberOfArms) xml += `      <NumberOfArms>${esc(structData.NumberOfArms)}</NumberOfArms>\n`;
        }
        if (duration) {
          xml += '      <Duration>\n';
          if (duration.Screening) xml += `        <Screening>${esc(duration.Screening)}</Screening>\n`;
          if (duration.Treatment) xml += `        <Treatment>${esc(duration.Treatment)}</Treatment>\n`;
          if (duration.FollowUp) xml += `        <FollowUp>${esc(duration.FollowUp)}</FollowUp>\n`;
          xml += '      </Duration>\n';
        }
        xml += '    </DesignOverview>\n';
        // Do not mark handled; continue to also include remaining content/subsections
      }
    }

    // Generic subsections parsing - ALWAYS executed to ensure all sections are included
    if (!handled) {
      // Parse subsections directly from raw HTML
      const subsections = parseSubsectionsFromHtml(rawContent, main);
      
      if (subsections.length > 0) {
      }

      // If we found subsections, output them
      if (subsections.length > 0) {
        // Process each subsection - tables will be extracted per subsection
        subsections.forEach(sub => {
          xml += `    <Subsection identifier="${esc(sub.identifier)}" name="${esc(sub.name)}">\n`;
          
          // Extract tables from the subsection's raw HTML
          let tablesXml = '';
          try {
            tablesXml = tablesHtmlToXml(sub.rawHtml);
            // Fallback to markdown if no HTML tables found
            if (!tablesXml) {
              tablesXml = tablesMarkdownToXml(sub.plainText);
            }
          } catch (e) {
            // ignore table extraction errors
          }

          if (tablesXml) {
            xml += tablesXml;
          }

          // Remove table representations (lines with multiple | separators) from plain text
          const textWithoutTables = sub.plainText.split('\n')
            .filter(line => {
              // Skip lines that look like table rows (multiple | separators)
              const pipeCount = (line.match(/\|/g) || []).length;
              // If line has 2+ pipes and looks like a table row, skip it
              if (pipeCount >= 2 && /^\s*\|.*\|\s*$/.test(line.trim())) {
                return false;
              }
              // Skip separator rows like |---|---|
              if (/^\s*\|[\s\-:]+\|\s*$/.test(line.trim())) {
                return false;
              }
              return true;
            })
            .join('\n')
            .trim();

          if (textWithoutTables) {
            const structData = parseStructuredData(textWithoutTables);
            if (structData) {
              xml += '      <StructuredData>\n';
              Object.entries(structData).forEach(([key, val]) => {
                xml += `        <${key}>${esc(val)}</${key}>\n`;
              });
              xml += '      </StructuredData>\n';
            } else {
              // Clean and format text content
              const cleanText = textWithoutTables.replace(/\n{3,}/g, '\n\n').trim();
              xml += `      <Text>\n        ${esc(cleanText)}\n      </Text>\n`;
            }
          } else if (!tablesXml) {
            // Only add empty Text if there are no tables
            xml += `      <Text></Text>\n`;
          }
          xml += '    </Subsection>\n';
        });
      } else {
        // No subsections found - output main content
        // First, extract tables from raw HTML (before stripping HTML tags)
        let mainTablesXml = '';
        try {
          // Extract tables from the entire raw content for main sections
          mainTablesXml += tablesHtmlToXml(rawContent);
          if (!mainTablesXml) {
            // Fallback to markdown table detection
            mainTablesXml += tablesMarkdownToXml(content);
          }
        } catch (e) {
          // ignore table extraction errors
        }

        // Output tables if found
        if (mainTablesXml) {
          xml += mainTablesXml;
        }

        // Then output the text content (with tables removed from text representation)
        const mainText = content;
        if (mainText) {
          // Remove table representations (lines with multiple | separators) from text
          const textWithoutTables = mainText.split('\n')
            .filter(line => {
              // Skip lines that look like table rows (multiple | separators)
              const pipeCount = (line.match(/\|/g) || []).length;
              // If line has 2+ pipes and looks like a table row, skip it
              if (pipeCount >= 2 && /^\s*\|.*\|\s*$/.test(line.trim())) {
                return false;
              }
              // Skip separator rows like |---|---|
              if (/^\s*\|[\s\-:]+\|\s*$/.test(line.trim())) {
                return false;
              }
              return true;
            })
            .join('\n')
            .trim();

          if (textWithoutTables) {
            const structData = parseStructuredData(textWithoutTables);
            if (structData) {
              xml += '    <StructuredData>\n';
              Object.entries(structData).forEach(([key, val]) => {
                xml += `      <${key}>${esc(val)}</${key}>\n`;
              });
              xml += '    </StructuredData>\n';
            } else {
              const cleanText = textWithoutTables.replace(/\n{3,}/g, '\n\n').trim();
              xml += `    <Text>\n      ${esc(cleanText)}\n    </Text>\n`;
            }
          } else if (!mainTablesXml) {
            // Only add empty Text if there are no tables
            xml += `    <Text></Text>\n`;
          }
        } else {
          // If no content at all, add empty Text to ensure section is not empty
          if (!mainTablesXml) {
            xml += `    <Text></Text>\n`;
          }
        }
      }
    }

    xml += '  </Section>\n';
  }

  /** If SOA data exists but no section matched the title heuristic, attach ScheduleTable at protocol root so USDM parser still finds it. */
  if (effectiveSoa && !scheduleTableEmitted) {
    const orphanSoa = buildScheduleTableXml(effectiveSoa, { nestInSection: false });
    if (orphanSoa) {
      xml += orphanSoa;
      scheduleTableEmitted = true;
    }
  }

  xml += '</Protocol>';

  // Persist protocol XML to backend if a projectId is available
  if (projectId) {
    try {
      await saveProtocolXml(projectId, xml);
    } catch (error) {
      console.error('Error saving protocol XML to backend:', error);
    }
  }

  return xml;
};

// "Export to XML" downloads a local copy; "Publish to External SDR" (below) only
// needs the backend's copy freshly saved, not a browser download — both share
// this builder so the External SDR publish never runs against a stale/never-
// generated project.protocolXml.
const exportToXML = async (sections, generatedSoAData = null, trialSchemaImageUrl = null, projectId = null, titlePageConfig = null) => {
  const xml = await buildAndSaveProtocolXml(sections, generatedSoAData, trialSchemaImageUrl, projectId, titlePageConfig);

  const blob = new Blob([xml], { type: 'application/xml' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'clinical-trial-protocol.xml';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const exportSectionToXML = (section) => {
  // Extract section number and title
  const match = section.title.match(/^(\d+)(?:\.(\d+))?/);
  if (!match) return;
  const mainNum = match[1];
  const sectionTitle = section.title.replace(/^(\d+(\.\d+)*\.?\s*)/, '').trim();
  const tagName = toCamelCase(sectionTitle) || 'section';
  // Parse content for subsections
  const parsed = parseSectionContent(section.output, mainNum);
  const sectionObj = {
    number: mainNum,
    title: sectionTitle,
    main: parsed.main,
    subsections: parsed.subsections
  };
  // Build XML
  let xmlContent = `<?xml version="1.0" encoding="UTF-8"?>\n` + jsonToXml(sectionObj, tagName, '');
  // Download
  const blob = new Blob([xmlContent], { type: 'application/xml' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `section-${mainNum}-${tagName}.xml`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

// Helper: Detect and render table from plain text
function renderTableFromText(text) {
  if (!text) return null;
  // Split into lines and filter out empty lines
  const lines = text.split(/\r?\n/).filter(line => line.trim() !== '');
  if (lines.length < 2) return null; // Need at least header + one row

  // Try splitting by tab or 2+ spaces
  const delimiter = lines[0].includes('\t') ? '\t' : '  ';
  const rows = lines.map(line => line.split(new RegExp(delimiter + '+')));
  const colCount = rows[0].length;
  // Check if all rows have the same number of columns
  if (!rows.every(row => row.length === colCount)) return null;

  return (
    <table style={{ borderCollapse: 'collapse', width: '100%', margin: '8px 0' }}>
      <thead>
        <tr>
          {rows[0].map((cell, idx) => (
            <th key={idx} style={{ border: '1px solid #bbb', padding: '6px', background: '#f5f5f5', fontWeight: 600 }}>{cell}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.slice(1).map((row, rIdx) => (
          <tr key={rIdx}>
            {row.map((cell, cIdx) => (
              <td key={cIdx} style={{ border: '1px solid #ddd', padding: '6px' }}>{cell}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// Helper: Transform SOA data ({visits, sections}) to flat table for DOCX export — see soaTableExportModel.js

const DocumentEditor = forwardRef((props, ref) => {
  const navigate = useNavigate();
  const { id: documentId } = useParams(); // Using documentId from URL params for specific document fetches
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const projectIdFromUrl = queryParams.get('projectId');

  const { currentProject, loading: projectContextLoading, error: projectContextError, loadProject } = useProject();

  // Determine the actual projectId to use
  const actualProjectId = currentProject?._id || projectIdFromUrl;
  /** When true (e.g. document workflow is approved), keep canvas read-only and block toolbar mutations */
  const protocolEditingDisabled = Boolean(props.protocolEditingDisabled);
  /** Document review workflow: show only Edit/Canvas mode toggle in the toolbar */
  const workflowReviewToolbar = Boolean(props.workflowReviewToolbar);
  /** Set by the USDM Template tab's "Generate Protocol" button (via StudyFormTabs.jsx) to
   *  auto-run the same "Generate from USDM" flow on mount, then call onAutoGenerateFromUsdmConsumed
   *  so it doesn't re-fire on every subsequent visit to this tab. */
  const autoGenerateFromUsdm = Boolean(props.autoGenerateFromUsdm);
  const onAutoGenerateFromUsdmConsumed = props.onAutoGenerateFromUsdmConsumed;
  const [sections, setSections] = useState([]);
  // Table/Figure numbering offsets per section id, in document order, so numbering
  // continues across sections sharing a main number (see computeMediaNumberOffsets).
  const mediaNumberOffsetsById = useMemo(() => {
    const ordered = normalizeProtocolSectionsList(sections);
    const offsets = computeMediaNumberOffsets(ordered);
    return new Map(ordered.map((s, i) => [s._id, offsets[i]]));
  }, [sections]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const [factsAnchorEl, setFactsAnchorEl] = useState(null);
  const [syncValidateAnchorEl, setSyncValidateAnchorEl] = useState(null);
  const [selectedSectionId, setSelectedSectionId] = useState(null);
  
  // Validation state
  const [isValidationDialogOpen, setIsValidationDialogOpen] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [validationResults, setValidationResults] = useState(null);
  // Export validation state
  const [isExportValidationDialogOpen, setIsExportValidationDialogOpen] = useState(false);
  const [isValidatingExport, setIsValidatingExport] = useState(false);
  const [exportValidationResults, setExportValidationResults] = useState([]);
  const [isAiValidationDialogOpen, setIsAiValidationDialogOpen] = useState(false);
  const [isValidatingAi, setIsValidatingAi] = useState(false);
  const [aiValidationResults, setAiValidationResults] = useState([]);
  const [styleCheckDialogOpen, setStyleCheckDialogOpen] = useState(false);
  const [styleCheckPanelCollapsed, setStyleCheckPanelCollapsed] = useState(false);
  const [styleCheckLoading, setStyleCheckLoading] = useState(false);
  const [styleCheckError, setStyleCheckError] = useState(null);
  const [styleCheckResult, setStyleCheckResult] = useState(null);
  const [styleFindingsExpandedSection, setStyleFindingsExpandedSection] = useState(null);
  /** Post-export .docx OOXML font report (shown before download). */
  const [postExportFontDialogOpen, setPostExportFontDialogOpen] = useState(false);
  const [postExportFontReport, setPostExportFontReport] = useState(null);
  const postExportFontAckRef = useRef(null);
  const fetchSectionsInFlightRef = useRef(null);
  const usdmCatalogueFileInputRef = useRef(null);
  // The "Generate from USDM" button itself now lives in the USDM Template tab
  // (as "Generate Protocol") — this button stays mounted but hidden here so
  // its exact, unmodified onClick logic can still be triggered via a
  // simulated click from the autoGenerateFromUsdm effect below.
  const generateFromUsdmButtonRef = useRef(null);
  const [usdmUploadSnackbar, setUsdmUploadSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [styleAutofixLoading, setStyleAutofixLoading] = useState(false);
  const [styleAutofixDialogError, setStyleAutofixDialogError] = useState(null);
  const [styleAutofixSnackbar, setStyleAutofixSnackbar] = useState({ open: false, message: '' });
  const [regenerateSummarySnackbar, setRegenerateSummarySnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [externalSdrPublishing, setExternalSdrPublishing] = useState(false);
  const [externalSdrSnackbar, setExternalSdrSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [isSyncingToKG, setIsSyncingToKG] = useState(false);
  const [syncType, setSyncType] = useState('standard'); // 'standard' or 'embeddings'
  const [isExtractingEntities, setIsExtractingEntities] = useState(false);
  const [isExtractingStandardEntities, setIsExtractingStandardEntities] = useState(false);
  const [isGenerating, setIsGenerating] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const [error, setError] = useState(null);
  const [showValidationIndicator, setShowValidationIndicator] = useState(false);
  const [validationReport, setValidationReport] = useState([]);
  const [availableSectionTitles, setAvailableSectionTitles] = useState([]);
  const [mode, setMode] = useState('preview'); // 'edit' | 'preview' | 'titlepage'
  const [lastSectionsMode, setLastSectionsMode] = useState('preview'); // last edit|preview before title page
  const [titlePageConfig, setTitlePageConfig] = useState(null);
  const [isGeneratingTitlePage, setIsGeneratingTitlePage] = useState(false);

  // State for Add Section dialog
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [selectedSectionTitle, setSelectedSectionTitle] = useState('');
  const [customSectionTitle, setCustomSectionTitle] = useState('');
  const [tabValue, setTabValue] = useState(0); // 0 for Template, 1 for Custom
  const [newSectionTitle, setNewSectionTitle] = useState(''); // Correctly declare setNewSectionTitle
  const [showTrialSchemaPreview, setShowTrialSchemaPreview] = useState(false);
  const [trialSchemaImageUrl, setTrialSchemaImageUrl] = useState(null);
  const [generatingSoA, setGeneratingSoA] = useState(false);
  const [generateSoAError, setGenerateSoAError] = useState(null);
  const [generatedSoAData, setGeneratedSoAData] = useState(null);
  const [s9SoaTables, setS9SoaTables] = useState([]);
  const [s9PublishedVersion, setS9PublishedVersion] = useState(null);
  const [soaProtocolId, setSoaProtocolId] = useState(null); // Store the actual protocolId for SOA
  const [showEditSoa, setShowEditSoa] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [microsoftWordDialogOpen, setMicrosoftWordDialogOpen] = useState(false);
  const [soaPromptDialogOpen, setSoaPromptDialogOpen] = useState(false);
  const [soaPrompt, setSoaPrompt] = useState('');
  
  // Image editing state
  const [showImageEditDialog, setShowImageEditDialog] = useState(false);
  const [imageEditMode, setImageEditMode] = useState(null); // 'crop' or 'delete'
  const [croppedImageUrl, setCroppedImageUrl] = useState(null);
  const [cropData, setCropData] = useState(null);
  
  // ADK Workflow state
  const [changeRequestDialogOpen, setChangeRequestDialogOpen] = useState(false);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [selectedSectionForModification, setSelectedSectionForModification] = useState(null);
  const [currentApprovalRequest, setCurrentApprovalRequest] = useState(null);
  const [workflowPlan, setWorkflowPlan] = useState(null);
  
  // Move these useState/useRef hooks to the top
  const [showDrawio, setShowDrawio] = useState(false);
  const drawioIframeRef = useRef(null);
  const [trialSchemaDiagramXml, setTrialSchemaDiagramXml] = useState(null);
  const [trialSchemaDiagramSvg, setTrialSchemaDiagramSvg] = useState(null);
  const [pendingLoadXml, setPendingLoadXml] = useState(false);
  const [diagramSaveSuccess, setDiagramSaveSuccess] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [bookmarkedSections, setBookmarkedSections] = useState(new Set());
  const [generationProgress, setGenerationProgress] = useState({ current: 0, total: 0, isGenerating: false, currentSection: '' });
  const [isGeneratingSections, setIsGeneratingSections] = useState(false);

  // KPI: user-perceived editor load (route open → sections ready)
  useEffect(() => {
    startSpan('ui_protocol_editor_load', { page: 'protocol_editor', meta: { projectId: actualProjectId || projectIdFromUrl || null } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isLoading && !projectContextLoading && !isGeneratingSections && actualProjectId) {
      endSpan('ui_protocol_editor_load', { status: error ? 'error' : 'ok' });
    }
  }, [isLoading, projectContextLoading, isGeneratingSections, actualProjectId, error]);

  // State to track edited text content for each section
  const [editedTextContent, setEditedTextContent] = useState({});
  const activeErrorMessage = projectContextError || error;

  // ============================================================================
  // EDIT MODE — save handler for the contentEditable section editor
  // ============================================================================

  // Every save is an audited update, so an edit is saved once it is finished — after
  // SECTION_AUTOSAVE_IDLE_MS without typing, or as soon as the section loses focus —
  // not on every pause. (A 500ms debounce turned one typed word into several audit
  // entries.) Saves for a section also run one at a time, sending only the latest
  // text: overlapping requests all read the same "before" state and could land out of
  // order, letting older text overwrite newer.
  const SECTION_AUTOSAVE_IDLE_MS = 3000;
  const sectionSaveTimers = useRef({});
  // Per section: { pending: html not yet sent, lastSaved: html last stored, inFlight }
  const sectionSaveState = useRef({});
  const sectionsRef = useRef(sections);
  sectionsRef.current = sections;

  const getSectionSaveState = (_id) => {
    if (!sectionSaveState.current[_id]) {
      sectionSaveState.current[_id] = { pending: null, lastSaved: null, inFlight: null };
    }
    return sectionSaveState.current[_id];
  };

  const flushSectionSave = (_id) => {
    if (sectionSaveTimers.current[_id]) {
      clearTimeout(sectionSaveTimers.current[_id]);
      delete sectionSaveTimers.current[_id];
    }
    const state = getSectionSaveState(_id);
    // A save is already running: it sends whatever is pending when it finishes.
    if (state.inFlight) return state.inFlight;
    const html = state.pending;
    if (html == null || html === state.lastSaved) {
      state.pending = null;
      return Promise.resolve();
    }
    state.pending = null;
    const currentSection = sectionsRef.current.find((s) => s._id === _id);
    state.inFlight = documentEditorService
      .updateSection(_id, {
        context: currentSection?.context,
        prompt: currentSection?.prompt,
        output: html,
        documentType: 'PROTOCOL'
      }, actualProjectId)
      .then(() => {
        state.lastSaved = html;
      })
      .catch((err) => {
        // Not retried automatically (a rejected save, e.g. a locked document, would
        // loop); the next edit or blur sends the latest text again.
        console.error('Failed to update section text content:', err);
      })
      .finally(() => {
        state.inFlight = null;
        if (state.pending != null && state.pending !== state.lastSaved) flushSectionSave(_id);
      });
    return state.inFlight;
  };

  const hasUnsavedSectionEdits = () =>
    Object.values(sectionSaveState.current).some(
      (state) => state.inFlight || (state.pending != null && state.pending !== state.lastSaved)
    );

  // Unsaved typing (inside the idle window, or a save still running) would be lost on
  // reload/close — ask the browser to confirm, and save everything on unmount.
  useEffect(() => {
    const warnIfUnsaved = (e) => {
      if (!hasUnsavedSectionEdits()) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warnIfUnsaved);
    return () => {
      window.removeEventListener('beforeunload', warnIfUnsaved);
      Object.keys(sectionSaveState.current).forEach((id) => flushSectionSave(id));
    };
  }, []);

  // Persist a section's edited HTML as-is (already sanitized by EditableSectionContent) —
  // no flatten/splice round-trip, so tag structure the user didn't touch stays untouched.
  const handleTextContentChange = (_id, newHtml) => {
    setEditedTextContent(prev => ({
      ...prev,
      [_id]: newHtml
    }));

    setSections(prevSections =>
      prevSections.map(section =>
        section._id === _id ? { ...section, output: newHtml } : section
      )
    );

    getSectionSaveState(_id).pending = newHtml;
    if (sectionSaveTimers.current[_id]) {
      clearTimeout(sectionSaveTimers.current[_id]);
    }
    sectionSaveTimers.current[_id] = setTimeout(() => flushSectionSave(_id), SECTION_AUTOSAVE_IDLE_MS);
  };

  const renderValidationReport = () => {
    if (!validationReport || validationReport.length === 0) {
      return null;
    }

    return (
      <Paper
        sx={{ mb: 2, p: 2, borderRadius: 2, border: '1px solid rgba(0, 0, 0, 0.12)' }}
        elevation={0}
      >
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          Validation Report
        </Typography>
        {validationReport.map((item) => {
          const isPassed = item?.passed;
          const messages = Array.isArray(item?.errors) ? item.errors : [];

          return (
            <Box
              key={item?.id || item?.label}
              sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5, gap: 1 }}
            >
              {isPassed ? (
                <CheckCircleIcon fontSize="small" sx={{ color: '#2e7d32', mt: '2px' }} />
              ) : (
                <ErrorOutlineIcon fontSize="small" sx={{ color: '#d32f2f', mt: '2px' }} />
              )}
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {item?.label || 'Validation'}
                </Typography>
                {isPassed ? (
                  <Typography variant="caption" sx={{ color: '#2e7d32' }}>
                    Passed
                  </Typography>
                ) : (
                  messages.map((msg, idx) => (
                    <Typography key={idx} variant="caption" sx={{ color: '#d32f2f', display: 'block' }}>
                      {formatValidationErrorMessage(msg)}
                    </Typography>
                  ))
                )}
              </Box>
            </Box>
          );
        })}
      </Paper>
    );
  };

  useEffect(() => {
    if (projectIdFromUrl && (!currentProject || currentProject._id !== projectIdFromUrl)) {
      loadProject(projectIdFromUrl);
    }
  }, [projectIdFromUrl, currentProject, loadProject]);

  useEffect(() => {
    if (protocolEditingDisabled && mode === 'edit') setMode('preview');
  }, [protocolEditingDisabled, mode]);

  // Restore authored title page for this project (if any)
  useEffect(() => {
    if (!actualProjectId) {
      setTitlePageConfig(null);
      return;
    }
    try {
      const raw = localStorage.getItem(`protocolTitlePageConfig:${actualProjectId}`);
      if (!raw) {
        setTitlePageConfig(null);
        return;
      }
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') {
        setTitlePageConfig({ ...DEFAULT_TITLE_CONFIG, ...parsed, enabled: true });
      }
    } catch (_e) {
      setTitlePageConfig(null);
    }
  }, [actualProjectId]);

  const persistTitlePageConfig = (config) => {
    setTitlePageConfig(config);
    if (!actualProjectId || !config) return;
    try {
      localStorage.setItem(`protocolTitlePageConfig:${actualProjectId}`, JSON.stringify(config));
    } catch (_e) {
      /* ignore quota */
    }
  };

  const resolveTitlePageConfigForExport = async () => {
    if (titlePageConfig?.enabled) return titlePageConfig;
    return getTitlePageConfigWithData(actualProjectId, currentProject);
  };

  const handleGenerateTitlePage = async () => {
    if (!actualProjectId || isGeneratingTitlePage) return;
    setIsGeneratingTitlePage(true);
    setError(null);
    try {
      const config = await getTitlePageConfigWithData(actualProjectId, currentProject);
      const next = { ...config, enabled: true };
      persistTitlePageConfig(next);
      setMode('titlepage');
    } catch (err) {
      console.error('Failed to generate title page:', err);
      setError(err?.message || 'Failed to generate title page');
    } finally {
      setIsGeneratingTitlePage(false);
    }
  };

  const handleTitlePageFieldChange = (fieldKey, text) => {
    if (!titlePageConfig || protocolEditingDisabled) return;
    const next = {
      ...titlePageConfig,
      [fieldKey]: {
        ...(titlePageConfig[fieldKey] || {}),
        text,
        enabled: true,
      },
      enabled: true,
    };
    persistTitlePageConfig(next);
  };

  // Keyboard shortcut to toggle preview mode
  useEffect(() => {
    const handleKeyPress = (event) => {
      if (protocolEditingDisabled) return;
      // Ctrl/Cmd + P to toggle between edit and canvas (leaves title page if open)
      if ((event.ctrlKey || event.metaKey) && event.key === 'p') {
        event.preventDefault();
        setMode((prev) => {
          if (prev === 'titlepage') {
            return lastSectionsMode === 'edit' && !protocolEditingDisabled ? 'edit' : 'preview';
          }
          const next = prev === 'edit' ? 'preview' : 'edit';
          setLastSectionsMode(next);
          return next;
        });
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [protocolEditingDisabled, lastSectionsMode]);



  // Fetch available section titles and sections on component mount, or when project changes
  useEffect(() => {
    let isMounted = true; // Flag to track if component is mounted

    const loadData = async () => {
      if (actualProjectId && !projectContextLoading) {
        try {
          setIsLoading(true); // Set loading true at the start of data fetching
          await Promise.all([
            fetchSections(),
            fetchSectionTitles()
          ]);
        } catch (error) {
          console.error('Error loading document data:', error);
          if (isMounted) { // Only update state if component is still mounted
            setError('Failed to load document data. Please try again.');
          }
        } finally {
          if (isMounted) { // Ensure loading is stopped even if there's an error
            setIsLoading(false);
          }
        }
      } else if (!actualProjectId && !projectContextLoading) {
        setError('Please select a project to view its documents.');
        setIsLoading(false);
      }
    };

    loadData();

    return () => {
      isMounted = false; // Set flag to false on unmount
    };
  }, [actualProjectId, projectContextLoading]); // Re-run when actualProjectId or projectContextLoading changes

  // Auto-run "Generate from USDM" when arriving here via the USDM Template
  // tab's "Generate Protocol" button (StudyFormTabs.jsx sets autoGenerateFromUsdm
  // then switches to this tab). Simulates a click on the (now hidden) button
  // above rather than duplicating its onClick body, so the exact same,
  // unmodified generation logic runs either way. Same visibility gate the
  // button itself uses (mode === 'preview' && !workflowReviewToolbar) so this
  // can't fire in a state where the button would've been hidden/disabled.
  //
  // Also gated on the SAME condition as the early-loading-spinner return
  // below ((isLoading || projectContextLoading) && !isGeneratingSections) —
  // that early return replaces this component's entire output (toolbar,
  // hidden button, everything) with just a spinner, so on a fresh mount the
  // button ref is still null until that first load finishes. Firing before
  // then was silently clicking nothing and burning the flag with no retry.
  useEffect(() => {
    if (!autoGenerateFromUsdm) return;
    if (!actualProjectId || projectContextLoading) return;
    if (isLoading && !isGeneratingSections) return;
    if (mode !== 'preview' || workflowReviewToolbar) return;
    generateFromUsdmButtonRef.current?.click();
    onAutoGenerateFromUsdmConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoGenerateFromUsdm, actualProjectId, projectContextLoading, isLoading, isGeneratingSections, mode, workflowReviewToolbar]);

  // Fetch available section titles
  const fetchSectionTitles = async () => {
    try {
      const titles = await getAvailableSectionTitles(actualProjectId); // Pass projectId
      setAvailableSectionTitles(titles);
      if (titles.length > 0) {
        setSelectedSectionTitle(titles[0]); // Set default to first available
      } else {
        setSelectedSectionTitle('');
      }
    } catch (err) {
      console.error('Error fetching section titles:', err);
      throw err; // Let the parent handle the error
    }
  };
  // Fetch sections
  const fetchSections = async () => {
    // Guard against concurrent invocations (e.g. StrictMode double-invoke,
    // or actualProjectId/projectContextLoading settling in two steps) racing
    // to auto-create the same missing appendix section twice.
    if (fetchSectionsInFlightRef.current) {
      return fetchSectionsInFlightRef.current;
    }
    const runPromise = fetchSectionsImpl().finally(() => {
      fetchSectionsInFlightRef.current = null;
    });
    fetchSectionsInFlightRef.current = runPromise;
    return runPromise;
  };

  // Restores a USDM-generated Schedule of Activities on reload. Section 1.3
  // is rendered exclusively by PublishedSoaView, which never reads
  // section.output — it shows S9 workspace tables if published, else
  // generatedSoAData (in-memory only), else an empty-state banner. The S9
  // fetch effect below only ever *sets* generatedSoAData when it finds real
  // S9 data — it never clears it — so seeding it here from the persisted
  // section is safe regardless of fetch order: a USDM-only study (the normal
  // case, no S9 workspace data) keeps what's set here; a study that also has
  // real S9 data still shows that instead, since PublishedSoaView checks
  // s9SoaTables first regardless of generatedSoAData's contents.
  const seedGeneratedSoADataFromUsdmSection = (loadedSections) => {
    const soaSection = (loadedSections || []).find((s) => s.title === '1.3 SCHEDULE OF ACTIVITIES');
    const soaData = soaSection?.scheduleOfActivitiesData;
    if (soaData && (soaData.tables?.length || soaData.visits?.length || soaData.sections?.length)) {
      setGeneratedSoAData(soaData);
    }
  };

  const fetchSectionsImpl = async () => {
    try {
      setError(null);
      if (!actualProjectId) {
        console.error('No project ID available');
        return;
      }

      // Mark that user has visited document editor (this will trigger form lock)
      await clinicalIntakeService.markClinicalIntakeWorkflowComplete(actualProjectId);

      const rawSections = await documentEditorService.getAllSections(actualProjectId);
      const normalizedSections = normalizeProtocolSectionsList(rawSections);

      // NEW: Do NOT auto-generate sections. If there are none, just show empty state
      if (!normalizedSections || normalizedSections.length === 0) {
        setSections([]);
        setIsLoading(false);
        return;
      }

      const syncedSections = [];
      for (const section of normalizedSections) {
        const sectionPrefix = sectionNumberPrefixFromTitle(section?.title);
        const mainNum = sectionPrefix?.match(/^(\d+)/)?.[1];
        const existing = rawSections.find((raw) => {
          const rawPrefix = sectionNumberPrefixFromTitle(raw?.title);
          return rawPrefix && sectionPrefix && rawPrefix === sectionPrefix;
        });

        if (!existing?._id) {
          const createTitle = canonicalizeM11AppendixSectionTitle(section.title) || section.title;
          const created = await documentEditorService.createSection({
            title: createTitle,
            context: section.context || '',
            prompt: section.prompt || '',
            output: section.output || '',
            projectId: actualProjectId,
            documentType: 'PROTOCOL',
          }, actualProjectId);
          syncedSections.push({ ...section, title: createTitle, _id: created.document._id });
          continue;
        }

        const canonicalTitle = canonicalizeM11AppendixSectionTitle(section.title || existing.title);
        const titleChanged = Boolean(canonicalTitle && canonicalTitle !== existing.title);
        const outputChanged = String(existing.output || '') !== String(section.output || '');
        const shouldPersistOutput =
          outputChanged &&
          (mainNum === '12' ||
            ((mainNum === '13' || mainNum === '14') && !String(existing.output || '').trim()));

        if (titleChanged || shouldPersistOutput) {
          await documentEditorService.updateSection(
            existing._id,
            {
              ...(titleChanged ? { title: canonicalTitle } : {}),
              ...(shouldPersistOutput ? { output: section.output || '' } : {}),
            },
            actualProjectId,
          );
        }

        syncedSections.push({
          ...existing,
          ...section,
          _id: existing._id,
          title: canonicalTitle || section.title || existing.title,
          output: shouldPersistOutput ? section.output : existing.output,
        });
      }

      const initialSections = syncedSections;

      // Legacy auto-generation logic (now unreachable due to early return above)
      // Only generate sections if there are absolutely no sections for this project
      if (!initialSections || initialSections.length === 0) {
        // Don't set loading to false yet - let the real-time generation handle it
        // setIsLoading(false); // Remove this line since real-time generation will handle loading state
        // Check for clinical intake form before generating sections
        let hasIntake = false;
        try {
          const intake = await clinicalIntakeService.getLatestClinicalIntake(actualProjectId);
          if (intake) hasIntake = true;
        } catch (err) {
          if (err.message === 'No intake form for current project') {
            setError('Could not generate any section because no clinical intake form exists for this project. Please fill out the clinical intake form first.');
            setIsLoading(false);
            return;
          } else {
            throw err;
          }
        }
        if (!hasIntake) {
          setError('Could not generate any section because no clinical intake form exists for this project. Please fill out the clinical intake form first.');
          setIsLoading(false);
          return;
        }
        const predefinedSections = await getPredefinedSections(actualProjectId);
        
        // Initialize progress state for automatic generation
        setGenerationProgress({
          current: 0,
          total: predefinedSections.length,
          isGenerating: true,
          currentSection: ''
        });

        // Initialize empty sections array for real-time updates
        const initialSections = predefinedSections.map(section => ({
          _id: `temp-${Date.now()}-${Math.random()}`,
          title: section.title,
          context: section.context,
          prompt: '',
          output: '',
          final: false,
          projectId: actualProjectId,
          documentType: 'PROTOCOL',
          useChunkedGeneration: section.useChunkedGeneration,
          chunks: section.chunks
        }));
        // Set initial empty sections for real-time updates and ensure loading is false
        setSections(initialSections);
        setIsLoading(false); // Ensure main loading is false when we start generation
        
        // Fetch latest clinical intake data once for all sections
        const latestIntake = await clinicalIntakeService.getLatestClinicalIntake(actualProjectId);
        
        // Generate sections one by one with real-time updates
        for (let i = 0; i < predefinedSections.length; i++) {
          const section = predefinedSections[i];
          const tempSectionId = initialSections[i]._id;
          
          try {
            // Update progress and UI to show this section is being generated
            setGenerationProgress(prev => ({ 
              ...prev, 
              current: i + 1,
              currentSection: section.title
            }));
            
            setSections(prevSections => 
              prevSections.map(s => 
                s._id === tempSectionId 
                  ? { ...s, output: 'Generating content...' }
                  : s
              )
            );
            
            // Filter clinical intake data for this specific section
            const filteredIntakeData = filterClinicalIntakeForSection(latestIntake, section.title);
            
            // Generate content using the template's prompt and filtered context
            let generatedContent = await generateContent(
              section.prompt || '', 
              section.context || '', 
              JSON.stringify(filteredIntakeData),
              'protocol',
              section.title,
              section,
              buildGenerationStylePayload(latestIntake, currentProject),
            );

            // Apply universal validation and regeneration for ALL sections in automatic generation
            try {
              
              // Get the current project's prompt from predefined sections
              const predefinedSectionsForAuto = await getPredefinedSections(actualProjectId);
              const originalSectionForAuto = findPredefinedSection(predefinedSectionsForAuto, section.title);
              const currentPromptForAuto = originalSectionForAuto?.prompt || section.prompt || '';
              
              const validationResult = await validateAndRegenerateSection(
                section.title,
                generatedContent,
                currentPromptForAuto, // Use current project's prompt
                section.context || '',
                3, // max attempts
                actualProjectId // Pass projectId to fetch fresh clinical intake data
              );
              
              if (validationResult.success) {
                generatedContent = validationResult.finalContent;
                
                // Store validation result in section data
                section.validationResult = validationResult.finalValidation;
              } else {
                console.warn(`⚠️ ${section.title} validation failed after ${validationResult.attemptNumber} attempts, using original content`);
                
                // Store failed validation result
                section.validationResult = validationResult.finalValidation || { isValid: false, overallScore: 0 };
              }
            } catch (error) {
              console.error(`❌ ${section.title} validation error in automatic generation:`, error);
              console.error(`❌ Error details:`, error.message);
              
              // Store error validation result
              section.validationResult = {
                isValid: false,
                overallScore: 0,
                errors: [{
                  subsection: 'system',
                  errorType: 'validation_error',
                  description: `Automatic validation failed: ${error.message}`,
                  severity: 'critical',
                  suggestion: 'Check validation service configuration'
                }],
                regenerationNeeded: true,
                regenerationReason: 'Validation service error'
              };
              
              // Continue with original content if validation fails
            }

            generatedContent = finalizeGeneratedProtocolOutput(generatedContent, section.title);

            if (
              String(section.title || '').match(/^12\b/) ||
              String(section.title || '').toUpperCase().includes('APPENDIX: SUPPORTING DETAILS')
            ) {
              generatedContent = mergeAppendicesIntoSection12Output(
                generatedContent,
                latestIntake?.appendices,
              );
            }

            // Create section in backend
            const createdSection = await documentEditorService.createSection({
              title: section.title,
              context: section.context,
              prompt: '',
              output: generatedContent,
              projectId: actualProjectId,
              documentType: 'PROTOCOL'
            }, actualProjectId);

            // Update UI with the generated content and real section ID
            setSections(prevSections => 
              prevSections.map(s => 
                s._id === tempSectionId 
                  ? { 
                      ...s, 
                      _id: createdSection.document._id,
                      output: generatedContent,
                      isNew: true // Flag to trigger animation
                    }
                  : s
              )
            );
            
            // Small delay to show the generation process
            await new Promise(resolve => setTimeout(resolve, 500));
            
          } catch (err) {
            console.error("Failed to create section:", err);
            // Update UI to show error
            setSections(prevSections => 
              prevSections.map(s => 
                s._id === tempSectionId 
                  ? { ...s, output: 'Error generating content. Please try again.' }
                  : s
              )
            );
          }
        }
        
        // Remove the isNew flag after a delay for animation
        setTimeout(() => {
          setSections(prevSections =>
            prevSections.map(s => ({ ...s, isNew: false }))
          );
        }, 2000);
        
        // Reset generation progress and ensure loading states are correct
        setGenerationProgress({ current: 0, total: 0, isGenerating: false, currentSection: '' });
        setIsLoading(false); // Ensure main loading is false
      } else {
        setSections(initialSections);
        seedGeneratedSoADataFromUsdmSection(initialSections);
      }
    } catch (error) {
      console.error('Error fetching sections:', error);
      setError(error.message || 'Error fetching sections');
    }
  };

  const handleInputChange = async (_id, field, value) => {
    // Update local state immediately for responsive UI
    setSections(prevSections =>
      prevSections.map(section =>
        section._id === _id ? { ...section, [field]: value } : section
      )
    );
    
    // Debounced save to backend
    try {
      const section = sections.find(s => s._id === _id);
      if (section) {
        const updatedData = { ...section, [field]: value };
        await documentEditorService.updateSection(_id, {
          context: updatedData.context,
          prompt: updatedData.prompt,
          output: updatedData.output,
          documentType: 'PROTOCOL'
        }, actualProjectId);

        if (field === 'output' || field === 'prompt') {
          // await documentEditorService.addNewVersion({
          //   sectionId: _id,
          //   title: updatedData.title, 
          //   context: updatedData.context,
          //   prompt: updatedData.prompt,
          //   output: updatedData.output,
          //   projectId: actualProjectId,
          //   documentType: updatedData.documentType // Ensure documentType is passed for versioning
          // });
        }
      }
    } catch (err) {
      console.error("Failed to update section:", err);
      // Consider showing a toast or notification here
    }
  };

  // Helper function to build hierarchical section structure (shared by export and sync)
  const buildHierarchicalSections = async (sectionsToProcess) => {
    // Helper function to convert HTML to plain text
    const htmlToPlainText = (html) => {
      if (!html) return '';
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;
      const blockElements = tempDiv.querySelectorAll('p, div, br, h1, h2, h3, h4, h5, h6, li, tr');
      blockElements.forEach(el => {
        if (el.tagName === 'BR') {
          el.replaceWith('\n');
        } else {
          el.insertAdjacentText('afterend', '\n');
        }
      });
      let plainText = tempDiv.textContent || tempDiv.innerText || '';
      plainText = plainText
        .split('\n')
        .map(line => line.trim())
        .join('\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
      return plainText;
    };

    const getSectionLevel = (sectionNumber) => {
      if (!sectionNumber) return 0;
      return (sectionNumber.match(/\./g) || []).length + 1;
    };

    // Extract subsections from content (same logic as in handleExportJSON)
    const extractSubsectionsFromContent = (content, mainSectionNumber) => {
      if (!content || !mainSectionNumber) return { mainContent: content, subsections: [] };
      
      const subsections = [];
      const mainEsc = mainSectionNumber.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const subsectionPattern = new RegExp(
        `(?:^|\\n)(${mainEsc}\\.\\d+(?:\\.\\d+)*)\\s+([A-Z][^\\n]{3,150}?)(?=\\n|$)`,
        'gm'
      );
      
      const matches = [];
      let match;
      let iterationCount = 0;
      const MAX_ITERATIONS = 500;
      
      while ((match = subsectionPattern.exec(content)) !== null && iterationCount < MAX_ITERATIONS) {
        iterationCount++;
        const subsectionNumber = match[1];
        let subsectionTitle = match[2].trim();
        let startIndex = match.index;
        if (content[startIndex] === '\n') {
          startIndex++;
        }
        
        const titleMatch = subsectionTitle.match(/^([A-Z][A-Za-z\s,:-]{0,100}?)(?:\s+(?:No|Not|This|The|All|To|For|From|With|By)\s)/);
        if (titleMatch) {
          subsectionTitle = titleMatch[1].trim();
        } else if (subsectionTitle.length > 100) {
          subsectionTitle = subsectionTitle.substring(0, 100).trim();
          subsectionTitle = subsectionTitle.replace(/\s+\S*$/, '');
        }
        
        matches.push({
          number: subsectionNumber,
          title: subsectionTitle,
          startIndex: startIndex,
          matchLength: match[0].length - (content[match.index] === '\n' ? 1 : 0)
        });
      }
      
      const extractedSubsections = [];
      for (let i = 0; i < matches.length; i++) {
        const current = matches[i];
        const next = matches[i + 1];
        const contentStart = current.startIndex + current.matchLength;
        const contentEnd = next ? next.startIndex : content.length;
        const subsectionContent = content.substring(contentStart, contentEnd).trim();
        const hasChildSubsections = next && next.number.startsWith(current.number + '.');
        const minContentLength = hasChildSubsections ? 0 : 5;
        const isValid = 
          subsectionContent.length >= minContentLength &&
          subsectionContent.length < 100000 &&
          current.title.length >= 3 &&
          current.title.length <= 200;
        
        if (isValid) {
          extractedSubsections.push({
            number: current.number,
            title: current.title,
            content: subsectionContent,
            startIndex: current.startIndex,
            endIndex: contentEnd
          });
        }
      }
      
      let mainContent = content;
      if (extractedSubsections.length > 0) {
        const firstSubsectionIndex = extractedSubsections[0].startIndex;
        mainContent = content.substring(0, firstSubsectionIndex).trim();
      }
      
      const subsectionObjects = extractedSubsections.map(sub => ({
        sectionNumber: sub.number,
        title: sub.title,
        content: sub.content,
        level: getSectionLevel(sub.number),
        isExtracted: true
      }));
      
      return {
        mainContent: mainContent,
        subsections: subsectionObjects
      };
    };

    // Parse sections
    const parsedSections = sectionsToProcess
      .filter((section) => {
        const hasContent = section.output && String(section.output).trim();
        return hasContent || isM11AppendixSectionTitle(section?.title);
      })
      .map((section) => {
        let sectionNumber = null;
        let sectionTitle = section.title;
        const sectionMatch = section.title.match(/^(\d+(?:\.\d+)*)\s+(.+)$/);
        if (sectionMatch) {
          sectionNumber = sectionMatch[1];
          sectionTitle = sectionMatch[2];
        } else {
          const pattern2Match = section.title.match(/^(\d+)[\.\s]+(.+)$/);
          if (pattern2Match) {
            sectionNumber = pattern2Match[1];
            sectionTitle = pattern2Match[2];
          } else {
            const pattern3Match = section.title.match(/^(\d+)(.*)$/);
            if (pattern3Match) {
              sectionNumber = pattern3Match[1];
              sectionTitle = pattern3Match[2].trim() || section.title;
            }
          }
        }
        const plainTextContent = section.output && String(section.output).trim()
          ? htmlToPlainText(section.output)
          : (isM11AppendixSectionTitle(section.title) ? 'Not applicable.' : '');
        return {
          sectionNumber: sectionNumber,
          title: sectionTitle,
          content: plainTextContent,
          level: getSectionLevel(sectionNumber),
          originalSection: section,
          originalTitle: section.title
        };
      })
      .filter(s => s.sectionNumber);

    // Extract subsections from main section content
    const sectionsWithExtractedSubsections = [];
    for (let i = 0; i < parsedSections.length; i++) {
      const section = parsedSections[i];
      if (section.level === 1) {
        try {
          const extracted = extractSubsectionsFromContent(section.content, section.sectionNumber);
          sectionsWithExtractedSubsections.push({
            ...section,
            content: extracted.mainContent,
            extractedSubsections: extracted.subsections
          });
        } catch (error) {
          console.error(`❌ Error extracting subsections from section ${section.sectionNumber}:`, error);
          sectionsWithExtractedSubsections.push({
            ...section,
            extractedSubsections: []
          });
        }
      } else {
        sectionsWithExtractedSubsections.push(section);
      }
    }

    // Combine database sections with extracted subsections
    const allSections = [];
    sectionsWithExtractedSubsections.forEach(section => {
      allSections.push(section);
      if (section.extractedSubsections && section.extractedSubsections.length > 0) {
        section.extractedSubsections.forEach((sub) => {
          const generatedId = `${section.originalSection._id}-${sub.sectionNumber.replace(/\./g, '-')}`;
          const subsectionEntry = {
            ...sub,
            originalSection: {
              ...section.originalSection,
              _id: generatedId
            },
            originalTitle: `${sub.sectionNumber} ${sub.title}`,
            isExtractedSubsection: true
          };
          allSections.push(subsectionEntry);
        });
      }
    });

    // Build hierarchical structure
    const buildHierarchy = (sections) => {
      const result = [];
      const sectionMapById = new Map();
      const sectionMapByNumber = new Map();

      sections.forEach(section => {
        const uniqueId = section.originalSection._id;
        const sectionNumber = section.sectionNumber;
        const sectionObj = {
          id: uniqueId,
          sectionNumber: sectionNumber,
          title: section.title,
          content: section.content,
          subsections: []
        };
        sectionMapById.set(uniqueId, sectionObj);
        if (!sectionMapByNumber.has(sectionNumber)) {
          sectionMapByNumber.set(sectionNumber, sectionObj);
        }
      });

      sections.forEach(section => {
        if (section.level === 1) {
          const uniqueId = section.originalSection._id;
          const sectionObj = sectionMapById.get(uniqueId);
          if (sectionObj && !result.find(s => s.id === uniqueId)) {
            result.push(sectionObj);
          }
        }
      });

      const sortedSections = [...sections].sort((a, b) => a.level - b.level);
      sortedSections.forEach(section => {
        if (section.level > 1) {
          const uniqueId = section.originalSection._id;
          const sectionObj = sectionMapById.get(uniqueId);
          if (sectionObj) {
            const sectionNumber = section.sectionNumber;
            const sectionNumberParts = sectionNumber.split('.');
            const parentNumber = sectionNumberParts.slice(0, -1).join('.');
            const parent = sectionMapByNumber.get(parentNumber);
            if (parent && !parent.subsections.find(s => s.id === uniqueId)) {
              parent.subsections.push(sectionObj);
            }
          }
        }
      });

      const mainSectionNumbers = new Set(
        sections.filter(s => s.level === 1).map(s => s.sectionNumber)
      );
      const filteredResult = result.filter(section => {
        const isMainSection = section.sectionNumber && mainSectionNumbers.has(section.sectionNumber);
        return isMainSection || !section.sectionNumber;
      });
      result.length = 0;
      result.push(...filteredResult);

      const sortBySectionNumber = (a, b) => {
        const aParts = a.sectionNumber.split('.').map(Number);
        const bParts = b.sectionNumber.split('.').map(Number);
        for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
          const aVal = aParts[i] || 0;
          const bVal = bParts[i] || 0;
          if (aVal !== bVal) return aVal - bVal;
        }
        return 0;
      };

      const sortRecursive = (sections) => {
        sections.sort(sortBySectionNumber);
        sections.forEach(section => {
          if (section.subsections && section.subsections.length > 0) {
            sortRecursive(section.subsections);
          }
        });
      };

      sortRecursive(result);
      return result;
    };

    return buildHierarchy(allSections);
  };

  // Sync sections to Knowledge Graph
  const handleSyncToKnowledgeGraph = async (requestedSyncType) => {
    try {
      setIsSyncingToKG(true);
      
      // Use parameter if provided, otherwise fall back to state
      const effectiveSyncType = requestedSyncType || syncType;

      if (!currentProject?._id) {
        throw new Error('Project not found. Please ensure project is properly loaded.');
      }

      const technicalStudyKey = resolveProjectTechnicalStudyKey(currentProject);

      if (sections.length === 0) {
        throw new Error('No sections to sync. Please generate sections first.');
      }

      // Build hierarchical structure (same as export)
      const hierarchicalSections = await buildHierarchicalSections(
        getProtocolSectionsForExport(sections),
      );

      let results;
      if (effectiveSyncType === 'embeddings') {
        // Use vector embeddings sync
        results = await validationService.syncToKnowledgeGraphWithEmbeddings(
          technicalStudyKey,
          currentProject._id,
          projectListPrimaryLabel(currentProject),
          hierarchicalSections,
          0.7 // similarity threshold
        );
        
        
        alert(`✅ Knowledge Graph synced with Vector Embeddings!\n\n` +
              `Study: ${results.data.studyId}\n` +
              `Sections: ${results.data.statistics.totalSections}\n` +
              `Relationships: ${results.data.statistics.totalRelationships}\n` +
              `Content Nodes: ${results.data.statistics.totalContentNodes}\n` +
              `Chunks: ${results.data.embeddings.chunks}\n` +
              `Semantic Relationships: ${results.data.embeddings.semanticRelationships}\n\n` +
              `Executed: ${results.data.execution.successful}/${results.data.execution.totalQueries} queries`);
      } else {
        // Use standard sync
        results = await validationService.syncToKnowledgeGraph(
          technicalStudyKey,
          currentProject._id,
          projectListPrimaryLabel(currentProject),
          hierarchicalSections
        );

        
        alert(`✅ Knowledge Graph synced successfully!\n\n` +
              `Study: ${results.data.studyId}\n` +
              `Sections: ${results.data.statistics.totalSections}\n` +
              `Relationships: ${results.data.statistics.totalRelationships}\n` +
              `Content Nodes: ${results.data.statistics.totalContentNodes}\n\n` +
              `Executed: ${results.data.execution.successful}/${results.data.execution.totalQueries} queries`);
      }

    } catch (error) {
      console.error('❌ Sync error:', error);
      alert(`❌ Sync failed: ${error.message}`);
    } finally {
      setIsSyncingToKG(false);
    }
  };

  // Extract entities from Knowledge Graph
  const handleExtractEntities = async () => {
    try {
      setIsExtractingEntities(true);


      if (!currentProject?._id) {
        throw new Error('Project ID not found. Please ensure project is properly loaded.');
      }

      // Call entity extraction API
      const results = await validationService.extractEntities(currentProject._id);

      
      const stats = results.data?.statistics || {};
      const sectionStats = results.data?.sectionStats || [];
      const errors = results.data?.errors || [];
      const phaseTimings = results.data?.phaseTimings || null;
      const queryGeneration = results.data?.queryGeneration || null;
      
      let summary = `✅ Entity extraction completed successfully!\n\n`;
      summary += `📊 Summary:\n`;
      summary += `   Sections processed: ${stats.sectionsProcessed || 0}/${stats.totalSections || 0}\n`;
      summary += `   Total entities created: ${stats.totalEntitiesCreated || 0}\n`;
      summary += `   Total relationships created: ${stats.totalRelationshipsCreated || 0}\n`;
      
      // Show timing information if available
      if (phaseTimings) {
        summary += `\n⏱️  Performance:\n`;
        summary += `   Phase 1 (Query Generation): ${(phaseTimings.phase1_generation / 1000).toFixed(2)}s\n`;
        summary += `   Phase 2 (Batch Execution): ${(phaseTimings.phase2_execution / 1000).toFixed(2)}s\n`;
        summary += `   Total Time: ${(phaseTimings.total / 1000).toFixed(2)}s\n`;
      }
      
      // Show query generation stats if available
      if (queryGeneration) {
        summary += `\n📝 Query Generation:\n`;
        summary += `   Successful: ${queryGeneration.successful || 0}/${queryGeneration.total || 0}\n`;
        summary += `   Skipped: ${queryGeneration.skipped || 0}\n`;
        if (queryGeneration.errors > 0) {
          summary += `   Errors: ${queryGeneration.errors}\n`;
        }
      }
      
      if (errors.length > 0) {
        summary += `\n⚠️ Errors: ${errors.length}\n`;
      }
      
      // Show section-by-section breakdown
      if (sectionStats.length > 0) {
        summary += `\n📋 Section Details (first 10):\n`;
        sectionStats.slice(0, 10).forEach(stat => {
          if (stat.status === 'success') {
            summary += `   ✓ ${stat.sectionNumber || stat.sectionId}: ${stat.entitiesCreated} entities, ${stat.relationshipsCreated} relationships\n`;
          } else if (stat.status === 'error') {
            summary += `   ✗ ${stat.sectionNumber || stat.sectionId}: Error - ${stat.error}\n`;
          } else if (stat.status === 'skipped_short_content') {
            summary += `   ⊘ ${stat.sectionNumber || stat.sectionId}: Skipped (short content)\n`;
          }
        });
        if (sectionStats.length > 10) {
          summary += `   ... and ${sectionStats.length - 10} more sections\n`;
        }
      }
      
      summary += `\n\nThe enhanced Knowledge Graph is now ready for use by the agent workflow.`;
      
      alert(summary);

    } catch (error) {
      console.error('❌ Entity extraction error:', error);
      alert(`❌ Entity extraction failed: ${error.message}`);
    } finally {
      setIsExtractingEntities(false);
    }
  };

  // Extract standard entities from Knowledge Graph (using ONLY 60+ standard entity types)
  const handleExtractStandardEntities = async () => {
    try {
      setIsExtractingStandardEntities(true);


      if (!currentProject?._id) {
        throw new Error('Project ID not found. Please ensure project is properly loaded.');
      }

      // Call standard entity extraction API
      const results = await validationService.extractStandardEntities(currentProject._id);

      
      const stats = results.data?.statistics || {};
      const sectionStats = results.data?.sectionStats || [];
      const errors = results.data?.errors || [];
      const phaseTimings = results.data?.phaseTimings || null;
      const queryGeneration = results.data?.queryGeneration || null;
      
      let summary = `✅ Standard Entity Extraction completed successfully!\n\n`;
      summary += `📊 Summary:\n`;
      summary += `   Sections processed: ${stats.sectionsProcessed || 0}/${stats.totalSections || 0}\n`;
      summary += `   Total entities created: ${stats.totalEntitiesCreated || 0}\n`;
      summary += `   Total relationships created: ${stats.totalRelationshipsCreated || 0}\n`;
      summary += `\n✨ Using ONLY standard entity types (60+ entities: StudyIdentifier, TreatmentArm, StudyEpoch, etc.)\n`;
      
      // Show timing information if available
      if (phaseTimings) {
        summary += `\n⏱️  Performance:\n`;
        summary += `   Phase 1 (Query Generation): ${(phaseTimings.phase1_generation / 1000).toFixed(2)}s\n`;
        summary += `   Phase 2 (Batch Execution): ${(phaseTimings.phase2_execution / 1000).toFixed(2)}s\n`;
        summary += `   Total Time: ${(phaseTimings.total / 1000).toFixed(2)}s\n`;
      }
      
      // Show query generation stats if available
      if (queryGeneration) {
        summary += `\n📝 Query Generation:\n`;
        summary += `   Successful: ${queryGeneration.successful || 0}/${queryGeneration.total || 0}\n`;
        summary += `   Skipped: ${queryGeneration.skipped || 0}\n`;
        if (queryGeneration.errors > 0) {
          summary += `   Errors: ${queryGeneration.errors}\n`;
        }
      }
      
      if (errors.length > 0) {
        summary += `\n⚠️ Errors: ${errors.length}\n`;
      }
      
      // Show section-by-section breakdown
      if (sectionStats.length > 0) {
        summary += `\n📋 Section Details (first 10):\n`;
        sectionStats.slice(0, 10).forEach(stat => {
          if (stat.status === 'success') {
            summary += `   ✓ ${stat.sectionNumber || stat.sectionId}: ${stat.entitiesCreated} entities, ${stat.relationshipsCreated} relationships\n`;
          } else if (stat.status === 'error') {
            summary += `   ✗ ${stat.sectionNumber || stat.sectionId}: Error - ${stat.error}\n`;
          } else if (stat.status === 'skipped_short_content') {
            summary += `   ⊘ ${stat.sectionNumber || stat.sectionId}: Skipped (short content)\n`;
          }
        });
        if (sectionStats.length > 10) {
          summary += `   ... and ${sectionStats.length - 10} more sections\n`;
        }
      }
      
      summary += `\n\nThe Knowledge Graph now contains standardized entities ready for cross-section validation and analysis.`;
      
      alert(summary);

    } catch (error) {
      console.error('❌ Standard entity extraction error:', error);
      alert(`❌ Standard entity extraction failed: ${error.message}`);
    } finally {
      setIsExtractingStandardEntities(false);
    }
  };

  // Validate sections against Knowledge Graph
  const handleValidateConsistency = async () => {
    try {
      setIsValidating(true);
      setIsValidationDialogOpen(true);
      setValidationResults(null);

      if (!currentProject?._id) {
        throw new Error('Project not found. Please ensure project is properly loaded.');
      }

      const technicalStudyKey = resolveProjectTechnicalStudyKey(currentProject);

      if (sections.length === 0) {
        throw new Error('No sections to validate. Please generate sections first.');
      }

      // Send sections to validation API
      const results = await validationService.validateSections(
        technicalStudyKey,
        currentProject._id,
        sections
      );

      setValidationResults(results);

    } catch (error) {
      console.error('❌ Validation error:', error);
      
      // Show error in dialog
      setValidationResults({
        success: false,
        message: error.message,
        data: {
          inconsistencies: [],
          summary: { total: 0, high: 0, medium: 0, low: 0 },
          error: error.message
        }
      });
    } finally {
      setIsValidating(false);
    }
  };

  // Validate sections for export (M11 and all export validations)
  const handleValidateSectionsForExport = async () => {
    try {
      setIsValidatingExport(true);
      setIsExportValidationDialogOpen(true);
      setExportValidationResults([]);


      if (sections.length === 0) {
        throw new Error('No sections to validate. Please generate sections first.');
      }

      // Validate the same title page and SoA payload the Word export uses
      // (USDM/S9 studies keep their SoA as tables, not generatedSoAData.visits).
      const titlePageConfigForExport = await resolveTitlePageConfigForExport();
      const soaDataToExport = await resolveSoaDataForExport({
        generatedSoAData,
        s9SoaTables,
        projectId: actualProjectId,
      });
      const results = await runAllExportValidations(
        sections,
        actualProjectId,
        soaDataToExport,
        titlePageConfigForExport,
      );

      setExportValidationResults(results);

    } catch (error) {
      console.error('❌ Export validation error:', error);
      
      // Show error in dialog
      setExportValidationResults([
        {
          id: 'error',
          label: 'Validation Error',
          passed: false,
          errors: [`Failed to run validation: ${error.message}`],
        }
      ]);
    } finally {
      setIsValidatingExport(false);
    }
  };

  const handleRunAiValidations = async () => {
    try {
      setIsValidatingAi(true);
      setIsAiValidationDialogOpen(true);
      setAiValidationResults([]);
      if (sections.length === 0) {
        throw new Error('No sections to validate. Please generate sections first.');
      }
      // Section 1.3 is never shown or exported from section.output: PublishedSoaView
      // and the Word/PDF/XML exports render the published S9 tables, else the
      // structured USDM schedule. Validate that same schedule — the stored output can
      // be an older text copy (e.g. one table instead of two, no PK rows).
      const soaForValidation = await resolveSoaDataForExport({
        generatedSoAData,
        s9SoaTables,
        projectId: actualProjectId,
      });
      const soaHtmlForValidation = soaForValidation ? buildScheduleOfActivitiesHtml(soaForValidation) : '';
      const isScheduleOfActivitiesSection = (section) =>
        /^1\.3\b|schedule of activities/i.test(String(section?.title || '').trim());

      // Validate the text as it is displayed and exported, not the raw stored output:
      // finalizeGeneratedProtocolOutput applies the deterministic clean-ups (captions,
      // curly quotes, dose trailing zeros, operator spacing, ...) on every display/
      // export, so checking the stored text reported issues the export doesn't have.
      const finalizeForValidation = (section) => ({
        ...section,
        output: finalizeGeneratedProtocolOutput(
          isScheduleOfActivitiesSection(section) && soaHtmlForValidation
            ? soaHtmlForValidation
            : section.output || '',
          section.title || '',
          mediaNumberOffsetsById.get(section._id),
        ),
        subsections: Array.isArray(section.subsections)
          ? section.subsections.map(finalizeForValidation)
          : section.subsections,
      });
      const results = await runAllAiValidations(sections.map(finalizeForValidation), {
        projectId: actualProjectId,
        studyId: resolveProjectTechnicalStudyKey(currentProject),
      });
      setAiValidationResults(results);
    } catch (error) {
      console.error('❌ AI validation error:', error);
      setAiValidationResults([
        {
          id: 'ai-validation-error',
          label: 'AI Validation Error',
          passed: false,
          errors: [error.message || 'Failed to run AI validations'],
        },
      ]);
    } finally {
      setIsValidatingAi(false);
    }
  };


  // Export all sections to JSON - Hierarchical structure with main sections and subsections
  const handleExportJSON = async () => {
    try {
      const sectionsForExport = getProtocolSectionsForExport(sections);
      // Allow UI to update before heavy processing
      await new Promise(resolve => setTimeout(resolve, 50));
      
      // Helper function to convert HTML to plain text
      const htmlToPlainText = (html) => {
        if (!html) return '';
        
        // Create a temporary div element
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;
        
        // Add newlines after block elements to preserve structure
        const blockElements = tempDiv.querySelectorAll('p, div, br, h1, h2, h3, h4, h5, h6, li, tr');
        blockElements.forEach(el => {
          if (el.tagName === 'BR') {
            el.replaceWith('\n');
          } else {
            el.insertAdjacentText('afterend', '\n');
          }
        });
        
        // Get plain text content
        let plainText = tempDiv.textContent || tempDiv.innerText || '';
        
        // Clean up extra whitespace BUT keep newlines
        plainText = plainText
          .split('\n')
          .map(line => line.trim()) // Trim each line
          .join('\n') // Join with newlines
          .replace(/\n{3,}/g, '\n\n') // Replace 3+ newlines with 2
          .trim();
        
        return plainText;
      };
      
      // Helper function to get section level from section number
      const getSectionLevel = (sectionNumber) => {
        if (!sectionNumber) return 0;
        return (sectionNumber.match(/\./g) || []).length + 1; // 1 = main, 2 = subsection, 3 = sub-subsection, etc.
      };
      
      // Helper function to check if sectionNumber is a child of parentNumber
      const isChildOf = (sectionNumber, parentNumber) => {
        if (!sectionNumber || !parentNumber) return false;
        return sectionNumber.startsWith(parentNumber + '.');
      };
      
      // Helper function to extract subsections from main section content
      const extractSubsectionsFromContent = (content, mainSectionNumber) => {
        if (!content || !mainSectionNumber) return { mainContent: content, subsections: [] };
        
        const subsections = [];
        const mainEsc = mainSectionNumber.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        
        const newlineCount = (content.match(/\n/g) || []).length;
        
        // Show first 1000 chars for debugging
        
        // Pattern to match subsection headings like "2.1 Purpose of Trial" or "2.2.1 Risk Summary"
        // More comprehensive pattern that captures subsections better
        // Matches: mainNumber.digit(s) followed by space and title starting with capital letter
        // Look for subsection at start of line (after newline or at beginning)
        const subsectionPattern = new RegExp(
          `(?:^|\\n)(${mainEsc}\\.\\d+(?:\\.\\d+)*)\\s+([A-Z][^\\n]{3,150}?)(?=\\n|$)`,
          'gm'
        );
        
        
        const matches = [];
        let match;
        let iterationCount = 0;
        const MAX_ITERATIONS = 500;
        
        // Find all subsection headings
        while ((match = subsectionPattern.exec(content)) !== null && iterationCount < MAX_ITERATIONS) {
          iterationCount++;
          const subsectionNumber = match[1];
          let subsectionTitle = match[2].trim();
          const rawMatch = match[0];
          
          
          // Adjust startIndex if pattern matched a newline at the start
          let startIndex = match.index;
          if (content[startIndex] === '\n') {
            startIndex++; // Skip the newline
          }
          
          // Clean up title - remove extra content after certain patterns
          // Stop at patterns like "No specific", "Not applicable", long sentences
          const titleMatch = subsectionTitle.match(/^([A-Z][A-Za-z\s,:-]{0,100}?)(?:\s+(?:No|Not|This|The|All|To|For|From|With|By)\s)/);
          if (titleMatch) {
            subsectionTitle = titleMatch[1].trim();
          } else {
            // Limit to first 100 chars to avoid capturing content
            if (subsectionTitle.length > 100) {
              subsectionTitle = subsectionTitle.substring(0, 100).trim();
              // Remove incomplete words at the end
              subsectionTitle = subsectionTitle.replace(/\s+\S*$/, '');
            }
          }
          
          matches.push({
            number: subsectionNumber,
            title: subsectionTitle,
            startIndex: startIndex,
            matchLength: match[0].length - (content[match.index] === '\n' ? 1 : 0) // Subtract newline if present
          });
          
        }
        
        
        // Extract content for each subsection
        const extractedSubsections = [];
        for (let i = 0; i < matches.length; i++) {
          const current = matches[i];
          const next = matches[i + 1];
          
          // Content starts after the heading
          const contentStart = current.startIndex + current.matchLength;
          // Content ends at start of next subsection or end of content
          const contentEnd = next ? next.startIndex : content.length;
          
          const subsectionContent = content.substring(contentStart, contentEnd).trim();
          
          
          // Check if next match is a child of current (e.g., current is 1.1, next is 1.1.1)
          const hasChildSubsections = next && next.number.startsWith(current.number + '.');
          
          if (hasChildSubsections) {
          }
          
          // Filter out false matches - validate subsection
          // Allow minimal content if this subsection has child subsections
          const minContentLength = hasChildSubsections ? 0 : 5;
          const isValid = 
            subsectionContent.length >= minContentLength && // Allow empty content if has children
            subsectionContent.length < 100000 && // Not too long
            current.title.length >= 3 && // Title is reasonable length
            current.title.length <= 200; // Title not too long (increased from 150)
          
          if (isValid) {
            extractedSubsections.push({
              number: current.number,
              title: current.title,
              content: subsectionContent,
              startIndex: current.startIndex,
              endIndex: contentEnd
            });
            if (hasChildSubsections) {
            } else {
            }
          } else {
            const reasons = [];
            if (subsectionContent.length < minContentLength) reasons.push(`content too short: ${subsectionContent.length} (min: ${minContentLength})`);
            if (subsectionContent.length >= 100000) reasons.push('content too long');
            if (current.title.length < 3) reasons.push('title too short');
            if (current.title.length > 200) reasons.push('title too long');
          }
        }
        
        
        // Remove subsection content from main section
        
        let mainContent = content;
        if (extractedSubsections.length > 0) {
          // Keep only content BEFORE the first subsection
          // Everything from the first subsection heading onwards should be removed
          const firstSubsectionIndex = extractedSubsections[0].startIndex;
          
          
          mainContent = content.substring(0, firstSubsectionIndex).trim();
        }
        
        if (mainContent.length <= 200) {
        } else {
        }
        
        // Convert to subsection objects
        const subsectionObjects = extractedSubsections.map(sub => ({
          sectionNumber: sub.number,
          title: sub.title,
          content: sub.content,
          level: getSectionLevel(sub.number),
          isExtracted: true
        }));
        
        if (subsectionObjects.length > 0) {
          subsectionObjects.forEach(s => {
            const hasChildren = subsectionObjects.some(other => 
              other.sectionNumber !== s.sectionNumber && 
              other.sectionNumber.startsWith(s.sectionNumber + '.')
            );
            const type = hasChildren ? '🌳 PARENT' : '🍃 LEAF';
          });
        }
        
        return {
          mainContent: mainContent,
          subsections: subsectionObjects
        };
      };
      
      // Parse all sections with their numbers and content
      
      const parsedSections = sectionsForExport
        .filter(section => {
          const hasContent = section.output && section.output.trim();
          if (!hasContent && isM11AppendixSectionTitle(section.title)) {
            return true;
          }
          return hasContent;
        })
        .map(section => {
          // Extract section number from title - try multiple patterns
          let sectionNumber = null;
          let sectionTitle = section.title;
          
          // Pattern 1: "1.2 Title" or "1 Title"
          const sectionMatch = section.title.match(/^(\d+(?:\.\d+)*)\s+(.+)$/);
          if (sectionMatch) {
            sectionNumber = sectionMatch[1];
            sectionTitle = sectionMatch[2];
          } else {
            // Pattern 2: "1." or "1 " at the start
            const pattern2Match = section.title.match(/^(\d+)[\.\s]+(.+)$/);
            if (pattern2Match) {
              sectionNumber = pattern2Match[1];
              sectionTitle = pattern2Match[2];
            } else {
              // Pattern 3: Just a number at the start
              const pattern3Match = section.title.match(/^(\d+)(.*)$/);
              if (pattern3Match) {
                sectionNumber = pattern3Match[1];
                sectionTitle = pattern3Match[2].trim() || section.title;
              }
            }
          }
          
          // Convert HTML output to plain text
          const plainTextContent = section.output && String(section.output).trim()
          ? htmlToPlainText(section.output)
          : (isM11AppendixSectionTitle(section.title) ? 'Not applicable.' : '');
          
          const parsed = {
            sectionNumber: sectionNumber,
            title: sectionTitle,
            content: plainTextContent,
            level: getSectionLevel(sectionNumber),
            originalSection: section,
            originalTitle: section.title
          };
          
          
          return parsed;
        })
        .filter(s => {
          if (!s.sectionNumber) {
          }
          return s.sectionNumber; // Only include sections with valid numbers
        });
      
      // Log sections by level to understand the structure
      const sectionsByLevel = {};
      parsedSections.forEach(s => {
        if (!sectionsByLevel[s.level]) sectionsByLevel[s.level] = [];
        sectionsByLevel[s.level].push(`${s.sectionNumber} - ${s.title}`);
      });
      // (debug logging removed)
      
      // Extract subsections from main section content since they're embedded
      
      const sectionsWithExtractedSubsections = [];
      for (let i = 0; i < parsedSections.length; i++) {
        const section = parsedSections[i];
        
        if (section.level === 1) {
          // This is a main section - extract subsections from its content
          try {
            const extracted = extractSubsectionsFromContent(section.content, section.sectionNumber);
            
            if (extracted.subsections.length > 0) {
              extracted.subsections.forEach(sub => {
              });
            } else {
            }
            
            sectionsWithExtractedSubsections.push({
              ...section,
              content: extracted.mainContent, // Main section content without subsections
              extractedSubsections: extracted.subsections // Subsections found in content
            });
          } catch (error) {
            console.error(`❌ Error extracting subsections from section ${section.sectionNumber}:`, error);
            // Fallback: use original section without extraction
            sectionsWithExtractedSubsections.push({
              ...section,
              extractedSubsections: []
            });
          }
        } else {
          sectionsWithExtractedSubsections.push(section);
        }
        
        // Yield to UI every 10 sections to prevent freezing
        if (i % 10 === 0) {
          await new Promise(resolve => setTimeout(resolve, 10));
        }
      }
      
      // Combine database sections with extracted subsections
      const allSections = [];
      sectionsWithExtractedSubsections.forEach(section => {
        // Add the section itself
        allSections.push(section);
        
        // Add extracted subsections as separate entries with unique generated IDs
        if (section.extractedSubsections && section.extractedSubsections.length > 0) {
          section.extractedSubsections.forEach((sub, index) => {
            // Generate a unique ID for extracted subsections (they don't exist in DB)
            // Format: parentId-sectionNumber (e.g., "690d758fd63d8980ddeba2fe-1-1-1")
            const generatedId = `${section.originalSection._id}-${sub.sectionNumber.replace(/\./g, '-')}`;
            
            const subsectionEntry = {
              ...sub,
              originalSection: {
                ...section.originalSection,
                _id: generatedId // Use generated ID instead of parent's ID
              },
              originalTitle: `${sub.sectionNumber} ${sub.title}`,
              isExtractedSubsection: true // Flag to identify extracted subsections
            };
            
            const contentInfo = sub.content.length === 0 ? 'EMPTY (parent)' : `${sub.content.length} chars`;
            allSections.push(subsectionEntry);
          });
        }
      });
      
      const finalParsedSections = allSections;
      
      finalParsedSections.forEach(s => {
        const source = s.isExtractedSubsection ? '📤 EXTRACTED' : '💾 DATABASE';
        if (s.isExtractedSubsection) {
        }
      });
      
      // Group by level for debugging
      const byLevel = {};
      finalParsedSections.forEach(s => {
        if (!byLevel[s.level]) byLevel[s.level] = [];
        byLevel[s.level].push(s);
      });
      
      // Build hierarchical structure
      const buildHierarchy = (sections) => {
        const result = [];
        // Each section has a unique _id (MongoDB ObjectId) as its primary identifier
        // We use sectionNumber for hierarchy organization, but track by unique _id
        const sectionMapById = new Map(); // Map by unique _id: _id -> section object
        const sectionMapByNumber = new Map(); // Map by sectionNumber: sectionNumber -> section object (for hierarchy)
        
        // First pass: create all section objects, indexed by both _id and sectionNumber
        sections.forEach(section => {
          const uniqueId = section.originalSection._id;
          const sectionNumber = section.sectionNumber;
          
          const sectionObj = {
            id: uniqueId, // Unique database identifier
            sectionNumber: sectionNumber,
            title: section.title,
            content: section.content,
            subsections: []
          };
          
          
          // Store by unique _id (primary identifier)
          sectionMapById.set(uniqueId, sectionObj);
          
          // Store by sectionNumber for hierarchy building
          // If same sectionNumber exists, we'll handle it in hierarchy building
          if (!sectionMapByNumber.has(sectionNumber)) {
            sectionMapByNumber.set(sectionNumber, sectionObj);
          } else {
            // Same sectionNumber but different _id - this is a duplicate number issue
            const existing = sectionMapByNumber.get(sectionNumber);
            console.warn(`    ⚠️ Duplicate section number "${sectionNumber}":`, {
              existing: { id: existing.id, title: existing.title },
              new: { id: uniqueId, title: section.title }
            });
            // Keep the first one for hierarchy, but both are tracked by _id
          }
        });
        
        
        // Second pass: build hierarchy
        // Step 1: Add only main sections (level 1) to result
        sections.forEach(section => {
          if (section.level === 1) {
            const uniqueId = section.originalSection._id;
            const sectionObj = sectionMapById.get(uniqueId);
            
            if (sectionObj && !result.find(s => s.id === uniqueId)) {
              result.push(sectionObj);
            }
          } else {
          }
        });
        
        // Step 2: Add all subsections (level > 1) to their parent's subsections array
        // Process subsections in order of depth (shallow to deep) to ensure parents exist first
        const sortedSections = [...sections].sort((a, b) => a.level - b.level);
        
        sortedSections.forEach(section => {
          if (section.level > 1) {
            const uniqueId = section.originalSection._id;
            const sectionObj = sectionMapById.get(uniqueId);
            
            if (!sectionObj) {
              console.error(`Section with _id ${uniqueId} not found in map`);
              return;
            }
            
            // Find the immediate parent (closest ancestor)
            const parentNumber = section.sectionNumber.split('.').slice(0, section.level - 1).join('.');
            
            if (sectionMapByNumber.has(parentNumber)) {
              const parent = sectionMapByNumber.get(parentNumber);
              
              // Make sure parent is in result (if it's a main section) or in its parent's subsections
              const ensureParentInHierarchy = (parentSection) => {
                // If parent is level 1, it should be in result
                if (parentSection.sectionNumber && getSectionLevel(parentSection.sectionNumber) === 1) {
                  if (!result.find(s => s.id === parentSection.id)) {
                    result.push(parentSection);
                  }
                }
              };
              
              // Ensure parent exists in hierarchy
              ensureParentInHierarchy(parent);
              
              // Check if this subsection is already added to parent
              if (!parent.subsections.find(s => s.id === uniqueId)) {
                parent.subsections.push(sectionObj);
              }
            } else {
              // Parent not found - need to create missing parent hierarchy
              
              // Create all missing ancestors recursively
              const createParentChain = (childNumber) => {
                const parts = childNumber.split('.');
                if (parts.length === 1) {
                  // This is a top-level section, add to result
                  const topSection = sectionMapByNumber.get(childNumber);
                  if (topSection && !result.find(s => s.sectionNumber === childNumber)) {
                    result.push(topSection);
                  }
                  return topSection;
                }
                
                const parentNum = parts.slice(0, -1).join('.');
                
                // Check if parent exists
                if (!sectionMapByNumber.has(parentNum)) {
                  // Try to find the parent in the original sections array (might be an extracted subsection)
                  const parentSection = sections.find(s => s.sectionNumber === parentNum);
                  
                  if (parentSection) {
                    // parentSection found
                  } else {
                  }
                  
                  // Create virtual parent, but use actual title if found
                  const virtualParent = {
                    id: parentSection ? parentSection.originalSection._id : `virtual-${parentNum}`,
                    sectionNumber: parentNum,
                    title: parentSection ? parentSection.title : `Section ${parentNum}`,
                    content: parentSection ? parentSection.content : '',
                    subsections: []
                  };
                  sectionMapByNumber.set(parentNum, virtualParent);
                  
                  if (parentSection) {
                  } else {
                  }
                  
                  // Recursively ensure grandparent exists and add this parent to it
                  const grandparent = createParentChain(parentNum);
                  if (grandparent && !grandparent.subsections.find(s => s.sectionNumber === parentNum)) {
                    grandparent.subsections.push(virtualParent);
                  }
                }
                
                return sectionMapByNumber.get(parentNum);
              };
              
              // Create parent chain and get the immediate parent
              const parent = createParentChain(section.sectionNumber);
              
              // Add this section to its parent
              if (parent && !parent.subsections.find(s => s.id === uniqueId)) {
                parent.subsections.push(sectionObj);
              }
            }
          }
        });
        
        // Step 3: Remove any subsections that were accidentally added to result
        // Filter instead of splice to avoid index shifting issues
        const mainSectionNumbers = new Set(
          sections.filter(s => s.level === 1).map(s => s.sectionNumber)
        );
        
        const beforeFilterCount = result.length;
        const filteredResult = result.filter(section => {
          const isMainSection = section.sectionNumber && mainSectionNumbers.has(section.sectionNumber);
          if (!isMainSection && section.sectionNumber) {
            console.warn(`⚠️ Removing subsection ${section.sectionNumber} from root (should be nested under parent)`);
          }
          return isMainSection || !section.sectionNumber; // Keep main sections and sections without numbers
        });
        
        // Clear and repopulate result array
        result.length = 0;
        result.push(...filteredResult);
        
        
        
        // Sort sections and subsections by section number
        const sortBySectionNumber = (a, b) => {
          const aParts = a.sectionNumber.split('.').map(Number);
          const bParts = b.sectionNumber.split('.').map(Number);
          
          for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
            const aVal = aParts[i] || 0;
            const bVal = bParts[i] || 0;
            if (aVal !== bVal) return aVal - bVal;
          }
          return 0;
        };
        
        // Recursively sort all levels
        const sortRecursive = (sections) => {
          sections.sort(sortBySectionNumber);
          sections.forEach(section => {
            if (section.subsections && section.subsections.length > 0) {
              sortRecursive(section.subsections);
            }
          });
        };
        
        sortRecursive(result);
        
        return result;
      };
      
      // Build hierarchical structure
      const hierarchicalSections = buildHierarchy(finalParsedSections);
      
      // Create final JSON structure with study/project info at the top
      const exportData = {
        studyInfo: {
          studyId: resolveProjectTechnicalStudyKey(currentProject) || actualProjectId || 'N/A',
          sponsorProtocolIdentifier: resolveProjectSponsorProtocolId(currentProject) || 'N/A',
          studyName: projectListPrimaryLabel(currentProject) || 'N/A',
          trialAcronym: resolveProjectTrialAcronym(currentProject) || 'N/A',
          sponsorName: resolveProjectSponsorName(currentProject) || 'N/A',
          projectId: currentProject?._id || actualProjectId || 'N/A',
          exportDate: new Date().toISOString(),
          totalSections: hierarchicalSections.length
        },
        sections: hierarchicalSections
      };
      
      // Convert to JSON string with pretty formatting
      const jsonString = JSON.stringify(exportData, null, 2);
      
      // Create blob and download
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const fileName = `protocol-sections-${actualProjectId || 'export'}-${new Date().toISOString().split('T')[0]}.json`;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      
      // Auto-sync to Knowledge Graph after successful export
      try {
        const syncResult = await validationService.syncToKnowledgeGraph(
          exportData.studyInfo.studyId,
          exportData.studyInfo.projectId,
          exportData.studyInfo.studyName,
          hierarchicalSections
        );
        
        alert(`✅ Protocol sections successfully exported as JSON!\n\n` +
              `Trial Acronym: ${exportData.studyInfo.trialAcronym}\n` +
              `Sponsor Protocol Identifier: ${exportData.studyInfo.sponsorProtocolIdentifier}\n` +
              `File: ${fileName}\n` +
              `Sections: ${hierarchicalSections.length}\n\n` +
              `📚 Knowledge Graph Updated:\n` +
              `  • Sections: ${syncResult.data?.statistics?.totalSections || 0}\n` +
              `  • Relationships: ${syncResult.data?.statistics?.totalRelationships || 0}\n` +
              `  • Content Nodes: ${syncResult.data?.statistics?.totalContentNodes || 0}\n\n` +
              `Check your downloads folder.`);
      } catch (syncError) {
        console.warn('⚠️ Auto-sync to Knowledge Graph failed:', syncError);
        alert(`✅ Protocol sections successfully exported as JSON!\n\n` +
              `Trial Acronym: ${exportData.studyInfo.trialAcronym}\n` +
              `Sponsor Protocol Identifier: ${exportData.studyInfo.sponsorProtocolIdentifier}\n` +
              `File: ${fileName}\n` +
              `Sections: ${hierarchicalSections.length}\n\n` +
              `⚠️ Note: Knowledge Graph sync failed: ${syncError.message}\n` +
              `You can manually sync using the "Sync to KG" button.\n\n` +
              `Check your downloads folder.`);
      }
    } catch (error) {
      console.error('Error exporting sections to JSON:', error);
      alert(`Error exporting sections to JSON: ${error.message}\nPlease try again.`);
    }
  };

  const waitForPostExportFontAck = useCallback(
    (report) =>
      new Promise((resolve) => {
        postExportFontAckRef.current = { resolve };
        setPostExportFontReport(report);
        setPostExportFontDialogOpen(true);
      }),
    [],
  );

  const handlePostExportFontDownload = useCallback(() => {
    const r = postExportFontAckRef.current?.resolve;
    postExportFontAckRef.current = null;
    setPostExportFontDialogOpen(false);
    setPostExportFontReport(null);
    r?.(true);
  }, []);

  const handlePostExportFontCancel = useCallback(() => {
    const r = postExportFontAckRef.current?.resolve;
    postExportFontAckRef.current = null;
    setPostExportFontDialogOpen(false);
    setPostExportFontReport(null);
    r?.(false);
  }, []);

  const handleExport = async () => {
    setShowValidationIndicator(false);
    setValidationReport([]);

    // Resolve the title page and SoA exactly as the export will, so the
    // checks below validate what actually ends up in the Word file.
    const titlePageConfigForExport = await resolveTitlePageConfigForExport();
    const soaDataToExport = await resolveSoaDataForExport({
      generatedSoAData,
      s9SoaTables,
      projectId: actualProjectId,
    });

    // Run all export validation checks using the reusable function
    const validationResults = await runAllExportValidations(
      sections,
      actualProjectId,
      soaDataToExport,
      titlePageConfigForExport,
    );

    const hasFailures = validationResults.some((item) => !item.passed);

    if (hasFailures) {
      // Show inline report only when export is blocked (M11, pre-export font HTML, etc.)
      setValidationReport(validationResults);
      const firstFailedResult = validationResults.find((item) => !item.passed);
      const firstErrorMessage = firstFailedResult?.errors?.[0] || firstFailedResult?.defaultMessage || 'Export blocked. Please resolve validation errors.';
      setError(firstErrorMessage);
      setShowValidationIndicator(false);
      return;
    }

    // Success path: keep the report so users can review what passed.
    setValidationReport(validationResults);
    setError(null);
    setShowValidationIndicator(true);

    await exportToWord(sections, trialSchemaImageUrl, soaDataToExport, FONT_CONFIG, titlePageConfigForExport, actualProjectId, {
      waitForUserAfterValidation: waitForPostExportFontAck,
    });
  };

  const flattenSectionTextForStyleLint = (section) => {
    const chunks = [];
    const mainRaw = section.output || section.content || '';
    if (mainRaw) {
      const t = stripHtmlTags(mainRaw).trim();
      if (t) chunks.push(t);
    }
    const walk = (subs) => {
      if (!subs || !subs.length) return;
      subs.forEach((sub) => {
        const subRaw = sub.output || sub.content || '';
        const subText = stripHtmlTags(subRaw).trim();
        const label = sub.title || sub.sectionNumber || '';
        if (label && subText) chunks.push(`${label}\n${subText}`);
        else if (subText) chunks.push(subText);
        else if (label) chunks.push(label);
        if (sub.subsections) walk(sub.subsections);
      });
    };
    walk(section.subsections);
    return chunks.join('\n\n');
  };

  const buildStyleLintDocumentPayload = () => ({
    documentId: String(actualProjectId || documentId || 'protocol-draft'),
    documentType: 'protocol',
    sections: sections
      .map((s, idx) => {
        const text = flattenSectionTextForStyleLint(s);
        const sid = String(s._id || s.sectionId || s.id || `section-${idx + 1}`);
        return {
          sectionId: sid,
          sectionTitle: s.title || '',
          sectionType: 'all_running_text',
          text,
        };
      })
      .filter((sec) => sec.text.length > 0),
  });

  const handleProtocolStyleCheck = async () => {
    setExportAnchorEl(null);
    setStyleCheckDialogOpen(true);
    setStyleCheckLoading(true);
    setStyleCheckError(null);
    setStyleCheckResult(null);
    setStyleAutofixDialogError(null);
    try {
      const documentPayload = buildStyleLintDocumentPayload();
      if (!documentPayload.sections.length) {
        setStyleCheckError('No section text to analyze. Generate or enter content first.');
        return;
      }
      const result = await styleComplianceService.lintDocument(documentPayload, {
        documentType: 'protocol',
        submissionRegion: 'US',
      });
      const typographyFindings = lintProtocolTypographyForSections(sections, FONT_CONFIG);
      setStyleCheckResult(mergeTypographyIntoLintResult(result, typographyFindings));
    } catch (e) {
      const rawMsg =
        e.response?.data?.error ||
        e.response?.data?.message ||
        e.message ||
        'Style check failed';

      // Backend misconfiguration: missing YAML rules catalog on server.
      // This is not a client-side issue; the server needs the file deployed.
      const msg = String(rawMsg || '');
      if (msg.includes('protocol_style_rules.yaml') || msg.includes('protocol_style_rules')) {
        setStyleCheckError(
          'Style check service is misconfigured: missing `config/protocol_style_rules.yaml` on the server. ' +
            'Please deploy that YAML file to the backend (e.g., `/home/site/wwwroot/config/protocol_style_rules.yaml`) or update the backend path.'
        );
      } else {
        setStyleCheckError(msg);
      }
    } finally {
      setStyleCheckLoading(false);
    }
  };

  const findSectionForStyleId = (styleSectionId) =>
    sections.find(
      (s) =>
        String(s._id) === String(styleSectionId) ||
        String(s.sectionId || '') === String(styleSectionId) ||
        String(s.id || '') === String(styleSectionId),
    );

  const getStyleFindingContextSnippet = (finding, radius = 80) => {
    if (!finding?.sectionId) return null;
    const section = findSectionForStyleId(finding.sectionId);
    if (!section) return null;
    const text = flattenSectionTextForStyleLint(section);
    if (!text) return null;

    const matched = typeof finding.matchedText === 'string' ? finding.matchedText : '';

    const startOffset = Number.isFinite(finding.startOffset) ? finding.startOffset : null;
    const endOffset = Number.isFinite(finding.endOffset) ? finding.endOffset : null;
    const hasValidOffsets =
      startOffset != null && endOffset != null && startOffset >= 0 && endOffset >= startOffset && endOffset <= text.length;

    // Prefer offsets, but ensure they actually point at the matched text.
    let matchIndex = hasValidOffsets ? startOffset : -1;
    if (matchIndex >= 0 && matched) {
      const observed = text.slice(matchIndex, Math.min(text.length, matchIndex + matched.length));
      if (observed !== matched) {
        matchIndex = -1;
      }
    }

    // If offsets aren't trustworthy, search for the matched text near the expected area, else first occurrence.
    if (matchIndex < 0 && matched) {
      const hint = hasValidOffsets ? startOffset : 0;
      const windowStart = Math.max(0, (hint ?? 0) - 400);
      const windowEnd = Math.min(text.length, (hint ?? 0) + 400);
      const window = text.slice(windowStart, windowEnd);
      const local = window.indexOf(matched);
      matchIndex = local >= 0 ? windowStart + local : text.indexOf(matched);
    }

    // Final fallback: show a slice by offsets if we have them, otherwise don't render context.
    if (matchIndex < 0) {
      if (!hasValidOffsets) return null;
      const safeStart = Math.max(0, startOffset - radius);
      const safeEnd = Math.min(text.length, endOffset + radius);
      const before = text.slice(safeStart, startOffset);
      const match = text.slice(startOffset, endOffset);
      const after = text.slice(endOffset, safeEnd);
      return { before, match, after, clippedStart: safeStart > 0, clippedEnd: safeEnd < text.length };
    }

    const baseStart = Math.max(0, matchIndex - radius);
    const baseEnd = Math.min(text.length, matchIndex + (matched ? matched.length : 0) + radius);

    // Snap to "nice" boundaries so snippets start/end at sentence-ish breaks.
    const slice = text.slice(baseStart, baseEnd);
    const localMatchStart = Math.max(0, matchIndex - baseStart);
    const localMatchEnd = Math.min(slice.length, localMatchStart + (matched ? matched.length : 0));

    const left = slice.slice(0, localMatchStart);
    const right = slice.slice(localMatchEnd);

    const lastBoundaryInLeft = Math.max(
      left.lastIndexOf('\n'),
      left.lastIndexOf('. '),
      left.lastIndexOf('? '),
      left.lastIndexOf('! '),
      left.lastIndexOf('; '),
    );
    const nextBoundaryInRightCandidates = [
      right.indexOf('\n'),
      right.indexOf('. '),
      right.indexOf('? '),
      right.indexOf('! '),
      right.indexOf('; '),
    ].filter((n) => n >= 0);
    const nextBoundaryInRight = nextBoundaryInRightCandidates.length ? Math.min(...nextBoundaryInRightCandidates) : -1;

    const niceLocalStart = lastBoundaryInLeft >= 0 ? lastBoundaryInLeft + 1 : 0;
    const niceLocalEnd = nextBoundaryInRight >= 0 ? localMatchEnd + nextBoundaryInRight + 1 : slice.length;

    const safeStart = baseStart + niceLocalStart;
    const safeEnd = baseStart + niceLocalEnd;

    const before = text.slice(safeStart, matchIndex);
    const match = matched ? text.slice(matchIndex, matchIndex + matched.length) : '';
    const after = text.slice(matchIndex + match.length, safeEnd);
    return { before, match, after, clippedStart: safeStart > 0, clippedEnd: safeEnd < text.length };
  };

  /** True if stored HTML still has real table markup (autofix cannot round-trip tables from plain text). */
  const sectionOutputHasTableMarkup = (section) => {
    const raw = section.output || section.content || '';
    return typeof raw === 'string' && /<\s*table[\s>]/i.test(raw);
  };

  /**
   * Applies server-side safe autofix to section plain text (same text used for lint) and writes HTML back via
   * markdownPlainTextToHtml (GFM: headings, pipe tables, lists). Skips subsections and real HTML tables.
   */
  const handleApplyStyleSafeAutofix = async () => {
    if (!styleCheckResult?.findings?.length) return;
    setStyleAutofixDialogError(null);
    const fixable = styleCheckResult.findings.filter((f) => f.autofixSafe && f.fix);
    if (!fixable.length) {
      setStyleAutofixDialogError('No autofixable findings in this report. Run a new check after editing.');
      return;
    }
    const bySection = new Map();
    for (const f of fixable) {
      if (!f.sectionId) continue;
      if (!bySection.has(f.sectionId)) bySection.set(f.sectionId, []);
      bySection.get(f.sectionId).push(f);
    }
    if (!bySection.size) {
      setStyleAutofixDialogError(
        'No per-section autofix targets (document-level issues must be fixed manually).',
      );
      return;
    }

    setStyleAutofixLoading(true);
    let appliedCount = 0;
    const skippedTitles = [];
    const outputBySectionId = new Map();
    try {
      for (const [sectionId, findingList] of bySection) {
        const section = findSectionForStyleId(sectionId);
        if (!section) {
          skippedTitles.push(`(unknown id ${sectionId})`);
          continue;
        }
        if (section.subsections && section.subsections.length > 0) {
          skippedTitles.push(`${section.title || sectionId} (subsections)`);
          continue;
        }
        if (sectionOutputHasTableMarkup(section)) {
          skippedTitles.push(`${section.title || sectionId} (table)`);
          continue;
        }
        const plain = flattenSectionTextForStyleLint(section);
        if (!plain.trim()) continue;
        const res = await styleComplianceService.applyAutofix(plain, findingList);
        if (res.text && res.text !== plain) {
          const html = markdownPlainTextToHtml(res.text);
          outputBySectionId.set(section._id, { html, section });
          appliedCount += res.appliedFindings?.length ?? 0;
        }
      }
      if (outputBySectionId.size) {
        setSections((prev) =>
          prev.map((s) => {
            const upd = outputBySectionId.get(s._id);
            return upd ? { ...s, output: upd.html } : s;
          }),
        );
        for (const { html, section } of outputBySectionId.values()) {
          try {
            await documentEditorService.updateSection(
              section._id,
              {
                title: section.title,
                context: section.context,
                prompt: section.prompt,
                output: html,
                documentType: 'PROTOCOL',
              },
              actualProjectId,
            );
          } catch (persistErr) {
            console.error('Failed to persist autofixed section:', persistErr);
          }
        }
      }
      let msg = appliedCount
        ? `Applied ${appliedCount} safe fix(es). Section HTML was rebuilt from markdown (headings, tables, lists preserved where possible); review formatting.`
        : 'No text changes were applied (fixes may overlap or no longer match).';
      if (skippedTitles.length) {
        msg += ` Skipped ${skippedTitles.length} section(s): ${skippedTitles.slice(0, 5).join('; ')}${skippedTitles.length > 5 ? '…' : ''}`;
      }
      setStyleCheckResult(null);
      setStyleCheckDialogOpen(false);
      setStyleAutofixSnackbar({ open: true, message: msg });
    } catch (e) {
      setStyleAutofixDialogError(e.response?.data?.error || e.message || 'Autofix failed');
    } finally {
      setStyleAutofixLoading(false);
    }
  };

  // Section HTML->PDF removed (not required)

  const buildProtocolHtml = () => {
    const css = `
      <style>
        @page { margin: 20mm; }
        @page landscapeSOA { size: A4 landscape; margin: 12mm; }
        body { font-family: 'Times New Roman', serif; font-size: 12pt; line-height: 1.35; }
        h1 { font-size: ${FONT_CONFIG?.heading?.size ? FONT_CONFIG.heading.size + 4 : 18}pt; font-weight: 700; color: #2F5496; margin: 18pt 0 12pt; }
        h2 { font-size: ${FONT_CONFIG?.heading?.size ? FONT_CONFIG.heading.size : 16}pt; font-weight: 700; color: #2F5496; margin: 16pt 0 10pt; }
        p { margin: 6pt 0; text-align: justify; }
        .break-before { break-before: page; }
        .soa-section { page: landscapeSOA; }
        table { width: 100%; border-collapse: collapse; }
        th, td { border: 1px solid #000; padding: 6pt 8pt; vertical-align: top; }
      </style>`;

    const escape = (s) => String(s || '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');

    const parts = [];
    // Optional document title
    parts.push(`<h1>Clinical Trial Protocol</h1>`);

    let encountered = false;
    for (const section of sections) {
      if (!section?.output || !section?.title) continue;
      const isSOA = /SCHEDULE OF ACTIVITIES|\bSOA\b/i.test(section.title);
      const klass = `${encountered ? 'break-before' : ''} ${isSOA ? 'soa-section' : ''}`.trim();

      // Treat output: if it looks like HTML (e.g., contains tags), keep as-is; else escape and convert newlines
      const looksHtml = /<\w+[^>]*>/i.test(section.output);
      const contentHtml = looksHtml
        ? section.output
        : `<p>${escape(section.output).replace(/\n\n+/g,'</p><p>').replace(/\n/g,'<br>')}</p>`;

      parts.push(`<section class="${klass}"><h2>${escape(section.title)}</h2>${contentHtml}</section>`);
      encountered = true;
    }

    return `<!doctype html><html><head><meta charset="utf-8">${css}</head><body>${parts.join('\n')}</body></html>`;
  };

  const downloadProtocolPdfBlob = async (blob) => {
    if (!blob || blob.size < 100) {
      throw new Error('PDF generation produced an empty file. Check the browser console for details.');
    }
    if (actualProjectId) {
      try {
        await documentExportService.saveProtocolPdfToBlob(actualProjectId, blob, {
          totalSections: sections?.length || 0,
          hasSoA: !!generatedSoAData,
          source: 'manual-export',
          documentType: 'Protocol',
        });
      } catch (saveErr) {
        console.error('Failed to save protocol PDF to blob/Mongo:', saveErr?.response?.data || saveErr?.message || saveErr);
      }
    }
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'clinical-trial-protocol.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const convertDocxToPdfViaGraph = async (docxBlob) => {
    const authInit = await microsoftAuthService.getAccessToken();
    if (!authInit.success) {
      const init = await microsoftAuthService.initializeAuth();
      if (init.needsAuth) {
        microsoftAuthService.startAuthFlow();
        return null;
      }
    }
    const tokenRes = await microsoftAuthService.getAccessToken();
    if (!tokenRes.success) throw new Error('Microsoft authentication required');
    microsoftGraphService.setAccessToken(tokenRes.token);

    const fileName = `clinical-trial-protocol-${Date.now()}.docx`;
    const up = await microsoftGraphService.uploadToOneDrive(fileName, docxBlob, '/Documents');
    if (!up.success || !up.fileId) throw new Error(up.error || 'Upload failed');

    const pdfRes = await microsoftGraphService.downloadAsPdf(up.fileId);
    microsoftGraphService.deleteFile(up.fileId);
    if (!pdfRes.success) throw new Error(pdfRes.error || 'PDF conversion failed');
    return pdfRes.blob;
  };

  const handleExportFullPDF = async () => {
    if (isExportingPdf) return;
    if (countExportableProtocolSections(sections) === 0) {
      alert('No protocol content to export. Generate sections first.');
      return;
    }
    setIsExportingPdf(true);
    setError(null);
    startSpan('ui_protocol_export_pdf_word', { page: 'protocol_editor', meta: { projectId: actualProjectId || null } });
    try {
      const docxBlob = await createWordDocument(sections, trialSchemaImageUrl);
      let pdfBlob = null;

      try {
        pdfBlob = await documentEditorService.convertDocxToPdf(docxBlob);
      } catch (serverErr) {
        console.warn('Server DOCX→PDF conversion failed, trying Microsoft Graph:', serverErr?.response?.data || serverErr?.message || serverErr);
      }

      if (!pdfBlob || pdfBlob.size < 100) {
        pdfBlob = await convertDocxToPdfViaGraph(docxBlob);
        if (!pdfBlob) {
          endSpan('ui_protocol_export_pdf_word', { status: 'auth_pending' });
          return;
        }
      }

      await downloadProtocolPdfBlob(pdfBlob);
      endSpan('ui_protocol_export_pdf_word', { status: 'ok' });
    } catch (e) {
      console.error('Failed to export full PDF:', e);
      const msg = e?.message || 'Failed to export PDF from Word. Try signing in to Microsoft or use Export to PDF (Native).';
      setError(msg);
      alert(msg);
      endSpan('ui_protocol_export_pdf_word', { status: 'error' });
    } finally {
      setIsExportingPdf(false);
    }
  };
  // Native PDF export using @react-pdf/renderer (no external converters)
  // Saves PDF to blob storage and metadata to Mongo, then triggers download
  const buildNativeProtocolPdfBlob = async () => {
    let soaDataToExport = await resolveSoaDataForExport({
      generatedSoAData,
      s9SoaTables,
      projectId: actualProjectId,
    });

    if (!hasUsableSoaExportData(soaDataToExport)) {
      soaDataToExport = null;
    }

    let titlePageConfigForExport = null;
    let headerFooterInfo = null;
    if (actualProjectId) {
      try {
        titlePageConfigForExport = await resolveTitlePageConfigForExport();
        headerFooterInfo = await resolveProtocolHeaderFooterInfo(actualProjectId, titlePageConfigForExport);
      } catch (hfErr) {
        console.warn('Could not resolve PDF title page / header/footer info:', hfErr);
      }
    }

    await registerPdfFonts();

    const pdfTrialSchemaUrl = trialSchemaImageUrl
      ? await resolveImageUrlForNativePdf(trialSchemaImageUrl)
      : null;

    return renderNativeProtocolPdfBlobWithDiagnosis({
      sections: getProtocolSectionsForExport(sections),
      fontConfig: FONT_CONFIG,
      soaData: soaDataToExport,
      trialSchemaImageUrl: pdfTrialSchemaUrl,
      headerFooterInfo,
      titlePageConfig: titlePageConfigForExport,
    });
  };

  const handleExportFullPDFNative = async () => {
    if (isExportingPdf) return;
    const exportableCount = countExportableProtocolSections(sections);
    if (exportableCount === 0) {
      alert('No protocol content to export. Generate sections first.');
      return;
    }

    setIsExportingPdf(true);
    setError(null);
    startSpan('ui_protocol_export_pdf_native', { page: 'protocol_editor', meta: { projectId: actualProjectId || null } });
    try {
      const blob = await buildNativeProtocolPdfBlob();
      await downloadProtocolPdfBlob(blob);
      endSpan('ui_protocol_export_pdf_native', { status: 'ok' });
    } catch (e) {
      console.error('Native PDF export failed:', e);
      const msg = e?.message || 'Failed to export PDF.';
      setError(msg);
      alert(`Failed to export PDF: ${msg}`);
      endSpan('ui_protocol_export_pdf_native', { status: 'error' });
    } finally {
      setIsExportingPdf(false);
    }
  };

  // Same "Publish to External SDR" action as CanonicalDataset.jsx's Core
  // Datasheet tab (protocolRegistrationService.publishUsdmToExternalSdr) —
  // the server derives the USDM it publishes from project.protocolXml, so
  // this first (re)saves that XML from the current editor content (without
  // triggering "Export to XML"'s browser download) so the publish reflects
  // what's actually being edited here, not a stale/never-generated copy.
  const handlePublishToExternalSdr = async () => {
    if (!actualProjectId || externalSdrPublishing) return;
    setExternalSdrPublishing(true);
    try {
      try {
        await buildAndSaveProtocolXml(sections, generatedSoAData, trialSchemaImageUrl, actualProjectId, titlePageConfig);
      } catch (xmlErr) {
        console.error('Error refreshing protocol XML before External SDR publish:', xmlErr);
      }

      let res;
      try {
        res = await protocolRegistrationService.publishUsdmToExternalSdr(actualProjectId);
      } catch (firstErr) {
        const body = firstErr.response?.data;
        const isCoreEngineCrash =
          firstErr.response?.status === 502 &&
          (body?.message?.includes('conformance') || body?.error?.message?.includes('CDISC Rules Engine'));

        if (isCoreEngineCrash) {
          // Auto-retry: CDISC Rules Engine crash inside SDR container (known bug).
          // Skip external conformance (local CORE already passed) and go straight to publish.
          console.warn('[Publish External SDR] CDISC engine crash detected — retrying without external conformance validation.');
          res = await protocolRegistrationService.publishUsdmToExternalSdr(actualProjectId, { skipConformance: true });
        } else {
          throw firstErr;
        }
      }

      if (res?.success) {
        const studyId = res.externalPublish?.study?.id || res.externalPublish?.studyId || res.externalPublish?.id || '';
        const warnMsg = res.conformanceWarning?.skipped
          ? ' (conformance validation skipped — CDISC engine bug in SDR container)'
          : '';
        setExternalSdrSnackbar({
          open: true,
          message: `Published to external SDR successfully${studyId ? ` · studyId: ${studyId}` : ''}${warnMsg}`,
          severity: res.conformanceWarning?.skipped ? 'warning' : 'success',
        });
        if (res.dbPersisted === false) {
          setExternalSdrSnackbar({
            open: true,
            message: `Warning: published to the external SDR, but the local copy failed to save (${res.persistWarning || 'unknown error'}).`,
            severity: 'error',
          });
        }
      } else {
        setExternalSdrSnackbar({ open: true, message: res?.message || 'Failed to publish to external SDR', severity: 'error' });
      }
    } catch (err) {
      const data = err.response?.data;
      const msg = data?.message || data?.error || err.message || 'Failed to publish to external SDR';
      setExternalSdrSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setExternalSdrPublishing(false);
    }
  };
  // ADK Workflow Handlers
  const handleWorkflowInitiated = (result) => {
    setCurrentApprovalRequest({
      approvalRequestId: result.approvalRequestId,
      workflowId: result.workflowId
    });
    setWorkflowPlan(result.plan);
    setApprovalDialogOpen(true);
  };

  const handleApprovalApproved = (result) => {
    // Show success message
    alert(`Modification plan approved! ${result.results?.length || 0} section(s) will be updated.`);
    // Optionally refresh sections
    // loadSections();
  };

  const handleApprovalRejected = (approvalRequestId) => {
    alert('Modification plan has been rejected.');
  };

  const handleSendToWorkflow = async () => {
    try {
      // First export the document to Word
      const doc = await createWordDocument(sections, trialSchemaImageUrl);
      
      // Create a FormData object to send the file
      const formData = new FormData();
      formData.append('document', doc, 'clinical-trial-protocol.docx');
      formData.append('projectId', actualProjectId);
      formData.append('documentType', 'PROTOCOL');
      formData.append('title', `Clinical Trial Protocol - ${new Date().toLocaleDateString()}`);
      
      // Get the auth token from localStorage
      const token = getToken();
      if (!token) {
        throw new Error('No authentication token found. Please log in again.');
      }
      
      // Send to workflow API with proper headers
      const response = await fetch(`${API_BASE_URL}/workflows/workflow/create`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (response.ok) {
        const result = await response.json();
        // Navigate to the workflow page using workflowId
        navigate(`/documents/workflow/${result.workflowId}`);
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error sending to workflow:', error);
      
      // Show user-friendly error message
      if (error.message.includes('authentication') || error.message.includes('401')) {
        alert('Authentication error. Please log in again and try sending to workflow.');
      } else if (error.message.includes('CORS')) {
        alert('Document exported successfully. CORS issue with image prevented workflow creation. Please manually upload the exported document to workflow.');
        const soaDataToExport = await resolveSoaDataForExport({
          generatedSoAData,
          s9SoaTables,
          projectId: actualProjectId,
        });
        exportToWord(sections, trialSchemaImageUrl, soaDataToExport, FONT_CONFIG, null, actualProjectId, null);
      } else {
        alert(`Error: ${error.message}. Document has been exported locally. Please manually upload to workflow system.`);
        const soaDataToExport = await resolveSoaDataForExport({
          generatedSoAData,
          s9SoaTables,
          projectId: actualProjectId,
        });
        exportToWord(sections, trialSchemaImageUrl, soaDataToExport, FONT_CONFIG, null, actualProjectId, null);
      }
    }
  };
  const createWordDocument = async (sections, trialSchemaImageUrl) => {
    const children = [];
    const exportSections = getProtocolSectionsForExport(sections);

    // Add document title
    children.push(
      new Paragraph({
        text: "Clinical Trial Protocol",
        style: "CTitle",
        outlineLevel: 0
      }),
      new Paragraph({ text: '' })
    );

    // Add all sections with their content
  let _encounteredSectionForCreateDoc = false;
    for (const section of exportSections) {
      const isAppendixSection = isM11AppendixSectionTitle(section?.title);
      const hasOutput = section.output && section.output.trim();
      if (!hasOutput && !isAppendixSection) continue;

        // Apply content separation to fix concatenated subsections
        const sectionRootMatch = section.title.match(/^(\d+(?:\.\d+)*)/);
        const sectionRootPrefix = sectionRootMatch ? sectionRootMatch[1] : null;
        const processedContent = hasOutput
          ? processContentForProperSubsections(section.output, section.title, sectionRootPrefix)
          : 'Not applicable.';
        
      // Ensure each main section starts on a new page (skip before the first)
      if (_encounteredSectionForCreateDoc) {
        children.push(new Paragraph({ children: [], pageBreakBefore: true }));
      }
        
        children.push(
          new Paragraph({
            text: section.title,
            style: "CHeading2",
            outlineLevel: 1
          }),
          new Paragraph({
            text: processedContent
          }),
          new Paragraph({ text: '' })
        );

      _encounteredSectionForCreateDoc = true;
    }

    const doc = new Document({
      sections: [{ children }],
    });

    return await Packer.toBlob(doc);
  };
  // True when this protocol was produced by "Generate from Uploaded USDM
  // Catalogue" (sections carry generationSource: 'usdm'). Regenerate, Add
  // section and the Approach buttons then stay on the USDM path: their Clinical
  // Intake prompts and example context files would otherwise pull an unrelated
  // intake record and example objectives/criteria into a USDM-based protocol.
  const isUsdmGeneratedProtocol = (section = null) =>
    section?.generationSource === 'usdm' || sections.some((s) => s.generationSource === 'usdm');

  // Builds one section exactly the way "Generate from Uploaded USDM Catalogue"
  // does (same facts, context, template-field text, prompt and validation).
  const generateUsdmSectionOutput = async (sectionTitle, userInstructions = '') => {
    const latestCatalogueRaw = await protocolUsdmCatalogueService.getLatestCatalogue(actualProjectId);
    if (!latestCatalogueRaw) {
      throw new Error('No USDM catalogue for current project. Upload or fetch a USDM catalogue first.');
    }
    const usdmSection = findPredefinedSection(getUsdmProtocolSections(), sectionTitle);
    if (!usdmSection) {
      throw new Error(`No USDM prompt exists for section "${sectionTitle}".`);
    }
    const usdmFacts = normalizeUsdmCatalogue(latestCatalogueRaw);

    if (usdmSection.title === '1.3 SCHEDULE OF ACTIVITIES') {
      const scheduleHtml = buildScheduleOfActivitiesHtml(scheduleOfActivitiesDataFromFacts(usdmFacts));
      if (scheduleHtml) return { output: scheduleHtml, context: '', usdmFacts, usdmSection };
    }

    const templateLanguageData = await fetchTemplateLanguageForUsdm(actualProjectId);
    let context = mergeTemplateLanguageBlock(buildUsdmCatalogueGlobalContext(usdmFacts), templateLanguageData);

    const { templateFieldSelections, templateFieldValues } =
      await protocolUsdmCatalogueService.getTemplateFieldState(actualProjectId);
    const fieldTexts = Object.entries(templateFieldSelections || {})
      .filter(([fieldName, checked]) =>
        checked &&
        (templateFieldValues || {})[fieldName] &&
        resolveUsdmSectionForField(fieldName)?.title === usdmSection.title)
      .map(([fieldName]) => templateFieldValues[fieldName]);
    if (fieldTexts.length) {
      context += `\n\n--- PRE-FINALIZED SUBSECTIONS (from USDM Template tab — incorporate verbatim, do not rewrite) ---\n${fieldTexts.join('\n\n')}`;
    }
    if (userInstructions && userInstructions.trim()) {
      context += `\n\n--- ADDITIONAL USER INSTRUCTIONS (apply within the MISSING-DATA RULE; never add study facts that are not in the data) ---\n${userInstructions.trim()}`;
    }

    const studyData = buildUsdmSectionStudyData(usdmFacts, usdmSection.prompt || '');
    let output = await generateContent(
      usdmSection.prompt || '',
      context,
      JSON.stringify(studyData),
      'protocol-usdm',
      usdmSection.title,
      usdmSection,
      buildGenerationStylePayload(null, currentProject),
    );
    try {
      const validationResult = await validateAndRegenerateSection(
        usdmSection.title,
        output,
        usdmSection.prompt || '',
        buildUsdmValidationContext(context, studyData),
        3,
        actualProjectId,
        { source: 'usdm', studyData: JSON.stringify(usdmFacts) }
      );
      if (validationResult.success) output = validationResult.finalContent;
    } catch (error) {
      console.error(`❌ ${usdmSection.title} validation error in USDM regeneration:`, error);
    }
    output = finalizeGeneratedProtocolOutput(output, usdmSection.title);
    return { output, context, usdmFacts, usdmSection };
  };

  // Regenerates and saves one section of a USDM-generated protocol. Returns
  // true on success. On failure the section keeps its previous content.
  const regenerateSectionFromUsdm = async (section) => {
    const _id = section._id;
    setIsGenerating(prev => ({ ...prev, [_id]: true }));
    setSections(prevSections =>
      prevSections.map(s =>
        s._id === _id ? { ...s, output: 'Regenerating content from USDM study data...' } : s
      )
    );
    try {
      const { output } = await generateUsdmSectionOutput(section.title, section.prompt);

      setSections(prevSections =>
        prevSections.map(s => (s._id === _id ? { ...s, output, prompt: '' } : s))
      );
      try {
        await documentEditorService.updateSection(_id, {
          title: section.title,
          context: section.context,
          prompt: '',
          output,
          documentType: 'PROTOCOL',
          provenance: 'aidrafted'
        }, actualProjectId);
        await documentEditorService.addNewVersion({
          sectionId: _id,
          title: section.title,
          context: section.context,
          prompt: `${section.prompt || ''} [Regenerated from USDM study data]`.trim(),
          output,
          documentType: 'PROTOCOL'
        }, actualProjectId);
      } catch (updateErr) {
        console.error('Failed to save USDM-regenerated section:', updateErr);
      }
      return true;
    } catch (error) {
      console.error(`Failed to regenerate ${section.title} from USDM:`, error);
      setSections(prevSections =>
        prevSections.map(s => (s._id === _id ? { ...s, output: section.output } : s))
      );
      setError(`Failed to regenerate "${section.title}" from USDM study data: ${error.message}`);
      return false;
    } finally {
      setIsGenerating(prev => {
        const updated = { ...prev };
        delete updated[_id];
        return updated;
      });
    }
  };

  // Function to regenerate multiple sections
  const handleRegenerateSections = async (sectionTitles) => {
    if (!sectionTitles || sectionTitles.length === 0) {
      console.warn('No sections specified for regeneration');
      return;
    }

    try {
      // Find sections by title
      const sectionsToRegenerate = sections.filter(s => sectionTitles.includes(s.title));

      if (sectionsToRegenerate.length !== sectionTitles.length) {
        const matchedTitles = new Set(sectionsToRegenerate.map(s => s.title));
        const unmatchedTitles = sectionTitles.filter(t => !matchedTitles.has(t));
        console.warn('Some selected section titles did not match any section in this document:', unmatchedTitles);
      }

      if (sectionsToRegenerate.length === 0) {
        console.warn('No matching sections found for regeneration');
        setRegenerateSummarySnackbar({
          open: true,
          severity: 'error',
          message: 'Regeneration failed: none of the selected sections matched a section in this document.',
        });
        return;
      }

      const regenerationOutcomes = { succeeded: [], failed: [] };

      // USDM-generated protocol: regenerate from the USDM catalogue, never from Clinical Intake.
      if (isUsdmGeneratedProtocol()) {
        for (const section of sectionsToRegenerate) {
          const ok = await regenerateSectionFromUsdm(section);
          (ok ? regenerationOutcomes.succeeded : regenerationOutcomes.failed).push(section.title);
        }
        const { succeeded, failed } = regenerationOutcomes;
        setRegenerateSummarySnackbar({
          open: true,
          severity: failed.length === 0 ? 'success' : succeeded.length > 0 ? 'warning' : 'error',
          message: failed.length === 0
            ? `Regenerated ${succeeded.length} section${succeeded.length === 1 ? '' : 's'} from USDM study data: ${succeeded.join(', ')}`
            : `Regenerated ${succeeded.length} of ${succeeded.length + failed.length} section(s) from USDM study data. Failed: ${failed.join(', ')}`,
        });
        return;
      }

      // IMPORTANT: Use the same workflow as initial "Generate from Clinical Intake":
      // - fetch latestIntake once
      // - for each section: generateContent(section.prompt, section.context, filteredIntakeData, ...)
      // - then validateAndRegenerateSection with the current project's prompt and section.context
      const latestIntake = await clinicalIntakeService.getLatestClinicalIntake(actualProjectId);
      const predefinedSectionsAll = await getPredefinedSections(actualProjectId);
      
      // Regenerate each section
      for (const section of sectionsToRegenerate) {
        try {
          setIsGenerating(prev => ({...prev, [section._id]: true}));
          
          // Update UI to show regeneration in progress
          setSections(prevSections =>
            prevSections.map(s =>
              s._id === section._id ? { ...s, output: 'Regenerating content...' } : s
            )
          );

          // Filter clinical intake data for this specific section (same as initial generation)
          const filteredIntakeData = filterClinicalIntakeForSection(latestIntake, section.title);

          // Use the current project's predefined prompt/context (same as initial generation)
          const predefinedSection = findPredefinedSection(predefinedSectionsAll, section.title) || section;
          const promptForGen = predefinedSection.prompt || '';
          const contextForGen = predefinedSection.context || '';

          // Generate content using the same call signature as initial generation
          let generatedContent = await generateContent(
            promptForGen,
            contextForGen,
            JSON.stringify(filteredIntakeData),
            'protocol',
            section.title,
            predefinedSection,
            buildGenerationStylePayload(latestIntake, currentProject),
          );

          // Apply the same universal validation/regeneration wrapper
          try {
            const validationResult = await validateAndRegenerateSection(
              section.title,
              generatedContent,
              promptForGen,
              contextForGen,
              3,
              actualProjectId
            );
            if (validationResult.success) {
              generatedContent = validationResult.finalContent;
              section.validationResult = validationResult.finalValidation;
            } else {
              section.validationResult = validationResult.finalValidation || { isValid: false, overallScore: 0 };
            }
          } catch (error) {
            console.error(`Validation error for ${section.title}:`, error);
          }

          generatedContent = finalizeGeneratedProtocolOutput(generatedContent, section.title);

          if (
            String(section.title || '').match(/^12\b/) ||
            String(section.title || '').toUpperCase().includes('APPENDIX: SUPPORTING DETAILS')
          ) {
            generatedContent = mergeAppendicesIntoSection12Output(
              generatedContent,
              latestIntake?.appendices,
            );
          }

          // Update section
          const updatedSection = {
            ...section,
            output: generatedContent,
            final: true,
            provenance: 'aidrafted',
            // preserve/attach validationResult when available
            validationResult: section.validationResult
          };

          // Save to database
          await documentEditorService.updateSection(section._id, updatedSection, actualProjectId);

          // Update UI
          setSections(prevSections =>
            prevSections.map(s =>
              s._id === section._id ? updatedSection : s
            )
          );

          regenerationOutcomes.succeeded.push(section.title);
        } catch (error) {
          console.error(`Error regenerating ${section.title}:`, error);
          setSections(prevSections =>
            prevSections.map(s =>
              s._id === section._id ? { ...s, output: `Error: ${error.message}` } : s
            )
          );
          regenerationOutcomes.failed.push(section.title);
        } finally {
          setIsGenerating(prev => ({...prev, [section._id]: false}));
        }
      }

      // Surface a clear summary — regeneration runs one section at a time in the
      // background, so without this a partial failure looks identical to
      // "only one section changed".
      const { succeeded, failed } = regenerationOutcomes;
      if (failed.length === 0) {
        setRegenerateSummarySnackbar({
          open: true,
          severity: 'success',
          message: `Regenerated ${succeeded.length} section${succeeded.length === 1 ? '' : 's'}: ${succeeded.join(', ')}`,
        });
      } else {
        setRegenerateSummarySnackbar({
          open: true,
          severity: succeeded.length > 0 ? 'warning' : 'error',
          message: `Regenerated ${succeeded.length} of ${succeeded.length + failed.length} section(s). Failed: ${failed.join(', ')}`,
        });
      }

      // After successful regeneration, acknowledge changes (update lockedVersion)
      // This will make the change indicator disappear
      try {
        await clinicalIntakeService.acknowledgeClinicalIntakeChanges(actualProjectId);
      } catch (ackError) {
        console.error('Error acknowledging changes:', ackError);
        // Don't fail regeneration if acknowledge fails
      }
    } catch (error) {
      console.error('Error in handleRegenerateSections:', error);
      setError(`Failed to regenerate sections: ${error.message}`);
    }
  };

  const handleGenerateContent = async (_id) => {
    const section = sections.find((s) => s._id === _id);
    if (!section) return;
    if (isUsdmGeneratedProtocol(section)) {
      await regenerateSectionFromUsdm(section);
      return;
    }

    if (!actualProjectId) {
      console.error('Cannot regenerate section without current projectId');
      setError('Open a project before regenerating sections.');
      return;
    }
  
    setIsGenerating(prev => ({...prev, [_id]: true}));
  
    try {
      // 1. Fetch all prior versions for this section
      const versionsResp = await documentEditorService.getAllVersions(_id, actualProjectId); // Pass projectId
      const priorVersions = (versionsResp?.data?.versions || []);
      
      // Sort versions by version number to ensure chronological order
      priorVersions.sort((a, b) => a.versionNumber - b.versionNumber);
  
      // 2. Fetch all sections (to check for finalized dependencies)
      const allSections = await documentEditorService.getAllSections(actualProjectId); // Pass projectId
  
      // 3. Fetch the latest clinical intake form
      const clinicalIntake = await clinicalIntakeService.fetchLatestClinicalIntakeData(actualProjectId);
  
      // 4. Get the original prompt from predefined sections
      const predefinedSections = await getPredefinedSections(actualProjectId); // Pass projectId
      const originalSection = findPredefinedSection(predefinedSections, section.title);
      const originalPrompt = originalSection?.prompt || '';
  
      // 5. Build structured context
      let context = '';
      
      // Add clinical intake form first as base context
      context += 'Base Clinical Intake Form:\n' + JSON.stringify(clinicalIntake, null, 2) + '\n\n';
      
      // Add original prompt from constants if it exists
      if (originalPrompt) {
        context += 'Original Section Prompt:\n' + originalPrompt + '\n\n';
      }
      
      // Add prior versions in chronological order
      if (priorVersions.length > 0) {
        context += 'Version History:\n';
        priorVersions.forEach((version, index) => {
          context += `\nVersion ${version.versionNumber}:\n`;
          if (version.prompt) {
            context += `User Prompt: ${version.prompt}\n`;
          }
          if (version.output) {
            context += `Output: ${version.output}\n`;
          }
          context += '---' + '\n';
        });
      }
  
      // 6. Add dependency context if needed
      if (section.context) {
        const dependencyTitles = Array.from(section.context.matchAll(/Section: ([^\n]+)/g)).map(m => m[1].trim());
        if (dependencyTitles.length > 0) {
          context += '\nDependencies:\n';
        for (const depTitle of dependencyTitles) {
          const depSection = allSections.find(s => s.title === depTitle && s.final);
          if (depSection) {
              context += `\n[${depTitle}]\n${depSection.output}\n`;
            }
          }
        }
      }
  
      // Filter clinical intake data for this specific section
      const latestIntake = await clinicalIntakeService.getLatestClinicalIntake(actualProjectId);
      const filteredIntakeData = filterClinicalIntakeForSection(latestIntake, section.title);
      
      // Debug logging for chunked generation
      
      // Additional debugging for Section 4
      
      
      // Generate content with the filtered context
      let generatedContent = await generateContent(
        section.prompt,
        context,
        JSON.stringify(filteredIntakeData),
        'protocol',
        section.title,
        section,
        buildGenerationStylePayload(latestIntake, currentProject),
      );

      // Apply validation and regeneration for all sections
      try {
        
        // Get the original prompt from predefined sections (current project's prompts, not stored DB prompt)
        const predefinedSectionsForRegen = await getPredefinedSections(actualProjectId);
        const originalSectionForRegen = findPredefinedSection(predefinedSectionsForRegen, section.title);
        const currentPrompt = originalSectionForRegen?.prompt || section.prompt || '';
        
        
        const validationResult = await validateAndRegenerateSection(
          section.title,
          generatedContent,
          currentPrompt, // Use current project's prompt, not stored DB prompt
          context,
          3, // max attempts
          actualProjectId // Pass projectId to fetch fresh clinical intake data
        );
        
        if (validationResult.success) {
          generatedContent = validationResult.finalContent;
          
          // Store validation result in section data
          section.validationResult = validationResult.finalValidation;
        } else {
          console.warn(`⚠️ ${section.title} validation failed, using original content`);
          // Store failed validation result
          section.validationResult = validationResult.finalValidation || { isValid: false, overallScore: 0 };
        }
      } catch (error) {
        console.error(`❌ ${section.title} validation error:`, error);
        // Continue with original content if validation fails
      }

      generatedContent = finalizeGeneratedProtocolOutput(generatedContent, section.title);

      // Section 12: append uploaded appendix HTML (text + images) from Clinical Intake
      if (
        String(section.title || '').match(/^12\b/) ||
        String(section.title || '').toUpperCase().includes('APPENDIX: SUPPORTING DETAILS')
      ) {
        generatedContent = mergeAppendicesIntoSection12Output(
          generatedContent,
          latestIntake?.appendices,
        );
      }
  
      // Update UI
      setSections(prevSections =>
        prevSections.map(s =>
          s._id === _id ? { ...s, output: generatedContent, prompt: '' } : s
        )
      );
      // Update backend
      try {
        const originalPromptForVersion = section.prompt; // Store the original prompt before clearing

        await documentEditorService.updateSection(_id, {
          title: section.title,
          context: section.context,
          prompt: '', // Clear prompt in the database for the section
          output: generatedContent,
          documentType: 'PROTOCOL',
          provenance: 'aidrafted'
        }, actualProjectId);
        
        // Add new version with the original prompt ONLY if it's the first update
        
          await documentEditorService.addNewVersion({
            sectionId: _id,
            title: section.title,
            context: section.context,
            prompt: originalPromptForVersion, // Always use full prompt
            output: generatedContent,
            documentType: 'PROTOCOL'
          }, actualProjectId);
        }
       catch (updateErr) {
        console.error("Failed to update section in backend:", updateErr);
      }
    } catch (error) {
      console.error("Failed to generate content:", error);
      setSections(prevSections =>
        prevSections.map(s =>
          s._id === _id ? { ...s, output: "Error generating content. Please try again." } : s
        )
      );
    } finally {
      setIsGenerating(prev => {
        const updated = {...prev};
        delete updated[_id];
        return updated;
      });
    }
  };

  /**
   * SECTION 10 APPROACH 1: Context-Augmented Generation
   * Adds the statistical context file as additional input with minimal changes to current flow
   */
  const handleGenerateSection10Approach1 = async (_id) => {
    const section = sections.find((s) => s._id === _id);
    if (!section) return;
    if (isUsdmGeneratedProtocol(section)) {
      // USDM-generated protocol: approach context files hold example content — stay on USDM data.
      await regenerateSectionFromUsdm(section);
      return;
    }
  
    setIsGenerating(prev => ({...prev, [_id]: true}));
  
    try {
      // Get the latest clinical intake form
      const clinicalIntake = await clinicalIntakeService.fetchLatestClinicalIntakeData(actualProjectId);
      
      // Get the original prompt from predefined sections
      const predefinedSections = await getPredefinedSections(actualProjectId);
      const originalSection = findPredefinedSection(predefinedSections, section.title);
      const originalPrompt = originalSection?.prompt || section.prompt || '';
      
      // Build context from other sections
      // For Section 10, automatically include Sections 3, 6, 7 (required per Section 10 prompt)
      let context = '';
      const allSections = await documentEditorService.getAllSections(actualProjectId);
      
      // Section 10 specifically needs Sections 3, 6, 7 for consistency
      const requiredSections = [
        '3. TRIAL OBJECTIVES AND ASSOCIATED ESTIMANDS',
        '6. TRIAL INTERVENTION AND CONCOMITANT THERAPY',
        '7. PARTICIPANT DISCONTINUATION OF TRIAL INTERVENTION AND DISCONTINUATION OR WITHDRAWAL FROM TRIAL'
      ];
      
      context += 'Contextual Sections (for consistency checking):\n';
      for (const sectionTitle of requiredSections) {
        const depSection = allSections.find(s => s.title === sectionTitle && s.final);
        if (depSection) {
          context += `\n[${depSection.title}]\n${depSection.output}\n`;
        } else {
        }
      }
      
      // Also check for any additional dependencies in section.context
      if (section.context) {
        const dependencyTitles = Array.from(section.context.matchAll(/Section: ([^\n]+)/g)).map(m => m[1].trim());
        for (const depTitle of dependencyTitles) {
          // Skip if already included above
          if (!requiredSections.includes(depTitle)) {
            const depSection = allSections.find(s => s.title === depTitle && s.final);
            if (depSection) {
              context += `\n[${depTitle}]\n${depSection.output}\n`;
            }
          }
        }
      }
      
      // Filter clinical intake data for Section 10
      const latestIntake = await clinicalIntakeService.getLatestClinicalIntake(actualProjectId);
      const filteredIntakeData = filterClinicalIntakeForSection(latestIntake, section.title);
      
      // Call Approach 1 endpoint
      const response = await axios.post(API_ENDPOINTS.ai.section10Approach1, {
        context: context,
        prompt: originalPrompt,
        output: JSON.stringify(filteredIntakeData),
        sectionTitle: section.title,
        ...buildGenerationStylePayload(latestIntake, currentProject),
      });
      
      let generatedContent = response.data.result;
      
      // Update UI
      setSections(prevSections =>
        prevSections.map(s =>
          s._id === _id ? { ...s, output: generatedContent, prompt: '' } : s
        )
      );
      
      // Update backend
      try {
        const originalPromptForVersion = section.prompt;
        
        await documentEditorService.updateSection(_id, {
          title: section.title,
          context: section.context,
          prompt: '',
          output: generatedContent,
          documentType: 'PROTOCOL',
          provenance: 'aidrafted'
        }, actualProjectId);
        
        await documentEditorService.addNewVersion({
          sectionId: _id,
          title: section.title,
          context: section.context,
          prompt: originalPromptForVersion + ' [Approach 1: Context-Augmented]',
          output: generatedContent,
          documentType: 'PROTOCOL'
        }, actualProjectId);
      } catch (updateErr) {
        console.error("Failed to update section in backend:", updateErr);
      }
    } catch (error) {
      console.error("Failed to generate Section 10 (Approach 1):", error);
      setSections(prevSections =>
        prevSections.map(s =>
          s._id === _id ? { ...s, output: "Error generating content. Please try again." } : s
        )
      );
    } finally {
      setIsGenerating(prev => {
        const updated = {...prev};
        delete updated[_id];
        return updated;
      });
    }
  };

  /**
   * SECTION 10 APPROACH 2: Full Protocol-Writer Mode
   * Uses a fully-specified system prompt with strict role, objective, and output structure
   */
  const handleGenerateSection10Approach2 = async (_id) => {
    const section = sections.find((s) => s._id === _id);
    if (!section) return;
    if (isUsdmGeneratedProtocol(section)) {
      // USDM-generated protocol: approach context files hold example content — stay on USDM data.
      await regenerateSectionFromUsdm(section);
      return;
    }
  
    setIsGenerating(prev => ({...prev, [_id]: true}));
  
    try {
      // Get the latest clinical intake form
      const clinicalIntake = await clinicalIntakeService.fetchLatestClinicalIntakeData(actualProjectId);
      
      // Get the original prompt from predefined sections
      const predefinedSections = await getPredefinedSections(actualProjectId);
      const originalSection = findPredefinedSection(predefinedSections, section.title);
      const originalPrompt = originalSection?.prompt || section.prompt || '';
      
      // Build context from other sections
      // For Section 10, automatically include Sections 3, 6, 7 (required per Section 10 prompt)
      let context = '';
      const allSections = await documentEditorService.getAllSections(actualProjectId);
      
      // Section 10 specifically needs Sections 3, 6, 7 for consistency
      const requiredSections = [
        '3. TRIAL OBJECTIVES AND ASSOCIATED ESTIMANDS',
        '6. TRIAL INTERVENTION AND CONCOMITANT THERAPY',
        '7. PARTICIPANT DISCONTINUATION OF TRIAL INTERVENTION AND DISCONTINUATION OR WITHDRAWAL FROM TRIAL'
      ];
      
      context += 'Contextual Sections (for consistency checking):\n';
      for (const sectionTitle of requiredSections) {
        const depSection = allSections.find(s => s.title === sectionTitle && s.final);
        if (depSection) {
          context += `\n[${depSection.title}]\n${depSection.output}\n`;
        } else {
        }
      }
      
      // Also check for any additional dependencies in section.context
      if (section.context) {
        const dependencyTitles = Array.from(section.context.matchAll(/Section: ([^\n]+)/g)).map(m => m[1].trim());
        for (const depTitle of dependencyTitles) {
          // Skip if already included above
          if (!requiredSections.includes(depTitle)) {
            const depSection = allSections.find(s => s.title === depTitle && s.final);
            if (depSection) {
              context += `\n[${depTitle}]\n${depSection.output}\n`;
            }
          }
        }
      }
      
      // Filter clinical intake data for Section 10
      const latestIntake = await clinicalIntakeService.getLatestClinicalIntake(actualProjectId);
      const filteredIntakeData = filterClinicalIntakeForSection(latestIntake, section.title);
      
      // Call Approach 2 endpoint
      const response = await axios.post(API_ENDPOINTS.ai.section10Approach2, {
        context: context,
        prompt: originalPrompt,
        output: JSON.stringify(filteredIntakeData),
        sectionTitle: section.title,
        ...buildGenerationStylePayload(latestIntake, currentProject),
      });
      
      let generatedContent = response.data.result;
      
      // Update UI
      setSections(prevSections =>
        prevSections.map(s =>
          s._id === _id ? { ...s, output: generatedContent, prompt: '' } : s
        )
      );
      
      // Update backend
      try {
        const originalPromptForVersion = section.prompt;
        
        await documentEditorService.updateSection(_id, {
          title: section.title,
          context: section.context,
          prompt: '',
          output: generatedContent,
          documentType: 'PROTOCOL',
          provenance: 'aidrafted'
        }, actualProjectId);
        
        await documentEditorService.addNewVersion({
          sectionId: _id,
          title: section.title,
          context: section.context,
          prompt: originalPromptForVersion + ' [Approach 2: Full Protocol-Writer Mode]',
          output: generatedContent,
          documentType: 'PROTOCOL'
        }, actualProjectId);
      } catch (updateErr) {
        console.error("Failed to update section in backend:", updateErr);
      }
    } catch (error) {
      console.error("Failed to generate Section 10 (Approach 2):", error);
      setSections(prevSections =>
        prevSections.map(s =>
          s._id === _id ? { ...s, output: "Error generating content. Please try again." } : s
        )
      );
    } finally {
      setIsGenerating(prev => {
        const updated = {...prev};
        delete updated[_id];
        return updated;
      });
    }
  };

  /**
   * SECTION 10 APPROACH 3: Generate → Evaluate → Refine
   * Two-step pipeline: generate section, then evaluate and revise using context file
   */
  const handleGenerateSection10Approach3 = async (_id) => {
    const section = sections.find((s) => s._id === _id);
    if (!section) return;
    if (isUsdmGeneratedProtocol(section)) {
      // USDM-generated protocol: approach context files hold example content — stay on USDM data.
      await regenerateSectionFromUsdm(section);
      return;
    }
  
    setIsGenerating(prev => ({...prev, [_id]: true}));
  
    try {
      // Get the latest clinical intake form
      const clinicalIntake = await clinicalIntakeService.fetchLatestClinicalIntakeData(actualProjectId);
      
      // Get the original prompt from predefined sections
      const predefinedSections = await getPredefinedSections(actualProjectId);
      const originalSection = findPredefinedSection(predefinedSections, section.title);
      const originalPrompt = originalSection?.prompt || section.prompt || '';
      
      // Build context from other sections
      // For Section 10, automatically include Sections 3, 6, 7 (required per Section 10 prompt)
      let context = '';
      const allSections = await documentEditorService.getAllSections(actualProjectId);
      
      // Section 10 specifically needs Sections 3, 6, 7 for consistency
      const requiredSections = [
        '3. TRIAL OBJECTIVES AND ASSOCIATED ESTIMANDS',
        '6. TRIAL INTERVENTION AND CONCOMITANT THERAPY',
        '7. PARTICIPANT DISCONTINUATION OF TRIAL INTERVENTION AND DISCONTINUATION OR WITHDRAWAL FROM TRIAL'
      ];
      
      context += 'Contextual Sections (for consistency checking):\n';
      for (const sectionTitle of requiredSections) {
        const depSection = allSections.find(s => s.title === sectionTitle && s.final);
        if (depSection) {
          context += `\n[${depSection.title}]\n${depSection.output}\n`;
        } else {
        }
      }
      
      // Also check for any additional dependencies in section.context
      if (section.context) {
        const dependencyTitles = Array.from(section.context.matchAll(/Section: ([^\n]+)/g)).map(m => m[1].trim());
        for (const depTitle of dependencyTitles) {
          // Skip if already included above
          if (!requiredSections.includes(depTitle)) {
            const depSection = allSections.find(s => s.title === depTitle && s.final);
            if (depSection) {
              context += `\n[${depTitle}]\n${depSection.output}\n`;
            }
          }
        }
      }
      
      // Filter clinical intake data for Section 10
      const latestIntake = await clinicalIntakeService.getLatestClinicalIntake(actualProjectId);
      const filteredIntakeData = filterClinicalIntakeForSection(latestIntake, section.title);
      
      // Call Approach 3 endpoint (this will run both generation and evaluation internally)
      const response = await axios.post(API_ENDPOINTS.ai.section10Approach3, {
        context: context,
        prompt: originalPrompt,
        output: JSON.stringify(filteredIntakeData),
        sectionTitle: section.title,
        ...buildGenerationStylePayload(latestIntake, currentProject),
      });
      
      let generatedContent = response.data.result;
      
      // Update UI
      setSections(prevSections =>
        prevSections.map(s =>
          s._id === _id ? { ...s, output: generatedContent, prompt: '' } : s
        )
      );
      
      // Update backend
      try {
        const originalPromptForVersion = section.prompt;
        
        await documentEditorService.updateSection(_id, {
          title: section.title,
          context: section.context,
          prompt: '',
          output: generatedContent,
          documentType: 'PROTOCOL',
          provenance: 'aidrafted'
        }, actualProjectId);
        
        await documentEditorService.addNewVersion({
          sectionId: _id,
          title: section.title,
          context: section.context,
          prompt: originalPromptForVersion + ' [Approach 3: Generate + Evaluate + Refine]',
          output: generatedContent,
          documentType: 'PROTOCOL'
        }, actualProjectId);
      } catch (updateErr) {
        console.error("Failed to update section in backend:", updateErr);
      }
    } catch (error) {
      console.error("Failed to generate Section 10 (Approach 3):", error);
      setSections(prevSections =>
        prevSections.map(s =>
          s._id === _id ? { ...s, output: "Error generating content. Please try again." } : s
        )
      );
    } finally {
      setIsGenerating(prev => {
        const updated = {...prev};
        delete updated[_id];
        return updated;
      });
    }
  };

  /**
   * SECTION 10 APPROACH 4: Original Generation + QC
   * Uses original generation method (no context file), then applies evaluation and refinement
   */
  const handleGenerateSection10Approach4 = async (_id) => {
    const section = sections.find((s) => s._id === _id);
    if (!section) return;
    if (isUsdmGeneratedProtocol(section)) {
      // USDM-generated protocol: approach context files hold example content — stay on USDM data.
      await regenerateSectionFromUsdm(section);
      return;
    }
  
    setIsGenerating(prev => ({...prev, [_id]: true}));
  
    try {
      // Get the latest clinical intake form
      const clinicalIntake = await clinicalIntakeService.fetchLatestClinicalIntakeData(actualProjectId);
      
      // Get the original prompt from predefined sections
      const predefinedSections = await getPredefinedSections(actualProjectId);
      const originalSection = findPredefinedSection(predefinedSections, section.title);
      const originalPrompt = originalSection?.prompt || section.prompt || '';
      
      // Build context from other sections
      // For Section 10, automatically include Sections 3, 6, 7 (required per Section 10 prompt)
      let context = '';
      const allSections = await documentEditorService.getAllSections(actualProjectId);
      
      // Section 10 specifically needs Sections 3, 6, 7 for consistency
      const requiredSections = [
        '3. TRIAL OBJECTIVES AND ASSOCIATED ESTIMANDS',
        '6. TRIAL INTERVENTION AND CONCOMITANT THERAPY',
        '7. PARTICIPANT DISCONTINUATION OF TRIAL INTERVENTION AND DISCONTINUATION OR WITHDRAWAL FROM TRIAL'
      ];
      
      context += 'Contextual Sections (for consistency checking):\n';
      for (const sectionTitle of requiredSections) {
        const depSection = allSections.find(s => s.title === sectionTitle && s.final);
        if (depSection) {
          context += `\n[${depSection.title}]\n${depSection.output}\n`;
        } else {
        }
      }
      
      // Also check for any additional dependencies in section.context
      if (section.context) {
        const dependencyTitles = Array.from(section.context.matchAll(/Section: ([^\n]+)/g)).map(m => m[1].trim());
        for (const depTitle of dependencyTitles) {
          // Skip if already included above
          if (!requiredSections.includes(depTitle)) {
            const depSection = allSections.find(s => s.title === depTitle && s.final);
            if (depSection) {
              context += `\n[${depTitle}]\n${depSection.output}\n`;
            }
          }
        }
      }
      
      // Filter clinical intake data for Section 10
      const latestIntake = await clinicalIntakeService.getLatestClinicalIntake(actualProjectId);
      const filteredIntakeData = filterClinicalIntakeForSection(latestIntake, section.title);
      
      // Call Approach 4 endpoint (original generation + QC)
      const response = await axios.post(API_ENDPOINTS.ai.section10Approach4, {
        context: context,
        prompt: originalPrompt,
        output: JSON.stringify(filteredIntakeData),
        sectionTitle: section.title,
        ...buildGenerationStylePayload(latestIntake, currentProject),
      });
      
      let generatedContent = response.data.result;
      
      // Update UI
      setSections(prevSections =>
        prevSections.map(s =>
          s._id === _id ? { ...s, output: generatedContent, prompt: '' } : s
        )
      );
      
      // Update backend
      try {
        const originalPromptForVersion = section.prompt;
        
        await documentEditorService.updateSection(_id, {
          title: section.title,
          context: section.context,
          prompt: '',
          output: generatedContent,
          documentType: 'PROTOCOL',
          provenance: 'aidrafted'
        }, actualProjectId);
        
        await documentEditorService.addNewVersion({
          sectionId: _id,
          title: section.title,
          context: section.context,
          prompt: originalPromptForVersion + ' [Approach 4: Original Generation + QC]',
          output: generatedContent,
          documentType: 'PROTOCOL'
        }, actualProjectId);
      } catch (updateErr) {
        console.error("Failed to update section in backend:", updateErr);
      }
    } catch (error) {
      console.error("Failed to generate Section 10 (Approach 4):", error);
      setSections(prevSections =>
        prevSections.map(s =>
          s._id === _id ? { ...s, output: "Error generating content. Please try again." } : s
        )
      );
    } finally {
      setIsGenerating(prev => {
        const updated = {...prev};
        delete updated[_id];
        return updated;
      });
    }
  };

  /**
   * SECTION 3 APPROACHES: Mirror Section 10 flows but for Trial Objectives/Estimands
   * These reuse the same backend approaches, but with Section 3 title and context.
   */
  const handleGenerateSection3Approach = async (_id, approachNumber) => {
    const section = sections.find((s) => s._id === _id);
    if (!section) return;
    if (isUsdmGeneratedProtocol(section)) {
      // USDM-generated protocol: approach context files hold example content — stay on USDM data.
      await regenerateSectionFromUsdm(section);
      return;
    }

    setIsGenerating(prev => ({ ...prev, [_id]: true }));

    try {
      // Get the latest clinical intake form
      const clinicalIntake = await clinicalIntakeService.fetchLatestClinicalIntakeData(actualProjectId);

      // Get the original prompt from predefined sections
      const predefinedSections = await getPredefinedSections(actualProjectId);
      const originalSection = findPredefinedSection(predefinedSections, section.title);
      const originalPrompt = originalSection?.prompt || section.prompt || '';

      // For Section 3, we rely primarily on:
      // - The dedicated DOCX context file (loaded server-side via contextFileMapping)
      // - Filtered clinical intake data for Section 3
      // We intentionally keep "context from other sections" minimal here for now.
      let context = '';

      // Filter clinical intake data for Section 3
      const latestIntake = await clinicalIntakeService.getLatestClinicalIntake(actualProjectId);
      const filteredIntakeData = filterClinicalIntakeForSection(latestIntake, section.title);

      // Choose backend endpoint based on approach
      let endpoint = null;
      switch (approachNumber) {
        case 1:
          endpoint = API_ENDPOINTS.ai.section10Approach1;
          break;
        case 2:
          endpoint = API_ENDPOINTS.ai.section10Approach2;
          break;
        case 3:
          endpoint = API_ENDPOINTS.ai.section10Approach3;
          break;
        case 4:
          endpoint = API_ENDPOINTS.ai.section10Approach4;
          break;
        default:
          throw new Error(`Invalid approach number for Section 3: ${approachNumber}`);
      }

      const response = await axios.post(endpoint, {
        context,
        prompt: originalPrompt,
        output: JSON.stringify(filteredIntakeData),
        sectionTitle: section.title,
        ...buildGenerationStylePayload(latestIntake, currentProject),
      });

      let generatedContent = response.data.result;

      // Update UI
      setSections(prevSections =>
        prevSections.map(s =>
          s._id === _id ? { ...s, output: generatedContent, prompt: '' } : s
        )
      );

      // Update backend
      try {
        const originalPromptForVersion = section.prompt;

        await documentEditorService.updateSection(_id, {
          title: section.title,
          context: section.context,
          prompt: '',
          output: generatedContent,
          documentType: 'PROTOCOL',
          provenance: 'aidrafted'
        }, actualProjectId);

        await documentEditorService.addNewVersion({
          sectionId: _id,
          title: section.title,
          context: section.context,
          prompt: originalPromptForVersion + ` [Section 3: Approach ${approachNumber}]`,
          output: generatedContent,
          documentType: 'PROTOCOL'
        }, actualProjectId);
      } catch (updateErr) {
        console.error("Failed to update Section 3 in backend:", updateErr);
      }
    } catch (error) {
      console.error(`Failed to generate Section 3 (Approach ${approachNumber}):`, error);
      setSections(prevSections =>
        prevSections.map(s =>
          s._id === _id ? { ...s, output: "Error generating content. Please try again." } : s
        )
      );
    } finally {
      setIsGenerating(prev => {
        const updated = { ...prev };
        delete updated[_id];
        return updated;
      });
    }
  };

  /**
   * SECTION 4 APPROACH 3 ONLY: Generate → Evaluate → Refine
   * Uses oncology_study_designs_master.md context file via section10-approach3 endpoint.
   */
  const handleGenerateSection4Approach3 = async (_id) => {
    const section = sections.find((s) => s._id === _id);
    if (!section) return;
    if (isUsdmGeneratedProtocol(section)) {
      // USDM-generated protocol: approach context files hold example content — stay on USDM data.
      await regenerateSectionFromUsdm(section);
      return;
    }

    setIsGenerating(prev => ({ ...prev, [_id]: true }));

    try {
      const predefinedSections = await getPredefinedSections(actualProjectId);
      const originalSection = findPredefinedSection(predefinedSections, section.title);
      // Section 4 uses chunked prompts in sectionData — merge all chunks for Approach 2 generation
      let originalPrompt = originalSection?.prompt || section.prompt || '';
      if (originalSection?.chunks) {
        const chunkParts = [
          originalSection.chunks.chunk1,
          originalSection.chunks.chunk2,
          originalSection.chunks.chunk3,
        ].filter(Boolean);
        if (chunkParts.length > 0) {
          originalPrompt = chunkParts.join('\n\n---\n\n');
        }
      }

      let context = '';
      const allSections = await documentEditorService.getAllSections(actualProjectId);

      const requiredSections = [
        '1. PROTOCOL SUMMARY',
        '3. TRIAL OBJECTIVES AND ASSOCIATED ESTIMANDS',
      ];

      context += 'Contextual Sections (for consistency checking):\n';
      for (const sectionTitle of requiredSections) {
        const depSection = allSections.find(s => s.title === sectionTitle && s.final);
        if (depSection) {
          context += `\n[${depSection.title}]\n${depSection.output}\n`;
        } else {
        }
      }

      const latestIntake = await clinicalIntakeService.getLatestClinicalIntake(actualProjectId);
      const filteredIntakeData = filterClinicalIntakeForSection(latestIntake, section.title);

      const response = await axios.post(API_ENDPOINTS.ai.section10Approach3, {
        context,
        prompt: originalPrompt,
        output: JSON.stringify(filteredIntakeData),
        sectionTitle: section.title,
        ...buildGenerationStylePayload(latestIntake, currentProject),
      });

      const generatedContent = response.data.result;

      setSections(prevSections =>
        prevSections.map(s =>
          s._id === _id ? { ...s, output: generatedContent, prompt: '' } : s
        )
      );

      try {
        const originalPromptForVersion = section.prompt;

        await documentEditorService.updateSection(_id, {
          title: section.title,
          context: section.context,
          prompt: '',
          output: generatedContent,
          documentType: 'PROTOCOL',
          provenance: 'aidrafted'
        }, actualProjectId);

        await documentEditorService.addNewVersion({
          sectionId: _id,
          title: section.title,
          context: section.context,
          prompt: originalPromptForVersion + ' [Section 4: Approach 3]',
          output: generatedContent,
          documentType: 'PROTOCOL'
        }, actualProjectId);
      } catch (updateErr) {
        console.error('Failed to update Section 4 in backend:', updateErr);
      }
    } catch (error) {
      console.error('Failed to generate Section 4 (Approach 3):', error);
      setSections(prevSections =>
        prevSections.map(s =>
          s._id === _id ? { ...s, output: 'Error generating content. Please try again.' } : s
        )
      );
    } finally {
      setIsGenerating(prev => {
        const updated = { ...prev };
        delete updated[_id];
        return updated;
      });
    }
  };

  /**
   * SECTION 5 APPROACH 3 ONLY: Generate → Evaluate → Refine
   * Uses Combined Trial population CF.pdf via section10-approach3 endpoint.
   */
  const handleGenerateSection5Approach3 = async (_id) => {
    const section = sections.find((s) => s._id === _id);
    if (!section) return;
    if (isUsdmGeneratedProtocol(section)) {
      // USDM-generated protocol: approach context files hold example content — stay on USDM data.
      await regenerateSectionFromUsdm(section);
      return;
    }

    setIsGenerating(prev => ({ ...prev, [_id]: true }));

    try {
      const predefinedSections = await getPredefinedSections(actualProjectId);
      const originalSection = findPredefinedSection(predefinedSections, section.title);
      const originalPrompt = originalSection?.prompt || section.prompt || '';

      let context = '';
      const allSections = await documentEditorService.getAllSections(actualProjectId);

      const requiredSections = [
        '3. TRIAL OBJECTIVES AND ASSOCIATED ESTIMANDS',
        '4. TRIAL DESIGN',
      ];

      context += 'Contextual Sections (for consistency checking):\n';
      for (const sectionTitle of requiredSections) {
        const depSection = allSections.find(s => s.title === sectionTitle && s.final);
        if (depSection) {
          context += `\n[${depSection.title}]\n${depSection.output}\n`;
        } else {
        }
      }

      const latestIntake = await clinicalIntakeService.getLatestClinicalIntake(actualProjectId);
      const filteredIntakeData = filterClinicalIntakeForSection(latestIntake, section.title);

      const response = await axios.post(API_ENDPOINTS.ai.section10Approach3, {
        context,
        prompt: originalPrompt,
        output: JSON.stringify(filteredIntakeData),
        sectionTitle: section.title,
        ...buildGenerationStylePayload(latestIntake, currentProject),
      });

      const generatedContent = response.data.result;

      setSections(prevSections =>
        prevSections.map(s =>
          s._id === _id ? { ...s, output: generatedContent, prompt: '' } : s
        )
      );

      try {
        const originalPromptForVersion = section.prompt;

        await documentEditorService.updateSection(_id, {
          title: section.title,
          context: section.context,
          prompt: '',
          output: generatedContent,
          documentType: 'PROTOCOL',
          provenance: 'aidrafted'
        }, actualProjectId);

        await documentEditorService.addNewVersion({
          sectionId: _id,
          title: section.title,
          context: section.context,
          prompt: originalPromptForVersion + ' [Section 5: Approach 3]',
          output: generatedContent,
          documentType: 'PROTOCOL'
        }, actualProjectId);
      } catch (updateErr) {
        console.error('Failed to update Section 5 in backend:', updateErr);
      }
    } catch (error) {
      console.error('Failed to generate Section 5 (Approach 3):', error);
      setSections(prevSections =>
        prevSections.map(s =>
          s._id === _id ? { ...s, output: 'Error generating content. Please try again.' } : s
        )
      );
    } finally {
      setIsGenerating(prev => {
        const updated = { ...prev };
        delete updated[_id];
        return updated;
      });
    }
  };

  /**
   * SECTION 8 APPROACH 3 ONLY: Generate → Evaluate → Refine
   * Uses TRIAL ASSESSMENTS AND PROCEDURES_Context file.pdf via section10-approach3 endpoint.
   */
  const handleGenerateSection8Approach3 = async (_id) => {
    const section = sections.find((s) => s._id === _id);
    if (!section) return;
    if (isUsdmGeneratedProtocol(section)) {
      // USDM-generated protocol: approach context files hold example content — stay on USDM data.
      await regenerateSectionFromUsdm(section);
      return;
    }

    setIsGenerating(prev => ({ ...prev, [_id]: true }));

    try {
      const predefinedSections = await getPredefinedSections(actualProjectId);
      const originalSection = findPredefinedSection(predefinedSections, section.title);
      const originalPrompt = originalSection?.prompt || section.prompt || '';

      let context = '';
      const allSections = await documentEditorService.getAllSections(actualProjectId);

      const requiredSections = [
        '3. TRIAL OBJECTIVES AND ASSOCIATED ESTIMANDS',
        '5. TRIAL POPULATION',
        '6. TRIAL INTERVENTION AND CONCOMITANT THERAPY',
        '7. PARTICIPANT DISCONTINUATION OF TRIAL INTERVENTION AND DISCONTINUATION OR WITHDRAWAL FROM TRIAL',
        '1.3 SCHEDULE OF ACTIVITIES',
      ];

      context += 'Contextual Sections (for consistency checking):\n';
      for (const sectionTitle of requiredSections) {
        const depSection = allSections.find(s => s.title === sectionTitle && s.final);
        if (depSection) {
          context += `\n[${depSection.title}]\n${depSection.output}\n`;
        } else {
        }
      }

      const latestIntake = await clinicalIntakeService.getLatestClinicalIntake(actualProjectId);
      const filteredIntakeData = filterClinicalIntakeForSection(latestIntake, section.title);

      const response = await axios.post(API_ENDPOINTS.ai.section10Approach3, {
        context,
        prompt: originalPrompt,
        output: JSON.stringify(filteredIntakeData),
        sectionTitle: section.title,
        ...buildGenerationStylePayload(latestIntake, currentProject),
      });

      const generatedContent = response.data.result;

      setSections(prevSections =>
        prevSections.map(s =>
          s._id === _id ? { ...s, output: generatedContent, prompt: '' } : s
        )
      );

      try {
        const originalPromptForVersion = section.prompt;

        await documentEditorService.updateSection(_id, {
          title: section.title,
          context: section.context,
          prompt: '',
          output: generatedContent,
          documentType: 'PROTOCOL',
          provenance: 'aidrafted'
        }, actualProjectId);

        await documentEditorService.addNewVersion({
          sectionId: _id,
          title: section.title,
          context: section.context,
          prompt: originalPromptForVersion + ' [Section 8: Approach 3]',
          output: generatedContent,
          documentType: 'PROTOCOL'
        }, actualProjectId);
      } catch (updateErr) {
        console.error('Failed to update Section 8 in backend:', updateErr);
      }
    } catch (error) {
      console.error('Failed to generate Section 8 (Approach 3):', error);
      setSections(prevSections =>
        prevSections.map(s =>
          s._id === _id ? { ...s, output: 'Error generating content. Please try again.' } : s
        )
      );
    } finally {
      setIsGenerating(prev => {
        const updated = { ...prev };
        delete updated[_id];
        return updated;
      });
    }
  };
  
  
  const handleViewVersionHistory = (_id) => {
    // Navigate to version history page with section ID
    navigate(`/section-history/${_id}?projectId=${actualProjectId}`); // Pass projectId
  };

  // State for 3 new section cards with dropdowns
  const [card1SelectedSection, setCard1SelectedSection] = useState('');
  const [card1SelectedApproach, setCard1SelectedApproach] = useState('1');
  const [card1IsGenerating, setCard1IsGenerating] = useState(false);
  const [card1Output, setCard1Output] = useState('');
  
  const [card2SelectedSection, setCard2SelectedSection] = useState('');
  const [card2SelectedApproach, setCard2SelectedApproach] = useState('1');
  const [card2IsGenerating, setCard2IsGenerating] = useState(false);
  const [card2Output, setCard2Output] = useState('');
  
  const [card3SelectedSection, setCard3SelectedSection] = useState('');
  const [card3SelectedApproach, setCard3SelectedApproach] = useState('1');
  const [card3IsGenerating, setCard3IsGenerating] = useState(false);
  const [card3Output, setCard3Output] = useState('');

  // Get available sections for dropdown
  const [availableSections, setAvailableSections] = useState([]);
  
  useEffect(() => {
    const loadAvailableSections = async () => {
      try {
        const predefinedSections = await getPredefinedSections(actualProjectId);
        const sectionTitles = predefinedSections.map(s => s.title).filter(Boolean);
        setAvailableSections(sectionTitles);
      } catch (error) {
        console.error('Error loading available sections:', error);
      }
    };
    if (actualProjectId) {
      loadAvailableSections();
    }
  }, [actualProjectId]);

  /**
   * Handler for accepting card output and updating the actual section
   */
  const handleAcceptCardOutput = async (cardNumber) => {
    let selectedSection, cardOutput;
    
    if (cardNumber === 1) {
      selectedSection = card1SelectedSection;
      cardOutput = card1Output;
    } else if (cardNumber === 2) {
      selectedSection = card2SelectedSection;
      cardOutput = card2Output;
    } else {
      selectedSection = card3SelectedSection;
      cardOutput = card3Output;
    }

    if (!selectedSection || !cardOutput) {
      alert('No generated content to accept');
      return;
    }

    try {
      // Find or create the section in the sections array
      let targetSection = sections.find(s => s.title === selectedSection);
      
      if (targetSection) {
        // Update existing section
        setSections(prevSections =>
          prevSections.map(s =>
            s._id === targetSection._id ? { ...s, output: cardOutput } : s
          )
        );

        // Update backend
        await documentEditorService.updateSection(targetSection._id, {
          title: selectedSection,
          context: targetSection.context,
          prompt: '',
          output: cardOutput,
          documentType: 'PROTOCOL',
          provenance: 'aidrafted'
        }, actualProjectId);

        // Clear the card output after acceptance
        if (cardNumber === 1) {
          setCard1Output('');
        } else if (cardNumber === 2) {
          setCard2Output('');
        } else {
          setCard3Output('');
        }

        alert('Section updated successfully!');
      } else {
        // Create new section
        const newSection = await documentEditorService.createSection({
          title: selectedSection,
          context: '',
          prompt: '',
          output: cardOutput,
          documentType: 'PROTOCOL',
          ...(isUsdmGeneratedProtocol() ? { generationSource: 'usdm' } : {})
        }, actualProjectId);

        // Refresh sections
        const updatedSections = await documentEditorService.getAllSections(actualProjectId);
        setSections(updatedSections);

        // Clear the card output after acceptance
        if (cardNumber === 1) {
          setCard1Output('');
        } else if (cardNumber === 2) {
          setCard2Output('');
        } else {
          setCard3Output('');
        }

        alert('Section created and updated successfully!');
      }
    } catch (error) {
      console.error(`Failed to accept card output (Card ${cardNumber}):`, error);
      alert(`Error accepting content: ${error.message}`);
    }
  };

  /**
   * Handler for generating section with selected approach (for new cards)
   */
  const handleGenerateWithApproach = async (cardNumber) => {
    let selectedSection, selectedApproach, setIsGenerating, setOutput;
    
    if (cardNumber === 1) {
      selectedSection = card1SelectedSection;
      selectedApproach = card1SelectedApproach;
      setIsGenerating = setCard1IsGenerating;
      setOutput = setCard1Output;
    } else if (cardNumber === 2) {
      selectedSection = card2SelectedSection;
      selectedApproach = card2SelectedApproach;
      setIsGenerating = setCard2IsGenerating;
      setOutput = setCard2Output;
    } else {
      selectedSection = card3SelectedSection;
      selectedApproach = card3SelectedApproach;
      setIsGenerating = setCard3IsGenerating;
      setOutput = setCard3Output;
    }

    if (!selectedSection) {
      alert('Please select a section first');
      return;
    }

    setIsGenerating(true);

    try {
      // USDM-generated protocol: the approach endpoints load example context
      // files and Clinical Intake data, so generate from USDM data instead.
      if (isUsdmGeneratedProtocol()) {
        const { output } = await generateUsdmSectionOutput(selectedSection);
        setOutput(output);
        return;
      }

      // Get the original prompt from predefined sections
      const predefinedSections = await getPredefinedSections(actualProjectId);
      const originalSection = predefinedSections.find(s => s.title === selectedSection);
      const originalPrompt = originalSection?.prompt || '';

      // Build context from other sections (if needed)
      let context = '';
      const allSections = await documentEditorService.getAllSections(actualProjectId);
      
      // For Section 10, automatically include Sections 3, 6, 7
      if (selectedSection.includes('10.') || selectedSection.toUpperCase().includes('STATISTICAL CONSIDERATIONS')) {
        const requiredSections = [
          '3. TRIAL OBJECTIVES AND ASSOCIATED ESTIMANDS',
          '6. TRIAL INTERVENTION AND CONCOMITANT THERAPY',
          '7. PARTICIPANT DISCONTINUATION OF TRIAL INTERVENTION AND DISCONTINUATION OR WITHDRAWAL FROM TRIAL'
        ];
        
        context += 'Contextual Sections (for consistency checking):\n';
        for (const sectionTitle of requiredSections) {
          const depSection = allSections.find(s => s.title === sectionTitle && s.final);
          if (depSection) {
            context += `\n[${depSection.title}]\n${depSection.output}\n`;
          }
        }
      }

      // Filter clinical intake data for the selected section
      const latestIntake = await clinicalIntakeService.getLatestClinicalIntake(actualProjectId);
      const filteredIntakeData = filterClinicalIntakeForSection(latestIntake, selectedSection);

      // Call generic endpoint
      const response = await axios.post(API_ENDPOINTS.ai.generateSectionWithApproach, {
        context: context,
        prompt: addFormattingInstructions(originalPrompt),
        output: JSON.stringify(filteredIntakeData),
        sectionTitle: selectedSection,
        approach: selectedApproach,
        ...buildGenerationStylePayload(latestIntake, currentProject),
      });

      let generatedContent = finalizeGeneratedProtocolOutput(
        response.data.result,
        selectedSection
      );

      // Store output in card state for display only (experimental/preview)
      // Do NOT update sections state or database - user must accept manually
      setOutput(generatedContent);
    } catch (error) {
      console.error(`Failed to generate section (Card ${cardNumber}):`, error);
      alert(`Error generating section: ${error.message}`);
    } finally {
      setIsGenerating(false);
    }
  };
    
  const handleMenuOpen = (event, _id) => {
    setAnchorEl(event.currentTarget);
    setSelectedSectionId(_id);
  };
  
  const handleMenuClose = () => setAnchorEl(null);
  
  // Add Section dialog handlers
  const handleOpenAddDialog = () => {
    setOpenAddDialog(true);
    setTabValue(0);
    setNewSectionTitle(''); // Ensure setNewSectionTitle is defined
    setCustomSectionTitle('');
  };
  
  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
    setSelectedSectionTitle('');
    setCustomSectionTitle('');
    setTabValue(0);
  };
  
  const handleSectionTitleChange = (event) => {
    setSelectedSectionTitle(event.target.value);
  };
  
  const handleCustomTitleChange = (event) => {
    setCustomSectionTitle(event.target.value);
  };
  
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    // Reset fields when switching tabs
    if (newValue === 0) {
      setCustomSectionTitle('');
    } else {
      setSelectedSectionTitle('');
    }
  };
  
  const handleAddSection = async () => {
    const title = tabValue === 0 ? selectedSectionTitle : customSectionTitle;
    if (!title) return;
    
    let newSection;
    const usdmProtocol = isUsdmGeneratedProtocol();
    if (tabValue === 1) { // Custom tab
      newSection = {
        title: customSectionTitle,
        context: '',
        output: '',
        prompt: '',
        projectId: actualProjectId,
        documentType: 'PROTOCOL',
        ...(usdmProtocol ? { generationSource: 'usdm' } : {})
      };
    } else if (usdmProtocol) { // Template tab, USDM-generated protocol: generate from USDM data
      try {
        const { output, context, usdmSection } = await generateUsdmSectionOutput(selectedSectionTitle);
        // Created by the shared createSection call below.
        newSection = {
          title: usdmSection.title,
          context,
          prompt: '',
          output,
          projectId: actualProjectId,
          documentType: 'PROTOCOL',
          generationSource: 'usdm'
        };
      } catch (error) {
        console.error('Failed to add section from USDM:', error);
        setError(`Failed to add "${selectedSectionTitle}" from USDM study data: ${error.message}`);
        return;
      }
    } else { // Template tab
      try {
        const predefinedSections = await getPredefinedSections(actualProjectId);
        const templateSection = predefinedSections.find(s => s.title === selectedSectionTitle);
        
        if (templateSection) {
          try {
            // Filter clinical intake data for this specific section
            const latestIntake = await clinicalIntakeService.getLatestClinicalIntake(actualProjectId);
            const filteredIntakeData = filterClinicalIntakeForSection(latestIntake, templateSection.title);
            
            // Generate content using filtered context
            let generatedContent = await generateContent(
              templateSection.prompt || '',
              templateSection.context || '',
              JSON.stringify(filteredIntakeData),
              'protocol',
              templateSection.title,
              templateSection,
              buildGenerationStylePayload(latestIntake, currentProject),
            );

            // Apply validation and regeneration for all sections
            try {
              const validationResult = await validateAndRegenerateSection(
                templateSection.title,
                generatedContent,
                templateSection.prompt || '',
                templateSection.context || '',
                3, // max attempts
                actualProjectId // Pass projectId to fetch fresh clinical intake data
              );
              
              if (validationResult.success) {
                generatedContent = validationResult.finalContent;
              } else {
                console.warn(`⚠️ ${templateSection.title} validation failed, using original content`);
              }
            } catch (error) {
              console.error(`❌ ${templateSection.title} validation error:`, error);
              // Continue with original content if validation fails
            }
            generatedContent = finalizeGeneratedProtocolOutput(
              generatedContent,
              templateSection.title
            );
            const createdSection = await documentEditorService.createSection({
              ...templateSection,
              output: generatedContent,
              prompt: '', // User sees blank prompt
              projectId: actualProjectId,
              documentType: 'PROTOCOL'
            }, actualProjectId);
            
            const createdSectionData = createdSection.document;
            const completeSection = {
              ...templateSection,
              _id: createdSectionData._id,
              output: generatedContent,
              prompt: '',
              projectId: actualProjectId,
              documentType: 'PROTOCOL'
            };
            setSections(prevSections => [...prevSections, completeSection]);
            
            // await documentEditorService.addNewVersion({
            //   sectionId: createdSectionData._id,
            //   title: templateSection.title,
            //   context: templateSection.context,
            //   prompt: templateSection.prompt,
            //   output: generatedContent,
            //   projectId: actualProjectId,
            //   documentType: 'PROTOCOL'
            // }, actualProjectId);
            
            handleCloseAddDialog();
          } catch (err) {
            console.error("Failed to create section in backend:", err);
          } finally {
            setIsLoading(false);
          }
          return;
        } else {
          newSection = {
            title: selectedSectionTitle,
            context: '',
            output: '',
            prompt: '',
            projectId: actualProjectId,
            documentType: 'PROTOCOL'
          };
        }
      } catch (error) {
        console.error("Error getting predefined sections:", error);
        newSection = {
          title: selectedSectionTitle,
          context: '',
          output: '',
          prompt: '',
          projectId: actualProjectId,
          documentType: 'PROTOCOL'
        };
      }
    }
    
    try {
      setIsLoading(true);
      const createdSection = await documentEditorService.createSection(newSection, actualProjectId);
      const createdSectionData = createdSection.document;
      const completeSection = {
        ...newSection,
        _id: createdSectionData._id,
      };
      setSections(prevSections => [...prevSections, completeSection]);
      // await documentEditorService.addNewVersion({
      //   sectionId: createdSectionData._id,
      //   title: newSection.title,
      //   prompt: newSection.prompt,
      //   output: newSection.output,
      //   projectId: actualProjectId,
      //   documentType: 'PROTOCOL'
      // }, actualProjectId);
      handleCloseAddDialog();
    } catch (err) {
      console.error("Failed to create section in backend:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = async () => {
    setIsLoading(true);
    try {      
      // Clear existing sections first
      if (window.confirm('Are you sure you want to delete All sections?')) {
        await documentEditorService.deleteAllSections(actualProjectId); // Pass projectId
        setSections([]);
      } else {
        // Restore the section in the UI since user canceled
        const fetchedSections = await documentEditorService.getAllSections(actualProjectId); // Pass projectId
        setSections(fetchedSections);
      }     

    } catch (err) {
      console.error("Failed to reset sections:", err);
      // Fallback to local reset if backend fails
      const fetchedSections = await documentEditorService.getAllSections(actualProjectId); // Pass projectId
      setSections(fetchedSections);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleDeleteSection = async (_id) => {
    try {
      // Close the menu
      handleMenuClose();
      
      // Optimistically update UI first for better user experience
      setSections(prevSections => prevSections.filter(section => section._id !== _id));
      
      // Delete from backend

      if (window.confirm('Are you sure you want to delete this section?')) {
        await documentEditorService.deleteSection(_id, actualProjectId); // Pass projectId

      } else {
        // Restore the section in the UI since user canceled
        const fetchedSections = await documentEditorService.getAllSections(actualProjectId); // Pass projectId
        setSections(fetchedSections);
      }
      
   
    } catch (err) {
      console.error("Failed to delete section from backend:", err);
      // If backend delete fails, refetch all sections to restore correct state
      try {
        const fetchedSections = await documentEditorService.getAllSections(actualProjectId); // Pass projectId
        setSections(fetchedSections);
      } catch (fetchErr) {
        console.error("Failed to refetch sections after delete error:", fetchErr);
      }
    }
  };
  
  const handleMarkAsFinal = async (_id) => {
    try {
      // Update backend
      await documentEditorService.updateSection(_id, { final: true }, actualProjectId);
      // Update UI
      setSections(prevSections =>
        prevSections.map(section =>
          section._id === _id ? { ...section, final: true } : section
        )
      );
    } catch (err) {
      console.error("Failed to mark section as final:", err);
      // Optionally show a notification
    }
  };
  
  const handleCopyContent = (text) => {
    navigator.clipboard.writeText(text);
    alert('Content copied to clipboard!');
  };

  const handleToggleBookmark = (sectionId) => {
    setBookmarkedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };
  useEffect(() => {
    async function fetchTrialSchemaImage() {
      if (!actualProjectId) return;
      try {
        const response = await clinicalIntakeService.fetchLatestClinicalIntakeWithTrialSchemaFromApi(actualProjectId);
        // FIX: Use the correct path for trial schema files
        const trialSchemaFiles = response?.document_uploads?.uploaded_files?.trial_schema || [];
  
        if (trialSchemaFiles.length > 0) {
          const file = trialSchemaFiles[0];
          const imageUrl = file.fileUrl || file.azureBlobUrl || file.url || null;
          setTrialSchemaImageUrl(imageUrl);
        } else if (response?.trialSchemaSvg) {
          // Use SVG from backend if available
          setTrialSchemaImageUrl(response.trialSchemaSvg);
        } else {
          // No trial schema image found, fallback to draw.io SVG if available
          if (trialSchemaDiagramSvg) {
            setTrialSchemaImageUrl(trialSchemaDiagramSvg);
            // Optionally, save it as the new trial schema image in the backend here
            // await clinicalIntakeService.saveTrialSchemaImage(actualProjectId, trialSchemaDiagramSvg);
        } else {
          setTrialSchemaImageUrl(null);
          }
        }
      } catch (err) {
        console.error("Error fetching trial schema image:", err);
        // Fallback to draw.io SVG if available
        if (trialSchemaDiagramSvg) {
          setTrialSchemaImageUrl(trialSchemaDiagramSvg);
          // Optionally, save it as the new trial schema image in the backend here
          // await clinicalIntakeService.saveTrialSchemaImage(actualProjectId, trialSchemaDiagramSvg);
        } else {
        setTrialSchemaImageUrl(null);
        }
      }
    }
  
    fetchTrialSchemaImage();
  }, [actualProjectId, trialSchemaDiagramSvg]);
  
  

  // Expose exportToWord to parent via ref (must be at top level, before any early returns)
  useImperativeHandle(ref, () => ({
    exportToWord: async () => {
      const titlePageConfigForExport = await resolveTitlePageConfigForExport();
      const soaDataToExport = await resolveSoaDataForExport({
        generatedSoAData,
        s9SoaTables,
        projectId: actualProjectId,
      });
      return exportToWord(
        sections,
        trialSchemaImageUrl,
        soaDataToExport,
        FONT_CONFIG,
        titlePageConfigForExport,
        actualProjectId,
        { returnBlobOnly: true },
      );
    },
    getSections: () => sections,
    exportToPdf: async () => {
      return buildNativeProtocolPdfBlob();
    }
  }), [sections, actualProjectId, generatedSoAData, s9SoaTables, trialSchemaImageUrl, titlePageConfig]);

  // Fetch SOA on mount/project change for section 1.3 — S9 workspaces/versions is the
  // single source of truth; the legacy protocol-number-keyed ScheduleOfActivities system
  // is deprecated and intentionally not consulted here.
  useEffect(() => {
    const fetchSOA = async () => {
      if (!actualProjectId) return;
      try {
        const s9Res = await s9AssessmentScheduleService.getLatestS9Soa({
          projectId: actualProjectId,
          studyId: '',
        });
        if (s9Res?.success && s9Res.data) {
          const tables = s9Res.data.soa?.tables || [];
          if (tables.length) {
            setS9SoaTables(tables);
            setS9PublishedVersion(s9Res.data.published ? s9Res.data.versionNumber : null);
          }
          const legacy = s9Res.data.legacyCompatible;
          if (legacy && (legacy.visits || legacy.sections || tables.length)) {
            setGeneratedSoAData(
              tables.length
                ? {
                    ...legacy,
                    visits: Array.isArray(legacy.visits) ? legacy.visits : [],
                    sections: Array.isArray(legacy.sections) ? legacy.sections : [],
                    s9: {
                      tables,
                      flags: Array.isArray(s9Res.data.soa?.flags) ? s9Res.data.soa.flags : [],
                    },
                  }
                : legacy,
            );
          }
        }
        setSoaProtocolId(actualProjectId);
      } catch (error) {
        if (error?.response?.status !== 404) {
          console.error('Error fetching SOA:', error);
        }
        setSoaProtocolId(actualProjectId);
      }
    };
    fetchSOA();
  }, [actualProjectId]);

  const handleSoADataUpdate = async (updatedData) => {
    // Update the local state with the new SOA data without regenerating
    setGeneratedSoAData(updatedData);
  };

  // On modal open, fetch diagram XML and SVG from backend
  useEffect(() => {
    if (showDrawio && currentProject?._id) {
      getTrialSchemaDiagram(currentProject._id)
        .then(res => {
          setTrialSchemaDiagramXml(res.data.xml);
          if (res.data.svg) {
            setTrialSchemaDiagramSvg(res.data.svg);
          }
        })
        .catch(() => {
          setTrialSchemaDiagramXml(null);
          setTrialSchemaDiagramSvg(null);
        });
    }
  }, [showDrawio, currentProject]);
  // Save handler
  const handleSaveDiagram = () => {
    if (drawioIframeRef.current) {
      // Fixed target origin, not '*' — a '*' target lets ANY origin the iframe
      // ever navigates to receive this message (security review SEC-011).
      drawioIframeRef.current.contentWindow.postMessage(JSON.stringify({
        action: 'export',
        format: 'xml',
        xml: 1
      }), 'https://embed.diagrams.net');
      drawioIframeRef.current.contentWindow.postMessage(JSON.stringify({
        action: 'export',
        format: 'svg',
        xml: 1
      }), 'https://embed.diagrams.net');
    }
  };
  // Handle trial schema image upload
  const handleTrialSchemaImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file.');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be less than 5MB.');
      return;
    }

    try {
      const rejectionReason = await getTrialSchemaImageRejectionReason(file);
      if (rejectionReason) {
        alert(rejectionReason);
        return;
      }
    } catch (analysisError) {
      console.warn('Trial schema image pre-check failed, deferring to server validation:', analysisError);
    }

    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('documentType', 'trial_schema');
      formData.append('studyId', resolveProjectTechnicalStudyKey(currentProject) || 'unknown');
      formData.append('projectId', currentProject?._id);
      formData.append('title', file.name);

      const response = await axios.post(`${API_BASE_URL}/clinical-intake/upload-document`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        const uploadedFile = response.data.file;
        // Set the uploaded image URL
        setTrialSchemaImageUrl(uploadedFile.azureBlobUrl || uploadedFile.fileUrl);
        alert('Trial Schema image uploaded successfully!');
      } else {
        throw new Error(response.data.error || 'Upload failed');
      }
    } catch (error) {
      console.error('Error uploading trial schema image:', error);
      const serverMessage = error.response?.data?.message || error.response?.data?.error || error.message;
      alert(`Upload failed: ${serverMessage}`);
    } finally {
      setUploadingImage(false);
    }
  };
  // Handle trial schema image deletion
  const handleDeleteTrialSchemaImage = async () => {
    if (!trialSchemaImageUrl) return;
    
    try {
      // Call API to delete the image from backend
      const response = await axios.delete(`${API_BASE_URL}/clinical-intake/delete-trial-schema-image`, {
        data: { 
          projectId: actualProjectId,
          imageUrl: trialSchemaImageUrl 
        }
      });
      
      if (response.data.success) {
        setTrialSchemaImageUrl(null);
        setShowImageEditDialog(false);
        alert('Trial Schema image deleted successfully!');
      } else {
        throw new Error(response.data.error || 'Delete failed');
      }
    } catch (error) {
      console.error('Error deleting trial schema image:', error);
      const serverMessage = error.response?.data?.message || error.response?.data?.error || error.message;
      alert(`Delete failed: ${serverMessage}`);
    }
  };
  // Handle trial schema image cropping
  const handleCropTrialSchemaImage = async () => {
    if (!trialSchemaImageUrl || !cropData) return;
    
    try {
      // Create a canvas to crop the image
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      // Set crossOrigin to handle CORS issues
      img.crossOrigin = 'anonymous';
      
      img.onload = async () => {
        try {
          // Set canvas dimensions to crop area
          canvas.width = Math.round(cropData.width);
          canvas.height = Math.round(cropData.height);
          
          // Draw the cropped portion
          ctx.drawImage(
            img,
            Math.round(cropData.x), Math.round(cropData.y), 
            Math.round(cropData.width), Math.round(cropData.height),
            0, 0, Math.round(cropData.width), Math.round(cropData.height)
          );
          
          // Convert canvas to blob
          canvas.toBlob(async (blob) => {
            if (!blob) {
              alert('Failed to process the cropped image. Please try again.');
              return;
            }
            
            try {
              // Upload the cropped image
              const formData = new FormData();
              formData.append('file', blob, 'cropped-trial-schema.png');
              formData.append('documentType', 'trial_schema');
              formData.append('studyId', resolveProjectTechnicalStudyKey(currentProject) || 'unknown');
              formData.append('projectId', currentProject?._id);
              formData.append('title', 'cropped-trial-schema.png');

              const response = await axios.post(`${API_BASE_URL}/clinical-intake/upload-document`, formData, {
                headers: {
                  'Content-Type': 'multipart/form-data',
                },
              });

              if (response.data.success) {
                const uploadedFile = response.data.file;
                setTrialSchemaImageUrl(uploadedFile.azureBlobUrl || uploadedFile.fileUrl);
                setShowImageEditDialog(false);
                setCropData(null);
                alert('Trial Schema image cropped and saved successfully!');
              } else {
                throw new Error(response.data.error || 'Upload failed');
              }
            } catch (error) {
              console.error('Error uploading cropped image:', error);
              alert(`Upload failed: ${error.message}`);
            }
          }, 'image/png');
        } catch (canvasError) {
          console.error('Canvas processing error:', canvasError);
          // Fallback: try without CORS
          try {
            img.crossOrigin = null;
            img.src = trialSchemaImageUrl;
          } catch (fallbackError) {
            console.error('Fallback also failed:', fallbackError);
            alert('Failed to process the image. Please try uploading a new image instead.');
          }
        }
      };
      
      img.onerror = () => {
        console.error('Failed to load image for cropping');
        alert('Failed to load the image for cropping. Please try again.');
      };
      
      img.src = trialSchemaImageUrl;
    } catch (error) {
      console.error('Error cropping trial schema image:', error);
      alert(`Crop failed: ${error.message}`);
    }
  };

  // Open image edit dialog
  const handleOpenImageEdit = (mode) => {
    setImageEditMode(mode);
    setShowImageEditDialog(true);
  };
  // Listen for messages from draw.io
  useEffect(() => {
    if (!showDrawio) return;

    function handleMessage(event) {
      // Exact origin + source match — `.includes('diagrams.net')` accepted
      // any origin containing that substring (e.g. diagrams.net.attacker.example)
      // and never checked event.source, so any window with a reference to this
      // one (not just our own iframe) could forge export/init messages that get
      // persisted as diagram XML/SVG (security review SEC-011).
      if (event.origin !== 'https://embed.diagrams.net') return;
      if (event.source !== drawioIframeRef.current?.contentWindow) return;
      if (event.data === null || typeof event.data !== 'string') return;
      let msg = null;
      try { msg = JSON.parse(event.data); } catch (e) { return; }
      if (msg.event === 'init') {
        // On init, load saved diagram or blank
        if (trialSchemaDiagramXml && trialSchemaDiagramXml.trim().startsWith('<mxfile')) {
          drawioIframeRef.current.contentWindow.postMessage(JSON.stringify({
            action: 'load',
            autosave: 1,
            xml: trialSchemaDiagramXml
          }), 'https://embed.diagrams.net');
        } else {
          const blankXml = '<mxfile><diagram name="Page-1">' +
            '&lt;mxGraphModel&gt;&lt;root&gt;&lt;mxCell id="0"/&gt;&lt;mxCell id="1" parent="0"/&gt;&lt;/root&gt;&lt;/mxGraphModel&gt;' +
            '</diagram></mxfile>';
          drawioIframeRef.current.contentWindow.postMessage(JSON.stringify({
            action: 'load',
            autosave: 1,
            xml: blankXml
          }), 'https://embed.diagrams.net');
        }
      }
      if (msg.event === 'export' && msg.data) {
        if (msg.format === 'xml') {
          const xml = msg.data;
          setTrialSchemaDiagramXml(xml);
          if (currentProject?._id) {
            saveTrialSchemaDiagram(currentProject._id, { xml, svg: trialSchemaDiagramSvg })
              .then(() => {/* Optionally show success */})
              .catch(() => {/* Optionally show error */});
          }
        } else if (msg.format === 'svg') {
          let svg = msg.data;
          if (svg.startsWith('data:image/svg+xml;base64,')) {
            svg = atob(svg.split(',')[1]);
          }
          setTrialSchemaDiagramSvg(svg);
          if (currentProject?._id) {
            saveTrialSchemaDiagram(currentProject._id, { xml: trialSchemaDiagramXml, svg })
              .then(() => {
                setDiagramSaveSuccess(true); // Show notification
              })
              .catch(() => {/* Optionally show error notification */});
            // Save SVG to backend for persistence
            fetch(`${API_BASE_URL}/clinical-intake/save-trial-schema-svg`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ projectId: currentProject._id, svg })
            });
          }
        }
      }
    }
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [showDrawio, trialSchemaDiagramXml]);

  if ((isLoading || projectContextLoading) && !isGeneratingSections) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>
          {isLoading ? 'Loading sections...' : 'Loading project...'}
        </Typography>
      </Box>
    );
  }
  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ position: 'relative', width: '100%', minHeight: '100vh' }}>
      {/* Animated Clinical Background */}
      <Box
        component="style"
        sx={{ display: 'none' }}
        dangerouslySetInnerHTML={{
          __html: `
            @keyframes gradientShift {
              0% { background-position: 0% 50%; }
              50% { background-position: 100% 50%; }
              100% { background-position: 0% 50%; }
            }
            @keyframes pulse {
              0%, 100% { opacity: 1; transform: scale(1); }
              50% { opacity: 0.8; transform: scale(1.05); }
            }
            @keyframes float1 {
              0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.3; }
              33% { transform: translate(30px, -40px) scale(1.2); opacity: 0.5; }
              66% { transform: translate(-20px, 30px) scale(0.8); opacity: 0.4; }
            }
            @keyframes float2 {
              0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.3; }
              33% { transform: translate(-50px, 20px) scale(1.15); opacity: 0.5; }
              66% { transform: translate(40px, -30px) scale(0.9); opacity: 0.4; }
            }
            @keyframes float3 {
              0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.3; }
              33% { transform: translate(25px, 45px) scale(1.25); opacity: 0.5; }
              66% { transform: translate(-35px, -25px) scale(0.85); opacity: 0.4; }
            }
            @keyframes float4 {
              0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.3; }
              33% { transform: translate(-40px, -50px) scale(1.1); opacity: 0.5; }
              66% { transform: translate(50px, 35px) scale(0.9); opacity: 0.4; }
            }
            @keyframes float5 {
              0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.3; }
              33% { transform: translate(60px, -25px) scale(1.3); opacity: 0.5; }
              66% { transform: translate(-30px, 40px) scale(0.75); opacity: 0.4; }
            }
          `,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 0,
          overflow: 'hidden',
          background: 'linear-gradient(135deg, #e3f2fd 0%, #e8f5e9 25%, #e0f2f1 50%, #e1f5fe 75%, #f3e5f5 100%)',
          backgroundSize: '400% 400%',
          animation: 'gradientShift 20s ease infinite',
          '&::before': {
            content: '""',
            position: 'absolute',
            width: '100%',
            height: '100%',
            background: 'radial-gradient(circle at 20% 50%, rgba(22, 138, 173, 0.15) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(118, 200, 147, 0.15) 0%, transparent 50%), radial-gradient(circle at 40% 20%, rgba(22, 138, 173, 0.12) 0%, transparent 50%)',
            animation: 'pulse 8s ease-in-out infinite',
          },
        }}
      >
        {/* Floating Particles with Clinical Colors */}
        {[
          { color: 'rgba(22, 138, 173, 0.2)', size: 120, left: '10%', top: '20%', anim: 'float1', delay: 0 },
          { color: 'rgba(76, 175, 80, 0.2)', size: 150, left: '80%', top: '30%', anim: 'float2', delay: 1 },
          { color: 'rgba(0, 172, 193, 0.2)', size: 100, left: '50%', top: '10%', anim: 'float3', delay: 2 },
          { color: 'rgba(22, 138, 173, 0.15)', size: 180, left: '20%', top: '70%', anim: 'float4', delay: 0.5 },
          { color: 'rgba(76, 175, 80, 0.18)', size: 140, left: '70%', top: '60%', anim: 'float5', delay: 1.5 },
          { color: 'rgba(0, 172, 193, 0.15)', size: 160, left: '40%', top: '80%', anim: 'float1', delay: 2.5 },
          { color: 'rgba(22, 138, 173, 0.12)', size: 110, left: '90%', top: '15%', anim: 'float2', delay: 0.8 },
          { color: 'rgba(76, 175, 80, 0.16)', size: 130, left: '15%', top: '50%', anim: 'float3', delay: 1.2 },
          { color: 'rgba(0, 172, 193, 0.14)', size: 170, left: '60%', top: '40%', anim: 'float4', delay: 2.2 },
          { color: 'rgba(22, 138, 173, 0.13)', size: 120, left: '30%', top: '90%', anim: 'float5', delay: 0.3 },
          { color: 'rgba(156, 39, 176, 0.12)', size: 140, left: '85%', top: '75%', anim: 'float1', delay: 1.8 },
          { color: 'rgba(76, 175, 80, 0.14)', size: 100, left: '5%', top: '40%', anim: 'float2', delay: 0.6 },
          { color: 'rgba(0, 172, 193, 0.16)', size: 150, left: '75%', top: '50%', anim: 'float3', delay: 2.8 },
          { color: 'rgba(22, 138, 173, 0.11)', size: 130, left: '55%', top: '25%', anim: 'float4', delay: 1.1 },
          { color: 'rgba(76, 175, 80, 0.15)', size: 160, left: '25%', top: '60%', anim: 'float5', delay: 0.9 },
        ].map((particle, i) => (
          <Box
            key={i}
            sx={{
              position: 'absolute',
              borderRadius: '50%',
              background: particle.color,
              width: `${particle.size}px`,
              height: `${particle.size}px`,
              left: particle.left,
              top: particle.top,
              animation: `${particle.anim} ${15 + i * 2}s ease-in-out infinite`,
              animationDelay: `${particle.delay}s`,
              filter: 'blur(40px)',
              transition: 'all 0.3s ease',
            }}
          />
        ))}
      </Box>
      
      <Box sx={{ 
        position: 'relative', 
        zIndex: 1, 
        height: '100%', 
        bgcolor: 'transparent', 
        minHeight: '100vh', 
        width: '100%',
        p: 3, 
        mt: 0 
      }}>
        {/* Clinical Intake Change Indicator */}
        {actualProjectId && !protocolEditingDisabled && (
          <ClinicalIntakeChangeIndicator
            projectId={actualProjectId}
            allSections={sections}
            onRegenerateRequest={async (type, sectionTitles, changes) => {
              // Handle regeneration request
              await handleRegenerateSections(sectionTitles);
            }}
            onRegenerationComplete={() => {
              // This callback is called after regeneration completes
              // The change indicator will automatically refresh
            }}
          />
        )}
        
        <AppBar 
          position="static" 
          elevation={0}
          sx={{
            position: 'relative',
            zIndex: 10,
            background: 'linear-gradient(135deg, rgba(248, 249, 250, 0.95) 0%, rgba(233, 236, 239, 0.95) 100%)',
            backdropFilter: 'blur(10px)',
            borderRadius: '12px 12px 0 0',
            mb: 3,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            border: '1px solid rgba(222, 226, 230, 0.5)'
          }}
        >
          {protocolEditingDisabled && (
            <Alert severity="info" sx={{ mx: 2, mt: 1.5, mb: 0, py: 0.5 }}>
              This document is approved. Protocol editing and generation controls are disabled.
            </Alert>
          )}
          <Toolbar sx={{ 
            minHeight: 'auto',
            px: 2,
            py: 1,
             ...(protocolEditingDisabled && !workflowReviewToolbar
                ? { pointerEvents: 'none', opacity: 0.55, userSelect: 'none' }
                : {}),
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1.5,
            width: '100%',
            boxSizing: 'border-box',
          }}>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flex: '1 1 220px',
              minWidth: 0,
            }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: '#1E6091',
                letterSpacing: '0.2px'
              }}
            >
              Clinical Trial Protocol Editor
                {mode === 'preview' && (
                  <Typography 
                    component="span" 
                    sx={{ 
                      ml: 2, 
                      fontSize: '0.8em', 
                      color: '#666',
                      fontStyle: 'italic'
                    }}
                  >
                    (Canvas Mode)
                  </Typography>
                )}
                {mode === 'titlepage' && (
                  <Typography
                    component="span"
                    sx={{
                      ml: 2,
                      fontSize: '0.8em',
                      color: '#666',
                      fontStyle: 'italic',
                    }}
                  >
                    (Title Page)
                  </Typography>
                )}
            </Typography>
              
              {/* Generation Progress Indicator */}
              {generationProgress.isGenerating && (
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1,
                  bgcolor: '#e3f2fd',
                  px: 2,
                  py: 1,
                  borderRadius: 2,
                  border: '1px solid #bbdefb'
                }}>
                  <CircularProgress size={16} sx={{ color: '#168AAD' }} />
                  <Typography variant="body2" sx={{ color: '#168AAD', fontWeight: 500 }}>
                    Generating: {generationProgress.current}/{generationProgress.total} sections
                    {generationProgress.currentSection && (
                      <Typography component="span" sx={{ ml: 1, fontSize: '0.8em', opacity: 0.8 }}>
                        ({generationProgress.currentSection})
                      </Typography>
                    )}
                  </Typography>
                </Box>
              )}
            </Box>
            
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              flexWrap: 'wrap',
              justifyContent: 'flex-end',
              gap: 0.5,
              flex: '1 1 320px',
              minWidth: 0,
              maxWidth: '100%',
              '& .MuiButton-root': {
                borderRadius: '6px',
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '0.75rem',
                transition: 'all 0.2s ease-in-out',
                px: 1.5,
                py: 0.6,
                minHeight: '32px',
                '&:hover': {
                  transform: 'translateY(-1px)',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.1)'
                }
              }
            }}>
              <Tooltip
                title={
                  protocolEditingDisabled
                    ? 'This document is approved — editing is disabled'
                    : mode === 'titlepage'
                      ? 'Back to sections (Edit / Canvas)'
                      : `${mode === 'edit' ? 'Switch to Canvas Mode' : 'Switch to Edit Mode'} (Ctrl+P)`
                }
              >
              <Button
                  startIcon={
                    mode === 'titlepage'
                      ? <VisibilityIcon />
                      : mode === 'edit'
                        ? <VisibilityIcon />
                        : <EditIcon />
                  }
                  onClick={() => {
                    if (mode === 'titlepage') {
                      const backTo =
                        lastSectionsMode === 'edit' && !protocolEditingDisabled
                          ? 'edit'
                          : 'preview';
                      setMode(backTo);
                      return;
                    }
                    if (protocolEditingDisabled) return;
                    const next = mode === 'edit' ? 'preview' : 'edit';
                    setLastSectionsMode(next);
                    setMode(next);
                  }}
                  disabled={protocolEditingDisabled && mode !== 'titlepage'}
                  variant="contained"
                  sx={{
                    background:
                      mode === 'titlepage'
                        ? '#76C893'
                        : mode === 'edit'
                          ? '#168AAD'
                          : '#76C893',
                    color: 'white',
                    minWidth: '110px',
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      background:
                        mode === 'titlepage'
                          ? '#5BA373'
                          : mode === 'edit'
                            ? '#147A9A'
                            : '#5BA373',
                    },
                  }}
                >
                  {mode === 'titlepage'
                    ? (lastSectionsMode === 'edit' ? 'Edit Mode' : 'Canvas Mode')
                    : mode === 'edit'
                      ? 'Canvas Mode'
                      : 'Edit Mode'}
              </Button>
            </Tooltip>
              <Tooltip title="Title Page — generate and include in Word/PDF export">
              <Button
                  startIcon={<DescriptionIcon />}
                  onClick={() => {
                    if (mode === 'edit' || mode === 'preview') {
                      setLastSectionsMode(mode);
                    }
                    setMode('titlepage');
                  }}
                  variant="contained"
                  sx={{
                    background: mode === 'titlepage' ? '#5E60CE' : '#90A4AE',
                    color: 'white',
                    minWidth: '110px',
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      background: mode === 'titlepage' ? '#4C4FB8' : '#78909C',
                    },
                  }}
                >
                  Title Page
              </Button>
            </Tooltip>
            {!workflowReviewToolbar && mode === 'preview' && (
            <>
            <Tooltip title="Generate from Latest Clinical Intake">
              <Button 
                startIcon={<AddIcon />} 
                onClick={async () => {
                  try {
                    setIsGeneratingSections(true);
                    // Get latest clinical intake data
                    let latestIntake;
                    try {
                      const response = await clinicalIntakeService.getLatestClinicalIntake(actualProjectId);
                      latestIntake = response;
                      if (!latestIntake) {
                        throw new Error('No intake form for current project');
                      }
                    } catch (err) {
                      if (err.message === 'No intake form for current project') {
                        setError('No intake form for current project. Please fill out the clinical intake form first.');
                        setIsLoading(false);
                        return;
                      } else {
                        throw err;
                      }
                    }

                    // Get predefined sections
                    const predefinedSections = await getPredefinedSections(actualProjectId);
                    
                    // Clear existing sections first
                    await documentEditorService.deleteAllSections(actualProjectId);
                    
                    // Initialize progress state
                    setGenerationProgress({ 
                      current: 0, 
                      total: predefinedSections.length, 
                      isGenerating: true,
                      currentSection: ''
                    });
                    
                    // Initialize empty sections array for real-time updates
                    const initialSections = predefinedSections.map(section => ({
                      _id: `temp-${Date.now()}-${Math.random()}`,
                      title: section.title,
                      context: section.context,
                      prompt: '',
                      output: '',
                      final: false,
                      projectId: actualProjectId,
                      documentType: 'PROTOCOL',
                      useChunkedGeneration: section.useChunkedGeneration,
                      chunks: section.chunks
                    }));
                    
                    // Set initial empty sections and ensure loading is false
                    setSections(initialSections);
                    setIsLoading(false); // Ensure main loading is false when we start generation
                    
                    // Generate sections one by one with real-time updates
                    for (let i = 0; i < predefinedSections.length; i++) {
                      const section = predefinedSections[i];
                      const tempSectionId = initialSections[i]._id;
                      
                      try {
                        // Update progress and UI to show this section is being generated
                        setGenerationProgress(prev => ({ 
                          ...prev, 
                          current: i + 1,
                          currentSection: section.title
                        }));
                        
                        setSections(prevSections => 
                          prevSections.map(s => 
                            s._id === tempSectionId 
                              ? { ...s, output: 'Generating content...' }
                              : s
                          )
                        );
                        
                        // Filter clinical intake data for this specific section
                        const filteredIntakeData = filterClinicalIntakeForSection(latestIntake, section.title);
                        
                        // Generate content using the template's prompt and filtered context
                        let generatedContent = await generateContent(
                          section.prompt || '', 
                          section.context || '', 
                          JSON.stringify(filteredIntakeData),
                          'protocol',
                          section.title,
                          section,
                          buildGenerationStylePayload(latestIntake, currentProject),
                        );

                        // Apply universal validation and regeneration for ALL sections in automatic generation
                        try {
                          
                          // Get the current project's prompt from predefined sections
                          const predefinedSectionsForAuto2 = await getPredefinedSections(actualProjectId);
                          const originalSectionForAuto2 = findPredefinedSection(predefinedSectionsForAuto2, section.title);
                          const currentPromptForAuto2 = originalSectionForAuto2?.prompt || section.prompt || '';
                          
                          const validationResult = await validateAndRegenerateSection(
                            section.title,
                            generatedContent,
                            currentPromptForAuto2, // Use current project's prompt
                            section.context || '',
                            3, // max attempts
                            actualProjectId // Pass projectId to fetch fresh clinical intake data
                          );
                          
                          if (validationResult.success) {
                            generatedContent = validationResult.finalContent;
                            
                            // Store validation result in section data
                            section.validationResult = validationResult.finalValidation;
                          } else {
                            console.warn(`⚠️ ${section.title} validation failed after ${validationResult.attemptNumber} attempts, using original content`);
                            
                            // Store failed validation result
                            section.validationResult = validationResult.finalValidation || { isValid: false, overallScore: 0 };
                          }
                        } catch (error) {
                          console.error(`❌ ${section.title} validation error in automatic generation:`, error);
                          console.error(`❌ Error details:`, error.message);
                          
                          // Store error validation result
                          section.validationResult = {
                            isValid: false,
                            overallScore: 0,
                            errors: [{
                              subsection: 'system',
                              errorType: 'validation_error',
                              description: `Automatic validation failed: ${error.message}`,
                              severity: 'critical',
                              suggestion: 'Check validation service configuration'
                            }],
                            regenerationNeeded: true,
                            regenerationReason: 'Validation service error'
                          };
                          
                          // Continue with original content if validation fails
                        }

                        generatedContent = finalizeGeneratedProtocolOutput(
                          generatedContent,
                          section.title
                        );

                        // Create section in backend
                        const createdSection = await documentEditorService.createSection({
                          title: section.title,
                          context: section.context,
                          prompt: '',
                          output: generatedContent,
                          projectId: actualProjectId,
                          documentType: 'PROTOCOL'
                        }, actualProjectId);

                        // Update UI with the generated content and real section ID
                        setSections(prevSections => 
                          prevSections.map(s => 
                            s._id === tempSectionId 
                              ? { 
                                  ...s, 
                                  _id: createdSection.document._id,
                                  output: generatedContent,
                                  isNew: true // Flag to trigger animation
                                }
                              : s
                          )
                        );
                        
                        // Small delay to show the generation process
                        await new Promise(resolve => setTimeout(resolve, 500));
                        
                      } catch (err) {
                        console.error("Failed to create section:", err);
                        // Update UI to show error
                        setSections(prevSections => 
                          prevSections.map(s => 
                            s._id === tempSectionId 
                              ? { ...s, output: 'Error generating content. Please try again.' }
                              : s
                          )
                        );
                      }
                    }
                    
                    // Remove the isNew flag after a delay for animation
                    setTimeout(() => {
                      setSections(prevSections => 
                        prevSections.map(s => ({ ...s, isNew: false }))
                      );
                    }, 2000);
                    
                    // Reset generation progress and ensure loading states are correct
                    setGenerationProgress({ current: 0, total: 0, isGenerating: false, currentSection: '' });
                    setIsLoading(false); // Ensure main loading is false
                    
                  } catch (err) {
                    console.error("Failed to generate from clinical intake:", err);
                    setError("Failed to generate content from clinical intake. Please try again.");
                  } finally {
                    // Ensure loading state is reset
                    setIsGeneratingSections(false);
                    setIsLoading(false); // Ensure main loading is also reset
                  }
                }}
                                  sx={{
                    background: '#76C893',
                    color: 'white',
                    minWidth: '180px',
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      background: '#5BA373'
                  }
                }}
              >
                Generate from Clinical Intake
              </Button>
            </Tooltip>
            <input
              type="file"
              accept=".json,application/json"
              ref={usdmCatalogueFileInputRef}
              style={{ display: 'none' }}
              onChange={async (e) => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (!file || !actualProjectId) return;
                try {
                  setIsLoading(true);
                  const result = await protocolUsdmCatalogueService.uploadCatalogue(actualProjectId, file);
                  setError(null);
                  const hasWarnings = Array.isArray(result?.warnings) && result.warnings.length > 0;
                  setUsdmUploadSnackbar({
                    open: true,
                    severity: hasWarnings ? 'warning' : 'success',
                    message: hasWarnings
                      ? `Uploaded successfully with warnings: ${result.warnings.join(' ')}`
                      : `Uploaded successfully — ${result?.classCount ?? 0} classes, ${result?.instanceCount ?? 0} instances.`,
                  });
                } catch (err) {
                  console.error('Failed to upload USDM catalogue:', err);
                  setError(err.message || 'Failed to upload USDM catalogue');
                } finally {
                  setIsLoading(false);
                }
              }}
            />
            <Tooltip title="Generate from Uploaded USDM Catalogue">
              <Button
                ref={generateFromUsdmButtonRef}
                startIcon={<AddIcon />}
                onClick={async () => {
                  try {
                    setIsGeneratingSections(true);
                    // Get latest uploaded USDM catalogue
                    let latestCatalogueRaw;
                    try {
                      latestCatalogueRaw = await protocolUsdmCatalogueService.getLatestCatalogue(actualProjectId);
                      if (!latestCatalogueRaw) {
                        throw new Error('No USDM catalogue for current project');
                      }
                    } catch (err) {
                      if (err.message === 'No USDM catalogue for current project') {
                        setError('No USDM catalogue for current project. Please upload a USDM catalogue JSON first.');
                        setIsLoading(false);
                        return;
                      } else {
                        throw err;
                      }
                    }

                    // USDM-native section prompts (usdmProtocolSectionData.js) reference
                    // this `facts` object's own field names directly (facts.objectivesByBucket,
                    // facts.arms, facts.population, ...) — no translation into the clinical
                    // intake taxonomy needed.
                    const usdmFacts = normalizeUsdmCatalogue(latestCatalogueRaw);
                    // USDM generation is deliberately independent of Clinical Intake — no
                    // intake record is fetched or referenced here, even if one happens to
                    // exist for this project. buildGenerationStylePayload(null, currentProject)
                    // below reflects that: sponsor name/trial acronym come from the project,
                    // everything intake-specific (submissionRegion/productName/therapeuticArea)
                    // is simply omitted rather than pulled from a different, unrelated flow.
                    // Template Language is merged unconditionally into every section here —
                    // matching exactly what "Generate from Clinical Intake" already does
                    // (getPredefinedSections(), sectionData.js): every row with content gets
                    // dumped into the shared context, regardless of any checkbox. Whether a
                    // given row is actually included is controlled entirely by that row's own
                    // "Template" checkbox on the Template Language tab (TemplateLanguageForm.jsx)
                    // — there is no separate USDM-specific selection to make here.
                    const usdmTemplateLanguageData = await fetchTemplateLanguageForUsdm(actualProjectId);
                    const baseUsdmContext = mergeTemplateLanguageBlock(
                      buildUsdmCatalogueGlobalContext(usdmFacts),
                      usdmTemplateLanguageData
                    );

                    // Per-field "Use Template" text from the USDM Template tab
                    // (usdmTemplateFieldGeneration.js) — group each checked field's
                    // already-generated, already-reviewed text under the top-level USDM
                    // section it belongs to (by leading section number, e.g. "9.1.1..."
                    // -> "9. ADVERSE EVENTS..."), so it can be appended to that section's
                    // context below instead of every section's.
                    const { templateFieldSelections: usdmFieldSelections, templateFieldValues: usdmFieldValues } =
                      await protocolUsdmCatalogueService.getTemplateFieldState(actualProjectId);
                    const templateFieldTextBySection = new Map();
                    for (const [fieldName, checked] of Object.entries(usdmFieldSelections || {})) {
                      const text = (usdmFieldValues || {})[fieldName];
                      if (!checked || !text) continue;
                      const targetSection = resolveUsdmSectionForField(fieldName);
                      if (!targetSection) continue;
                      const list = templateFieldTextBySection.get(targetSection.title) || [];
                      list.push(text);
                      templateFieldTextBySection.set(targetSection.title, list);
                    }

                    const predefinedSections = getUsdmProtocolSections();

                    // Clear existing sections first (same as "Generate from Clinical Intake")
                    await documentEditorService.deleteAllSections(actualProjectId);

                    setGenerationProgress({
                      current: 0,
                      total: predefinedSections.length,
                      isGenerating: true,
                      currentSection: ''
                    });

                    const initialSections = predefinedSections.map(section => ({
                      _id: `temp-${Date.now()}-${Math.random()}`,
                      title: section.title,
                      // Placeholder shown before this section's real (possibly
                      // Template-Language-merged) context is computed in the loop below.
                      context: baseUsdmContext,
                      prompt: '',
                      output: '',
                      final: false,
                      projectId: actualProjectId,
                      documentType: 'PROTOCOL',
                      generationSource: 'usdm',
                      useChunkedGeneration: section.useChunkedGeneration,
                      chunks: section.chunks
                    }));

                    setSections(initialSections);
                    setIsLoading(false);

                    for (let i = 0; i < predefinedSections.length; i++) {
                      const section = predefinedSections[i];
                      const tempSectionId = initialSections[i]._id;

                      try {
                        setGenerationProgress(prev => ({
                          ...prev,
                          current: i + 1,
                          currentSection: section.title
                        }));

                        setSections(prevSections =>
                          prevSections.map(s =>
                            s._id === tempSectionId
                              ? { ...s, output: 'Generating content...' }
                              : s
                          )
                        );

                        // 1.3 SCHEDULE OF ACTIVITIES is the one section USDM actually
                        // models as structured data (ScheduleTimeline) rather than prose —
                        // render the real encounter x activity grid directly instead of
                        // asking the LLM to narrate it. Every other section is untouched:
                        // still LLM-prompt-generated exactly as before, and the Clinical
                        // Intake-driven "Generate from Clinical Intake" path for this same
                        // section title (sectionData.js) isn't touched by this at all.
                        const scheduleOfActivitiesHtml =
                          section.title === '1.3 SCHEDULE OF ACTIVITIES'
                            ? buildScheduleOfActivitiesHtml(scheduleOfActivitiesDataFromFacts(usdmFacts))
                            : '';

                        let generatedContent;
                        let sectionContext = baseUsdmContext;

                        if (scheduleOfActivitiesHtml) {
                          // Directly built from USDM data — no LLM call, no
                          // validateAndRegenerateSection (nothing to validate against a
                          // prompt when there was none), no finalizeGeneratedProtocolOutput
                          // (built for LLM prose cleanup, not table HTML that must stay
                          // exactly as built).
                          generatedContent = scheduleOfActivitiesHtml;
                        } else {
                          // Any USDM Template tab fields whose target section is this one
                          // (e.g. "9.1.1 Definition of Adverse event" -> "9. ADVERSE EVENTS...")
                          // get their already-generated, already-reviewed text appended here,
                          // with an instruction to incorporate it rather than rewrite it.
                          const fieldTexts = templateFieldTextBySection.get(section.title);
                          sectionContext = fieldTexts?.length
                            ? `${baseUsdmContext}\n\n--- PRE-FINALIZED SUBSECTIONS (from USDM Template tab — incorporate verbatim, do not rewrite) ---\n${fieldTexts.join('\n\n')}`
                            : baseUsdmContext;

                          // The USDM-native prompt for this section references facts.*
                          // fields directly (see usdmProtocolSectionData.js); the section
                          // is sent only the facts its prompt points at.
                          const sectionStudyData = buildUsdmSectionStudyData(usdmFacts, section.prompt || '');
                          generatedContent = await generateContent(
                            section.prompt || '',
                            sectionContext,
                            JSON.stringify(sectionStudyData),
                            'protocol-usdm',
                            section.title,
                            section,
                            buildGenerationStylePayload(null, currentProject),
                          );

                          // Apply universal validation and regeneration for ALL sections
                          try {
                            const validationResult = await validateAndRegenerateSection(
                              section.title,
                              generatedContent,
                              section.prompt || '',
                              buildUsdmValidationContext(sectionContext, sectionStudyData),
                              3, // max attempts
                              actualProjectId,
                              // Server skips Clinical Intake and accepts "Not yet defined
                              // in Study Design" instead of forcing invented content.
                              // studyData is the same facts JSON the generator received.
                              { source: 'usdm', studyData: JSON.stringify(usdmFacts) }
                            );

                            if (validationResult.success) {
                              generatedContent = validationResult.finalContent;
                              section.validationResult = validationResult.finalValidation;
                            } else {
                              console.warn(`⚠️ ${section.title} validation failed after ${validationResult.attemptNumber} attempts, using original content`);
                              section.validationResult = validationResult.finalValidation || { isValid: false, overallScore: 0 };
                            }
                          } catch (error) {
                            console.error(`❌ ${section.title} validation error in USDM generation:`, error);
                            section.validationResult = {
                              isValid: false,
                              overallScore: 0,
                              errors: [{
                                subsection: 'system',
                                errorType: 'validation_error',
                                description: `Automatic validation failed: ${error.message}`,
                                severity: 'critical',
                                suggestion: 'Check validation service configuration'
                              }],
                              regenerationNeeded: true,
                              regenerationReason: 'Validation service error'
                            };
                          }

                          generatedContent = finalizeGeneratedProtocolOutput(generatedContent, section.title);
                        }

                        const createdSection = await documentEditorService.createSection({
                          title: section.title,
                          context: sectionContext,
                          prompt: '',
                          output: generatedContent,
                          projectId: actualProjectId,
                          documentType: 'PROTOCOL',
                          generationSource: 'usdm',
                          // Only truthy for 1.3 SCHEDULE OF ACTIVITIES when real USDM
                          // schedule data was extracted (see scheduleOfActivitiesHtml
                          // above) — undefined/null for every other section, matching
                          // the model's own default.
                          // Prefer the rich {tables:[...]} shape (extension:soaGrid) when
                          // the upload has it — same {columns, rowGroups, grid, footnotes}
                          // shape SoaTableRenderer (the S9 module's own renderer) consumes,
                          // so PublishedSoaView shows the identical grouped/tiered-header
                          // table for USDM-generated content. Falls back to the flatter
                          // {visits,sections,footnotes} reconstruction otherwise.
                          scheduleOfActivitiesData: scheduleOfActivitiesHtml
                            ? scheduleOfActivitiesDataFromFacts(usdmFacts)
                            : null,
                        }, actualProjectId);

                        // PublishedSoaView (Section 1.3's actual renderer) reads this
                        // React state, never section.output — set it immediately so the
                        // table shows without needing a reload. seedGeneratedSoADataFromUsdmSection
                        // (fetchSectionsImpl) restores it from the section record above on
                        // the next reload.
                        if (scheduleOfActivitiesHtml) {
                          setGeneratedSoAData(scheduleOfActivitiesDataFromFacts(usdmFacts));
                        }

                        setSections(prevSections =>
                          prevSections.map(s =>
                            s._id === tempSectionId
                              ? {
                                  ...s,
                                  _id: createdSection.document._id,
                                  output: generatedContent,
                                  isNew: true
                                }
                              : s
                          )
                        );

                        await new Promise(resolve => setTimeout(resolve, 500));

                      } catch (err) {
                        console.error("Failed to create section from USDM:", err);
                        setSections(prevSections =>
                          prevSections.map(s =>
                            s._id === tempSectionId
                              ? { ...s, output: 'Error generating content. Please try again.' }
                              : s
                          )
                        );
                      }
                    }

                    setTimeout(() => {
                      setSections(prevSections =>
                        prevSections.map(s => ({ ...s, isNew: false }))
                      );
                    }, 2000);

                    setGenerationProgress({ current: 0, total: 0, isGenerating: false, currentSection: '' });
                    setIsLoading(false);

                  } catch (err) {
                    console.error("Failed to generate from USDM catalogue:", err);
                    setError("Failed to generate content from USDM catalogue. Please try again.");
                  } finally {
                    setIsGeneratingSections(false);
                    setIsLoading(false);
                  }
                }}
                // Moved to the USDM Template tab as "Generate Protocol" — kept mounted
                // but hidden here (rather than deleted) so its exact onClick logic can
                // still run unmodified via generateFromUsdmButtonRef.current?.click()
                // (see the autoGenerateFromUsdm effect above).
                sx={{
                  display: 'none',
                  background: '#5E60CE',
                  color: 'white',
                  minWidth: '160px',
                  whiteSpace: 'nowrap',
                  '&:hover': {
                    background: '#4C4FB8'
                  }
                }}
              >
                Generate from USDM
              </Button>
            </Tooltip>
            {/* <Tooltip title="Email Protocol PDF">
              <Button
                startIcon={<SendIcon />}
                onClick={async () => {
                  startSpan('ui_protocol_email_pdf_native', { page: 'protocol_editor', meta: { projectId: actualProjectId || null } });
                  try {
                    if (!actualProjectId) {
                      setError('No project selected. Cannot email protocol PDF.');
                      endSpan('ui_protocol_email_pdf_native', { status: 'error' });
                      return;
                    }
                    if (!sections || sections.length === 0) {
                      setError('No sections available to export. Generate the protocol first.');
                      endSpan('ui_protocol_email_pdf_native', { status: 'error' });
                      return;
                    }
                    setIsLoading(true);
                    
                    const pdfBlob = await buildNativeProtocolPdfBlob();

                    // Email the PDF instead of downloading
                    const response = await documentExportService.emailProtocolPdfFromFile(actualProjectId, pdfBlob);
                    setError(null);
                    // Show success message
                    alert(response.message || 'PDF sent successfully via email!');
                    endSpan('ui_protocol_email_pdf_native', { status: 'ok' });
                  } catch (err) {
                    console.error('Failed to email protocol PDF:', err);
                    setError(err.message || 'Failed to email protocol PDF');
                    endSpan('ui_protocol_email_pdf_native', { status: 'error' });
                  } finally {
                    setIsLoading(false);
                  }
                }}
                variant="outlined"
                color="primary"
                sx={{
                  minWidth: '120px',
                  whiteSpace: 'nowrap',
                }}
              >
                Email PDF
              </Button>
            </Tooltip> */}
        
            
            <Tooltip title="Export to Word, PDF (from Word), PDF (native), XML, JSON, and more">
              <Button 
                startIcon={isExportingPdf ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <ExportIcon />}
                variant="contained"
                disabled={isExportingPdf}
                onClick={(event) => setExportAnchorEl(event.currentTarget)}
                sx={{
                  background: '#168AAD',
                  color: 'white',
                  minWidth: '80px',
                  whiteSpace: 'nowrap',
                  '&:hover': {
                    background: '#168AAD'
                  }
                }}
              >
                Export
              </Button>
            </Tooltip>
            
            {/* Sync & Validate Dropdown */}
            <Tooltip title="Sync to KG, extract entities, and validate sections for export (M11, fonts §4.1, etc.)">
                <Button
                startIcon={isSyncingToKG || isExtractingEntities ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <UploadIcon />}
                  variant="contained"
                onClick={(event) => setSyncValidateAnchorEl(event.currentTarget)}
                disabled={sections.length === 0 || !currentProject?._id || isSyncingToKG || isExtractingEntities}
                  sx={{
                  background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                    color: 'white',
                  minWidth: '140px',
                    whiteSpace: 'nowrap',
                    '&:hover': {
                    background: 'linear-gradient(135deg, #0d7f72 0%, #2dd368 100%)'
                    },
                    '&:disabled': {
                      background: '#cccccc',
                      color: '#666666'
                    }
                  }}
                >
                {isSyncingToKG ? 'Syncing...' : isExtractingEntities ? 'Extracting...' : 'Sync & Validate'}
                </Button>
              </Tooltip>
            
            {/* Sync & Validate Menu */}
            <Menu
              anchorEl={syncValidateAnchorEl}
              open={Boolean(syncValidateAnchorEl)}
              onClose={() => setSyncValidateAnchorEl(null)}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              PaperProps={{
                sx: {
                  borderRadius: '8px',
                  mt: 1,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                  minWidth: 280,
                  '& .MuiMenuItem-root': {
                    borderRadius: '6px',
                    mx: 0.5,
                    my: 0.25,
                  '&:hover': {
                      backgroundColor: 'rgba(22, 138, 173, 0.08)'
                    }
                  }
                }
              }}
            >
              <MenuItem 
                onClick={async () => {
                  setSyncType('standard');
                  setSyncValidateAnchorEl(null);
                  await handleSyncToKnowledgeGraph('standard');
                }}
                disabled={isSyncingToKG || isExtractingEntities}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  {isSyncingToKG && syncType === 'standard' ? (
                    <CircularProgress size={16} sx={{ mr: 1, color: '#11998e' }} />
                  ) : (
                    <UploadIcon sx={{ mr: 1, color: '#11998e' }} />
                  )}
                  {isSyncingToKG && syncType === 'standard' ? 'Syncing...' : 'Sync to KG (Standard)'}
                </Box>
              </MenuItem>
            
              <MenuItem 
                onClick={async () => {
                  setSyncType('embeddings');
                  setSyncValidateAnchorEl(null);
                  await handleSyncToKnowledgeGraph('embeddings');
                }}
                disabled={isSyncingToKG || isExtractingEntities}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  {isSyncingToKG && syncType === 'embeddings' ? (
                    <CircularProgress size={16} sx={{ mr: 1, color: '#667eea' }} />
                  ) : (
                    <UploadIcon sx={{ mr: 1, color: '#667eea' }} />
                  )}
                  {isSyncingToKG && syncType === 'embeddings' ? 'Syncing with Embeddings...' : 'Sync to KG Vector Embeddings'}
                </Box>
              </MenuItem>
              
              <MenuItem 
                onClick={async () => {
                  setSyncValidateAnchorEl(null);
                  try {
                    // First sync with embeddings
                    setSyncType('embeddings');
                    await handleSyncToKnowledgeGraph();
                    // Wait a bit to ensure sync is fully complete
                    await new Promise(resolve => setTimeout(resolve, 500));
                    // Then extract entities
                    await handleExtractEntities();
                  } catch (error) {
                    console.error('Error in sync and extract flow:', error);
                    alert(`Error: ${error.message}`);
                  }
                }}
                disabled={isSyncingToKG || isExtractingEntities || !currentProject?._id}
                sx={{ display: 'none' }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  {isSyncingToKG || isExtractingEntities ? (
                    <CircularProgress size={16} sx={{ mr: 1, color: '#667eea' }} />
                  ) : (
                    <AutoFixHighIcon sx={{ mr: 1, color: '#667eea' }} />
                  )}
                  {isSyncingToKG ? 'Syncing...' : isExtractingEntities ? 'Extracting Entities...' : 'Sync to KG Vector Embeddings Entity'}
                </Box>
              </MenuItem>
              
              <Divider sx={{ my: 0.5, display: 'none' }} />
              
              <MenuItem 
                onClick={() => {
                  setSyncValidateAnchorEl(null);
                  handleExtractEntities();
                }}
                disabled={!currentProject?._id || isExtractingEntities || isExtractingStandardEntities || isSyncingToKG}
                sx={{ display: 'none' }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  {isExtractingEntities ? (
                    <CircularProgress size={16} sx={{ mr: 1, color: '#9c27b0' }} />
                  ) : (
                    <AutoFixHighIcon sx={{ mr: 1, color: '#9c27b0' }} />
                  )}
                  {isExtractingEntities ? 'Extracting...' : 'Extract Entities (Flexible)'}
                </Box>
              </MenuItem>
              
              <MenuItem 
                onClick={() => {
                  setSyncValidateAnchorEl(null);
                  handleExtractStandardEntities();
                }}
                disabled={!currentProject?._id || isExtractingEntities || isExtractingStandardEntities || isSyncingToKG}
                sx={{ display: 'none' }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  {isExtractingStandardEntities ? (
                    <CircularProgress size={16} sx={{ mr: 1, color: '#2e7d32' }} />
                  ) : (
                    <AutoFixHighIcon sx={{ mr: 1, color: '#2e7d32' }} />
                  )}
                  {isExtractingStandardEntities ? 'Extracting...' : 'Extract Standard Entities (60+ Types)'}
                </Box>
              </MenuItem>
              
              <MenuItem 
                onClick={() => {
                  setSyncValidateAnchorEl(null);
                  handleValidateSectionsForExport();
                }}
                disabled={sections.length === 0 || isSyncingToKG || isExtractingEntities || isValidatingExport}
                sx={{ alignItems: 'flex-start', py: 1.25, display: 'none' }}
              >
                {isValidatingExport ? (
                  <CircularProgress size={16} sx={{ mr: 1, color: '#667eea', mt: 0.25 }} />
                ) : (
                  <CheckCircleIcon sx={{ mr: 1, color: '#667eea', mt: 0.25 }} />
                )}
                <ListItemText
                  primary={isValidatingExport ? 'Validating…' : 'Validate Sections for export'}
                  secondary="Includes Font Compliance (Times New Roman, §4.1 sizes in HTML), M11 sections, and other export gates."
                  primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                  secondaryTypographyProps={{ variant: 'caption', sx: { whiteSpace: 'normal', opacity: 0.85 } }}
                  sx={{ my: 0 }}
                />
              </MenuItem>

              {/* <MenuItem
                onClick={() => {
                  setSyncValidateAnchorEl(null);
                  handleValidateConsistency();
                }}
                disabled={
                  sections.length === 0 ||
                  !currentProject?._id ||
                  isSyncingToKG ||
                  isExtractingEntities ||
                  isExtractingStandardEntities ||
                  isValidating
                }
                sx={{ alignItems: 'flex-start', py: 1.25 }}
              >
                {isValidating ? (
                  <CircularProgress size={16} sx={{ mr: 1, color: '#0d47a1', mt: 0.25 }} />
                ) : (
                  <KnowledgeGraphIcon sx={{ mr: 1, color: '#0d47a1', mt: 0.25 }} />
                )}
                <ListItemText
                  primary={isValidating ? 'Validating…' : 'Validate against Knowledge Graph'}
                  secondary="Compares protocol sections to expected facts in Neo4j (drug, dose, population, etc.). Sync to KG first if the graph is empty."
                  primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                  secondaryTypographyProps={{ variant: 'caption', sx: { whiteSpace: 'normal', opacity: 0.85 } }}
                  sx={{ my: 0 }}
                />
              </MenuItem> */}

              <Divider sx={{ my: 0.5 }} />

              <MenuItem
                onClick={() => {
                  setSyncValidateAnchorEl(null);
                  handleRunAiValidations();
                }}
                disabled={
                  sections.length === 0 ||
                  isSyncingToKG ||
                  isExtractingEntities ||
                  isExtractingStandardEntities ||
                  isValidatingAi
                }
                sx={{ alignItems: 'flex-start', py: 1.25 }}
              >
                {isValidatingAi ? (
                  <CircularProgress size={16} sx={{ mr: 1, color: '#6a1b9a', mt: 0.25 }} />
                ) : (
                  <SmartToyIcon sx={{ mr: 1, color: '#6a1b9a', mt: 0.25 }} />
                )}
                <ListItemText
                  primary={isValidatingAi ? 'Running AI validations…' : 'AI Validations (Dizzaroo Style)'}
                  secondary="Style guide rules (abbreviations, cross-refs, fonts) plus Gemini semantic checks and KG consistency. Based on AI_Validation_Checks workbook."
                  primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                  secondaryTypographyProps={{ variant: 'caption', sx: { whiteSpace: 'normal', opacity: 0.85 } }}
                  sx={{ my: 0 }}
                />
              </MenuItem>
            </Menu>
            <Menu
              anchorEl={exportAnchorEl}
              open={Boolean(exportAnchorEl)}
              onClose={() => setExportAnchorEl(null)}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
                                PaperProps={{
                    sx: {
                      borderRadius: '8px',
                      mt: 1,
                      boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                      '& .MuiMenuItem-root': {
                        borderRadius: '6px',
                        mx: 0.5,
                        my: 0.25,
                        '&:hover': {
                          backgroundColor: 'rgba(22, 138, 173, 0.08)'
                        }
                      }
                    }
                  }}
            >
              <MenuItem
                onClick={() => {
                  handleProtocolStyleCheck();
                }}
                disabled={sections.length === 0}
                sx={{ alignItems: 'flex-start', py: 1.25 }}
              >
                <SpellcheckIcon sx={{ mr: 1, color: '#168AAD', mt: 0.25 }} />
                <ListItemText
                  primary="Check protocol style & typography"
                  secondary="Server YAML rules plus inline HTML fonts vs Draft 1.0 §4.1 (FONT_INLINE / FONT_LEGACY)."
                  primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                  secondaryTypographyProps={{ variant: 'caption', sx: { whiteSpace: 'normal', opacity: 0.85 } }}
                  sx={{ my: 0 }}
                />
              </MenuItem>
              <MenuItem onClick={() => {
                handleExport();
                setExportAnchorEl(null);
              }}>
                <ExportIcon sx={{ mr: 1, color: '#168AAD' }} />
                Export to DOCX
              </MenuItem>
              <MenuItem
                disabled={isExportingPdf}
                onClick={() => {
                  setExportAnchorEl(null);
                  handleExportFullPDF();
                }}
                sx={{ alignItems: 'flex-start', py: 1.25, display: 'none' }}
              >
                <ExportIcon sx={{ mr: 1, color: '#168AAD', mt: 0.25 }} />
                <ListItemText
                  primary={isExportingPdf ? 'Exporting PDF…' : 'Export to PDF (from Word)'}
                  secondary="Recommended. Builds DOCX first, then converts via server or Microsoft Graph for correct tables and layout."
                  primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                  secondaryTypographyProps={{ variant: 'caption', sx: { whiteSpace: 'normal', opacity: 0.85 } }}
                  sx={{ my: 0 }}
                />
              </MenuItem>
              <MenuItem
                disabled={isExportingPdf}
                onClick={() => {
                  setExportAnchorEl(null);
                  handleExportFullPDFNative();
                }}
                sx={{ alignItems: 'flex-start', py: 1.25 }}
              >
                <ExportIcon sx={{ mr: 1, color: '#168AAD', mt: 0.25 }} />
                <ListItemText
                  primary={isExportingPdf ? 'Exporting PDF…' : 'Export to PDF (Native)'}
                  secondary="Browser-only fallback. May have table or footer layout issues."
                  primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                  secondaryTypographyProps={{ variant: 'caption', sx: { whiteSpace: 'normal', opacity: 0.85 } }}
                  sx={{ my: 0 }}
                />
              </MenuItem>
              
              <MenuItem onClick={() => {
                exportToXML(sections, generatedSoAData, trialSchemaImageUrl, actualProjectId, titlePageConfig);
                setExportAnchorEl(null);
              }}>
                <ExportIcon sx={{ mr: 1, color: '#168AAD' }} />
                Export to XML
              </MenuItem>

              <MenuItem
                disabled={!actualProjectId || externalSdrPublishing}
                onClick={() => {
                  setExportAnchorEl(null);
                  handlePublishToExternalSdr();
                }}
                title="Validate USDM conformance and publish to an external TransCelerate SDR API (configured by EXTERNAL_SDR_* env vars on the server)."
              >
                {externalSdrPublishing ? <CircularProgress size={16} sx={{ mr: 1 }} /> : <UploadIcon sx={{ mr: 1, color: '#168AAD' }} />}
                {externalSdrPublishing ? 'Publishing…' : 'Publish to External SDR'}
              </MenuItem>

              <MenuItem
                onClick={() => {
                  handleExportJSON();
                  setExportAnchorEl(null);
                }}
                sx={{ display: 'none' }}
              >
                <ExportIcon sx={{ mr: 1, color: '#168AAD' }} />
                Export to JSON
              </MenuItem>
              
              <MenuItem 
                onClick={(event) => {
                  setFactsAnchorEl(event.currentTarget);
                  event.stopPropagation();
                }}
                sx={{ display: 'none' }}
              >
                <ExportIcon sx={{ mr: 1, color: '#168AAD' }} />
                Export Protocol Facts
                <ArrowRightIcon sx={{ ml: 'auto' }} />
              </MenuItem>
            </Menu>
            
            {/* Facts Submenu */}
            <Menu
              anchorEl={factsAnchorEl}
              open={Boolean(factsAnchorEl)}
              onClose={() => setFactsAnchorEl(null)}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              PaperProps={{
                sx: {
                  borderRadius: '8px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                  '& .MuiMenuItem-root': {
                    borderRadius: '6px',
                    mx: 0.5,
                    my: 0.25,
                    '&:hover': {
                      backgroundColor: 'rgba(22, 138, 173, 0.08)'
                    }
                  }
                }
              }}
            >
              <MenuItem onClick={() => {
                exportProtocolFactsAsText(sections, actualProjectId);
                setFactsAnchorEl(null);
                setExportAnchorEl(null);
              }}>
                <DescriptionIcon sx={{ mr: 1, color: '#168AAD' }} />
                Export as Text (.txt)
              </MenuItem>
              <MenuItem onClick={() => {
                exportProtocolFactsAsPDF(sections, actualProjectId);
                setFactsAnchorEl(null);
                setExportAnchorEl(null);
              }}>
                <PictureAsPdfIcon sx={{ mr: 1, color: '#d32f2f' }} />
                Export as PDF
              </MenuItem>
            </Menu>
            

            
            <Tooltip title="Send to Workflow Review">
              <Button 
                startIcon={<WorkflowIcon />} 
                variant="contained" 
                onClick={handleSendToWorkflow}
                sx={{
                  background: '#388e3c',
                  color: 'white',
                  minWidth: '150px',
                  whiteSpace: 'nowrap',
                  '&:hover': {
                    background: '#ef6c00'
                  }
                }}
              >
                Send to Workflow
              </Button>
            </Tooltip>
            </>
            )}
            </Box>
          </Toolbar>
        </AppBar>

        {activeErrorMessage && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            onClose={error ? () => setError(null) : undefined}
          >
            {activeErrorMessage}
          </Alert>
        )}
        {!activeErrorMessage && showValidationIndicator && (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#2e7d32', mr: 1 }} />
            <Typography variant="caption" sx={{ color: '#2e7d32', fontWeight: 600 }}>
              Validations passed
            </Typography>
          </Box>
        )}

        {renderValidationReport()}

        {/* Add Section Dialog */}
        <Dialog 
          open={openAddDialog} 
          onClose={handleCloseAddDialog}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              maxWidth: 500,
            }
          }}
        >
          <DialogTitle sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
            pb: 1
          }}>
            Add New Section
            <IconButton onClick={handleCloseAddDialog} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          
          <DialogContent sx={{ pt: 2, pb: 1, px: 3 }}>
            <Tabs 
              value={tabValue} 
              onChange={handleTabChange} 
              sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
              indicatorColor="primary"
              textColor="primary"
              variant="fullWidth"
            >
              <Tab label="Template" sx={{ textTransform: 'none' }} />
              <Tab label="Custom" sx={{ textTransform: 'none' }} />
            </Tabs>
            
            {tabValue === 0 ? (
              <Box>
                {/* Field-level Template Selection Indicator */}
                <Box sx={{ mb: 2, p: 2, backgroundColor: 'rgba(22, 138, 173, 0.08)', borderRadius: 1, border: '1px solid #168AAD' }}>
                  <Typography variant="body2" sx={{ color: '#168AAD', fontWeight: 'bold' }}>
                    📋 Field-level Template Mode
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#666', display: 'block', mt: 0.5 }}>
                    Sections will be generated using template data for fields marked "Use Template" in Clinical Intake Form
                  </Typography>
                </Box>
                
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="section-select-label">Section Template</InputLabel>
                <Select
                  labelId="section-select-label"
                  value={selectedSectionTitle}
                  onChange={handleSectionTitleChange}
                  input={<OutlinedInput label="Section Template" />}
                  displayEmpty
                >
                  {availableSectionTitles.map((title) => (
                    <MenuItem key={title} value={title}>
                      {title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              </Box>
            ) : (
              <TextField
                fullWidth
                label="Custom Section Title"
                value={customSectionTitle}
                onChange={handleCustomTitleChange}
                sx={{ mb: 2 }}
                variant="outlined"
                placeholder="Enter a title for your custom section"
              />
            )}
          </DialogContent>
          
          <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid rgba(0, 0, 0, 0.12)' }}>
            <Button 
              onClick={handleCloseAddDialog} 
              variant="outlined"
              sx={{ borderRadius: 1 }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleAddSection}
              variant="contained"
              disabled={tabValue === 0 ? !selectedSectionTitle : !customSectionTitle}
              sx={{ ml: 1, borderRadius: 1 }}
            >
              Add Section
            </Button>
          </DialogActions>
        </Dialog>
        <Box sx={{ p: 1 }}>
          {/* Section navigation chips in Edit Mode (same behavior as Canvas Mode) */}
          {mode === 'edit' && sections && sections.length > 0 && (
            <Box
              sx={{
                mb: 2,
                p: 1.5,
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                backgroundColor: '#ffffff',
                borderRadius: 2,
                boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
                border: '1px solid rgba(0,0,0,0.05)',
              }}
            >
              {sections
                // Ensure we have unique sections by ID
                .filter((section, index, self) =>
                  index === self.findIndex(s => s._id === section._id)
                )
                // Sort sections by their numbered titles
                .sort((a, b) => {
                  const aMatch = a.title.match(/^(\d+)/);
                  const bMatch = b.title.match(/^(\d+)/);
                  if (aMatch && bMatch) {
                    return parseInt(aMatch[1], 10) - parseInt(bMatch[1], 10);
                  }
                  return a.title.localeCompare(b.title);
                })
                .map(section => (
                  <Chip
                    key={section._id}
                    label={section.title}
                    color="primary"
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      const element = document.getElementById(`section-${section._id}`);
                      if (element) {
                        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }
                    }}
                    sx={{
                      fontWeight: 500,
                      cursor: 'pointer'
                    }}
                  />
                ))}
            </Box>
          )}
                      {mode === 'titlepage' ? (
              <Paper sx={{ p: 3, borderRadius: 2, border: '1px solid #e0e0e0' }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#5E60CE', mb: 0.5 }}>
                      Title Page
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Generate the protocol title page from administrative info and clinical intake. It is included at the front of Word and PDF exports.
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        const backTo =
                          lastSectionsMode === 'edit' && !protocolEditingDisabled
                            ? 'edit'
                            : 'preview';
                        setMode(backTo);
                      }}
                      sx={{ textTransform: 'none' }}
                    >
                      Back to sections
                    </Button>
                    <Button
                    variant="contained"
                    startIcon={isGeneratingTitlePage ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <AutoAwesomeIcon />}
                    onClick={handleGenerateTitlePage}
                    disabled={isGeneratingTitlePage || !actualProjectId || protocolEditingDisabled}
                    sx={{ background: '#5E60CE', '&:hover': { background: '#4C4FB8' }, textTransform: 'none' }}
                  >
                    {titlePageConfig ? 'Regenerate Title Page' : 'Generate Title Page'}
                  </Button>
                  </Box>
                </Box>

                {!titlePageConfig ? (
                  <Box sx={{ py: 6, textAlign: 'center', bgcolor: '#fafafa', borderRadius: 2, border: '1px dashed #ccc' }}>
                    <DescriptionIcon sx={{ fontSize: 48, color: '#b0bec5', mb: 1 }} />
                    <Typography variant="body1" color="text.secondary">
                      No title page yet. Click Generate Title Page to build it from project data.
                    </Typography>
                  </Box>
                ) : (
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                      Title page fields (included in export)
                    </Typography>
                    <MuiTableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                      <MuiTable size="small">
                        <MuiTableHead>
                          <MuiTableRow sx={{ bgcolor: '#f5f5f5' }}>
                            <MuiTableCell sx={{ fontWeight: 700, width: '38%' }}>Field</MuiTableCell>
                            <MuiTableCell sx={{ fontWeight: 700 }}>Value</MuiTableCell>
                          </MuiTableRow>
                        </MuiTableHead>
                        <MuiTableBody>
                          {TITLE_PAGE_TABLE_FIELD_KEYS.map((fieldKey) => {
                            const field = titlePageConfig[fieldKey];
                            if (!field) return null;
                            const label = field.label || fieldKey;
                            const text = field.text || '';
                            if (!shouldIncludeTitlePageTableRow(fieldKey, field) && !text && protocolEditingDisabled) {
                              return null;
                            }
                            return (
                              <MuiTableRow key={fieldKey}>
                                <MuiTableCell sx={{ verticalAlign: 'top', fontWeight: 600 }}>{label}</MuiTableCell>
                                <MuiTableCell>
                                  {protocolEditingDisabled ? (
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                      {text || '—'}
                                    </Typography>
                                  ) : (
                                    <TextField
                                      fullWidth
                                      multiline
                                      minRows={fieldKey === 'regulatoryIdentifiers' || fieldKey === 'sponsorNameAddress' ? 2 : 1}
                                      size="small"
                                      value={text}
                                      onChange={(e) => handleTitlePageFieldChange(fieldKey, e.target.value)}
                                      placeholder="Enter value"
                                    />
                                  )}
                                </MuiTableCell>
                              </MuiTableRow>
                            );
                          })}
                        </MuiTableBody>
                      </MuiTable>
                    </MuiTableContainer>
                    <Typography variant="caption" color="text.secondary">
                      Edits are saved for this project and used on the next Word / PDF export.
                    </Typography>
                  </Box>
                )}
              </Paper>
            ) : mode === 'preview' ? (
              // Canvas Mode - Show document in ChatGPT-like canvas format
                          <DocumentCanvas
              sections={sections
                // Ensure we have unique sections by ID
                .filter((section, index, self) => 
                  index === self.findIndex(s => s._id === section._id)
                )
                // Sort sections by their numbered titles
                .sort((a, b) => {
                  const aMatch = a.title.match(/^(\d+)/);
                  const bMatch = b.title.match(/^(\d+)/);
                  if (aMatch && bMatch) {
                    return parseInt(aMatch[1]) - parseInt(bMatch[1]);
                  }
                  return a.title.localeCompare(b.title);
                })}
              mode={mode}
              trialSchemaImageUrl={trialSchemaImageUrl}
              generatedSoAData={generatedSoAData}
              s9SoaTables={s9SoaTables}
              s9PublishedVersion={s9PublishedVersion}
              actualProjectId={actualProjectId}
               handleSoADataUpdate={protocolEditingDisabled ? undefined : handleSoADataUpdate}
              readonlyContent={protocolEditingDisabled}
              onSectionClick={(sectionId) => {
                // Find the section and scroll to it
                const element = document.getElementById(`section-${sectionId}`);
                if (element) {
                  element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }}
              onCopyContent={handleCopyContent}
              onToggleBookmark={handleToggleBookmark}
               onUpdateSection={
                protocolEditingDisabled
                  ? undefined
                  : async (sectionId, updates) => {
                try {
                  // Update the section in the backend
                  await documentEditorService.updateSection(sectionId, {
                    title: sections.find(s => s._id === sectionId)?.title,
                    context: sections.find(s => s._id === sectionId)?.context,
                    prompt: updates.prompt,
                    output: updates.output,
                    documentType: 'PROTOCOL'
                  }, actualProjectId);

                  // Update local state
                  setSections(prevSections =>
                    prevSections.map(s =>
                      s._id === sectionId 
                        ? { ...s, output: updates.output, prompt: updates.prompt }
                        : s
                    )
                  );

                  return true;
                } catch (error) {
                  console.error('Failed to update section:', error);
                  throw error;
                }
              }}
              bookmarkedSections={bookmarkedSections}
              showSectionNumbers={false}
              enableAnimations={true}
            />
            ) : (
            // Edit Mode - Show traditional edit interface
            sections && sections.map((section) => (
            <Paper 
              key={section._id} 
              id={`section-${section._id}`}
              sx={{ 
                mb: 3, 
                p: 3, 
                borderRadius: 2, 
                position: 'relative',
                background: 'rgba(255, 255, 255, 0.85)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                transition: 'all 0.3s ease',
                '&:hover': { 
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  background: 'rgba(255, 255, 255, 0.92)',
                  transform: 'translateY(-2px)'
                }
              }}
            >
              {/* AI Assistant Button for this section */}
              <SectionAssistantButton
                sectionTitle={section.title}
                sectionContent={section.output}
                allSections={sections}
                position="top-right"
                variant="floating"
              />
              <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #eee', pb: 1 }}>
                <Typography variant="h6" sx={{ 
                  fontWeight: 400, 
                  color: 'primary.main'
                }}>
                  Section: {section.title}
                </Typography>
                
                {/* Section Validation Indicator */}
                <SectionValidationIndicator
                  sectionTitle={section.title}
                  sectionContent={section.output}
                  sectionPrompt={section.prompt}
                  context={section.context}
                  compact={true}
                  initialValidationResult={section.validationResult}
                />
                {/* Debug validation result */}
                {section.validationResult && (
                  <Box sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 1 }}>
                    Debug: {section.validationResult.isValid ? 'Valid' : 'Invalid'} 
                    (Score: {section.validationResult.overallScore || 0})
                  </Box>
                )}
                <Box sx={{ flexGrow: 1 }} />
                {/* Edit Mode Actions */}
                  <>
                <Tooltip title="Request AI Modification">
                  <IconButton 
                    size="small" 
                    onClick={() => {
                      setSelectedSectionForModification({
                        sectionId: section._id,
                        sectionNumber: section.sectionNumber || section.title?.match(/^(\d+(?:\.\d+)*)/)?.[1] || '',
                        sectionTitle: section.title || 'Untitled Section'
                      });
                      setChangeRequestDialogOpen(true);
                    }}
                    sx={{ 
                      color: '#168AAD',
                      '&:hover': { 
                        bgcolor: 'rgba(22, 138, 173, 0.08)',
                        color: '#168AAD'
                      }
                    }}
                  >
                    <AutoFixHighIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Copy Section">
                  <IconButton size="small" onClick={() => handleCopyContent(section.output)}><CopyIcon /></IconButton>
                </Tooltip>
                <Tooltip title="Version History">
                  <IconButton 
                    size="small" 
                    onClick={() => handleViewVersionHistory(section._id)}
                  >
                    <HistoryIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="More Options">
                  <IconButton size="small" onClick={(event) => handleMenuOpen(event, section._id)}>
                    <MoreIcon />
                  </IconButton>
                </Tooltip>
                  </>
              </Box>
              {/* Section rendering logic based on title */}
              {section.title === '1.2 TRIAL SCHEMA' ? (
                // Only show trial schema image for 1.2
                <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 500 }}>
                    Trial Schema Image
                  </Typography>
                  {trialSchemaImageUrl ? (
                    <Box sx={{ textAlign: 'center', position: 'relative' }}>
                      {trialSchemaImageUrl.trim().startsWith('<svg') ? (
                        <img
                          src={`data:image/svg+xml;utf8,${encodeURIComponent(trialSchemaImageUrl)}`}
                          alt="Trial Schema"
                          style={{ 
                            maxWidth: '100%', 
                            maxHeight: 300, 
                            border: '1px solid #ccc', 
                            borderRadius: 8,
                            cursor: 'pointer'
                          }}
                          onClick={() => setShowTrialSchemaPreview(true)}
                        />
                      ) : (
                      <img
                        src={trialSchemaImageUrl}
                        alt="Trial Schema"
                        style={{ 
                          maxWidth: '100%', 
                          maxHeight: 300, 
                          border: '1px solid #ccc', 
                          borderRadius: 8,
                          cursor: 'pointer'
                        }}
                        onClick={() => setShowTrialSchemaPreview(true)}
                      />
                      )}
                      <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                        Click to view full size
                      </Typography>
                      
                      {/* Image Edit Buttons */}
                      {mode === 'edit' && (
                        <Box sx={{ 
                          position: 'absolute', 
                          top: 8, 
                          right: 8, 
                          display: 'flex', 
                          gap: 1,
                          backgroundColor: 'rgba(255, 255, 255, 0.9)',
                          borderRadius: 1,
                          p: 0.5
                        }}>
                          <Tooltip title="Crop Image">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenImageEdit('crop')}
                              sx={{ 
                                backgroundColor: '#168AAD',
                                color: 'white',
                                '&:hover': { backgroundColor: '#168AAD' }
                              }}
                            >
                              <CropIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Image">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenImageEdit('delete')}
                              sx={{ 
                                backgroundColor: '#f44336',
                                color: 'white',
                                '&:hover': { backgroundColor: '#d32f2f' }
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </Box>
                  ) : (
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        No trial schema image or diagram available. Upload one or create a diagram.
                    </Typography>
                      {mode === 'edit' && (
                        <Box>
                          <input
                            accept="image/*"
                            style={{ display: 'none' }}
                            id="trial-schema-upload"
                            type="file"
                            onChange={handleTrialSchemaImageUpload}
                            disabled={uploadingImage}
                          />
                          <label htmlFor="trial-schema-upload">
                            <Button
                              variant="contained"
                              component="span"
                              startIcon={<UploadIcon />}
                              disabled={uploadingImage}
                              sx={{
                                backgroundColor: '#168AAD',
                                color: 'white',
                                '&:hover': {
                                  backgroundColor: '#168AAD'
                                }
                              }}
                            >
                              {uploadingImage ? 'Uploading...' : 'Upload Trial Schema Image'}
                            </Button>
                          </label>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                            Supported formats: JPG, PNG, GIF, SVG (Max 5MB)
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  )}
                  {/* Draw.io Integration Button */}
                  {mode === 'edit' && (
                  <Box sx={{ mt: 2, textAlign: 'center' }}>
                    <Button variant="outlined" color="primary" onClick={() => setShowDrawio(true)}>
                      Edit Trial Schema with draw.io
                    </Button>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      Tip: Click <b>File &gt; New</b> in the toolbar to start a new diagram.
                    </Typography>
                  </Box>
                  )}
                  {/* Draw.io Modal */}
                  <Dialog open={showDrawio || false} onClose={() => setShowDrawio(false)} maxWidth="xl" fullWidth>
                    <DialogTitle>Edit Trial Schema Diagram</DialogTitle>
                    <DialogContent sx={{ p: 0 }}>
                      <Box sx={{ p: 2, bgcolor: 'info.light' }}>
                        <Typography variant="body2" color="info.dark">
                          Tip: A blank diagram will be created automatically. Start drawing!
                        </Typography>
                      </Box>
                      <iframe
                        ref={drawioIframeRef}
                        title="Draw.io Editor"
                        width="100%"
                        height="700"
                        frameBorder="0"
                        src="https://embed.diagrams.net/?embed=1&ui=atlas&spin=1&proto=json"
                        style={{ border: 'none' }}
                        sandbox="allow-scripts allow-same-origin allow-popups allow-forms allow-downloads"
                      />
                    </DialogContent>
                    <DialogActions>
                      <Button onClick={handleSaveDiagram} color="primary" variant="contained">
                        Save Diagram to Trial Schema
                      </Button>
                      <Button onClick={() => setShowDrawio(false)} color="primary">Close</Button>
                    </DialogActions>
                  </Dialog>
                </Box>
              ) : section.title === '1.3 SCHEDULE OF ACTIVITIES' ? (
                <Box sx={{ mb: 3, p: 2, bgcolor: 'blue.50', borderRadius: 1, width: '100%', minWidth: 0, overflow: 'hidden' }}>
                  {(() => {
                    // The S9 tab (chat-based generation) and the USDM-catalogue-derived
                    // Edit SOA dialog are two separate SOA sources — PublishedSoaView
                    // prefers S9 data when it exists. Only point people at the S9 tab when
                    // it's actually the relevant source (real S9 data) or there's nothing
                    // to edit yet (no USDM-derived data either, so S9 is the only way to
                    // create a SOA at all). Otherwise "Open SOA tab" just sends someone to
                    // an unrelated generator for data they aren't actually using.
                    const hasS9Data = Boolean(s9SoaTables?.length || s9PublishedVersion != null);
                    const hasUsdmSoaData = Boolean(
                      generatedSoAData?.tables?.length || generatedSoAData?.visits?.length || generatedSoAData?.sections?.length
                    );
                    return (
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                          {hasS9Data
                            ? `Schedule of Activities is authored in the SOA tab (chat-based Assessment Schedule).${
                                s9PublishedVersion != null ? ` Published version: v${s9PublishedVersion}.` : ''
                              }`
                            : hasUsdmSoaData
                            ? 'Schedule of Activities is derived from the uploaded USDM catalogue.'
                            : 'No Schedule of Activities yet. Generate one from the SOA tab, or upload a USDM catalogue.'}
                        </Typography>
                        {mode === 'edit' && (hasS9Data || !hasUsdmSoaData) && (
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={() => {
                              const params = new URLSearchParams(location.search);
                              params.set('protocolSubTab', 'soa');
                              navigate({ search: params.toString() });
                            }}
                          >
                            Open SOA tab
                          </Button>
                        )}
                        {mode === 'edit' && hasUsdmSoaData && (
                          <Button variant="outlined" color="primary" onClick={() => setShowEditSoa(true)}>
                            Edit SOA
                          </Button>
                        )}
                      </Stack>
                    );
                  })()}
                  {generateSoAError && (
                    <Alert severity="error" sx={{ mb: 2 }}>{generateSoAError}</Alert>
                  )}
                  <PublishedSoaView
                    s9SoaTables={s9SoaTables}
                    s9PublishedVersion={s9PublishedVersion}
                    generatedSoAData={generatedSoAData}
                    protocolId={soaProtocolId || actualProjectId}
                    showHeader={false}
                  />
                  <EditSoaDialog
                    open={showEditSoa}
                    onClose={() => setShowEditSoa(false)}
                    sectionId={section._id}
                    projectId={actualProjectId}
                    generatedSoAData={generatedSoAData}
                    initialChatHistory={section.soaEditChatHistory}
                    onSaved={(updatedSoaData, chatHistory) => {
                      setGeneratedSoAData(updatedSoaData);
                      setSections((prevSections) =>
                        prevSections.map((s) =>
                          s._id === section._id
                            ? { ...s, scheduleOfActivitiesData: updatedSoaData, soaEditChatHistory: chatHistory }
                            : s
                        )
                      );
                      setShowEditSoa(false);
                    }}
                  />
                </Box>
              ) : (
                // All sections, including 1, 1.1, 2, etc.
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                    {mode === 'edit' ? 'Generated Content' : 'Document Preview'}
                  </Typography>
                    {mode === 'edit' ? (
                  <Box>
                    {/* Editable content — edits happen directly on the rendered HTML,
                        so tag structure the user doesn't touch stays untouched. */}
                    {(() => {
                      const baseOutput = editedTextContent[section._id] ?? section.output ?? '';
                      const normalized = finalizeGeneratedProtocolOutput(
                        baseOutput,
                        section.title,
                        mediaNumberOffsetsById.get(section._id),
                      );
                      const hasHtml = /<table[\s>]|<h[1-6]|<p[\s>]|<ul|<ol|<strong/i.test(normalized);
                      // hasHtml only means SOME real HTML is present — the model can still mix in
                      // literal '#'/'##'/'###' markdown headings alongside it (see
                      // convertStrayMarkdownHeadingsToHtml's doc comment), so convert those too
                      // instead of leaving them as raw text in the editable content. Same for a
                      // dropped-leading-'<' tag like "li>" (repairOrphanedOpeningTags).
                      const editableHtml = hasHtml
                        ? repairOrphanedOpeningTags(convertStrayMarkdownHeadingsToHtml(normalized))
                        : convertMarkdownToHTML(normalized);
                      return (
                        <EditableSectionContent
                          html={editableHtml}
                          resetKey={section._id}
                          placeholder="Edit content here..."
                          onChange={(newHtml) => {
                            handleTextContentChange(section._id, newHtml);
                          }}
                          onBlur={() => flushSectionSave(section._id)}
                        />
                      );
                    })()}
                  </Box>
                    ) : (
                      <Box sx={{ 
                        minHeight: 200, 
                        bgcolor: section.isNew ? '#f0f8ff' : '#ffffff', 
                        border: section.isNew ? '2px solid #168AAD' : '1px solid #e0e0e0', 
                        borderRadius: 2, 
                        p: 3, 
                        fontFamily: 'Times New Roman, serif',
                        fontSize: 14,
                        color: '#333333',
                        lineHeight: 1.6,
                        boxShadow: section.isNew ? '0 4px 12px rgba(22, 138, 173, 0.2)' : '0 1px 3px rgba(0,0,0,0.1)',
                        transition: 'all 0.3s ease-in-out',
                        animation: section.isNew ? 'pulse 2s ease-in-out' : 'none',
                        '@keyframes pulse': {
                          '0%': {
                            transform: 'scale(1)',
                            boxShadow: '0 4px 12px rgba(22, 138, 173, 0.2)'
                          },
                          '50%': {
                            transform: 'scale(1.02)',
                            boxShadow: '0 6px 20px rgba(22, 138, 173, 0.3)'
                          },
                          '100%': {
                            transform: 'scale(1)',
                            boxShadow: '0 4px 12px rgba(22, 138, 173, 0.2)'
                          }
                        }
                      }}>
                        {/* Section Title */}
                        <Typography 
                          variant="h5" 
                          sx={{ 
                            fontWeight: 'bold', 
                            color: '#2c3e50',
                            mb: 2,
                            borderBottom: '2px solid #3498db',
                            pb: 1
                          }}
                        >
                          {section.title}
                        </Typography>
                        
                        {/* Section Content */}
                        {section.output && section.output.trim() ? (
                          <Box sx={{ 
                            '& h1, & h2, & h3, & h4, & h5, & h6': {
                              fontWeight: 'bold',
                              color: '#2c3e50',
                              mt: 2,
                              mb: 1
                            },
                            '& h1': { fontSize: '1.5em' },
                            '& h2': { fontSize: '1.3em' },
                            '& h3': { fontSize: '1.1em' },
                            '& p': { mb: 1 },
                            '& ul, & ol': { pl: 2, mb: 1 },
                            '& li': { mb: 0.5 },
                            '& table': { 
                              borderCollapse: 'collapse',
                              width: '100%',
                              mb: 2
                            },
                            '& th, & td': {
                              border: '1px solid #ddd',
                              padding: '8px',
                              textAlign: 'left'
                            },
                            '& th': {
                              backgroundColor: '#f8f9fa',
                              fontWeight: 'bold'
                            }
                          }}>
                            {section.output === 'Generating content...' ? (
                              <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center',
                                py: 4,
                                color: '#168AAD'
                              }}>
                                <CircularProgress size={24} sx={{ mr: 2, color: 'inherit' }} />
                                <Typography variant="body1">
                                  Generating content for {section.title}...
                                </Typography>
                              </Box>
                            ) : (() => {
                              const displayContent = finalizeGeneratedProtocolOutput(
                                section.output,
                                section.title,
                                mediaNumberOffsetsById.get(section._id)
                              );
                              const processed = processContentForDisplay(displayContent);
                              const hasHtml =
                                /<table[\s>]|<h[1-6]|<p[\s>]|<ul|<ol|<strong/i.test(processed);
                              if (hasHtml) {
                                return (
                                  <Box
                                    dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(processed) }}
                                    sx={{
                                      '& table': {
                                        borderCollapse: 'collapse',
                                        width: '100%',
                                        mb: 2,
                                      },
                                      '& th, & td': {
                                        border: '1px solid #ddd',
                                        padding: '8px',
                                        textAlign: 'left',
                                      },
                                      '& th': {
                                        backgroundColor: '#f8f9fa',
                                        fontWeight: 'bold',
                                      },
                                    }}
                                  />
                                );
                              }
                              return (
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                  {displayContent}
                                </ReactMarkdown>
                              );
                            })()}
                          </Box>
                        ) : (
                          <Box sx={{ 
                            color: '#999',
                            fontStyle: 'italic',
                            textAlign: 'center',
                            py: 4
                          }}>
                            [No content available for this section]
                          </Box>
                        )}
                      </Box>
                    )}
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>Prompt</Typography>
                    {mode === 'edit' ? (
                                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'stretch' }}>
                  <TextField
                    multiline
                        rows={3}
                    value={section.prompt || ''}
                    onChange={(e) => handleInputChange(section._id, 'prompt', e.target.value)}
                    variant="outlined"
                    size="small"
                        sx={{ flex: 1, maxWidth: '85%' }}
                        placeholder="Optional: Enter a custom prompt for this section. Leave empty to use the default prompt when generating content."
                      />
                      {/* Conditional rendering for Section 10 and Section 3 - Four Approaches */}
                      {((section.title.includes('10.') || section.title.toUpperCase().includes('STATISTICAL CONSIDERATIONS')) && section.title.startsWith('10')) ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 220, justifyContent: 'space-between'}}>
                          <Tooltip title="Approach 1: Adds statistical context file as additional guidance with minimal changes">
                            <Button
                              variant="contained" 
                              color="primary"
                              size="small"
                              disabled={isGenerating[section._id]}
                              onClick={() => handleGenerateSection10Approach1(section._id)}
                              sx={{ whiteSpace: 'nowrap', flex: 1, fontSize: '0.75rem' }}
                            >
                              {isGenerating[section._id] ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : '📊 Context+'}
                            </Button>
                          </Tooltip>
                          <Tooltip title="Approach 2: Full protocol-writer mode with structured prompt and ICH M11 requirements">
                            <Button
                              variant="contained" 
                              color="secondary"
                              size="small"
                              disabled={isGenerating[section._id]}
                              onClick={() => handleGenerateSection10Approach2(section._id)}
                              sx={{ whiteSpace: 'nowrap', flex: 1, fontSize: '0.75rem' }}
                            >
                              {isGenerating[section._id] ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : '📝 Protocol Writer'}
                            </Button>
                          </Tooltip>
                          <Tooltip title="Approach 3: Generate → Evaluate → Refine using context-driven evaluation agent">
                            <Button
                              variant="contained" 
                              color="success"
                              size="small"
                              disabled={isGenerating[section._id]}
                              onClick={() => handleGenerateSection10Approach3(section._id)}
                              sx={{ whiteSpace: 'nowrap', flex: 1, fontSize: '0.75rem' }}
                            >
                              {isGenerating[section._id] ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : '🔍 Gen+Eval+Refine'}
                            </Button>
                          </Tooltip>
                          <Tooltip title="Approach 4: Original generation (no context file) + QC evaluation and refinement">
                            <Button
                              variant="contained" 
                              color="warning"
                              size="small"
                              disabled={isGenerating[section._id]}
                              onClick={() => handleGenerateSection10Approach4(section._id)}
                              sx={{ whiteSpace: 'nowrap', flex: 1, fontSize: '0.75rem' }}
                            >
                              {isGenerating[section._id] ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : '🔄 Original + QC'}
                            </Button>
                          </Tooltip>
                          <Button
                            variant={section.final ? "outlined" : "contained"}
                            color={section.final ? "success" : "primary"}
                            size="small"
                            disabled={section.final}
                            onClick={() => handleMarkAsFinal(section._id)}
                            sx={{ whiteSpace: 'nowrap', flex: 1 }}
                          >
                            {section.final ? 'Section is Final' : 'Mark as Final'}
                          </Button>
                        </Box>
                      ) : (section.title.startsWith('3') && (section.title.includes('3.') || section.title.toUpperCase().includes('TRIAL OBJECTIVES'))) ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 220, justifyContent: 'space-between'}}>
                          <Tooltip title="Approach 3: Generate → Evaluate → Refine using Section 3 context">
                            <Button
                              variant="contained"
                              color="success"
                              size="small"
                              disabled={isGenerating[section._id]}
                              onClick={() => handleGenerateSection3Approach(section._id, 3)}
                              sx={{ whiteSpace: 'nowrap', flex: 1, fontSize: '0.75rem' }}
                            >
                              {isGenerating[section._id] ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : '🔍 Gen+Eval+Refine'}
                            </Button>
                          </Tooltip>
                          <Button
                            variant={section.final ? "outlined" : "contained"}
                            color={section.final ? "success" : "primary"}
                            size="small"
                            disabled={section.final}
                            onClick={() => handleMarkAsFinal(section._id)}
                            sx={{ whiteSpace: 'nowrap', flex: 1 }}
                          >
                            {section.final ? 'Section is Final' : 'Mark as Final'}
                          </Button>
                        </Box>
                        ) : (section.title.startsWith('4') && (section.title.includes('4.') || section.title.toUpperCase().includes('TRIAL DESIGN'))) ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 220, justifyContent: 'space-between'}}>
                          <Tooltip title="Approach 3: Generate → Evaluate → Refine using oncology study designs master context file">
                            <Button
                              variant="contained"
                              color="success"
                              size="small"
                              disabled={isGenerating[section._id]}
                              onClick={() => handleGenerateSection4Approach3(section._id)}
                              sx={{ whiteSpace: 'nowrap', flex: 1, fontSize: '0.75rem' }}
                            >
                              {isGenerating[section._id] ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : '🔍 Gen+Eval+Refine'}
                            </Button>
                          </Tooltip>
                          <Button
                            variant={section.final ? "outlined" : "contained"}
                            color={section.final ? "success" : "primary"}
                            size="small"
                            disabled={section.final}
                            onClick={() => handleMarkAsFinal(section._id)}
                            sx={{ whiteSpace: 'nowrap', flex: 1 }}
                          >
                            {section.final ? 'Section is Final' : 'Mark as Final'}
                          </Button>
                        </Box>
                         ) : (section.title.startsWith('5') && (section.title.includes('5.') || section.title.toUpperCase().includes('TRIAL POPULATION'))) ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 220, justifyContent: 'space-between'}}>
                          <Tooltip title="Approach 3: Generate → Evaluate → Refine using Combined Trial Population context file (PDF)">
                            <Button
                              variant="contained"
                              color="success"
                              size="small"
                              disabled={isGenerating[section._id]}
                              onClick={() => handleGenerateSection5Approach3(section._id)}
                              sx={{ whiteSpace: 'nowrap', flex: 1, fontSize: '0.75rem' }}
                            >
                              {isGenerating[section._id] ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : '🔍 Gen+Eval+Refine'}
                            </Button>
                          </Tooltip>
                          <Button
                            variant={section.final ? "outlined" : "contained"}
                            color={section.final ? "success" : "primary"}
                            size="small"
                            disabled={section.final}
                            onClick={() => handleMarkAsFinal(section._id)}
                            sx={{ whiteSpace: 'nowrap', flex: 1 }}
                          >
                            {section.final ? 'Section is Final' : 'Mark as Final'}
                          </Button>
                        </Box>
                         ) : (section.title.startsWith('8') && (section.title.includes('8.') || section.title.toUpperCase().includes('TRIAL ASSESSMENTS'))) ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 220, justifyContent: 'space-between'}}>
                          <Tooltip title="Approach 3: Generate → Evaluate → Refine using TRIAL ASSESSMENTS AND PROCEDURES context file (PDF)">
                            <Button
                              variant="contained"
                              color="success"
                              size="small"
                              disabled={isGenerating[section._id]}
                              onClick={() => handleGenerateSection8Approach3(section._id)}
                              sx={{ whiteSpace: 'nowrap', flex: 1, fontSize: '0.75rem' }}
                            >
                              {isGenerating[section._id] ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : '🔍 Gen+Eval+Refine'}
                            </Button>
                          </Tooltip>
                          <Button
                            variant={section.final ? "outlined" : "contained"}
                            color={section.final ? "success" : "primary"}
                            size="small"
                            disabled={section.final}
                            onClick={() => handleMarkAsFinal(section._id)}
                            sx={{ whiteSpace: 'nowrap', flex: 1 }}
                          >
                            {section.final ? 'Section is Final' : 'Mark as Final'}
                          </Button>
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 200, justifyContent: 'space-between'}}>
                          <Button
                            variant="contained" 
                            size="small"
                            disabled={isGenerating[section._id]}
                            onClick={() => handleGenerateContent(section._id)}
                            sx={{ whiteSpace: 'nowrap', flex: 1 }}
                          >
                            {isGenerating[section._id] ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Generate Content'}
                          </Button>
                          <Button
                            variant={section.final ? "outlined" : "contained"}
                            color={section.final ? "success" : "primary"}
                            size="small"
                            disabled={section.final}
                            onClick={() => handleMarkAsFinal(section._id)}
                            sx={{ whiteSpace: 'nowrap', flex: 1 }}
                          >
                            {section.final ? 'Section is Final' : 'Mark as Final'}
                          </Button>
                        </Box>
                      )}
                    </Box>
                    ) : (
                      <Box sx={{
                        minHeight: 100,
                        bgcolor: 'grey.50',
                        border: '1px solid #e0e0e0',
                        borderRadius: 2,
                        p: 2,
                        whiteSpace: 'pre-wrap',
                        fontFamily: 'inherit',
                        fontSize: 15,
                        color: 'text.secondary',
                      }}>
                        {section.prompt || <span style={{ color: '#aaa' }}>No custom prompt - will use default prompt when generating content</span>}
                      </Box>
                    )}
                </Grid>
              </Grid>
              )}
            </Paper>
          ))
        )}

        {/* 3 New Generator Cards - Edit Mode only */}
        {mode === 'edit' && (
        <>
        {/* Card 1 */}
        <Paper 
          key="generator-card-1"
          sx={{ 
            mb: 3, 
            p: 3, 
            borderRadius: 2, 
            position: 'relative',
            background: 'rgba(255, 255, 255, 0.85)',
            backdropFilter: 'blur(10px)',
            border: '2px solid #3498db',
            transition: 'all 0.3s ease',
            '&:hover': { 
              boxShadow: '0 4px 12px rgba(52, 152, 219, 0.2)',
              background: 'rgba(255, 255, 255, 0.92)',
              transform: 'translateY(-2px)'
            }
          }}
        >
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #eee', pb: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 400, color: '#3498db' }}>
              Generator Card 1
            </Typography>
          </Box>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Select Section</InputLabel>
                  <Select
                    value={card1SelectedSection}
                    onChange={(e) => {
                      setCard1SelectedSection(e.target.value);
                      setCard1Output(''); // Clear output when section changes
                    }}
                    label="Select Section"
                  >
                    {availableSections.map((title) => (
                      <MenuItem key={title} value={title}>{title}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth size="small">
                  <InputLabel>Select Approach</InputLabel>
                  <Select
                    value={card1SelectedApproach}
                    onChange={(e) => setCard1SelectedApproach(e.target.value)}
                    label="Select Approach"
                  >
                    <MenuItem value="1">Approach 1: Context+</MenuItem>
                    <MenuItem value="2">Approach 2: Protocol Writer</MenuItem>
                    <MenuItem value="3">Approach 3: Gen+Eval+Refine</MenuItem>
                    <MenuItem value="4">Approach 4: Original + QC</MenuItem>
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => handleGenerateWithApproach(1)}
                  disabled={card1IsGenerating || !card1SelectedSection}
                  fullWidth
                >
                  {card1IsGenerating ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Generate Section'}
                </Button>
              </Box>
            </Grid>
          </Grid>
          
          {/* Output Display */}
          {card1Output && (
            <Box sx={{ mt: 3, borderTop: '1px solid #eee', pt: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                  Generated Output (Preview Only)
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="contained"
                    color="success"
                    size="small"
                    startIcon={<CheckCircleIcon />}
                    onClick={() => handleAcceptCardOutput(1)}
                    sx={{ textTransform: 'none' }}
                  >
                    Accept
                  </Button>
                  <IconButton 
                    size="small" 
                    onClick={() => handleCopyContent(card1Output)}
                    title="Copy output"
                  >
                    <CopyIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
              <Box
                sx={{
                  maxHeight: 400,
                  overflow: 'auto',
                  bgcolor: '#ffffff',
                  border: '1px solid #e0e0e0',
                  borderRadius: 2,
                  p: 3,
                  fontFamily: 'Times New Roman, serif',
                  fontSize: 14,
                  color: '#333333',
                  lineHeight: 1.6,
                  '& h1, & h2, & h3, & h4, & h5, & h6': {
                    fontWeight: 'bold',
                    color: '#2c3e50',
                    mt: 2,
                    mb: 1
                  },
                  '& h1': { fontSize: '1.5em' },
                  '& h2': { fontSize: '1.3em' },
                  '& h3': { fontSize: '1.1em' },
                  '& p': { mb: 1 },
                  '& ul, & ol': { pl: 2, mb: 1 },
                  '& li': { mb: 0.5 },
                  '& table': { 
                    borderCollapse: 'collapse',
                    width: '100%',
                    mb: 2
                  },
                  '& th, & td': {
                    border: '1px solid #ddd',
                    padding: '8px',
                    textAlign: 'left'
                  },
                  '& th': {
                    backgroundColor: '#f8f9fa',
                    fontWeight: 'bold'
                  }
                }}
                dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(card1Output) }}
              />
            </Box>
          )}
        </Paper>

        {/* Card 2 */}
        <Paper 
          key="generator-card-2"
          sx={{ 
            mb: 3, 
            p: 3, 
            borderRadius: 2, 
            position: 'relative',
            background: 'rgba(255, 255, 255, 0.85)',
            backdropFilter: 'blur(10px)',
            border: '2px solid #9b59b6',
            transition: 'all 0.3s ease',
            '&:hover': { 
              boxShadow: '0 4px 12px rgba(155, 89, 182, 0.2)',
              background: 'rgba(255, 255, 255, 0.92)',
              transform: 'translateY(-2px)'
            }
          }}
        >
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #eee', pb: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 400, color: '#9b59b6' }}>
              Generator Card 2
            </Typography>
          </Box>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Select Section</InputLabel>
                  <Select
                    value={card2SelectedSection}
                    onChange={(e) => {
                      setCard2SelectedSection(e.target.value);
                      setCard2Output(''); // Clear output when section changes
                    }}
                    label="Select Section"
                  >
                    {availableSections.map((title) => (
                      <MenuItem key={title} value={title}>{title}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth size="small">
                  <InputLabel>Select Approach</InputLabel>
                  <Select
                    value={card2SelectedApproach}
                    onChange={(e) => setCard2SelectedApproach(e.target.value)}
                    label="Select Approach"
                  >
                    <MenuItem value="1">Approach 1: Context+</MenuItem>
                    <MenuItem value="2">Approach 2: Protocol Writer</MenuItem>
                    <MenuItem value="3">Approach 3: Gen+Eval+Refine</MenuItem>
                    <MenuItem value="4">Approach 4: Original + QC</MenuItem>
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => handleGenerateWithApproach(2)}
                  disabled={card2IsGenerating || !card2SelectedSection}
                  fullWidth
                >
                  {card2IsGenerating ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Generate Section'}
                </Button>
              </Box>
            </Grid>
          </Grid>
          
          {/* Output Display */}
          {card2Output && (
            <Box sx={{ mt: 3, borderTop: '1px solid #eee', pt: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                  Generated Output (Preview Only)
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="contained"
                    color="success"
                    size="small"
                    startIcon={<CheckCircleIcon />}
                    onClick={() => handleAcceptCardOutput(2)}
                    sx={{ textTransform: 'none' }}
                  >
                    Accept
                  </Button>
                  <IconButton 
                    size="small" 
                    onClick={() => handleCopyContent(card2Output)}
                    title="Copy output"
                  >
                    <CopyIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
              <Box
                sx={{
                  maxHeight: 400,
                  overflow: 'auto',
                  bgcolor: '#ffffff',
                  border: '1px solid #e0e0e0',
                  borderRadius: 2,
                  p: 3,
                  fontFamily: 'Times New Roman, serif',
                  fontSize: 14,
                  color: '#333333',
                  lineHeight: 1.6,
                  '& h1, & h2, & h3, & h4, & h5, & h6': {
                    fontWeight: 'bold',
                    color: '#2c3e50',
                    mt: 2,
                    mb: 1
                  },
                  '& h1': { fontSize: '1.5em' },
                  '& h2': { fontSize: '1.3em' },
                  '& h3': { fontSize: '1.1em' },
                  '& p': { mb: 1 },
                  '& ul, & ol': { pl: 2, mb: 1 },
                  '& li': { mb: 0.5 },
                  '& table': { 
                    borderCollapse: 'collapse',
                    width: '100%',
                    mb: 2
                  },
                  '& th, & td': {
                    border: '1px solid #ddd',
                    padding: '8px',
                    textAlign: 'left'
                  },
                  '& th': {
                    backgroundColor: '#f8f9fa',
                    fontWeight: 'bold'
                  }
                }}
                dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(card2Output) }}
              />
            </Box>
          )}
        </Paper>

        {/* Card 3 */}
        <Paper 
          key="generator-card-3"
          sx={{ 
            mb: 3, 
            p: 3, 
            borderRadius: 2, 
            position: 'relative',
            background: 'rgba(255, 255, 255, 0.85)',
            backdropFilter: 'blur(10px)',
            border: '2px solid #e67e22',
            transition: 'all 0.3s ease',
            '&:hover': { 
              boxShadow: '0 4px 12px rgba(230, 126, 34, 0.2)',
              background: 'rgba(255, 255, 255, 0.92)',
              transform: 'translateY(-2px)'
            }
          }}
        >
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #eee', pb: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 400, color: '#e67e22' }}>
              Generator Card 3
            </Typography>
          </Box>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Select Section</InputLabel>
                  <Select
                    value={card3SelectedSection}
                    onChange={(e) => {
                      setCard3SelectedSection(e.target.value);
                      setCard3Output(''); // Clear output when section changes
                    }}
                    label="Select Section"
                  >
                    {availableSections.map((title) => (
                      <MenuItem key={title} value={title}>{title}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth size="small">
                  <InputLabel>Select Approach</InputLabel>
                  <Select
                    value={card3SelectedApproach}
                    onChange={(e) => setCard3SelectedApproach(e.target.value)}
                    label="Select Approach"
                  >
                    <MenuItem value="1">Approach 1: Context+</MenuItem>
                    <MenuItem value="2">Approach 2: Protocol Writer</MenuItem>
                    <MenuItem value="3">Approach 3: Gen+Eval+Refine</MenuItem>
                    <MenuItem value="4">Approach 4: Original + QC</MenuItem>
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  color="warning"
                  onClick={() => handleGenerateWithApproach(3)}
                  disabled={card3IsGenerating || !card3SelectedSection}
                  fullWidth
                >
                  {card3IsGenerating ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Generate Section'}
                </Button>
              </Box>
            </Grid>
          </Grid>
          
          {/* Output Display */}
          {card3Output && (
            <Box sx={{ mt: 3, borderTop: '1px solid #eee', pt: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                  Generated Output (Preview Only)
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="contained"
                    color="success"
                    size="small"
                    startIcon={<CheckCircleIcon />}
                    onClick={() => handleAcceptCardOutput(3)}
                    sx={{ textTransform: 'none' }}
                  >
                    Accept
                  </Button>
                  <IconButton 
                    size="small" 
                    onClick={() => handleCopyContent(card3Output)}
                    title="Copy output"
                  >
                    <CopyIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
              <Box
                sx={{
                  maxHeight: 400,
                  overflow: 'auto',
                  bgcolor: '#ffffff',
                  border: '1px solid #e0e0e0',
                  borderRadius: 2,
                  p: 3,
                  fontFamily: 'Times New Roman, serif',
                  fontSize: 14,
                  color: '#333333',
                  lineHeight: 1.6,
                  '& h1, & h2, & h3, & h4, & h5, & h6': {
                    fontWeight: 'bold',
                    color: '#2c3e50',
                    mt: 2,
                    mb: 1
                  },
                  '& h1': { fontSize: '1.5em' },
                  '& h2': { fontSize: '1.3em' },
                  '& h3': { fontSize: '1.1em' },
                  '& p': { mb: 1 },
                  '& ul, & ol': { pl: 2, mb: 1 },
                  '& li': { mb: 0.5 },
                  '& table': { 
                    borderCollapse: 'collapse',
                    width: '100%',
                    mb: 2
                  },
                  '& th, & td': {
                    border: '1px solid #ddd',
                    padding: '8px',
                    textAlign: 'left'
                  },
                  '& th': {
                    backgroundColor: '#f8f9fa',
                    fontWeight: 'bold'
                  }
                }}
                dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(card3Output) }}
              />
            </Box>
          )}
        </Paper>
        </>
        )}
        </Box>

        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
          {/* Removed "Edit Section" as it was not in the previous GUI's menu */}
          <MenuItem onClick={() => handleDeleteSection(selectedSectionId)}>
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Delete Section
          </MenuItem>
          <MenuItem onClick={() => { 
            const section = sections.find(s => s._id === selectedSectionId);
            if (section) {
              exportSectionToXML(section);
            }
            handleMenuClose();
          }}>
            <ExportIcon fontSize="small" sx={{ mr: 1 }} /> Export to XML
          </MenuItem>
          
          <Divider />
          <MenuItem onClick={() => { handleViewVersionHistory(selectedSectionId); handleMenuClose(); }}>
            <HistoryIcon fontSize="small" sx={{ mr: 1 }} /> View History
          </MenuItem>
        </Menu>

        <Dialog
          open={showTrialSchemaPreview}
          onClose={() => setShowTrialSchemaPreview(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Trial Schema Image Preview</DialogTitle>
          <DialogContent sx={{ textAlign: 'center' }}>
            {trialSchemaImageUrl ? (
              <img
                src={trialSchemaImageUrl}
                alt="Trial Schema"
                style={{ maxWidth: '100%', maxHeight: 500, border: '1px solid #ccc', borderRadius: 8 }}
              />
            ) : (
              <Typography>No trial schema image available.</Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowTrialSchemaPreview(false)}>Close</Button>
          </DialogActions>
        </Dialog>
        <Backdrop
          open={isExportingPdf}
          sx={{ zIndex: (theme) => theme.zIndex.modal + 2, color: '#fff', flexDirection: 'column' }}
        >
          <CircularProgress color="inherit" size={48} />
          <Typography variant="body1" sx={{ mt: 2, px: 3, textAlign: 'center' }}>
            Generating PDF… large protocols can take 1–2 minutes.
          </Typography>
        </Backdrop>
        {/* The "Publishing…" state on the menu item itself is invisible — the
            menu closes the instant it's clicked (setExportAnchorEl(null)),
            unmounting that MenuItem before its spinner could ever be seen. */}
        <Backdrop
          open={externalSdrPublishing}
          sx={{ zIndex: (theme) => theme.zIndex.modal + 2, color: '#fff', flexDirection: 'column' }}
        >
          <CircularProgress color="inherit" size={48} />
          <Typography variant="body1" sx={{ mt: 2, px: 3, textAlign: 'center' }}>
            Publishing to External SDR…
          </Typography>
        </Backdrop>
        <Snackbar
          open={diagramSaveSuccess}
          autoHideDuration={3000}
          onClose={() => setDiagramSaveSuccess(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={() => setDiagramSaveSuccess(false)} severity="success" sx={{ width: '100%' }}>
            Diagram saved successfully!
          </Alert>
        </Snackbar>
        <Snackbar
          open={styleAutofixSnackbar.open}
          autoHideDuration={8000}
          onClose={() => setStyleAutofixSnackbar((s) => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setStyleAutofixSnackbar((s) => ({ ...s, open: false }))}
            severity="success"
            sx={{ width: '100%' }}
          >
            {styleAutofixSnackbar.message}
          </Alert>
        </Snackbar>
        <Snackbar
          open={regenerateSummarySnackbar.open}
          autoHideDuration={8000}
          onClose={() => setRegenerateSummarySnackbar((s) => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setRegenerateSummarySnackbar((s) => ({ ...s, open: false }))}
            severity={regenerateSummarySnackbar.severity}
            sx={{ width: '100%' }}
          >
            {regenerateSummarySnackbar.message}
          </Alert>
        </Snackbar>
        <Snackbar
          open={externalSdrSnackbar.open}
          autoHideDuration={8000}
          onClose={() => setExternalSdrSnackbar((s) => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setExternalSdrSnackbar((s) => ({ ...s, open: false }))}
            severity={externalSdrSnackbar.severity}
            sx={{ width: '100%' }}
          >
            {externalSdrSnackbar.message}
          </Alert>
        </Snackbar>
        <Snackbar
          open={usdmUploadSnackbar.open}
          autoHideDuration={6000}
          onClose={() => setUsdmUploadSnackbar((s) => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setUsdmUploadSnackbar((s) => ({ ...s, open: false }))}
            severity={usdmUploadSnackbar.severity}
            sx={{ width: '100%' }}
          >
            {usdmUploadSnackbar.message}
          </Alert>
        </Snackbar>
        {/* Word Preview Dialog */}
        <WordPreviewDialog
          open={previewDialogOpen}
          onClose={() => setPreviewDialogOpen(false)}
          sections={sections}
          trialSchemaImageUrl={trialSchemaImageUrl}
          generatedSoAData={generatedSoAData}
          fontConfig={FONT_CONFIG}
          titlePageConfig={titlePageConfig}
        />

        {/* Microsoft Word Dialog */}
        <MicrosoftWordDialog
          open={microsoftWordDialogOpen}
          onClose={() => setMicrosoftWordDialogOpen(false)}
          sections={sections}
          trialSchemaImageUrl={trialSchemaImageUrl}
          generatedSoAData={generatedSoAData}
          fontConfig={FONT_CONFIG}
          titlePageConfig={titlePageConfig}
        />

        {/* Image Edit Dialog */}
        <Dialog 
          open={showImageEditDialog} 
          onClose={() => setShowImageEditDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {imageEditMode === 'delete' ? 'Delete Trial Schema Image' : 'Crop Trial Schema Image'}
          </DialogTitle>
          <DialogContent>
            {imageEditMode === 'delete' ? (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <DeleteIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Are you sure you want to delete this trial schema image?
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  This action cannot be undone. The image will be permanently removed from your document.
                </Typography>
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <CropIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Crop Trial Schema Image
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Click and drag on the image below to select the area you want to keep.
                </Typography>
                
                {/* Simple crop interface */}
                <Box sx={{ 
                  position: 'relative', 
                  display: 'inline-block',
                  border: '2px dashed #ccc',
                  borderRadius: 1,
                  p: 2
                }}>
                  <img
                    src={trialSchemaImageUrl}
                    alt="Trial Schema for cropping"
                    style={{ 
                      maxWidth: '100%', 
                      maxHeight: 400,
                      cursor: 'crosshair'
                    }}
                    onMouseDown={(e) => {
                      const rect = e.target.getBoundingClientRect();
                      const startX = e.clientX - rect.left;
                      const startY = e.clientY - rect.top;
                      
                      const handleMouseMove = (moveEvent) => {
                        const currentX = moveEvent.clientX - rect.left;
                        const currentY = moveEvent.clientY - rect.top;
                        
                        const width = Math.abs(currentX - startX);
                        const height = Math.abs(currentY - startY);
                        const x = Math.min(startX, currentX);
                        const y = Math.min(startY, currentY);
                        
                        setCropData({ x, y, width, height });
                      };
                      
                      const handleMouseUp = () => {
                        document.removeEventListener('mousemove', handleMouseMove);
                        document.removeEventListener('mouseup', handleMouseUp);
                      };
                      
                      document.addEventListener('mousemove', handleMouseMove);
                      document.addEventListener('mouseup', handleMouseUp);
                    }}
                  />
                  
                  {cropData && (
                    <Box
                      sx={{
                        position: 'absolute',
                        left: cropData.x,
                        top: cropData.y,
                        width: cropData.width,
                        height: cropData.height,
                        border: '2px solid #168AAD',
                        backgroundColor: 'rgba(22, 138, 173, 0.1)',
                        pointerEvents: 'none'
                      }}
                    />
                  )}
                </Box>
                
                {cropData && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 2 }}>
                    Crop area: {Math.round(cropData.width)} × {Math.round(cropData.height)} pixels
                  </Typography>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowImageEditDialog(false)}>
              Cancel
            </Button>
            {imageEditMode === 'delete' ? (
              <Button 
                onClick={handleDeleteTrialSchemaImage}
                color="error"
                variant="contained"
                startIcon={<DeleteIcon />}
              >
                Delete Image
              </Button>
            ) : (
              <Button 
                onClick={handleCropTrialSchemaImage}
                color="primary"
                variant="contained"
                disabled={!cropData}
                startIcon={<CropIcon />}
              >
                Crop & Save
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* Validation Dialog */}
        <ValidationDialog
          isOpen={isValidationDialogOpen}
          onClose={() => setIsValidationDialogOpen(false)}
          validationResults={validationResults}
          isLoading={isValidating}
        />
        
        <ExportValidationDialog
          isOpen={isExportValidationDialogOpen}
          onClose={() => setIsExportValidationDialogOpen(false)}
          validationResults={exportValidationResults}
          isLoading={isValidatingExport}
           reportMeta={{
            studyId: resolveProjectSponsorProtocolId(currentProject) || '',
            trialAcronym: resolveProjectTrialAcronym(currentProject) || '',
            sponsorName: resolveProjectSponsorName(currentProject) || '',
            projectName: resolveProjectTrialAcronym(currentProject) || '',
            sectionCount: sections?.length || 0,
          }}
           infoMessage={
            <>
              This report runs before DOCX export. It includes{' '}
              <strong>font compliance</strong> (inline <code>font-family</code> / <code>font-size</code> in
              section HTML must be Times New Roman and allowed §4.1 sizes). After a successful export, a separate{' '}
              <strong>Word export — font compliance report</strong> scans the generated .docx (OOXML).
            </>
          }
        />
        <ExportValidationDialog
          isOpen={isAiValidationDialogOpen}
          onClose={() => setIsAiValidationDialogOpen(false)}
          validationResults={aiValidationResults}
          isLoading={isValidatingAi}
          dialogTitle="AI Validation Report"
          dialogSubtitle="Dizzaroo Regulatory Style Guide v1.0 — rule checks, Gemini semantic review, and Knowledge Graph consistency."
          loadingMessage="Running AI validation checks (rules + Gemini)…"
          reportMeta={{
            studyId: resolveProjectSponsorProtocolId(currentProject) || '',
            trialAcronym: resolveProjectTrialAcronym(currentProject) || '',
            sponsorName: resolveProjectSponsorName(currentProject) || '',
            projectName: resolveProjectTrialAcronym(currentProject) || '',
            sectionCount: sections?.length || 0,
          }}
          infoMessage={
            <>
              <strong>Rule-based checks</strong> run locally (abbreviations, cross-references, dates, TNR fonts, SI
              units, table numbering). <strong>Gemini</strong> evaluates synopsis flow, objectives/endpoints, and template
              consistency. <strong>Knowledge Graph</strong> checks run when a project is loaded — sync to KG first for best
              results.
            </>
          }
        />
        <Dialog
          open={styleCheckDialogOpen}
           onClose={(_, reason) => {
            if (reason === 'backdropClick') return;
            setStyleCheckDialogOpen(false);
            setStyleAutofixDialogError(null);
          }}
          maxWidth={false}
          disableScrollLock
          sx={{ pointerEvents: 'none' }}
          BackdropProps={{ invisible: true, sx: { pointerEvents: 'none' } }}
          PaperProps={{
            sx: {
              pointerEvents: 'auto',
              position: 'fixed',
              right: 0,
              top: 80,
              m: 0,
              height: 'calc(100vh - 80px)',
              maxHeight: 'calc(100vh - 80px)',
              /* When collapsed show only a narrow 40px tab; when expanded show full 460px */
              width: styleCheckPanelCollapsed ? 40 : 460,
              minWidth: styleCheckPanelCollapsed ? 40 : 460,
              borderRadius: '8px 0 0 8px',
              boxShadow: '-4px 0 20px rgba(0,0,0,0.15)',
              display: 'flex',
              flexDirection: styleCheckPanelCollapsed ? 'column' : 'column',
              overflow: 'hidden',
              transition: 'width 0.25s ease, min-width 0.25s ease',
            },
          }}
        >
          {styleCheckPanelCollapsed ? (
            /* ── collapsed tab: vertical label + expand button ── */
            <Box
              sx={{
                width: 40,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-start',
                pt: 1,
                gap: 1,
                bgcolor: 'primary.main',
                cursor: 'pointer',
              }}
              onClick={() => setStyleCheckPanelCollapsed(false)}
              title="Expand style check panel"
            >
              <IconButton size="small" sx={{ color: 'white', p: 0.5 }}>
                <AutoFixHighIcon fontSize="small" />
              </IconButton>
              <Typography
                variant="caption"
                sx={{
                  color: 'white',
                  fontWeight: 700,
                  writingMode: 'vertical-rl',
                  textOrientation: 'mixed',
                  transform: 'rotate(180deg)',
                  userSelect: 'none',
                  fontSize: '0.65rem',
                  letterSpacing: '0.05em',
                  mt: 1,
                }}
              >
                Style &amp; Typography
              </Typography>
            </Box>
          ) : (
            <>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Protocol style &amp; typography
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {/* Hide / collapse button */}
              <Tooltip title="Collapse panel">
                <IconButton size="small" onClick={() => setStyleCheckPanelCollapsed(true)}>
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {/* Close button */}
              <Tooltip title="Close">
                <IconButton
                  size="small"
                  onClick={() => {
                    setStyleCheckDialogOpen(false);
                    setStyleCheckPanelCollapsed(false);
                    setStyleAutofixDialogError(null);
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </DialogTitle>

          <DialogContent dividers sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', minHeight: 0 }}>
            {styleAutofixDialogError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setStyleAutofixDialogError(null)}>
                {styleAutofixDialogError}
              </Alert>
            )}
            {styleCheckLoading && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
                <CircularProgress size={28} />
                <Typography>Running writing-style rules and inline font checks…</Typography>
              </Box>
            )}
            {styleCheckError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {styleCheckError}
              </Alert>
            )}
            {!styleCheckLoading && styleCheckResult && (
              <>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {styleCheckResult.summary?.totalFindings ?? 0} finding(s) ·{' '}
                  {styleCheckResult.summary?.autofixableCount ?? 0} autofixable · Errors:{' '}
                  {styleCheckResult.summary?.bySeverity?.error ?? 0}
                  {typeof styleCheckResult.summary?.inlineFontFindingsCount === 'number' ? (
                    <>
                      {' '}
                      · Inline font / typography:{' '}
                      <strong>{styleCheckResult.summary.inlineFontFindingsCount}</strong> (rules FONT_INLINE,
                      FONT_LEGACY)
                    </>
                  ) : null}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  <strong>Fonts:</strong> FONT_* rows compare inline <code>font-family</code> and{' '}
                  <code>font-size</code> in stored HTML to the export profile (Times New Roman; Draft 1.0 §4.1
                  sizes by element role). Plain unstyled text is not flagged — it inherits defaults on export.{' '}
                  <strong>Also run</strong> Sync & Validate → Validate Sections for pre-export font scanning,
                  and Export → DOCX for the post-export OOXML font report.
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Safe text fixes rewrite section HTML as simple paragraphs. Sections that contain HTML tables or
                  subsections are skipped so tabular layout is not destroyed.
                </Typography>
                {Array.isArray(styleCheckResult.diagnostics) && styleCheckResult.diagnostics.length > 0 && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    {styleCheckResult.diagnostics.length} diagnostic(s): some rules were skipped or need extra
                    configuration (for example sponsor dictionaries or publishing metadata).
                  </Alert>
                )}
                {(() => {
                  const raw = Array.isArray(styleCheckResult.findings) ? styleCheckResult.findings : [];
                  const findings = raw.slice(0, 200);

                  // Group by rule (error group), e.g. NUM_001, TERM_003, FONT_*
                  const groups = new Map();
                  for (const f of findings) {
                    const key = String(f.ruleId || '(no rule)');
                    if (!groups.has(key)) groups.set(key, []);
                    groups.get(key).push(f);
                  }

                  const entries = [...groups.entries()].sort((a, b) => a[0].localeCompare(b[0]));

                  return entries.map(([ruleKey, ruleFindings]) => {
                    const errors = ruleFindings.filter((f) => f.severity === 'error').length;
                    const warnings = ruleFindings.filter((f) => f.severity === 'warning').length;
                    const suggestions = ruleFindings.filter((f) => f.severity === 'suggestion').length;
                    const expanded = styleFindingsExpandedSection === ruleKey;

                    const title = ruleFindings.find((f) => f.title)?.title;

                    return (
                      <Accordion
                        key={ruleKey}
                        disableGutters
                        elevation={0}
                        expanded={expanded}
                        onChange={(_, next) => setStyleFindingsExpandedSection(next ? ruleKey : null)}
                        sx={{ border: '1px solid', borderColor: 'divider', mb: 1 }}
                      >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                            <Typography variant="subtitle2" sx={{ flex: 1 }}>
                              {ruleKey}
                              {title ? `: ${title}` : ''}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {ruleFindings.length} issue(s)
                              {errors ? ` · ${errors} error` : ''}
                              {warnings ? ` · ${warnings} warning` : ''}
                              {suggestions ? ` · ${suggestions} suggestion` : ''}
                            </Typography>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ pt: 0 }}>
                          <List dense disablePadding>
                            {ruleFindings.map((f) => (
                              <ListItem
                                key={f.id}
                                alignItems="flex-start"
                                sx={{
                                  flexDirection: 'column',
                                  alignItems: 'stretch',
                                  borderTop: '1px solid',
                                  borderColor: 'divider',
                                }}
                              >
                                <ListItemText
                                  primary={
                                    <Typography variant="subtitle2" component="span">
                                      [{f.severity}] {f.ruleId}: {f.title}
                                    </Typography>
                                  }
                                  secondary={
                                    <>
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        component="span"
                                        display="block"
                                      >
                                        {f.message}
                                      </Typography>
                                      {f.sectionTitle ? (
                                        <Typography variant="caption" display="block">
                                          Section: {f.sectionTitle}
                                        </Typography>
                                      ) : null}
                                      {f.matchedText ? (
                                        <Typography variant="caption" display="block">
                                          Matched:{' '}
                                          {f.matchedText.length > 120
                                            ? `${f.matchedText.slice(0, 120)}…`
                                            : f.matchedText}
                                        </Typography>
                                      ) : null}
                                      {(() => {
                                        const ctx = getStyleFindingContextSnippet(f);
                                        return ctx ? (
                                          <Typography
                                            variant="caption"
                                            display="block"
                                            sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}
                                          >
                                            Context:{' '}
                                            {ctx.clippedStart ? '…' : ''}
                                            {String(ctx.before || '').replace(/\s+/g, ' ')}
                                            {ctx.match ? (
                                              <Box
                                                component="span"
                                                sx={{
                                                  bgcolor: 'rgba(255, 235, 59, 0.35)',
                                                  borderRadius: '2px',
                                                  px: 0.25,
                                                }}
                                              >
                                                {String(ctx.match).replace(/\s+/g, ' ')}
                                              </Box>
                                            ) : null}
                                            {String(ctx.after || '').replace(/\s+/g, ' ')}
                                            {ctx.clippedEnd ? '…' : ''}
                                          </Typography>
                                        ) : null;
                                      })()}
                                      {f.suggestedReplacement != null && f.suggestedReplacement !== '' ? (
                                        <Typography variant="caption" display="block" color="primary">
                                          Suggested: {f.suggestedReplacement}
                                        </Typography>
                                      ) : null}
                                    </>
                                  }
                                />
                              </ListItem>
                            ))}
                          </List>
                        </AccordionDetails>
                      </Accordion>
                    );
                  });
                })()}
                {(styleCheckResult.findings || []).length > 200 ? (
                  <Typography variant="caption" color="text.secondary">
                    Showing first 200 of {styleCheckResult.findings.length} findings.
                  </Typography>
                ) : null}
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button
            fullWidth
              variant="outlined"
              disabled={
                styleCheckLoading ||
                styleAutofixLoading ||
                !styleCheckResult ||
                !(styleCheckResult.summary?.autofixableCount > 0)
              }
              onClick={handleApplyStyleSafeAutofix}
                            startIcon={styleAutofixLoading ? <CircularProgress size={18} color="inherit" /> : null}

            >
              {styleAutofixLoading ? 'Applying…' : 'Apply safe fixes'}
            </Button>
            {/* <Button onClick={() => setStyleCheckDialogOpen(false)}>Close</Button> */}
          </DialogActions>
          </>
          )}
        </Dialog>

        <Dialog
          open={postExportFontDialogOpen}
          onClose={handlePostExportFontCancel}
          maxWidth="md"
          fullWidth
          aria-labelledby="post-export-font-dialog-title"
        >
          <DialogTitle id="post-export-font-dialog-title">Word export — font compliance report</DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Pre-export checks (M11, inline HTML font hints, etc.) already passed. After building the .docx, this
              report scans WordprocessingML (OOXML) for explicit run fonts and sizes in the file itself.
            </Typography>
            {postExportFontReport ? (
              <>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Parts scanned: <strong>{postExportFontReport.partsScanned ?? 0}</strong>
                </Typography>
                {postExportFontReport.ok ? (
                  <Alert severity="success" sx={{ mb: 1 }}>
                    No font family or size issues were detected in scanned OOXML runs (against FONT_CONFIG allowlist).
                  </Alert>
                ) : (
                  <Alert severity="warning" sx={{ mb: 1 }}>
                    {postExportFontReport.errors?.length ?? 0} finding(s). Review below; you can still download the
                    file.
                  </Alert>
                )}
                {!postExportFontReport.ok && (postExportFontReport.errors || []).length > 0 ? (
                  <List dense disablePadding sx={{ maxHeight: 320, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    {(postExportFontReport.errors || []).slice(0, 150).map((line, idx) => (
                      <ListItem key={idx} sx={{ py: 0.5, alignItems: 'flex-start' }}>
                        <ListItemText
                          primaryTypographyProps={{ variant: 'caption', component: 'div', sx: { whiteSpace: 'pre-wrap' } }}
                          primary={line}
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : null}
                {(postExportFontReport.errors || []).length > 150 ? (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                    Showing first 150 of {postExportFontReport.errors.length} lines.
                  </Typography>
                ) : null}
              </>
            ) : (
              <Typography variant="body2">Preparing report…</Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handlePostExportFontCancel}>Close without downloading</Button>
            <Button variant="contained" onClick={handlePostExportFontDownload}>
              Download .docx
            </Button>
          </DialogActions>
        </Dialog>

        {/* ADK Workflow Dialogs */}
        {selectedSectionForModification && (
          <ChangeRequestDialog
            open={changeRequestDialogOpen}
            onClose={() => {
              setChangeRequestDialogOpen(false);
              setSelectedSectionForModification(null);
            }}
            sectionId={selectedSectionForModification.sectionId}
            sectionNumber={selectedSectionForModification.sectionNumber}
            sectionTitle={selectedSectionForModification.sectionTitle}
            projectId={actualProjectId}
            studyId={resolveProjectTechnicalStudyKey(currentProject) || 'default'}
            onWorkflowInitiated={handleWorkflowInitiated}
          />
        )}

        <ApprovalDialog
          open={approvalDialogOpen}
          onClose={() => {
            setApprovalDialogOpen(false);
            setCurrentApprovalRequest(null);
            setWorkflowPlan(null);
          }}
          approvalRequestId={currentApprovalRequest?.approvalRequestId}
          plan={workflowPlan}
          onApproved={handleApprovalApproved}
          onRejected={handleApprovalRejected}
        />

        {/* Scroll to Top Button */}
        <ScrollToTop />

      </Box>
      </Box>
    </ThemeProvider>
  );
});

export default DocumentEditor;
